import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaBan,
  FaBuilding,
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaClipboardCheck,
  FaClock,
  FaCog,
  FaConciergeBell,
  FaFileExcel,
  FaFilePdf,
  FaFilter,
  FaDumbbell,
  FaLayerGroup,
  FaMoneyBillWave,
  FaReceipt,
  FaRedoAlt,
  FaSearch,
  FaStore,
  FaTimesCircle,
  FaUndoAlt,
  FaUsers,
  FaWallet,
} from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import { hasAccess } from './adminAccessControl';
import { normalizeOutletTypeValue, resolveOutletExperience } from './outletTypeOptions';
import { isValidReportCenterDateInput } from './reportCenterLinks';
import { formatReportDateTime, getReportDateTimestamp, parseReportDateTime } from './reportDateTime';
import './ReportCenter.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const POS_API_BASE_URL = process.env.REACT_APP_API_PRODUCT_URL || '';
const currencyFormatter = new Intl.NumberFormat('id-ID');
const decimalFormatter = new Intl.NumberFormat('id-ID', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const isSuccessResponse = value => value === 0 || value === '0';
const formatCurrency = value =>
  `Rp ${currencyFormatter.format(Number(value || 0))}`;
const formatCount = value => currencyFormatter.format(Number(value || 0));
const formatDecimalNumber = value => decimalFormatter.format(Number(value || 0));
const formatPercentage = value => `${decimalFormatter.format(Number(value || 0))}%`;
const buildAIFeedbackStateKey = (recommendationType, recommendationKey, action) =>
  `${String(recommendationType || '').trim().toLowerCase()}::${String(recommendationKey || '').trim()}::${String(action || '').trim().toLowerCase()}`;
const formatAIFeedbackActionLabel = value => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'accepted') {
    return 'Diterima';
  }
  if (normalized === 'ignored') {
    return 'Diabaikan';
  }
  if (normalized === 'campaign_used') {
    return 'Dipakai jadi campaign';
  }
  if (normalized === 'sales_improved') {
    return 'Berhasil meningkatkan sales';
  }
  return '';
};
const resolveAIMarginTone = safetyLabel => {
  const normalized = String(safetyLabel || '').trim().toLowerCase();
  if (normalized === 'aman') {
    return 'safe';
  }
  if (normalized === 'cukup') {
    return 'fair';
  }
  if (normalized === 'tipis') {
    return 'thin';
  }
  if (normalized === 'rugi') {
    return 'loss';
  }
  return 'review';
};
const isAIFeedbackOutcomeAction = value => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'campaign_used' || normalized === 'sales_improved';
};
const readAIFeedbackNumericInput = inputId => {
  const value = document.getElementById(inputId)?.value?.trim() || '';
  if (!value) {
    return 0;
  }
  const normalized = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(normalized) ? normalized : 0;
};

const EXPORT_CURRENCY_KEYWORDS = [
  'amount',
  'revenue',
  'sales',
  'debit',
  'credit',
  'balance',
  'saldo',
  'gross',
  'price',
  'cost',
  'ticket',
  'spread',
  'saving',
  'tax',
  'discount',
  'paid',
  'outstanding',
  'refund',
  'purchase',
  'nilai',
  'value',
  'acquisition',
  'depreciation',
  'depr',
];

const EXPORT_PERCENTAGE_KEYWORDS = [
  'percentage',
  'percent',
  'rate',
  'share',
];

const EXPORT_IDENTIFIER_KEYWORDS = [
  ' id',
  'id',
  'journalnumber',
  'journal number',
  'accountcode',
  'account code',
  'coa',
  'booking',
  'billno',
  'bill no',
  'code',
  'rank',
];

const getExportSemanticKey = (key, row) => {
  if (key === 'Value' && row?.Field) {
    return String(row.Field || '').trim().toLowerCase();
  }

  if (key === 'Value' && row?.Metric) {
    return String(row.Metric || '').trim().toLowerCase();
  }

  return String(key || '').trim().toLowerCase();
};

const isExportIdentifierField = semanticKey => {
  if (!semanticKey) {
    return false;
  }

  if (semanticKey.includes('count') || semanticKey.includes('qty') || semanticKey.includes('quantity')) {
    return false;
  }

  return EXPORT_IDENTIFIER_KEYWORDS.some(keyword => semanticKey.includes(keyword));
};

const isExportCurrencyField = semanticKey =>
  EXPORT_CURRENCY_KEYWORDS.some(keyword => semanticKey.includes(keyword));

const isExportPercentageField = semanticKey =>
  EXPORT_PERCENTAGE_KEYWORDS.some(keyword => semanticKey.includes(keyword));

const formatExportCellValue = (key, value, row) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return value;
  }

  const semanticKey = getExportSemanticKey(key, row);
  if (isExportIdentifierField(semanticKey)) {
    return String(value);
  }
  if (isExportCurrencyField(semanticKey)) {
    return formatCurrency(value);
  }
  if (isExportPercentageField(semanticKey)) {
    return formatPercentage(value);
  }
  if (Number.isInteger(value)) {
    return formatCount(value);
  }

  return formatDecimalNumber(value);
};

const formatExportRowsForDisplay = rows =>
  (Array.isArray(rows) ? rows : []).map(row =>
    Object.fromEntries(
      Object.entries(row || {}).map(([key, value]) => [key, formatExportCellValue(key, value, row)])
    )
  );

const formatDateInput = value => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTime = value => {
  return formatReportDateTime(value);
};

const REPORT_CENTER_TIME_ZONE = 'Asia/Makassar';

const getReportCenterToday = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORT_CENTER_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const readPart = type => parts.find(part => part.type === type)?.value || '';
  const year = Number(readPart('year'));
  const month = Number(readPart('month'));
  const day = Number(readPart('day'));

  if (!year || !month || !day) {
    return new Date();
  }

  return new Date(year, month - 1, day);
};

const formatDateLabel = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateRangeLabel = (startValue, endValue) => {
  if (startValue && endValue) {
    return `${formatDateLabel(startValue)} - ${formatDateLabel(endValue)}`;
  }
  if (startValue) {
    return `Mulai ${formatDateLabel(startValue)}`;
  }
  if (endValue) {
    return `Sampai ${formatDateLabel(endValue)}`;
  }
  return 'Semua periode';
};

const isBalancedAmount = (debitValue, creditValue) =>
  Math.abs(Number(debitValue || 0) - Number(creditValue || 0)) < 0.005;

const formatAccountingSourceSystemLabel = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'fitness':
      return 'Fitness';
    case 'pos':
      return 'POS';
    case 'manual':
      return 'Manual';
    default:
      return 'Unknown';
  }
};

const formatAccountingBusinessUnitLabel = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'fitness':
      return 'Fitness';
    case 'pos':
      return 'POS';
    default:
      return 'Unassigned';
  }
};

const formatAccountingPostingStatusLabel = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'pending':
      return 'Pending';
    default:
      return 'Posted';
  }
};

const formatAccountingSourceTypeLabel = value => {
  switch (String(value || '').trim().toLowerCase()) {
    case 'membership_payment':
      return 'Pembayaran Membership';
    case 'pos_sale':
      return 'Penjualan POS';
    case 'pos_cogs':
      return 'HPP POS';
    case 'pos_refund':
      return 'Refund POS';
    case 'pos_purchase':
      return 'Pembelian POS';
    case 'pos_operational_expense':
      return 'Biaya Operasional POS';
    case 'pos_vendor_bill':
      return 'Hutang Vendor POS';
    case 'pos_vendor_payment':
      return 'Pembayaran Vendor POS';
    case 'pos_stock_adjustment':
      return 'Adjustment Stok POS';
    case 'pos_stock_opname':
      return 'Stock Opname POS';
    default:
      return value || 'Unknown type';
  }
};

const formatAccountingJournalNumber = (journalId, occurredAt) => {
  const numericId = Number(journalId || 0);
  if (!numericId) {
    return '-';
  }

  const parsed = parseReportDateTime(occurredAt);
  const periodKey = parsed && !Number.isNaN(parsed.getTime())
    ? `${parsed.getFullYear()}${String(parsed.getMonth() + 1).padStart(2, '0')}${String(parsed.getDate()).padStart(2, '0')}`
    : '00000000';

  return `JU/${periodKey}/${String(numericId).padStart(6, '0')}`;
};

const sortAccountingJournalLinesForDisplay = lines => {
  const scopedLines = Array.isArray(lines) ? [...lines] : [];
  const sideOrder = {
    debit: 0,
    credit: 1,
    mixed: 2,
    empty: 3,
  };

  return scopedLines.sort((left, right) => {
    const sideDiff = (sideOrder[getAccountingLineSideKey(left)] ?? 99) - (sideOrder[getAccountingLineSideKey(right)] ?? 99);
    if (sideDiff !== 0) {
      return sideDiff;
    }

    return String(left?.account_id || left?.account_name || '').localeCompare(String(right?.account_id || right?.account_name || ''));
  });
};

const getAccountingLineSideKey = line => {
  const debit = Number(line?.debit || 0);
  const credit = Number(line?.credit || 0);

  if (debit > 0 && credit <= 0) {
    return 'debit';
  }
  if (credit > 0 && debit <= 0) {
    return 'credit';
  }
  if (debit > 0 && credit > 0) {
    return 'mixed';
  }
  return 'empty';
};

const formatAccountingLineSideLabel = line => {
  switch (getAccountingLineSideKey(line)) {
    case 'debit':
      return 'Debit';
    case 'credit':
      return 'Credit';
    case 'mixed':
      return 'Debit & Credit';
    default:
      return 'Tanpa nilai';
  }
};

const getAccountingSideLines = (lines, side) =>
  (Array.isArray(lines) ? lines : []).filter(line => {
    if (side === 'debit') {
      return Number(line?.debit || 0) > 0;
    }
    if (side === 'credit') {
      return Number(line?.credit || 0) > 0;
    }
    return false;
  });

const buildAccountingSidePreview = (lines, side) => {
  const scopedLines = getAccountingSideLines(lines, side);
  if (!scopedLines.length) {
    return side === 'debit' ? 'Tidak ada akun debit' : 'Tidak ada akun credit';
  }

  const labels = scopedLines
    .map(line => line.account_name || line.account_id || '')
    .filter(Boolean)
    .slice(0, 2);

  if (!labels.length) {
    return side === 'debit' ? 'Akun debit tersedia' : 'Akun credit tersedia';
  }

  return scopedLines.length > 2
    ? `${labels.join(' • ')} +${scopedLines.length - 2} akun`
    : labels.join(' • ');
};

const formatCashierLabel = row => {
  if (row?.cashier_name) {
    return row.cashier_name;
  }
  if (row?.cashier_id) {
    return `Cashier #${row.cashier_id}`;
  }
  return 'Cashier belum tercatat';
};

const getPOSTimeSlotKey = value => {
  if (!value) {
    return 'unknown';
  }

  const parsed = parseReportDateTime(value);
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return 'unknown';
  }

  const hour = parsed.getHours();
  if (hour >= 5 && hour < 11) {
    return 'morning';
  }
  if (hour >= 11 && hour < 15) {
    return 'midday';
  }
  if (hour >= 15 && hour < 19) {
    return 'afternoon';
  }
  return 'evening';
};

const formatPOSTimeSlotLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'morning':
      return 'Pagi';
    case 'midday':
      return 'Siang';
    case 'afternoon':
      return 'Sore';
    case 'evening':
      return 'Malam';
    default:
      return 'Waktu tidak tercatat';
  }
};

const formatDayValue = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${formatCount(value)} hari`;
};

const formatMonthValue = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${formatDecimalNumber(value)} bulan`;
};

const addDaysToDateInput = (value, days) => {
  if (!value) {
    return '';
  }

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  parsed.setDate(parsed.getDate() + days);
  return formatDateInput(parsed);
};

const formatPTClientTypeLabel = value => {
  if (value === 'member') {
    return 'Member';
  }
  if (value === 'non_member') {
    return 'Non Member';
  }
  return 'Tidak diketahui';
};

const formatPTPaymentStatusLabel = value => {
  switch (value) {
    case 'paid':
      return 'Paid';
    case 'partial_paid':
      return 'Partial Paid';
    case 'pending':
      return 'Pending';
    case 'cancelled':
      return 'Cancelled';
    case 'failed':
      return 'Failed';
    case 'refunded':
      return 'Refunded';
    default:
      return 'Unknown';
  }
};

const formatPTContractStatusLabel = value => {
  switch (value) {
    case 'active':
      return 'Active';
    case 'frozen':
      return 'Frozen';
    case 'completed':
      return 'Completed';
    case 'expired':
      return 'Expired';
    case 'cancelled':
      return 'Cancelled';
    case 'not_created':
      return 'Not Created';
    default:
      return 'Unknown';
  }
};

const formatPTProductTypeLabel = value => {
  switch (value) {
    case 'single_session':
      return 'Single Session';
    case 'package':
      return 'Package';
    default:
      return value || '-';
  }
};

const formatPTBookingStatusLabel = value => {
  switch (value) {
    case 'requested':
      return 'Requested';
    case 'confirmed':
      return 'Confirmed';
    case 'rescheduled':
      return 'Rescheduled';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Unknown';
  }
};

const formatPTAttendanceStatusLabel = value => {
  switch (value) {
    case 'pending':
      return 'Pending';
    case 'checked_in':
      return 'Checked In';
    case 'completed':
      return 'Completed';
    case 'no_show':
      return 'No Show';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Unknown';
  }
};

const formatPurchasePaymentStatusLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'open':
      return 'Open';
    case 'partial_paid':
      return 'Partial Paid';
    case 'paid':
      return 'Paid';
    default:
      return value || '-';
  }
};

const formatVendorBillTypeLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'inventory':
      return 'Inventory';
    case 'expense':
      return 'Expense';
    case 'asset':
      return 'Asset';
    case 'rent':
      return 'Rent';
    default:
      return value || '-';
  }
};

const formatVendorAgingBucketLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'current':
      return 'Belum jatuh tempo';
    case 'due_today':
      return 'Jatuh tempo hari ini';
    case 'overdue_1_30':
      return 'Overdue 1-30 hari';
    case 'overdue_31_60':
      return 'Overdue 31-60 hari';
    case 'overdue_61_90':
      return 'Overdue 61-90 hari';
    case 'overdue_over_90':
      return 'Overdue > 90 hari';
    default:
      return value || '-';
  }
};

const formatVendorDueStatusLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'current':
      return 'Belum jatuh tempo';
    case 'due_today':
      return 'Jatuh tempo hari ini';
    case 'overdue':
      return 'Lewat jatuh tempo';
    default:
      return value || '-';
  }
};

const formatAccountingFinanceDocumentScopeLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'operational':
      return 'Operational';
    case 'asset':
      return 'Asset';
    default:
      return value || '-';
  }
};

const formatAccountingFinanceStatusLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'unpaid':
      return 'Unpaid';
    case 'partial_paid':
      return 'Partial Paid';
    case 'paid':
      return 'Paid';
    case 'cancelled':
      return 'Cancelled';
    default:
      return value || '-';
  }
};

const formatAccountingFinanceAgingBucketLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'current':
      return 'Current';
    case '1-30':
      return '1-30 hari';
    case '31-60':
      return '31-60 hari';
    case '>60':
      return '> 60 hari';
    default:
      return value || '-';
  }
};

const formatAccountingFinanceDueStatusLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'current':
      return 'Belum jatuh tempo';
    case 'overdue':
      return 'Lewat jatuh tempo';
    case 'no_due_date':
      return 'Tanpa jatuh tempo';
    default:
      return value || '-';
  }
};

const formatAccountingAssetCategoryLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'building':
      return 'Bangunan';
    case 'building_improvement':
      return 'Renovasi / Improvement';
    case 'gym_equipment':
      return 'Alat gym';
    case 'furniture_fixture':
      return 'Furniture / Fixture';
    case 'electronic_equipment':
      return 'Elektronik / Mesin';
    case 'vehicle':
      return 'Kendaraan';
    case 'other_asset':
      return 'Aset lain-lain';
    default:
      return value || '-';
  }
};

const formatAccountingDepreciationMethodLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'straight_line':
    case '':
      return 'Straight line';
    default:
      return value || '-';
  }
};

const formatInventoryMovementTypeLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'purchase':
      return 'Purchase';
    case 'sale':
      return 'Sale';
    case 'refund':
      return 'Refund';
    case 'recipe_consumption':
      return 'BOM Consumption';
    case 'adjustment_in':
      return 'Adjustment In';
    case 'adjustment_out':
      return 'Adjustment Out';
    case 'opname_gain':
      return 'Opname Gain';
    case 'opname_loss':
      return 'Opname Loss';
    default:
      return value || '-';
  }
};

const formatInventoryReferenceLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'purchase':
      return 'Purchase';
    case 'sale':
      return 'Sale';
    case 'refund':
      return 'Refund';
    case 'stock_adjustment':
      return 'Stock Adjustment';
    case 'stock_opname':
      return 'Stock Opname';
    default:
      return value || '-';
  }
};

const formatStockOpnameVarianceLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'gain':
      return 'Surplus';
    case 'loss':
      return 'Shortage';
    case 'matched':
      return 'Sesuai Sistem';
    case 'variance_only':
      return 'Ada Selisih';
    default:
      return value || '-';
  }
};

const formatProductItemTypeLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'raw_material':
      return 'Raw Material';
    case 'resale_item':
      return 'Resale Item';
    case 'finished_good':
      return 'Finished Good';
    case 'service':
      return 'Service';
    default:
      return value || '-';
  }
};

const formatVendorPriceComparisonModeLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'latest_price':
      return 'Harga terakhir';
    case 'lowest_price':
      return 'Harga terendah';
    default:
      return 'Rata-rata harga';
  }
};

const formatPOSApprovalRequestTypeLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'refund':
      return 'Refund';
    case 'void':
      return 'Void';
    default:
      return value || '-';
  }
};

const formatPOSApprovalStatusLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'approved':
      return 'Approved';
    case 'pending':
      return 'Pending';
    case 'rejected':
      return 'Rejected';
    default:
      return value || '-';
  }
};

const formatPOSTransactionDocumentStatusLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'posted':
      return 'Posted';
    case 'voided':
      return 'Voided';
    case 'draft':
      return 'Draft';
    case 'cancelled':
      return 'Cancelled';
    default:
      return value || '-';
  }
};

const getPOSTransactionGrossSales = transaction =>
  Number(transaction?.subtotal || transaction?.total || 0);

const getPOSTransactionServiceAmount = transaction =>
  Number(transaction?.service || 0);

const getPOSTransactionFinalTotal = transaction =>
  Number(transaction?.grand_total || 0) ||
  getPOSTransactionGrossSales(transaction) -
    Number(transaction?.discount || 0) +
    getPOSTransactionServiceAmount(transaction) +
    Number(transaction?.tax || 0);

const formatVendorPriceRiskLabel = value => {
  switch (String(value || '').toLowerCase()) {
    case 'single_source':
      return 'Single source';
    case 'price_gap_high':
      return 'Price gap high';
    case 'stale_data':
      return 'Stale data';
    case 'stable_best_vendor':
      return 'Stable best vendor';
    default:
      return value || '-';
  }
};

const POS_SALES_PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'transfer', label: 'Transfer / Bank' },
  { value: 'qris', label: 'QRIS' },
];

const POS_PURCHASE_PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'transfer', label: 'Transfer / Bank' },
  { value: 'qris', label: 'QRIS' },
  { value: 'credit', label: 'Tempo / Hutang Vendor' },
];

const POS_VENDOR_BILL_TYPE_OPTIONS = [
  { value: 'inventory', label: 'Inventory' },
  { value: 'expense', label: 'Expense' },
  { value: 'asset', label: 'Asset' },
  { value: 'rent', label: 'Rent' },
];

const POS_VENDOR_BILL_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'partial_paid', label: 'Partial Paid' },
  { value: 'paid', label: 'Paid' },
];

const POS_VENDOR_AGING_BUCKET_OPTIONS = [
  { value: 'current', label: 'Belum jatuh tempo' },
  { value: 'due_today', label: 'Jatuh tempo hari ini' },
  { value: 'overdue_1_30', label: 'Overdue 1-30 hari' },
  { value: 'overdue_31_60', label: 'Overdue 31-60 hari' },
  { value: 'overdue_61_90', label: 'Overdue 61-90 hari' },
  { value: 'overdue_over_90', label: 'Overdue > 90 hari' },
];

const ACCOUNTING_FINANCE_SCOPE_OPTIONS = [
  { value: '', label: 'Semua scope' },
  { value: 'operational', label: 'Operational' },
  { value: 'asset', label: 'Asset' },
];

const ACCOUNTING_FINANCE_STATUS_OPTIONS = [
  { value: '', label: 'Semua status aktif' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial_paid', label: 'Partial Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
];

const ACCOUNTING_FINANCE_AGING_BUCKET_OPTIONS = [
  { value: '', label: 'Semua bucket' },
  { value: 'current', label: 'Current' },
  { value: '1-30', label: '1-30 hari' },
  { value: '31-60', label: '31-60 hari' },
  { value: '>60', label: '> 60 hari' },
];

const POS_STOCK_MOVEMENT_TYPE_OPTIONS = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'sale', label: 'Sale' },
  { value: 'refund', label: 'Refund' },
  { value: 'recipe_consumption', label: 'BOM Consumption' },
  { value: 'adjustment_in', label: 'Adjustment In' },
  { value: 'adjustment_out', label: 'Adjustment Out' },
  { value: 'opname_gain', label: 'Opname Gain' },
  { value: 'opname_loss', label: 'Opname Loss' },
];

const POS_STOCK_REFERENCE_TYPE_OPTIONS = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'sale', label: 'Sale' },
  { value: 'refund', label: 'Refund' },
  { value: 'stock_adjustment', label: 'Stock Adjustment' },
  { value: 'stock_opname', label: 'Stock Opname' },
];

const POS_STOCK_OPNAME_VARIANCE_OPTIONS = [
  { value: 'variance_only', label: 'Ada selisih' },
  { value: 'gain', label: 'Surplus' },
  { value: 'loss', label: 'Shortage' },
  { value: 'matched', label: 'Sesuai sistem' },
  { value: 'all', label: 'Semua baris opname' },
];

const POS_VENDOR_PRICE_COMPARISON_MODE_OPTIONS = [
  { value: 'average_price', label: 'Rata-rata harga' },
  { value: 'latest_price', label: 'Harga terakhir' },
  { value: 'lowest_price', label: 'Harga terendah' },
];

const POS_VENDOR_PRICE_ITEM_TYPE_OPTIONS = [
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'resale_item', label: 'Resale Item' },
  { value: 'finished_good', label: 'Finished Good' },
];

const POS_APPROVAL_REQUEST_TYPE_OPTIONS = [
  { value: 'refund', label: 'Refund' },
  { value: 'void', label: 'Void' },
];

const POS_APPROVAL_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const parseJsonResponse = async response => {
  const raw = await response.text();
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return {
      error: 1,
      message: raw,
    };
  }
};

const requestMembershipSalesReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/membership-sales`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Membership Sales Report belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Membership Sales Report');
  }
  return data;
};

const requestMembershipGrowthAssistant = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/membership-growth-ai`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint AI membership growth belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat AI membership growth');
  }
  if (!data?.data || !Array.isArray(data?.data?.upsell_suggestions) || !Array.isArray(data?.data?.promo_candidates)) {
    throw new Error('Format AI membership growth tidak valid');
  }
  return data.data;
};

const requestMembershipGrowthFeedback = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/membership-growth-ai/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint feedback AI membership belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal menyimpan feedback AI membership');
  }
  return data?.data || null;
};

const requestStaffTransactionSummaryReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/staff-transaction-summary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Staff Transaction Summary belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Staff Transaction Summary');
  }
  return data;
};

const requestMemberActivityReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/member-activity`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Member Activity Report belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Member Activity Report');
  }
  return data;
};

const requestCheckinReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/check-in`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Check-in Report belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Check-in Report');
  }
  return data;
};

const requestPTSalesReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/pt-sales`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Private Session Sales Report belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Private Session Sales Report');
  }
  return data;
};

const requestPTSessionReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/pt-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Private Session Report belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Private Session Report');
  }
  return data;
};

const requestPTTrainerPerformanceReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}business-report/pt-trainer-performance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Private Session Staff Performance Report belum aktif di backend. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Private Session Staff Performance Report');
  }
  return data;
};

const requestAccountingJournalReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/journal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Journal Report belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Journal Report');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format Journal Report tidak valid');
  }
  return data.data;
};

const requestAccountingGeneralLedgerReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/general-ledger`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint General Ledger belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat General Ledger');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format General Ledger tidak valid');
  }
  return data.data;
};

const requestAccountingTrialBalanceReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/trial-balance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Trial Balance belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Trial Balance');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format Trial Balance tidak valid');
  }
  return data.data;
};

const requestAccountingProfitLossReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/profit-loss`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Profit & Loss belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Profit & Loss');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format Profit & Loss tidak valid');
  }
  return data.data;
};

const requestAccountingBalanceSheetReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/balance-sheet`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Balance Sheet belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Balance Sheet');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format Balance Sheet tidak valid');
  }
  return data.data;
};

const requestAccountingCashFlowReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/cash-flow`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Cash Flow belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Cash Flow');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format Cash Flow tidak valid');
  }
  return data.data;
};

const requestAccountingAccountsPayableAgingReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/accounts-payable-aging`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint AP Aging belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat AP Aging');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format AP Aging tidak valid');
  }
  return data.data;
};

const requestAccountingExpenseBreakdownReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/expense-breakdown`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Expense Breakdown belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Expense Breakdown');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format Expense Breakdown tidak valid');
  }
  return data.data;
};

const requestAccountingAssetRegisterReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/asset-register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Asset Register belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Asset Register');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format Asset Register tidak valid');
  }
  return data.data;
};

const requestAccountingDepreciationScheduleReport = async (token, payload) => {
  const response = await fetch(`${API_BASE_URL}accounting/report/depreciation-schedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok || !isSuccessResponse(data?.error)) {
    if (response.status === 404) {
      throw new Error('Endpoint Depreciation Schedule belum aktif di backend accounting. Restart backend lalu buka ulang halaman ini.');
    }
    throw new Error(data?.message || 'Gagal memuat Depreciation Schedule');
  }
  if (!data?.data || !Array.isArray(data?.data?.results)) {
    throw new Error('Format Depreciation Schedule tidak valid');
  }
  return data.data;
};

const requestPOSSalesReport = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', addDaysToDateInput(payload.dateTo, 1));
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.payment_method) {
    query.set('payment_method', payload.payment_method);
  }

  const response = await fetch(`${POS_API_BASE_URL}api/transactions/salesreport?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint POS Sales Report belum aktif di backend POS. Cek route backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat POS Sales Report');
  }
  if (!Array.isArray(data)) {
    throw new Error(data?.error || data?.message || 'Format POS Sales Report tidak valid');
  }
  return data;
};

const requestPOSPricingAssistant = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', payload.dateTo);
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.payment_method) {
    query.set('payment_method', payload.payment_method);
  }
  if (payload?.product_id) {
    query.set('product_id', String(payload.product_id));
  }

  const response = await fetch(`${POS_API_BASE_URL}api/transactions/ai-pricing-assistant?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint AI pricing POS belum aktif di backend POS. Restart backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat AI pricing POS');
  }
  if (!data || !Array.isArray(data?.bundling_recommendations) || !Array.isArray(data?.promo_candidates)) {
    throw new Error(data?.error || data?.message || 'Format AI pricing POS tidak valid');
  }
  return data;
};

const requestPOSPricingFeedback = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const response = await fetch(`${POS_API_BASE_URL}api/transactions/ai-pricing-assistant/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint feedback AI pricing POS belum aktif di backend POS. Restart backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal menyimpan feedback AI pricing POS');
  }
  return data?.data || null;
};

const requestPOSPurchaseReport = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', payload.dateTo);
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.payment_method) {
    query.set('payment_method', payload.payment_method);
  }
  if (payload?.payment_status) {
    query.set('payment_status', payload.payment_status);
  }
  if (payload?.supplier_name) {
    query.set('supplier', payload.supplier_name);
  }
  if (payload?.search) {
    query.set('search', payload.search);
  }

  const response = await fetch(`${POS_API_BASE_URL}api/purchase/reports?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint Purchase Report belum aktif di backend POS. Cek route backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat Purchase Report');
  }
  if (!Array.isArray(data?.results)) {
    throw new Error(data?.error || data?.message || 'Format Purchase Report tidak valid');
  }
  return data;
};

const requestPOSVendorAgingReport = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', payload.dateTo);
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.vendor_name) {
    query.set('vendor', payload.vendor_name);
  }
  if (payload?.vendor_bill_type) {
    query.set('bill_type', payload.vendor_bill_type);
  }
  if (payload?.vendor_bill_status) {
    query.set('status', payload.vendor_bill_status);
  }
  if (payload?.aging_bucket) {
    query.set('aging_bucket', payload.aging_bucket);
  }
  if (payload?.search) {
    query.set('search', payload.search);
  }

  const response = await fetch(`${POS_API_BASE_URL}api/vendor-bills/reports/aging?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint Vendor Aging belum aktif di backend POS. Cek route backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat Vendor Aging');
  }
  if (!Array.isArray(data?.results)) {
    throw new Error(data?.error || data?.message || 'Format Vendor Aging tidak valid');
  }
  return data;
};

const requestPOSStockMovementReport = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', payload.dateTo);
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.product_id) {
    query.set('product_id', String(payload.product_id));
  }
  if (payload?.movement_type) {
    query.set('movement_type', payload.movement_type);
  }
  if (payload?.reference_type) {
    query.set('reference_type', payload.reference_type);
  }
  if (payload?.search) {
    query.set('search', payload.search);
  }

  const response = await fetch(`${POS_API_BASE_URL}api/inventory-ledger/reports/stock-movement?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint Stock Movement belum aktif di backend POS. Cek route backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat Stock Movement');
  }
  if (!Array.isArray(data?.results)) {
    throw new Error(data?.error || data?.message || 'Format Stock Movement tidak valid');
  }
  return data;
};

const requestPOSBOMConsumptionReport = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', payload.dateTo);
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.product_id) {
    query.set('product_id', String(payload.product_id));
  }
  if (payload?.ingredient_product_id) {
    query.set('ingredient_product_id', String(payload.ingredient_product_id));
  }
  if (payload?.payment_method) {
    query.set('payment_method', payload.payment_method);
  }
  if (payload?.search) {
    query.set('search', payload.search);
  }

  const response = await fetch(`${POS_API_BASE_URL}api/inventory-ledger/reports/bom-consumption?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint BOM Consumption belum aktif di backend POS. Cek route backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat BOM Consumption');
  }
  if (!Array.isArray(data?.results)) {
    throw new Error(data?.error || data?.message || 'Format BOM Consumption tidak valid');
  }
  return data;
};

const requestPOSStockOpnameVarianceReport = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', payload.dateTo);
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.product_id) {
    query.set('product_id', String(payload.product_id));
  }
  if (payload?.variance_type) {
    query.set('variance_type', payload.variance_type);
  }
  if (payload?.search) {
    query.set('search', payload.search);
  }

  const response = await fetch(`${POS_API_BASE_URL}api/stock-opnames/reports/variance?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint Stock Opname Variance belum aktif di backend POS. Cek route backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat Stock Opname Variance');
  }
  if (!Array.isArray(data?.results)) {
    throw new Error(data?.error || data?.message || 'Format Stock Opname Variance tidak valid');
  }
  return data;
};

const requestPOSVendorPriceComparisonReport = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', payload.dateTo);
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.category_id) {
    query.set('category_id', String(payload.category_id));
  }
  if (payload?.product_id) {
    query.set('product_id', String(payload.product_id));
  }
  if (payload?.vendor_name) {
    query.set('vendor', payload.vendor_name);
  }
  if (payload?.item_type) {
    query.set('item_type', payload.item_type);
  }
  if (payload?.payment_method) {
    query.set('payment_method', payload.payment_method);
  }
  if (payload?.purchase_payment_status) {
    query.set('payment_status', payload.purchase_payment_status);
  }
  if (payload?.comparison_mode) {
    query.set('comparison_mode', payload.comparison_mode);
  }
  if (payload?.min_purchase_count) {
    query.set('min_purchase_count', String(payload.min_purchase_count));
  }
  if (payload?.search) {
    query.set('search', payload.search);
  }

  const response = await fetch(`${POS_API_BASE_URL}api/purchase/reports/vendor-price-comparison?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint Vendor Price Comparison belum aktif di backend POS. Cek route backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat Vendor Price Comparison');
  }
  if (!Array.isArray(data?.results)) {
    throw new Error(data?.error || data?.message || 'Format Vendor Price Comparison tidak valid');
  }
  return data;
};

const requestPOSRefundVoidReport = async (token, payload) => {
  if (!POS_API_BASE_URL) {
    throw new Error('REACT_APP_API_PRODUCT_URL belum diset. Hubungkan adminfitness ke backend POS terlebih dahulu.');
  }

  const query = new URLSearchParams();
  if (payload?.dateFrom) {
    query.set('start_date', payload.dateFrom);
  }
  if (payload?.dateTo) {
    query.set('end_date', payload.dateTo);
  }
  if (payload?.outlet) {
    query.set('outlet_id', String(payload.outlet));
  }
  if (payload?.request_type) {
    query.set('request_type', payload.request_type);
  }
  if (payload?.approval_status) {
    query.set('status', payload.approval_status);
  }
  if (payload?.search) {
    query.set('search', payload.search);
  }

  const response = await fetch(`${POS_API_BASE_URL}api/approval-requests/?${query.toString()}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await parseJsonResponse(response);
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Endpoint Refund / Void Report belum aktif di backend POS. Cek route backend POS lalu refresh halaman ini.');
    }
    throw new Error(data?.error || data?.message || 'Gagal memuat Refund / Void Report');
  }
  if (!Array.isArray(data?.results)) {
    throw new Error(data?.error || data?.message || 'Format Refund / Void Report tidak valid');
  }
  return data;
};

const buildDefaultRange = preset => {
  const reportToday = getReportCenterToday();
  const current = new Date(reportToday.getFullYear(), reportToday.getMonth(), reportToday.getDate());

  switch (preset) {
    case 'today':
      return { dateFrom: formatDateInput(current), dateTo: formatDateInput(current) };
    case 'this-month': {
      const start = new Date(current.getFullYear(), current.getMonth(), 1);
      return { dateFrom: formatDateInput(start), dateTo: formatDateInput(current) };
    }
    case 'last-7-days': {
      const start = new Date(current);
      start.setDate(start.getDate() - 6);
      return { dateFrom: formatDateInput(start), dateTo: formatDateInput(current) };
    }
    case 'last-30-days':
    default: {
      const start = new Date(current);
      start.setDate(start.getDate() - 29);
      return { dateFrom: formatDateInput(start), dateTo: formatDateInput(current) };
    }
  }
};

const resolveQuickRangePreset = (dateFrom, dateTo) =>
  ['today', 'this-month', 'last-7-days', 'last-30-days'].find(preset => {
    const range = buildDefaultRange(preset);
    return range.dateFrom === dateFrom && range.dateTo === dateTo;
  }) || 'custom';

const buildReportFilterState = ({
  scopedOutletId = '',
  queryOutletId = '',
  dateFrom = '',
  dateTo = '',
} = {}) => ({
  outlet: scopedOutletId || queryOutletId || '',
  category_id: '',
  membership_id: '',
  pt_user_id: '',
  product_id: '',
  client_id: '',
  ingredient_product_id: '',
  approval_request_type: '',
  approval_status: '',
  vendor_name: '',
  vendor_bill_type: '',
  vendor_bill_status: '',
  aging_bucket: '',
  movement_type: '',
  reference_type: '',
  variance_type: 'variance_only',
  item_type: '',
  accounting_document_scope: '',
  source_system: '',
  business_unit: '',
  accounting_posting_status: '',
  comparison_mode: 'average_price',
  min_purchase_count: '1',
  client_type: '',
  payment_method: '',
  purchase_payment_status: '',
  status: '',
  pt_payment_status: '',
  contract_status: '',
  booking_status: '',
  attendance_status: '',
  lifecycle: '',
  activity: '',
  search: '',
  dateFrom,
  dateTo,
});

const normalizeFilterSnapshot = value => ({
  outlet: String(value?.outlet || ''),
  category_id: String(value?.category_id || ''),
  membership_id: String(value?.membership_id || ''),
  pt_user_id: String(value?.pt_user_id || ''),
  product_id: String(value?.product_id || ''),
  client_id: String(value?.client_id || ''),
  approval_request_type: String(value?.approval_request_type || ''),
  approval_status: String(value?.approval_status || ''),
  ingredient_product_id: String(value?.ingredient_product_id || ''),
  vendor_name: String(value?.vendor_name || ''),
  vendor_bill_type: String(value?.vendor_bill_type || ''),
  vendor_bill_status: String(value?.vendor_bill_status || ''),
  aging_bucket: String(value?.aging_bucket || ''),
  movement_type: String(value?.movement_type || ''),
  reference_type: String(value?.reference_type || ''),
  variance_type: String(value?.variance_type || ''),
  item_type: String(value?.item_type || ''),
  accounting_document_scope: String(value?.accounting_document_scope || ''),
  source_system: String(value?.source_system || ''),
  business_unit: String(value?.business_unit || ''),
  accounting_posting_status: String(value?.accounting_posting_status || ''),
  comparison_mode: String(value?.comparison_mode || ''),
  min_purchase_count: String(value?.min_purchase_count || ''),
  client_type: String(value?.client_type || ''),
  payment_method: String(value?.payment_method || ''),
  purchase_payment_status: String(value?.purchase_payment_status || ''),
  status: String(value?.status ?? ''),
  pt_payment_status: String(value?.pt_payment_status || ''),
  contract_status: String(value?.contract_status || ''),
  booking_status: String(value?.booking_status || ''),
  attendance_status: String(value?.attendance_status || ''),
  lifecycle: String(value?.lifecycle || ''),
  activity: String(value?.activity || ''),
  search: String(value?.search || '').trim(),
  dateFrom: String(value?.dateFrom || ''),
  dateTo: String(value?.dateTo || ''),
});

const REPORT_TABS = [
  {
    key: 'business',
    label: 'Business',
    copy: 'Membership, visit, private session, dan performa staff.',
    icon: FaChartLine,
  },
  {
    key: 'pos',
    label: 'POS',
    copy: 'Penjualan produk, stok, refund, dan vendor.',
    icon: FaClipboardCheck,
  },
  {
    key: 'accounting',
    label: 'Accounting',
    copy: 'Jurnal, neraca, laba rugi, arus kas, dan hutang.',
    icon: FaReceipt,
  },
];

const STAFF_TRANSACTION_SUMMARY_REPORT = {
  key: 'staff-transaction-summary',
  label: 'Staff Transaction Summary',
  copy: 'Ringkasan transaksi staf aktif yang menggabungkan penjualan membership, class sale, PT sales, dan POS sales per periode, lengkap dengan info closing shift POS.',
  available: true,
};

const BUSINESS_REPORTS = [
  {
    key: 'membership-sales',
    label: 'Membership Sales',
    copy: 'Report utama untuk nilai penjualan membership, paket, status bayar, dan petugas.',
    available: true,
  },
  {
    key: 'member-activity',
    label: 'Member Activity',
    copy: 'Aktivitas kunjungan, renewal, risiko churn, dan engagement member.',
    available: true,
  },
  {
    key: 'check-in',
    label: 'Check-in',
    copy: 'Ringkasan check-in dan pola kunjungan outlet per periode.',
    available: true,
  },
  {
    key: 'pt-sales',
    label: 'Private Session Sales',
    copy: 'Penjualan layanan private session dan histori payment per client.',
    available: true,
  },
  {
    key: 'pt-session',
    label: 'Private Session',
    copy: 'Booking, sesi selesai, sisa sesi, dan penggunaan paket layanan.',
    available: true,
  },
  {
    key: 'pt-trainer',
    label: 'Private Session Staff Performance',
    copy: 'Produktivitas staff layanan berdasarkan sesi selesai dan revenue terkait.',
    available: true,
  },
];

const POS_REPORTS = [
  {
    key: 'sales-summary',
    label: 'Sales Summary',
    copy: 'Omzet POS, payment mix, produk terjual, dan ritme transaksi outlet.',
    available: true,
  },
  {
    key: 'cashier-sales-summary',
    label: 'Cashier Sales Summary',
    copy: 'Performa kasir berdasarkan omzet, transaksi, average ticket, dan metode bayar dominan.',
    available: true,
  },
  {
    key: 'product-sales-report',
    label: 'Product Sales Report',
    copy: 'Produk paling laku, qty terjual, harga jual rata-rata, dan kontribusi revenue per produk.',
    available: true,
  },
  {
    key: 'refund-void-report',
    label: 'Pembatalan Transaksi',
    copy: 'Audit refund dan void berdasarkan status approval, pengaju, reviewer, nominal pembatalan, dan sinkronisasi refund.',
    available: true,
  },
  {
    key: 'purchase-report',
    label: 'Purchase Report',
    copy: 'Pembelian stok, metode bayar, hutang vendor otomatis, dan audit dokumen pembelian.',
    available: true,
  },
  {
    key: 'vendor-aging',
    label: 'Vendor Aging',
    copy: 'Outstanding hutang vendor berdasarkan umur dan jatuh tempo.',
    available: true,
  },
  {
    key: 'stock-movement',
    label: 'Stock Movement',
    copy: 'Mutasi stok dari purchase, sale, refund, adjustment, opname, dan BOM.',
    available: true,
  },
  {
    key: 'stock-opname-variance',
    label: 'Stock Opname Variance',
    copy: 'Audit selisih stok hasil opname per produk, nilai variance, dan outlet terdampak.',
    available: true,
  },
  {
    key: 'bom-consumption-report',
    label: 'BOM Consumption Report',
    copy: 'Konsumsi bahan baku dari penjualan finished good berdasarkan recipe/BOM dan nilai cost per transaksi.',
    available: true,
  },
  {
    key: 'vendor-price-comparison',
    label: 'Vendor Price Comparison',
    copy: 'Bandingkan vendor termurah per product, spread harga, dan potensi penghematan pengadaan.',
    available: true,
  },
];

const ACCOUNTING_REPORTS = [
  {
    key: 'journal-report',
    label: 'Journal Report',
    copy: 'Audit jurnal pusat berdasarkan outlet, periode, source system, business unit, status posting, dan line account.',
    available: true,
  },
  {
    key: 'general-ledger',
    label: 'General Ledger',
    copy: 'Mutasi saldo per akun lengkap dengan saldo awal, debit, credit, saldo akhir, dan detail ledger per akun.',
    available: true,
  },
  {
    key: 'trial-balance',
    label: 'Trial Balance',
    copy: 'Saldo awal, mutasi debit-credit, dan saldo akhir semua akun untuk verifikasi keseimbangan ledger pusat.',
    available: true,
  },
  {
    key: 'profit-loss',
    label: 'Profit & Loss',
    copy: 'Ringkasan revenue, expense, dan net profit per akun untuk periode yang dipilih.',
    available: true,
  },
  {
    key: 'balance-sheet',
    label: 'Balance Sheet',
    copy: 'Posisi asset, liability, dan equity berbasis saldo akhir ledger pusat.',
    available: true,
  },
  {
    key: 'cash-flow',
    label: 'Cash Flow',
    copy: 'Arus kas operasi, investasi, pendanaan, dan transfer internal berdasarkan mutasi akun kas/bank di ledger pusat.',
    available: true,
  },
  {
    key: 'accounts-payable-aging',
    label: 'AP Aging',
    copy: 'Outstanding hutang operasional dan aset berdasarkan bucket umur, vendor, dan jatuh tempo dokumen finance pusat.',
    available: true,
  },
  {
    key: 'expense-breakdown',
    label: 'Expense Breakdown',
    copy: 'Breakdown biaya dan dokumen finance per kategori, vendor, scope, serta nominal periode terpilih.',
    available: true,
  },
  {
    key: 'asset-register',
    label: 'Asset Register',
    copy: 'Daftar aset berdasarkan dokumen pembelian aset pusat lengkap dengan nilai perolehan, akun aset, vendor, dan status pembayaran.',
    available: true,
  },
  {
    key: 'depreciation-schedule',
    label: 'Depreciation Schedule',
    copy: 'Estimasi penyusutan straight-line per aset berdasarkan kategori aset dan policy default sampai periode report yang dipilih.',
    available: true,
  },
];

const emptySummary = {
  total_transactions: 0,
  total_members: 0,
  total_amount: 0,
  success_count: 0,
  pending_count: 0,
  failed_count: 0,
  success_amount: 0,
  pending_amount: 0,
  failed_amount: 0,
  average_ticket: 0,
};

const emptyReport = {
  summary: emptySummary,
  status_breakdown: [],
  payment_method_breakdown: [],
  source_breakdown: [],
  membership_breakdown: [],
  outlet_breakdown: [],
  processor_breakdown: [],
  trends: [],
  results: [],
};

const emptyMembershipGrowthAISummary = {
  memberships_analyzed: 0,
  transactions_analyzed: 0,
  total_revenue: 0,
  repeat_buyers: 0,
  top_entry_package: '',
};

const emptyMembershipGrowthAIReport = {
  summary: emptyMembershipGrowthAISummary,
  upsell_suggestions: [],
  promo_candidates: [],
};

const emptyMemberActivitySummary = {
  total_members: 0,
  active_memberships: 0,
  expiring_soon: 0,
  expired_memberships: 0,
  no_membership: 0,
  checked_in_period: 0,
  inactive_more_than_14_days: 0,
  never_checkin: 0,
};

const emptyMemberActivityReport = {
  summary: emptyMemberActivitySummary,
  lifecycle_breakdown: [],
  activity_breakdown: [],
  membership_breakdown: [],
  outlet_breakdown: [],
  results: [],
};

const emptyStaffTransactionSummary = {
  total_active_staff: 0,
  staff_with_transactions: 0,
  staff_without_transactions: 0,
  total_membership_transactions: 0,
  total_membership_sales: 0,
  total_class_transactions: 0,
  total_class_sales: 0,
  total_pt_transactions: 0,
  total_pt_sales: 0,
  total_pos_transactions: 0,
  total_pos_sales: 0,
  total_pos_closing_shift_sales: 0,
  total_transactions: 0,
  total_sales_amount: 0,
  average_sales_per_staff: 0,
  average_ticket: 0,
  top_staff_name: '',
  top_staff_sales_amount: 0,
  top_staff_transaction_count: 0,
  top_staff_pos_closing_shift_sales: 0,
};

const emptyStaffTransactionSummaryReport = {
  summary: emptyStaffTransactionSummary,
  outlet_breakdown: [],
  position_breakdown: [],
  channel_breakdown: [],
  top_staff_breakdown: [],
  trends: [],
  results: [],
};

const emptyCheckinSummary = {
  total_checkins: 0,
  unique_members: 0,
  repeat_members: 0,
  today_checkins: 0,
  average_daily_checkin: 0,
};

const emptyCheckinReport = {
  summary: emptyCheckinSummary,
  membership_breakdown: [],
  outlet_breakdown: [],
  weekday_breakdown: [],
  hour_breakdown: [],
  top_member_breakdown: [],
  trends: [],
  results: [],
};

const emptyAccountingJournalReport = {
  summary: {
    total_journals: 0,
    total_lines: 0,
    total_debit: 0,
    total_credit: 0,
    posted_journals: 0,
    pending_journals: 0,
    unique_accounts: 0,
    unique_outlets: 0,
    unique_sources: 0,
  },
  source_breakdown: [],
  business_unit_breakdown: [],
  posting_status_breakdown: [],
  outlet_breakdown: [],
  account_breakdown: [],
  trends: [],
  results: [],
};

const emptyAccountingGeneralLedgerReport = {
  summary: {
    total_accounts: 0,
    accounts_with_opening: 0,
    accounts_with_movement: 0,
    total_entries: 0,
    total_debit: 0,
    total_credit: 0,
    unique_outlets: 0,
    unique_sources: 0,
  },
  account_type_breakdown: [],
  source_breakdown: [],
  outlet_breakdown: [],
  trends: [],
  results: [],
};

const emptyAccountingTrialBalanceReport = {
  summary: {
    total_accounts: 0,
    accounts_with_opening: 0,
    accounts_with_movement: 0,
    opening_debit: 0,
    opening_credit: 0,
    period_debit: 0,
    period_credit: 0,
    closing_debit: 0,
    closing_credit: 0,
    net_difference: 0,
  },
  account_type_breakdown: [],
  results: [],
};

const emptyAccountingProfitLossReport = {
  summary: {
    total_revenue: 0,
    total_expenses: 0,
    net_profit: 0,
    total_accounts: 0,
    revenue_accounts: 0,
    expense_accounts: 0,
    accounts_with_movement: 0,
  },
  account_type_breakdown: [],
  results: [],
};

const emptyAccountingBalanceSheetReport = {
  summary: {
    total_assets: 0,
    total_liabilities: 0,
    total_equity: 0,
    total_liabilities_and_equity: 0,
    balance_gap: 0,
    total_accounts: 0,
    accounts_with_opening: 0,
    accounts_with_movement: 0,
  },
  account_type_breakdown: [],
  results: [],
};

const emptyAccountingCashFlowReport = {
  summary: {
    opening_cash_balance: 0,
    cash_inflow: 0,
    cash_outflow: 0,
    net_cash_flow: 0,
    closing_cash_balance: 0,
    operating_cash_flow: 0,
    investing_cash_flow: 0,
    financing_cash_flow: 0,
    internal_transfer_flow: 0,
    cash_accounts: 0,
    journals_with_cash: 0,
    entries_with_cash: 0,
  },
  section_breakdown: [],
  cash_account_breakdown: [],
  trends: [],
  results: [],
};

const emptyAccountingAccountsPayableAgingReport = {
  summary: {
    total_bills: 0,
    open_bills: 0,
    overdue_bills: 0,
    current_bills: 0,
    outstanding_total: 0,
    overdue_total: 0,
    current_total: 0,
    within_30_days_total: 0,
    within_60_days_total: 0,
    above_60_days_total: 0,
    average_outstanding_per_bill: 0,
    unique_vendors: 0,
    operational_outstanding_total: 0,
    asset_outstanding_total: 0,
  },
  aging_bucket_breakdown: [],
  vendor_breakdown: [],
  scope_breakdown: [],
  trends: [],
  results: [],
};

const emptyAccountingExpenseBreakdownReport = {
  summary: {
    total_documents: 0,
    payable_documents: 0,
    cash_documents: 0,
    total_amount: 0,
    paid_amount: 0,
    outstanding_amount: 0,
    average_document_amount: 0,
    unique_categories: 0,
    unique_vendors: 0,
    operational_document_total: 0,
    asset_document_total: 0,
  },
  category_breakdown: [],
  vendor_breakdown: [],
  scope_breakdown: [],
  trends: [],
  results: [],
};

const emptyAccountingAssetRegisterReport = {
  summary: {
    total_assets: 0,
    payable_assets: 0,
    cash_assets: 0,
    posted_assets: 0,
    total_acquisition_cost: 0,
    paid_amount: 0,
    outstanding_amount: 0,
    average_acquisition_cost: 0,
    unique_categories: 0,
    unique_vendors: 0,
    estimated_monthly_depreciation: 0,
    estimated_book_value: 0,
  },
  category_breakdown: [],
  vendor_breakdown: [],
  account_breakdown: [],
  trends: [],
  results: [],
  policy: {
    method: 'straight_line',
    method_label: 'Straight line',
    residual_value_default: 0,
    depreciation_start_rule: 'acquisition_month',
    depreciation_start_rule_label: 'Mulai dari bulan perolehan',
    period_start: '',
    period_end: '',
    as_of_date: '',
    useful_life_defaults: [],
  },
};

const emptyAccountingDepreciationScheduleReport = {
  summary: {
    total_assets: 0,
    depreciable_assets: 0,
    total_acquisition_cost: 0,
    depreciable_base: 0,
    monthly_depreciation: 0,
    period_depreciation: 0,
    accumulated_depreciation: 0,
    current_book_value: 0,
    fully_depreciated_assets: 0,
    average_remaining_life_months: 0,
  },
  category_breakdown: [],
  useful_life_breakdown: [],
  trends: [],
  results: [],
  policy: {
    method: 'straight_line',
    method_label: 'Straight line',
    residual_value_default: 0,
    depreciation_start_rule: 'acquisition_month',
    depreciation_start_rule_label: 'Mulai dari bulan perolehan',
    period_start: '',
    period_end: '',
    as_of_date: '',
    useful_life_defaults: [],
  },
};

const emptyPTSalesSummary = {
  total_sales_amount: 0,
  total_invoices: 0,
  paid_amount: 0,
  outstanding_amount: 0,
  pending_count: 0,
  partial_paid_count: 0,
  paid_count: 0,
  average_ticket: 0,
  new_clients: 0,
  repeat_clients: 0,
};

const emptyPTSalesReport = {
  summary: emptyPTSalesSummary,
  trainer_breakdown: [],
  product_breakdown: [],
  client_type_breakdown: [],
  outlet_breakdown: [],
  payment_status_breakdown: [],
  contract_breakdown: [],
  trends: [],
  results: [],
};

const emptyPTSessionSummary = {
  total_sessions_scheduled: 0,
  total_sessions_completed: 0,
  total_sessions_cancelled: 0,
  total_sessions_rescheduled: 0,
  no_show_count: 0,
  sessions_remaining: 0,
  completion_rate: 0,
};

const emptyPTSessionReport = {
  summary: emptyPTSessionSummary,
  trainer_breakdown: [],
  client_breakdown: [],
  product_breakdown: [],
  outlet_breakdown: [],
  booking_status_breakdown: [],
  attendance_status_breakdown: [],
  trends: [],
  results: [],
};

const emptyPTTrainerPerformanceSummary = {
  active_trainers: 0,
  clients_handled: 0,
  packages_sold: 0,
  sessions_scheduled: 0,
  sessions_completed: 0,
  sessions_cancelled: 0,
  sessions_no_show: 0,
  sales_attributed: 0,
  paid_sales_amount: 0,
  completion_rate: 0,
  average_sessions_per_trainer: 0,
  settlement_eligible_amount: 0,
};

const emptyPTTrainerPerformanceReport = {
  summary: emptyPTTrainerPerformanceSummary,
  trainer_breakdown: [],
  outlet_breakdown: [],
  product_type_breakdown: [],
  trends: [],
  results: [],
};

const emptyPOSSalesSummary = {
  total_transactions: 0,
  gross_sales: 0,
  total_service: 0,
  total_tax: 0,
  total_discount: 0,
  average_ticket: 0,
  total_item_quantity: 0,
  unique_cashiers: 0,
  unique_products: 0,
};

const emptyPOSSalesReport = {
  summary: emptyPOSSalesSummary,
  payment_method_breakdown: [],
  outlet_breakdown: [],
  cashier_breakdown: [],
  product_breakdown: [],
  trends: [],
  results: [],
};

const emptyPOSPricingAISummary = {
  transactions_analyzed: 0,
  unique_products: 0,
  gross_sales: 0,
  average_ticket: 0,
  total_item_quantity: 0,
  dominant_time_slot: '',
};

const emptyPOSPricingAIReport = {
  summary: emptyPOSPricingAISummary,
  bundling_recommendations: [],
  promo_candidates: [],
};

const emptyPOSCashierSalesSummary = {
  total_cashiers: 0,
  total_transactions: 0,
  gross_sales: 0,
  total_service: 0,
  average_sales_per_cashier: 0,
  average_ticket: 0,
  total_item_quantity: 0,
  active_outlets: 0,
  top_cashier_name: '',
  top_cashier_sales: 0,
  top_cashier_transactions: 0,
};

const emptyPOSCashierSalesReport = {
  summary: emptyPOSCashierSalesSummary,
  payment_method_breakdown: [],
  outlet_breakdown: [],
  cashier_breakdown: [],
  time_slot_breakdown: [],
  trends: [],
  results: [],
};

const emptyPOSProductSalesSummary = {
  total_products: 0,
  total_quantity: 0,
  gross_sales: 0,
  average_selling_price: 0,
  average_sales_per_product: 0,
  unique_outlets: 0,
  unique_cashiers: 0,
  unique_item_types: 0,
  top_product_name: '',
  top_product_sales: 0,
  top_product_quantity: 0,
};

const emptyPOSProductSalesReport = {
  summary: emptyPOSProductSalesSummary,
  payment_method_breakdown: [],
  outlet_breakdown: [],
  cashier_breakdown: [],
  item_type_breakdown: [],
  product_breakdown: [],
  trends: [],
  results: [],
};

const emptyPOSPurchaseSummary = {
  total_documents: 0,
  total_amount: 0,
  paid_amount: 0,
  outstanding_amount: 0,
  average_document: 0,
  total_quantity: 0,
  unique_vendors: 0,
  unique_products: 0,
  credit_documents: 0,
  open_documents: 0,
};

const emptyPOSPurchaseReport = {
  summary: emptyPOSPurchaseSummary,
  vendor_breakdown: [],
  payment_method_breakdown: [],
  payment_status_breakdown: [],
  item_breakdown: [],
  trends: [],
  results: [],
};

const emptyPOSVendorAgingSummary = {
  total_bills: 0,
  total_amount: 0,
  total_paid_amount: 0,
  total_outstanding_amount: 0,
  unique_vendors: 0,
  overdue_bills: 0,
  overdue_amount: 0,
  due_today_amount: 0,
  current_amount: 0,
  average_age_days: 0,
  max_age_days: 0,
};

const emptyPOSVendorAgingReport = {
  summary: emptyPOSVendorAgingSummary,
  vendor_breakdown: [],
  bill_type_breakdown: [],
  status_breakdown: [],
  aging_bucket_breakdown: [],
  due_timeline: [],
  results: [],
};

const emptyPOSStockMovementSummary = {
  total_movements: 0,
  total_quantity_in: 0,
  total_quantity_out: 0,
  net_quantity: 0,
  total_cost_in: 0,
  total_cost_out: 0,
  net_cost: 0,
  unique_products: 0,
  unique_references: 0,
};

const emptyPOSStockMovementReport = {
  summary: emptyPOSStockMovementSummary,
  movement_breakdown: [],
  reference_breakdown: [],
  product_breakdown: [],
  item_type_breakdown: [],
  trends: [],
  results: [],
};

const emptyPOSBOMConsumptionSummary = {
  total_transactions: 0,
  total_rows: 0,
  total_sold_quantity: 0,
  total_consumed_quantity: 0,
  total_consumption_cost: 0,
  active_finished_goods: 0,
  active_ingredients: 0,
  active_outlets: 0,
  average_cost_per_sale: 0,
};

const emptyPOSBOMConsumptionReport = {
  summary: emptyPOSBOMConsumptionSummary,
  finished_good_breakdown: [],
  ingredient_breakdown: [],
  outlet_breakdown: [],
  ingredient_type_breakdown: [],
  trends: [],
  results: [],
};

const emptyPOSStockOpnameVarianceSummary = {
  total_opnames: 0,
  total_variance_items: 0,
  gain_items: 0,
  loss_items: 0,
  matched_items: 0,
  total_gain_quantity: 0,
  total_loss_quantity: 0,
  net_variance_quantity: 0,
  total_variance_cost: 0,
  affected_products: 0,
  affected_outlets: 0,
};

const emptyPOSStockOpnameVarianceReport = {
  summary: emptyPOSStockOpnameVarianceSummary,
  variance_breakdown: [],
  outlet_breakdown: [],
  product_breakdown: [],
  item_type_breakdown: [],
  trends: [],
  results: [],
};

const emptyPOSVendorPriceComparisonSummary = {
  products_compared: 0,
  active_vendors: 0,
  cheapest_vendor_wins: 0,
  potential_savings: 0,
  single_source_products: 0,
  high_spread_products: 0,
};

const emptyPOSVendorPriceComparisonReport = {
  summary: emptyPOSVendorPriceComparisonSummary,
  comparison_mode: 'average_price',
  vendor_win_breakdown: [],
  category_spread_breakdown: [],
  top_price_gap_products: [],
  vendor_details: [],
  results: [],
};

const emptyPOSRefundVoidSummary = {
  total_requests: 0,
  refund_requests: 0,
  void_requests: 0,
  approved_requests: 0,
  pending_requests: 0,
  rejected_requests: 0,
  total_request_amount: 0,
  approved_amount: 0,
  executed_refund_amount: 0,
  unique_requesters: 0,
  unique_reviewers: 0,
};

const emptyPOSRefundVoidReport = {
  summary: emptyPOSRefundVoidSummary,
  request_type_breakdown: [],
  status_breakdown: [],
  requester_breakdown: [],
  reviewer_breakdown: [],
  reason_breakdown: [],
  trends: [],
  results: [],
};

const buildBreakdownItems = map =>
  Array.from(map.values()).sort((left, right) => {
    const amountDiff = Number(right.amount || 0) - Number(left.amount || 0);
    if (amountDiff !== 0) {
      return amountDiff;
    }
    return Number(right.count || 0) - Number(left.count || 0);
  });

const buildPOSSalesReport = (rows, resolveOutletName, search = '') => {
  const normalizedRows = (Array.isArray(rows) ? rows : []).map(row => {
    const items = Array.isArray(row?.items) ? row.items : [];
    const productNames = items
      .map(item => item?.product?.name || '')
      .filter(Boolean);
    const itemQuantity = items.reduce((total, item) => total + Number(item?.quantity || 0), 0);
    return {
      ...row,
      outlet_name: resolveOutletName(row?.outlet_id),
      item_quantity: itemQuantity,
      item_count: items.length,
      product_names: productNames,
    };
  });

  const keyword = String(search || '').trim().toLowerCase();
  const filteredRows = keyword
    ? normalizedRows.filter(row =>
        [
          row?.id,
          row?.outlet_name,
          row?.payment_method,
          row?.note,
          row?.cashier_name,
          row?.cashier_id,
          row?.accounting_sync_status,
          ...(Array.isArray(row?.product_names) ? row.product_names : []),
        ]
          .map(value => String(value || '').toLowerCase())
          .some(value => value.includes(keyword))
      )
    : normalizedRows;

  const paymentMethodBreakdown = new Map();
  const outletBreakdown = new Map();
  const cashierBreakdown = new Map();
  const productBreakdown = new Map();
  const trendBreakdown = new Map();

  let grossSales = 0;
  let totalService = 0;
  let totalTax = 0;
  let totalDiscount = 0;
  let totalItemQuantity = 0;

  filteredRows.forEach(row => {
    const total = getPOSTransactionGrossSales(row);
    const service = getPOSTransactionServiceAmount(row);
    const tax = Number(row?.tax || 0);
    const discount = Number(row?.discount || 0);
    const paymentMethod = String(row?.payment_method || 'Other').trim() || 'Other';
    const outletLabel = row?.outlet_name || `Outlet #${row?.outlet_id || '-'}`;
    const cashierLabel = formatCashierLabel(row);
    const periodKey = formatDateInput(parseReportDateTime(row?.created_at));
    const periodLabel = periodKey || '-';

    grossSales += total;
    totalService += service;
    totalTax += tax;
    totalDiscount += discount;
    totalItemQuantity += Number(row?.item_quantity || 0);

    const paymentEntry = paymentMethodBreakdown.get(paymentMethod) || {
      key: paymentMethod.toLowerCase(),
      label: paymentMethod,
      count: 0,
      amount: 0,
    };
    paymentEntry.count += 1;
    paymentEntry.amount += total;
    paymentMethodBreakdown.set(paymentMethod, paymentEntry);

    const outletEntry = outletBreakdown.get(outletLabel) || {
      key: String(row?.outlet_id || outletLabel).toLowerCase(),
      label: outletLabel,
      count: 0,
      amount: 0,
    };
    outletEntry.count += 1;
    outletEntry.amount += total;
    outletBreakdown.set(outletLabel, outletEntry);

    const cashierEntry = cashierBreakdown.get(cashierLabel) || {
      key: String(row?.cashier_name || row?.cashier_id || cashierLabel).toLowerCase(),
      label: cashierLabel,
      count: 0,
      amount: 0,
    };
    cashierEntry.count += 1;
    cashierEntry.amount += total;
    cashierBreakdown.set(cashierLabel, cashierEntry);

    (Array.isArray(row?.items) ? row.items : []).forEach(item => {
      const productLabel = item?.product?.name || `Product #${item?.product_id || '-'}`;
      const productEntry = productBreakdown.get(productLabel) || {
        key: String(item?.product_id || productLabel).toLowerCase(),
        label: productLabel,
        count: 0,
        amount: 0,
      };
      productEntry.count += Number(item?.quantity || 0);
      productEntry.amount += Number(item?.total || 0);
      productBreakdown.set(productLabel, productEntry);
    });

    if (periodKey) {
      const trendEntry = trendBreakdown.get(periodKey) || {
        period_key: periodKey,
        period_label: periodLabel,
        total_transactions: 0,
        total_amount: 0,
        total_item_quantity: 0,
      };
      trendEntry.total_transactions += 1;
      trendEntry.total_amount += total;
      trendEntry.total_item_quantity += Number(row?.item_quantity || 0);
      trendBreakdown.set(periodKey, trendEntry);
    }
  });

  const uniqueCashiers = new Set(
    filteredRows
      .map(row => String(row?.cashier_name || row?.cashier_id || '').trim())
      .filter(Boolean)
  ).size;
  const uniqueProducts = new Set(
    filteredRows.flatMap(row =>
      (Array.isArray(row?.items) ? row.items : [])
        .map(item => String(item?.product_id || '').trim())
        .filter(Boolean)
    )
  ).size;

  return {
    summary: {
      total_transactions: filteredRows.length,
      gross_sales: grossSales,
      total_service: totalService,
      total_tax: totalTax,
      total_discount: totalDiscount,
      average_ticket: filteredRows.length ? grossSales / filteredRows.length : 0,
      total_item_quantity: totalItemQuantity,
      unique_cashiers: uniqueCashiers,
      unique_products: uniqueProducts,
    },
    payment_method_breakdown: buildBreakdownItems(paymentMethodBreakdown),
    outlet_breakdown: buildBreakdownItems(outletBreakdown),
    cashier_breakdown: buildBreakdownItems(cashierBreakdown),
    product_breakdown: buildBreakdownItems(productBreakdown),
    trends: Array.from(trendBreakdown.values()).sort((left, right) =>
      String(left.period_key || '').localeCompare(String(right.period_key || ''))
    ),
    results: filteredRows.sort((left, right) =>
      getReportDateTimestamp(right?.created_at) - getReportDateTimestamp(left?.created_at)
    ),
  };
};

const buildPOSCashierSalesReport = (rows, resolveOutletName, search = '') => {
  const normalizedRows = (Array.isArray(rows) ? rows : []).map(row => {
    const items = Array.isArray(row?.items) ? row.items : [];
    const productNames = items
      .map(item => item?.product?.name || '')
      .filter(Boolean);
    const itemQuantity = items.reduce((total, item) => total + Number(item?.quantity || 0), 0);

    return {
      ...row,
      outlet_name: resolveOutletName(row?.outlet_id),
      item_quantity: itemQuantity,
      item_count: items.length,
      product_names: productNames,
    };
  });

  const keyword = String(search || '').trim().toLowerCase();
  const filteredRows = keyword
    ? normalizedRows.filter(row =>
        [
          row?.id,
          row?.outlet_name,
          row?.payment_method,
          row?.note,
          row?.cashier_name,
          row?.cashier_id,
          ...(Array.isArray(row?.product_names) ? row.product_names : []),
        ]
          .map(value => String(value || '').toLowerCase())
          .some(value => value.includes(keyword))
      )
    : normalizedRows;

  const paymentMethodBreakdown = new Map();
  const outletBreakdown = new Map();
  const cashierBreakdown = new Map();
  const timeSlotBreakdown = new Map();
  const trendBreakdown = new Map();
  const cashierAggregates = new Map();
  const uniqueOutletIDs = new Set();

  filteredRows.forEach(row => {
    const total = getPOSTransactionGrossSales(row);
    const service = getPOSTransactionServiceAmount(row);
    const tax = Number(row?.tax || 0);
    const discount = Number(row?.discount || 0);
    const itemQuantity = Number(row?.item_quantity || 0);
    const paymentMethod = String(row?.payment_method || 'Other').trim() || 'Other';
    const outletLabel = row?.outlet_name || `Outlet #${row?.outlet_id || '-'}`;
    const cashierLabel = formatCashierLabel(row);
    const timeSlotKey = getPOSTimeSlotKey(row?.created_at);
    const timeSlotLabel = formatPOSTimeSlotLabel(timeSlotKey);
    const periodKey = formatDateInput(parseReportDateTime(row?.created_at));
    const cashierKey = String(row?.cashier_name || row?.cashier_id || cashierLabel).trim().toLowerCase() || cashierLabel.toLowerCase();

    if (row?.outlet_id) {
      uniqueOutletIDs.add(String(row.outlet_id));
    }

    const registerBreakdown = (map, key, label, count, amount) => {
      const entry = map.get(key) || {
        key,
        label,
        count: 0,
        amount: 0,
      };
      entry.count += count;
      entry.amount += amount;
      map.set(key, entry);
    };

    registerBreakdown(paymentMethodBreakdown, paymentMethod.toLowerCase(), paymentMethod, 1, total);
    registerBreakdown(outletBreakdown, String(row?.outlet_id || outletLabel).toLowerCase(), outletLabel, 1, total);
    registerBreakdown(cashierBreakdown, cashierKey, cashierLabel, 1, total);
    registerBreakdown(timeSlotBreakdown, timeSlotKey, timeSlotLabel, 1, total);

    const cashierEntry = cashierAggregates.get(cashierKey) || {
      key: cashierKey,
      cashier_name: cashierLabel,
      transaction_count: 0,
      gross_sales: 0,
      item_quantity: 0,
      total_service: 0,
      total_tax: 0,
      total_discount: 0,
      outlet_ids: new Set(),
      outlet_amounts: new Map(),
      payment_method_amounts: new Map(),
      time_slot_amounts: new Map(),
      active_days: new Set(),
      first_sale_at: '',
      last_sale_at: '',
    };
    cashierEntry.transaction_count += 1;
    cashierEntry.gross_sales += total;
    cashierEntry.item_quantity += itemQuantity;
    cashierEntry.total_service += service;
    cashierEntry.total_tax += tax;
    cashierEntry.total_discount += discount;
    if (row?.outlet_id) {
      cashierEntry.outlet_ids.add(String(row.outlet_id));
    }
    cashierEntry.outlet_amounts.set(
      outletLabel,
      Number(cashierEntry.outlet_amounts.get(outletLabel) || 0) + total
    );
    cashierEntry.payment_method_amounts.set(
      paymentMethod,
      Number(cashierEntry.payment_method_amounts.get(paymentMethod) || 0) + total
    );
    cashierEntry.time_slot_amounts.set(
      timeSlotLabel,
      Number(cashierEntry.time_slot_amounts.get(timeSlotLabel) || 0) + total
    );
    if (periodKey) {
      cashierEntry.active_days.add(periodKey);
    }
    if (!cashierEntry.first_sale_at || getReportDateTimestamp(row?.created_at) < getReportDateTimestamp(cashierEntry.first_sale_at)) {
      cashierEntry.first_sale_at = row?.created_at;
    }
    if (!cashierEntry.last_sale_at || getReportDateTimestamp(row?.created_at) > getReportDateTimestamp(cashierEntry.last_sale_at)) {
      cashierEntry.last_sale_at = row?.created_at;
    }
    cashierAggregates.set(cashierKey, cashierEntry);

    if (periodKey) {
      const trendEntry = trendBreakdown.get(periodKey) || {
        period_key: periodKey,
        period_label: periodKey,
        total_transactions: 0,
        total_amount: 0,
        total_item_quantity: 0,
        cashiers: new Set(),
      };
      trendEntry.total_transactions += 1;
      trendEntry.total_amount += total;
      trendEntry.total_item_quantity += itemQuantity;
      trendEntry.cashiers.add(cashierLabel);
      trendBreakdown.set(periodKey, trendEntry);
    }
  });

  const results = Array.from(cashierAggregates.values())
    .map(entry => {
      const topPaymentMethod = Array.from(entry.payment_method_amounts.entries())
        .sort((left, right) => Number(right[1] || 0) - Number(left[1] || 0))[0];
      const dominantOutlet = Array.from(entry.outlet_amounts.entries())
        .sort((left, right) => Number(right[1] || 0) - Number(left[1] || 0))[0];
      const dominantTimeSlot = Array.from(entry.time_slot_amounts.entries())
        .sort((left, right) => Number(right[1] || 0) - Number(left[1] || 0))[0];

      return {
        key: entry.key,
        cashier_name: entry.cashier_name,
        transaction_count: entry.transaction_count,
        gross_sales: entry.gross_sales,
        average_ticket: entry.transaction_count > 0 ? entry.gross_sales / entry.transaction_count : 0,
        item_quantity: entry.item_quantity,
        average_items_per_transaction: entry.transaction_count > 0 ? entry.item_quantity / entry.transaction_count : 0,
        total_service: entry.total_service,
        total_tax: entry.total_tax,
        total_discount: entry.total_discount,
        outlet_count: entry.outlet_ids.size,
        dominant_outlet: dominantOutlet?.[0] || '-',
        payment_method_count: entry.payment_method_amounts.size,
        top_payment_method: topPaymentMethod?.[0] || '-',
        top_payment_sales: Number(topPaymentMethod?.[1] || 0),
        top_payment_share: entry.gross_sales > 0
          ? (Number(topPaymentMethod?.[1] || 0) / entry.gross_sales) * 100
          : 0,
        dominant_time_slot: dominantTimeSlot?.[0] || 'Waktu tidak tercatat',
        active_days: entry.active_days.size,
        first_sale_at: entry.first_sale_at,
        last_sale_at: entry.last_sale_at,
      };
    })
    .sort((left, right) => {
      const amountDiff = Number(right.gross_sales || 0) - Number(left.gross_sales || 0);
      if (amountDiff !== 0) {
        return amountDiff;
      }
      return Number(right.transaction_count || 0) - Number(left.transaction_count || 0);
    });

  const topCashier = results[0] || {};

  return {
    summary: {
      total_cashiers: results.length,
      total_transactions: filteredRows.length,
      gross_sales: filteredRows.reduce((sum, row) => sum + getPOSTransactionGrossSales(row), 0),
      total_service: filteredRows.reduce((sum, row) => sum + getPOSTransactionServiceAmount(row), 0),
      average_sales_per_cashier: results.length
        ? filteredRows.reduce((sum, row) => sum + getPOSTransactionGrossSales(row), 0) / results.length
        : 0,
      average_ticket: filteredRows.length
        ? filteredRows.reduce((sum, row) => sum + getPOSTransactionGrossSales(row), 0) / filteredRows.length
        : 0,
      total_item_quantity: filteredRows.reduce((sum, row) => sum + Number(row?.item_quantity || 0), 0),
      active_outlets: uniqueOutletIDs.size,
      top_cashier_name: topCashier.cashier_name || '',
      top_cashier_sales: Number(topCashier.gross_sales || 0),
      top_cashier_transactions: Number(topCashier.transaction_count || 0),
    },
    payment_method_breakdown: buildBreakdownItems(paymentMethodBreakdown),
    outlet_breakdown: buildBreakdownItems(outletBreakdown),
    cashier_breakdown: buildBreakdownItems(cashierBreakdown),
    time_slot_breakdown: buildBreakdownItems(timeSlotBreakdown),
    trends: Array.from(trendBreakdown.values())
      .map(entry => ({
        period_key: entry.period_key,
        period_label: entry.period_label,
        active_cashiers: entry.cashiers.size,
        total_transactions: entry.total_transactions,
        total_item_quantity: entry.total_item_quantity,
        total_amount: entry.total_amount,
        average_ticket: entry.total_transactions > 0 ? entry.total_amount / entry.total_transactions : 0,
      }))
      .sort((left, right) => String(left.period_key || '').localeCompare(String(right.period_key || ''))),
    results,
  };
};

const buildPOSProductSalesReport = (rows, resolveOutletName, filters = {}) => {
  const productFilter = String(filters?.product_id || '').trim();
  const keyword = String(filters?.search || '').trim().toLowerCase();
  const normalizedItems = [];

  (Array.isArray(rows) ? rows : []).forEach(row => {
    const items = Array.isArray(row?.items) ? row.items : [];
    items.forEach(item => {
      const quantity = Number(item?.quantity || 0);
      const total = Number(item?.total || 0);
      const productID = Number(item?.product_id || item?.product?.id || 0);
      const productName = item?.product?.name || `Product #${productID || '-'}`;

      normalizedItems.push({
        product_id: productID,
        product_name: productName,
        item_type: item?.product?.item_type || '',
        unit: item?.product?.satuan || '',
        quantity,
        line_total: total,
        unit_price: quantity > 0 ? total / quantity : Number(item?.unit_price || 0),
        transaction_id: row?.id,
        created_at: row?.created_at,
        outlet_id: row?.outlet_id,
        outlet_name: resolveOutletName(row?.outlet_id),
        cashier_name: formatCashierLabel(row),
        payment_method: String(row?.payment_method || 'Other').trim() || 'Other',
        note: row?.note || '',
      });
    });
  });

  const filteredItems = normalizedItems.filter(item => {
    if (productFilter && String(item.product_id || '') !== productFilter) {
      return false;
    }

    if (!keyword) {
      return true;
    }

    return [
      item.product_id,
      item.product_name,
      item.item_type,
      item.unit,
      item.transaction_id,
      item.outlet_name,
      item.cashier_name,
      item.payment_method,
      item.note,
    ]
      .map(value => String(value || '').toLowerCase())
      .some(value => value.includes(keyword));
  });

  const paymentMethodBreakdown = new Map();
  const outletBreakdown = new Map();
  const cashierBreakdown = new Map();
  const itemTypeBreakdown = new Map();
  const productBreakdown = new Map();
  const trendBreakdown = new Map();
  const productAggregates = new Map();
  const uniqueOutletIDs = new Set();
  const uniqueCashierLabels = new Set();
  const uniqueItemTypes = new Set();

  let totalQuantity = 0;
  let grossSales = 0;

  const registerBreakdown = (map, key, label, count, amount) => {
    const entry = map.get(key) || {
      key,
      label,
      count: 0,
      amount: 0,
    };
    entry.count += count;
    entry.amount += amount;
    map.set(key, entry);
  };

  filteredItems.forEach(item => {
    const paymentMethod = item.payment_method || 'Other';
    const outletLabel = item.outlet_name || `Outlet #${item.outlet_id || '-'}`;
    const cashierLabel = item.cashier_name || 'Cashier belum tercatat';
    const itemTypeLabel = formatProductItemTypeLabel(item.item_type || 'unknown');
    const periodKey = formatDateInput(parseReportDateTime(item.created_at));

    totalQuantity += Number(item.quantity || 0);
    grossSales += Number(item.line_total || 0);

    if (item.outlet_id) {
      uniqueOutletIDs.add(String(item.outlet_id));
    }
    if (cashierLabel) {
      uniqueCashierLabels.add(cashierLabel);
    }
    if (item.item_type) {
      uniqueItemTypes.add(String(item.item_type));
    }

    registerBreakdown(paymentMethodBreakdown, paymentMethod.toLowerCase(), paymentMethod, Number(item.quantity || 0), Number(item.line_total || 0));
    registerBreakdown(outletBreakdown, String(item.outlet_id || outletLabel).toLowerCase(), outletLabel, Number(item.quantity || 0), Number(item.line_total || 0));
    registerBreakdown(cashierBreakdown, cashierLabel.toLowerCase(), cashierLabel, Number(item.quantity || 0), Number(item.line_total || 0));
    registerBreakdown(itemTypeBreakdown, String(item.item_type || itemTypeLabel).toLowerCase(), itemTypeLabel, Number(item.quantity || 0), Number(item.line_total || 0));

    const productKey = String(item.product_id || item.product_name).toLowerCase();
    const productEntry = productAggregates.get(productKey) || {
      key: productKey,
      product_id: item.product_id,
      product_name: item.product_name,
      item_type: item.item_type,
      unit: item.unit,
      quantity_sold: 0,
      gross_sales: 0,
      transaction_ids: new Set(),
      outlet_ids: new Set(),
      cashier_labels: new Set(),
      payment_method_amounts: new Map(),
      last_sold_at: '',
    };
    productEntry.quantity_sold += Number(item.quantity || 0);
    productEntry.gross_sales += Number(item.line_total || 0);
    productEntry.transaction_ids.add(String(item.transaction_id || ''));
    if (item.outlet_id) {
      productEntry.outlet_ids.add(String(item.outlet_id));
    }
    if (cashierLabel) {
      productEntry.cashier_labels.add(cashierLabel);
    }
    if (!productEntry.last_sold_at || getReportDateTimestamp(item.created_at) > getReportDateTimestamp(productEntry.last_sold_at)) {
      productEntry.last_sold_at = item.created_at;
    }
    productEntry.payment_method_amounts.set(
      paymentMethod,
      Number(productEntry.payment_method_amounts.get(paymentMethod) || 0) + Number(item.line_total || 0)
    );
    productAggregates.set(productKey, productEntry);

    registerBreakdown(productBreakdown, productKey, item.product_name, Number(item.quantity || 0), Number(item.line_total || 0));

    if (periodKey) {
      const trendEntry = trendBreakdown.get(periodKey) || {
        period_key: periodKey,
        period_label: periodKey,
        total_quantity: 0,
        total_sales: 0,
        transaction_ids: new Set(),
        product_keys: new Set(),
      };
      trendEntry.total_quantity += Number(item.quantity || 0);
      trendEntry.total_sales += Number(item.line_total || 0);
      trendEntry.transaction_ids.add(String(item.transaction_id || ''));
      trendEntry.product_keys.add(productKey);
      trendBreakdown.set(periodKey, trendEntry);
    }
  });

  const results = Array.from(productAggregates.values())
    .map(entry => {
      const topPaymentMethod = Array.from(entry.payment_method_amounts.entries())
        .sort((left, right) => Number(right[1] || 0) - Number(left[1] || 0))[0];

      return {
        key: entry.key,
        product_id: entry.product_id,
        product_name: entry.product_name,
        item_type: entry.item_type,
        unit: entry.unit,
        quantity_sold: entry.quantity_sold,
        gross_sales: entry.gross_sales,
        average_selling_price: entry.quantity_sold > 0 ? entry.gross_sales / entry.quantity_sold : 0,
        transaction_count: entry.transaction_ids.size,
        outlet_count: entry.outlet_ids.size,
        cashier_count: entry.cashier_labels.size,
        top_payment_method: topPaymentMethod?.[0] || '-',
        top_payment_amount: Number(topPaymentMethod?.[1] || 0),
        last_sold_at: entry.last_sold_at,
      };
    })
    .sort((left, right) => {
      const amountDiff = Number(right.gross_sales || 0) - Number(left.gross_sales || 0);
      if (amountDiff !== 0) {
        return amountDiff;
      }
      return Number(right.quantity_sold || 0) - Number(left.quantity_sold || 0);
    });

  const topProduct = results[0] || {};

  return {
    summary: {
      total_products: results.length,
      total_quantity: totalQuantity,
      gross_sales: grossSales,
      average_selling_price: totalQuantity > 0 ? grossSales / totalQuantity : 0,
      average_sales_per_product: results.length > 0 ? grossSales / results.length : 0,
      unique_outlets: uniqueOutletIDs.size,
      unique_cashiers: uniqueCashierLabels.size,
      unique_item_types: uniqueItemTypes.size,
      top_product_name: topProduct.product_name || '',
      top_product_sales: Number(topProduct.gross_sales || 0),
      top_product_quantity: Number(topProduct.quantity_sold || 0),
    },
    payment_method_breakdown: buildBreakdownItems(paymentMethodBreakdown),
    outlet_breakdown: buildBreakdownItems(outletBreakdown),
    cashier_breakdown: buildBreakdownItems(cashierBreakdown),
    item_type_breakdown: buildBreakdownItems(itemTypeBreakdown),
    product_breakdown: buildBreakdownItems(productBreakdown),
    trends: Array.from(trendBreakdown.values())
      .map(entry => ({
        period_key: entry.period_key,
        period_label: entry.period_label,
        total_quantity: entry.total_quantity,
        total_sales: entry.total_sales,
        total_transactions: entry.transaction_ids.size,
        unique_products: entry.product_keys.size,
      }))
      .sort((left, right) => String(left.period_key || '').localeCompare(String(right.period_key || ''))),
    results,
  };
};

const normalizePOSPurchaseReport = (data, resolveOutletName) => {
  const results = (Array.isArray(data?.results) ? data.results : []).map(row => ({
    ...row,
    outlet_name: resolveOutletName(row?.outlet_id),
  }));

  return {
    summary: data?.summary || emptyPOSPurchaseSummary,
    vendor_breakdown: Array.isArray(data?.vendor_breakdown) ? data.vendor_breakdown : [],
    payment_method_breakdown: Array.isArray(data?.payment_method_breakdown) ? data.payment_method_breakdown : [],
    payment_status_breakdown: Array.isArray(data?.payment_status_breakdown) ? data.payment_status_breakdown : [],
    item_breakdown: Array.isArray(data?.item_breakdown) ? data.item_breakdown : [],
    trends: Array.isArray(data?.trends) ? data.trends : [],
    results,
  };
};

const normalizePOSVendorAgingReport = (data, resolveOutletName) => ({
  summary: data?.summary || emptyPOSVendorAgingSummary,
  vendor_breakdown: Array.isArray(data?.vendor_breakdown) ? data.vendor_breakdown : [],
  bill_type_breakdown: Array.isArray(data?.bill_type_breakdown) ? data.bill_type_breakdown : [],
  status_breakdown: Array.isArray(data?.status_breakdown) ? data.status_breakdown : [],
  aging_bucket_breakdown: Array.isArray(data?.aging_bucket_breakdown) ? data.aging_bucket_breakdown : [],
  due_timeline: Array.isArray(data?.due_timeline) ? data.due_timeline : [],
  results: (Array.isArray(data?.results) ? data.results : []).map(row => ({
    ...row,
    outlet_name: resolveOutletName(row?.outlet_id),
  })),
});

const normalizePOSStockMovementReport = (data, resolveOutletName) => ({
  summary: data?.summary || emptyPOSStockMovementSummary,
  movement_breakdown: Array.isArray(data?.movement_breakdown) ? data.movement_breakdown : [],
  reference_breakdown: Array.isArray(data?.reference_breakdown) ? data.reference_breakdown : [],
  product_breakdown: Array.isArray(data?.product_breakdown) ? data.product_breakdown : [],
  item_type_breakdown: Array.isArray(data?.item_type_breakdown) ? data.item_type_breakdown : [],
  trends: Array.isArray(data?.trends) ? data.trends : [],
  results: (Array.isArray(data?.results) ? data.results : []).map(row => ({
    ...row,
    outlet_name: resolveOutletName(row?.outlet_id),
  })),
});

const normalizePOSBOMConsumptionReport = (data, resolveOutletName) => ({
  summary: data?.summary || emptyPOSBOMConsumptionSummary,
  finished_good_breakdown: Array.isArray(data?.finished_good_breakdown) ? data.finished_good_breakdown : [],
  ingredient_breakdown: Array.isArray(data?.ingredient_breakdown) ? data.ingredient_breakdown : [],
  outlet_breakdown: (Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : []).map(item => ({
    ...item,
    label: resolveOutletName(item?.key || item?.label),
  })),
  ingredient_type_breakdown: Array.isArray(data?.ingredient_type_breakdown) ? data.ingredient_type_breakdown : [],
  trends: Array.isArray(data?.trends) ? data.trends : [],
  results: (Array.isArray(data?.results) ? data.results : []).map(row => ({
    ...row,
    outlet_name: resolveOutletName(row?.outlet_id),
  })),
});

const normalizePOSStockOpnameVarianceReport = (data, resolveOutletName) => ({
  summary: data?.summary || emptyPOSStockOpnameVarianceSummary,
  variance_breakdown: Array.isArray(data?.variance_breakdown) ? data.variance_breakdown : [],
  outlet_breakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
  product_breakdown: Array.isArray(data?.product_breakdown) ? data.product_breakdown : [],
  item_type_breakdown: Array.isArray(data?.item_type_breakdown) ? data.item_type_breakdown : [],
  trends: Array.isArray(data?.trends) ? data.trends : [],
  results: (Array.isArray(data?.results) ? data.results : []).map(row => ({
    ...row,
    outlet_name: resolveOutletName(row?.outlet_id),
  })),
});

const normalizePOSVendorPriceComparisonReport = data => ({
  summary: data?.summary || emptyPOSVendorPriceComparisonSummary,
  comparison_mode: data?.comparison_mode || 'average_price',
  vendor_win_breakdown: Array.isArray(data?.vendor_win_breakdown) ? data.vendor_win_breakdown : [],
  category_spread_breakdown: Array.isArray(data?.category_spread_breakdown) ? data.category_spread_breakdown : [],
  top_price_gap_products: Array.isArray(data?.top_price_gap_products) ? data.top_price_gap_products : [],
  vendor_details: Array.isArray(data?.vendor_details) ? data.vendor_details : [],
  results: Array.isArray(data?.results) ? data.results : [],
});

const buildPOSRefundVoidReport = (data, resolveOutletName, filters = {}) => {
  const paymentMethodFilter = String(filters?.payment_method || '').trim().toLowerCase();
  const keyword = String(filters?.search || '').trim().toLowerCase();
  const normalizedRows = (Array.isArray(data?.results) ? data.results : []).map(row => {
    const transaction = row?.transaction || {};
    const refund = row?.refund || {};
    const requestedItems = Array.isArray(row?.requested_items) && row.requested_items.length
      ? row.requested_items
      : Array.isArray(transaction?.items)
        ? transaction.items
        : [];
    const itemQuantity = requestedItems.reduce((total, item) => total + Number(item?.quantity || 0), 0);
    const itemNames = requestedItems
      .map(item => item?.product_name || '')
      .filter(Boolean);

    return {
      ...row,
      outlet_name: resolveOutletName(row?.outlet_id),
      payment_method: String(transaction?.payment_method || '').trim(),
      cashier_name: formatCashierLabel(transaction),
      transaction_total: getPOSTransactionFinalTotal(transaction),
      transaction_created_at: transaction?.created_at,
      transaction_document_status: transaction?.document_status
        || (String(row?.request_type || '').toLowerCase() === 'void' && String(row?.status || '').toLowerCase() === 'approved'
          ? 'voided'
          : 'posted'),
      item_quantity: itemQuantity,
      item_names: itemNames,
      refund_total: Number(refund?.refund_total || 0),
      accounting_sync_status: refund?.accounting_sync_status || '',
      accounting_sync_error: refund?.accounting_sync_error || '',
      accounting_synced_at: refund?.accounting_synced_at || '',
    };
  });

  const filteredRows = normalizedRows.filter(row => {
    if (paymentMethodFilter && String(row?.payment_method || '').toLowerCase() !== paymentMethodFilter) {
      return false;
    }

    if (!keyword) {
      return true;
    }

    return [
      row?.id,
      row?.transaction_id,
      row?.outlet_name,
      row?.payment_method,
      row?.cashier_name,
      row?.reason,
      row?.request_note,
      row?.review_note,
      row?.requested_by_name,
      row?.reviewed_by_name,
      row?.request_type,
      row?.status,
      row?.transaction_document_status,
      ...(Array.isArray(row?.item_names) ? row.item_names : []),
    ]
      .map(value => String(value || '').toLowerCase())
      .some(value => value.includes(keyword));
  });

  const requestTypeBreakdown = new Map();
  const statusBreakdown = new Map();
  const requesterBreakdown = new Map();
  const reviewerBreakdown = new Map();
  const reasonBreakdown = new Map();
  const trendBreakdown = new Map();
  let totalRequestAmount = 0;
  let approvedAmount = 0;
  let executedRefundAmount = 0;
  let approvedRequests = 0;
  let pendingRequests = 0;
  let rejectedRequests = 0;
  let refundRequests = 0;
  let voidRequests = 0;

  filteredRows.forEach(row => {
    const requestType = String(row?.request_type || 'refund').toLowerCase();
    const status = String(row?.status || 'pending').toLowerCase();
    const requestAmount = Number(row?.request_total || 0);
    const refundAmount = Number(row?.refund_total || 0);
    const requesterLabel = String(row?.requested_by_name || 'User tidak diketahui').trim() || 'User tidak diketahui';
    const reviewerLabel = String(row?.reviewed_by_name || '').trim();
    const reasonLabel = String(row?.reason || 'Tanpa alasan').trim() || 'Tanpa alasan';
    const periodKey = formatDateInput(parseReportDateTime(row?.requested_at));

    totalRequestAmount += requestAmount;
    executedRefundAmount += refundAmount;

    if (requestType === 'void') {
      voidRequests += 1;
    } else {
      refundRequests += 1;
    }

    if (status === 'approved') {
      approvedRequests += 1;
      approvedAmount += requestAmount;
    } else if (status === 'rejected') {
      rejectedRequests += 1;
    } else {
      pendingRequests += 1;
    }

    const requestTypeEntry = requestTypeBreakdown.get(requestType) || {
      key: requestType,
      label: formatPOSApprovalRequestTypeLabel(requestType),
      count: 0,
      amount: 0,
    };
    requestTypeEntry.count += 1;
    requestTypeEntry.amount += requestAmount;
    requestTypeBreakdown.set(requestType, requestTypeEntry);

    const statusEntry = statusBreakdown.get(status) || {
      key: status,
      label: formatPOSApprovalStatusLabel(status),
      count: 0,
      amount: 0,
    };
    statusEntry.count += 1;
    statusEntry.amount += requestAmount;
    statusBreakdown.set(status, statusEntry);

    const requesterEntry = requesterBreakdown.get(requesterLabel) || {
      key: requesterLabel.toLowerCase(),
      label: requesterLabel,
      count: 0,
      amount: 0,
    };
    requesterEntry.count += 1;
    requesterEntry.amount += requestAmount;
    requesterBreakdown.set(requesterLabel, requesterEntry);

    if (reviewerLabel) {
      const reviewerEntry = reviewerBreakdown.get(reviewerLabel) || {
        key: reviewerLabel.toLowerCase(),
        label: reviewerLabel,
        count: 0,
        amount: 0,
      };
      reviewerEntry.count += 1;
      reviewerEntry.amount += requestAmount;
      reviewerBreakdown.set(reviewerLabel, reviewerEntry);
    }

    const reasonEntry = reasonBreakdown.get(reasonLabel) || {
      key: reasonLabel.toLowerCase(),
      label: reasonLabel,
      count: 0,
      amount: 0,
    };
    reasonEntry.count += 1;
    reasonEntry.amount += requestAmount;
    reasonBreakdown.set(reasonLabel, reasonEntry);

    if (periodKey) {
      const trendEntry = trendBreakdown.get(periodKey) || {
        period_key: periodKey,
        period_label: periodKey,
        total_requests: 0,
        approved_requests: 0,
        pending_requests: 0,
        rejected_requests: 0,
        request_amount: 0,
        refund_amount: 0,
      };
      trendEntry.total_requests += 1;
      trendEntry.request_amount += requestAmount;
      trendEntry.refund_amount += refundAmount;
      if (status === 'approved') {
        trendEntry.approved_requests += 1;
      } else if (status === 'rejected') {
        trendEntry.rejected_requests += 1;
      } else {
        trendEntry.pending_requests += 1;
      }
      trendBreakdown.set(periodKey, trendEntry);
    }
  });

  return {
    summary: {
      total_requests: filteredRows.length,
      refund_requests: refundRequests,
      void_requests: voidRequests,
      approved_requests: approvedRequests,
      pending_requests: pendingRequests,
      rejected_requests: rejectedRequests,
      total_request_amount: totalRequestAmount,
      approved_amount: approvedAmount,
      executed_refund_amount: executedRefundAmount,
      unique_requesters: new Set(
        filteredRows.map(row => String(row?.requested_by_name || '').trim()).filter(Boolean)
      ).size,
      unique_reviewers: new Set(
        filteredRows.map(row => String(row?.reviewed_by_name || '').trim()).filter(Boolean)
      ).size,
    },
    request_type_breakdown: buildBreakdownItems(requestTypeBreakdown),
    status_breakdown: buildBreakdownItems(statusBreakdown),
    requester_breakdown: buildBreakdownItems(requesterBreakdown),
    reviewer_breakdown: buildBreakdownItems(reviewerBreakdown),
    reason_breakdown: buildBreakdownItems(reasonBreakdown),
    trends: Array.from(trendBreakdown.values()).sort((left, right) =>
      String(left.period_key || '').localeCompare(String(right.period_key || ''))
    ),
    results: filteredRows.sort((left, right) =>
      getReportDateTimestamp(right?.requested_at) - getReportDateTimestamp(left?.requested_at)
    ),
  };
};

const getStatusVariantClass = value => {
  switch (Number(value || 0)) {
    case 1:
      return 'is-success';
    case 2:
      return 'is-failed';
    default:
      return 'is-pending';
  }
};

const getScopedOutletId = userData =>
  String(userData?.outlet_id || userData?.OutletID || '');

const getAdminActorType = userData =>
  String(userData?.actor_type ?? userData?.actorType ?? '')
    .trim()
    .toLowerCase();

const getAvailableReportTabsForUser = (userData, outletType = '') => {
  const actorType = getAdminActorType(userData);
  const normalizedOutletType = normalizeOutletTypeValue(
    outletType || userData?.outlet_type || userData?.OutletType || ''
  );

  if (actorType !== 'outlet' && !normalizedOutletType) {
    return REPORT_TABS;
  }

  const experience = resolveOutletExperience(normalizedOutletType);
  if (!experience.type) {
    return REPORT_TABS;
  }

  return REPORT_TABS.filter(item => experience.reportTabs.includes(item.key));
};

const getStandaloneBusinessReportMeta = key => {
  const normalizedKey = String(key || '').trim();
  if (!normalizedKey) {
    return null;
  }

  if (normalizedKey === STAFF_TRANSACTION_SUMMARY_REPORT.key) {
    return STAFF_TRANSACTION_SUMMARY_REPORT;
  }

  return BUSINESS_REPORTS.find(item => item.key === normalizedKey) || null;
};

function ReportCenter({ standaloneConfig = null }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch (error) {
      return {};
    }
  })();
  const standaloneBusinessReportKey = String(standaloneConfig?.businessReport || '').trim();
  const standaloneMode = standaloneBusinessReportKey !== '';
  const standaloneBusinessReportMeta = getStandaloneBusinessReportMeta(standaloneBusinessReportKey);
  const scopedOutletId = getScopedOutletId(currentUser);
  const defaultRange = buildDefaultRange('last-30-days');
  const initialOutletFilter = scopedOutletId || String(searchParams.get('outlet_id') || '').trim();
  const initialDateFrom = isValidReportCenterDateInput(searchParams.get('date_from'))
    ? searchParams.get('date_from')
    : defaultRange.dateFrom;
  const initialDateTo = isValidReportCenterDateInput(searchParams.get('date_to'))
    ? searchParams.get('date_to')
    : defaultRange.dateTo;
  const initialFilterState = buildReportFilterState({
    scopedOutletId,
    queryOutletId: initialOutletFilter,
    dateFrom: initialDateFrom,
    dateTo: initialDateTo,
  });
  const [outlets, setOutlets] = useState([]);
  const [filters, setFilters] = useState(initialFilterState);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterState);
  const quickRange = resolveQuickRangePreset(filters.dateFrom, filters.dateTo);
  const scopedOutletExperience = resolveOutletExperience(currentUser?.outlet_type ?? currentUser?.OutletType ?? '');
  const selectedAnalyticsOutlet = outlets.find(outlet => String(outlet.id) === String(appliedFilters.outlet || ''));
  const activeAnalyticsOutletType = scopedOutletId
    ? scopedOutletExperience.type
    : selectedAnalyticsOutlet?.type || '';
  const activeAnalyticsExperience = resolveOutletExperience(activeAnalyticsOutletType || scopedOutletExperience.type);
  const availableTabs = standaloneMode
    ? REPORT_TABS.filter(item => item.key === 'business')
    : getAvailableReportTabsForUser(currentUser, activeAnalyticsExperience.type);
  const availableBusinessReports = standaloneMode
    ? (standaloneBusinessReportMeta ? [standaloneBusinessReportMeta] : [])
    : availableTabs.some(item => item.key === 'business')
      ? BUSINESS_REPORTS
      : [];
  const availablePosReports = availableTabs.some(item => item.key === 'pos') ? POS_REPORTS : [];
  const availableAccountingReports = availableTabs.some(item => item.key === 'accounting') ? ACCOUNTING_REPORTS : [];
  const defaultTab = standaloneMode ? 'business' : availableTabs[0]?.key || 'business';
  const defaultBusinessReport = standaloneMode
    ? standaloneBusinessReportKey || availableBusinessReports[0]?.key || 'membership-sales'
    : availableBusinessReports[0]?.key || 'membership-sales';
  const defaultPosReport = availablePosReports[0]?.key || 'sales-summary';
  const defaultAccountingReport = availableAccountingReports[0]?.key || 'journal-report';
  const activeTab = standaloneMode
    ? 'business'
    : availableTabs.some(item => item.key === searchParams.get('tab'))
      ? searchParams.get('tab')
      : defaultTab;
  const activeBusinessReport = standaloneMode
    ? defaultBusinessReport
    : availableBusinessReports.some(item => item.key === searchParams.get('report'))
      ? searchParams.get('report')
      : defaultBusinessReport;
  const activePosReport = availablePosReports.some(item => item.key === searchParams.get('pos_report'))
    ? searchParams.get('pos_report')
    : defaultPosReport;
  const activeAccountingReport = availableAccountingReports.some(item => item.key === searchParams.get('accounting_report'))
    ? searchParams.get('accounting_report')
    : defaultAccountingReport;
  const activeTabMeta =
    availableTabs.find(item => item.key === activeTab) || availableTabs[0] || REPORT_TABS[0];
  const activeBusinessReportMeta =
    availableBusinessReports.find(item => item.key === activeBusinessReport) || availableBusinessReports[0] || BUSINESS_REPORTS[0];
  const activePosReportMeta =
    availablePosReports.find(item => item.key === activePosReport) || availablePosReports[0] || POS_REPORTS[0];
  const activeAccountingReportMeta =
    availableAccountingReports.find(item => item.key === activeAccountingReport) || availableAccountingReports[0] || ACCOUNTING_REPORTS[0];
  const analyticsDescription = activeAnalyticsExperience.type
    ? `${activeAnalyticsExperience.analyticsDescription} Tab Accounting tetap tersedia supaya outlet tetap bisa menutup operasional dengan pembacaan finansial yang konsisten.`
    : 'Satu pusat laporan untuk business, POS, dan accounting. Business reports utama sudah aktif, POS report inti sudah lengkap, dan Accounting kini mencakup Journal Report, General Ledger, Trial Balance, Profit & Loss, Balance Sheet, Cash Flow, AP Aging, Expense Breakdown, Asset Register, serta Depreciation Schedule.';
  const pageEyebrow = standaloneMode
    ? standaloneConfig?.eyebrow || activeBusinessReportMeta.label || 'Report'
    : activeAnalyticsExperience.type
      ? `${activeAnalyticsExperience.displayLabel} Analytics`
      : 'Analytics Center';
  const pageTitle = standaloneMode
    ? standaloneConfig?.title || activeBusinessReportMeta.label || 'Report'
    : activeAnalyticsExperience.type
      ? `${activeAnalyticsExperience.displayLabel} Analytics Center`
      : 'Analytics Center';
  const pageDescription = standaloneMode
    ? standaloneConfig?.description || activeBusinessReportMeta.copy || analyticsDescription
    : analyticsDescription;
  const pageBreadcrumbs = standaloneMode
    ? [
      { label: 'Dashboard', to: '/dashboardadmin' },
      { label: standaloneConfig?.breadcrumbLabel || pageTitle },
    ]
    : [
      { label: 'Dashboard', to: '/dashboardadmin' },
      { label: 'Analytics Center' },
    ];
  const canOpenAccountingControl = hasAccess('/accounting-setup', currentUser);
  const canOpenExpensesPayables = hasAccess('/operasional-finance', currentUser);
  const canOpenAssetsInvestments = hasAccess('/asset-management', currentUser);
  const [activePurchaseSection, setActivePurchaseSection] = useState('summary');
  const [activeVendorPriceSection, setActiveVendorPriceSection] = useState('summary');
  const [activeAccountingJournalSection, setActiveAccountingJournalSection] = useState('summary');
  const [activeAccountingGeneralLedgerSection, setActiveAccountingGeneralLedgerSection] = useState('summary');
  const [activeAccountingJournalDetailSection, setActiveAccountingJournalDetailSection] = useState('overview');
  const [selectedAccountingJournalId, setSelectedAccountingJournalId] = useState(null);
  const [selectedAccountingGeneralLedgerAccountId, setSelectedAccountingGeneralLedgerAccountId] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [ptUsers, setPTUsers] = useState([]);
  const [ptProducts, setPTProducts] = useState([]);
  const [ptClients, setPTClients] = useState([]);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backendError, setBackendError] = useState('');
  const [reportData, setReportData] = useState(emptyReport);
  const [membershipGrowthAIData, setMembershipGrowthAIData] = useState(emptyMembershipGrowthAIReport);
  const [membershipGrowthAILoading, setMembershipGrowthAILoading] = useState(false);
  const [membershipGrowthAIError, setMembershipGrowthAIError] = useState('');
  const [membershipGrowthFeedbackSubmitting, setMembershipGrowthFeedbackSubmitting] = useState({});
  const [staffTransactionData, setStaffTransactionData] = useState(emptyStaffTransactionSummaryReport);
  const [memberActivityData, setMemberActivityData] = useState(emptyMemberActivityReport);
  const [checkinData, setCheckinData] = useState(emptyCheckinReport);
  const [ptSalesData, setPTSalesData] = useState(emptyPTSalesReport);
  const [ptSessionData, setPTSessionData] = useState(emptyPTSessionReport);
  const [ptTrainerData, setPTTrainerData] = useState(emptyPTTrainerPerformanceReport);
  const [posSalesData, setPOSSalesData] = useState(emptyPOSSalesReport);
  const [posPricingAIData, setPOSPricingAIData] = useState(emptyPOSPricingAIReport);
  const [posPricingAILoading, setPOSPricingAILoading] = useState(false);
  const [posPricingAIError, setPOSPricingAIError] = useState('');
  const [posPricingFeedbackSubmitting, setPOSPricingFeedbackSubmitting] = useState({});
  const [posCashierSalesData, setPOSCashierSalesData] = useState(emptyPOSCashierSalesReport);
  const [posProductSalesData, setPOSProductSalesData] = useState(emptyPOSProductSalesReport);
  const [posRefundVoidData, setPOSRefundVoidData] = useState(emptyPOSRefundVoidReport);
  const [posPurchaseData, setPOSPurchaseData] = useState(emptyPOSPurchaseReport);
  const [posVendorAgingData, setPOSVendorAgingData] = useState(emptyPOSVendorAgingReport);
  const [posStockMovementData, setPOSStockMovementData] = useState(emptyPOSStockMovementReport);
  const [posBOMConsumptionData, setPOSBOMConsumptionData] = useState(emptyPOSBOMConsumptionReport);
  const [posStockOpnameVarianceData, setPOSStockOpnameVarianceData] = useState(emptyPOSStockOpnameVarianceReport);
  const [posVendorPriceComparisonData, setPOSVendorPriceComparisonData] = useState(emptyPOSVendorPriceComparisonReport);
  const [accountingJournalData, setAccountingJournalData] = useState(emptyAccountingJournalReport);
  const [accountingGeneralLedgerData, setAccountingGeneralLedgerData] = useState(emptyAccountingGeneralLedgerReport);
  const [accountingTrialBalanceData, setAccountingTrialBalanceData] = useState(emptyAccountingTrialBalanceReport);
  const [accountingProfitLossData, setAccountingProfitLossData] = useState(emptyAccountingProfitLossReport);
  const [accountingBalanceSheetData, setAccountingBalanceSheetData] = useState(emptyAccountingBalanceSheetReport);
  const [accountingCashFlowData, setAccountingCashFlowData] = useState(emptyAccountingCashFlowReport);
  const [accountingAccountsPayableAgingData, setAccountingAccountsPayableAgingData] = useState(emptyAccountingAccountsPayableAgingReport);
  const [accountingExpenseBreakdownData, setAccountingExpenseBreakdownData] = useState(emptyAccountingExpenseBreakdownReport);
  const [accountingAssetRegisterData, setAccountingAssetRegisterData] = useState(emptyAccountingAssetRegisterReport);
  const [accountingDepreciationScheduleData, setAccountingDepreciationScheduleData] = useState(emptyAccountingDepreciationScheduleReport);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const hasPendingFilterChanges =
    JSON.stringify(normalizeFilterSnapshot(filters)) !==
    JSON.stringify(normalizeFilterSnapshot(appliedFilters));
  const canUseReportActions =
    (activeTab === 'business' &&
      ['membership-sales', 'staff-transaction-summary', 'member-activity', 'check-in', 'pt-sales', 'pt-session', 'pt-trainer'].includes(activeBusinessReport)) ||
    (activeTab === 'pos' && ['sales-summary', 'cashier-sales-summary', 'product-sales-report', 'refund-void-report', 'purchase-report', 'vendor-aging', 'stock-movement', 'stock-opname-variance', 'bom-consumption-report', 'vendor-price-comparison'].includes(activePosReport)) ||
    (activeTab === 'accounting' && ['journal-report', 'general-ledger', 'trial-balance', 'profit-loss', 'balance-sheet', 'cash-flow', 'accounts-payable-aging', 'expense-breakdown', 'asset-register', 'depreciation-schedule'].includes(activeAccountingReport));
  const scopedAIOutletId = Number(appliedFilters.outlet || 0);
  const canCaptureAIOutletFeedback = scopedAIOutletId > 0;

  const collectAIFeedbackOutcomeMetrics = async feedbackAction => {
    if (!isAIFeedbackOutcomeAction(feedbackAction)) {
      return {};
    }

    const isSalesOutcome = String(feedbackAction).trim().toLowerCase() === 'sales_improved';
    const result = await Swal.fire({
      title: isSalesOutcome ? 'Catat hasil sales' : 'Catat campaign AI',
      html: `
        <div style="display:grid;gap:12px;text-align:left;">
          <div>
            <label for="ai-feedback-campaign-start" style="display:block;font-size:12px;margin-bottom:4px;">Mulai campaign</label>
            <input id="ai-feedback-campaign-start" type="date" class="swal2-input" style="margin:0;width:100%;" />
          </div>
          <div>
            <label for="ai-feedback-campaign-end" style="display:block;font-size:12px;margin-bottom:4px;">Selesai campaign</label>
            <input id="ai-feedback-campaign-end" type="date" class="swal2-input" style="margin:0;width:100%;" />
          </div>
          <div>
            <label for="ai-feedback-estimated-lift" style="display:block;font-size:12px;margin-bottom:4px;">Estimasi lift omzet</label>
            <input id="ai-feedback-estimated-lift" type="number" min="0" step="1000" class="swal2-input" style="margin:0;width:100%;" placeholder="Contoh 500000" />
          </div>
          <div>
            <label for="ai-feedback-observed-lift" style="display:block;font-size:12px;margin-bottom:4px;">Realisasi lift omzet</label>
            <input id="ai-feedback-observed-lift" type="number" min="0" step="1000" class="swal2-input" style="margin:0;width:100%;" placeholder="Contoh 750000" />
          </div>
          <div>
            <label for="ai-feedback-transaction-lift" style="display:block;font-size:12px;margin-bottom:4px;">Lift transaksi (%)</label>
            <input id="ai-feedback-transaction-lift" type="number" min="0" step="0.1" class="swal2-input" style="margin:0;width:100%;" placeholder="Contoh 12.5" />
          </div>
          <div>
            <label for="ai-feedback-note" style="display:block;font-size:12px;margin-bottom:4px;">Catatan</label>
            <textarea id="ai-feedback-note" class="swal2-textarea" style="margin:0;width:100%;" placeholder="Contoh: dipakai untuk promo akhir pekan atau terlihat menaikkan pembelian paket 3 bulan."></textarea>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Simpan outcome',
      cancelButtonText: 'Batal',
      preConfirm: () => {
        const campaignStartDate = document.getElementById('ai-feedback-campaign-start')?.value?.trim() || '';
        const campaignEndDate = document.getElementById('ai-feedback-campaign-end')?.value?.trim() || '';
        if (!campaignStartDate || !campaignEndDate) {
          Swal.showValidationMessage('Tanggal mulai dan selesai campaign wajib diisi agar sistem bisa menghitung before vs after otomatis.');
          return false;
        }
        if (campaignStartDate && campaignEndDate && campaignEndDate < campaignStartDate) {
          Swal.showValidationMessage('Tanggal selesai campaign tidak boleh lebih kecil dari tanggal mulai.');
          return false;
        }

        const estimatedRevenueLift = readAIFeedbackNumericInput('ai-feedback-estimated-lift');
        const observedRevenueLift = readAIFeedbackNumericInput('ai-feedback-observed-lift');
        const observedTransactionLiftPercent = readAIFeedbackNumericInput('ai-feedback-transaction-lift');

        return {
          campaign_start_date: campaignStartDate,
          campaign_end_date: campaignEndDate,
          estimated_revenue_lift: estimatedRevenueLift,
          observed_revenue_lift: observedRevenueLift,
          observed_transaction_lift_percent: observedTransactionLiftPercent,
          note: document.getElementById('ai-feedback-note')?.value?.trim() || '',
        };
      },
    });

    if (!result.isConfirmed) {
      return null;
    }
    return result.value || {};
  };

  const handleMembershipGrowthFeedback = async (recommendationType, recommendationKey, feedbackAction) => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      Swal.fire('Sesi berakhir', 'Silakan login ulang sebelum menyimpan feedback AI.', 'warning');
      return;
    }
    if (!canCaptureAIOutletFeedback) {
      Swal.fire('Pilih outlet dulu', 'Feedback AI membership disimpan per outlet. Pilih satu outlet spesifik lalu coba lagi.', 'info');
      return;
    }

    const outcomePayload = await collectAIFeedbackOutcomeMetrics(feedbackAction);
    if (outcomePayload === null) {
      return;
    }

    const stateKey = buildAIFeedbackStateKey(recommendationType, recommendationKey, feedbackAction);
    setMembershipGrowthFeedbackSubmitting(previous => ({
      ...previous,
      [stateKey]: true,
    }));

    try {
      await requestMembershipGrowthFeedback(token, {
        outlet: scopedAIOutletId,
        recommendation_type: recommendationType,
        recommendation_key: recommendationKey,
        feedback_action: feedbackAction,
        ...outcomePayload,
      });

      const refreshed = await requestMembershipGrowthAssistant(token, {
        outlet: scopedAIOutletId,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        limit: 4,
      });
      setMembershipGrowthAIData({
        summary: refreshed?.summary || emptyMembershipGrowthAISummary,
        upsell_suggestions: Array.isArray(refreshed?.upsell_suggestions) ? refreshed.upsell_suggestions : [],
        promo_candidates: Array.isArray(refreshed?.promo_candidates) ? refreshed.promo_candidates : [],
      });
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `Feedback ${formatAIFeedbackActionLabel(feedbackAction).toLowerCase()} tersimpan`,
        showConfirmButton: false,
        timer: 1800,
      });
    } catch (error) {
      Swal.fire('Gagal menyimpan feedback', error.message || 'Gagal menyimpan feedback AI membership', 'error');
    } finally {
      setMembershipGrowthFeedbackSubmitting(previous => ({
        ...previous,
        [stateKey]: false,
      }));
    }
  };

  const handlePOSPricingFeedback = async (recommendationType, recommendationKey, feedbackAction) => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      Swal.fire('Sesi berakhir', 'Silakan login ulang sebelum menyimpan feedback AI.', 'warning');
      return;
    }
    if (!canCaptureAIOutletFeedback) {
      Swal.fire('Pilih outlet dulu', 'Feedback AI pricing POS disimpan per outlet. Pilih satu outlet spesifik lalu coba lagi.', 'info');
      return;
    }

    const outcomePayload = await collectAIFeedbackOutcomeMetrics(feedbackAction);
    if (outcomePayload === null) {
      return;
    }

    const stateKey = buildAIFeedbackStateKey(recommendationType, recommendationKey, feedbackAction);
    setPOSPricingFeedbackSubmitting(previous => ({
      ...previous,
      [stateKey]: true,
    }));

    try {
      await requestPOSPricingFeedback(token, {
        outlet_id: scopedAIOutletId,
        recommendation_type: recommendationType,
        recommendation_key: recommendationKey,
        feedback_action: feedbackAction,
        ...outcomePayload,
      });

      const refreshed = await requestPOSPricingAssistant(token, {
        outlet: scopedAIOutletId,
        payment_method: appliedFilters.payment_method,
        dateFrom: appliedFilters.dateFrom,
        dateTo: appliedFilters.dateTo,
        product_id: Number(appliedFilters.product_id || 0),
      });
      setPOSPricingAIData({
        summary: refreshed?.summary || emptyPOSPricingAISummary,
        bundling_recommendations: Array.isArray(refreshed?.bundling_recommendations) ? refreshed.bundling_recommendations : [],
        promo_candidates: Array.isArray(refreshed?.promo_candidates) ? refreshed.promo_candidates : [],
      });
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `Feedback ${formatAIFeedbackActionLabel(feedbackAction).toLowerCase()} tersimpan`,
        showConfirmButton: false,
        timer: 1800,
      });
    } catch (error) {
      Swal.fire('Gagal menyimpan feedback', error.message || 'Gagal menyimpan feedback AI pricing POS', 'error');
    } finally {
      setPOSPricingFeedbackSubmitting(previous => ({
        ...previous,
        [stateKey]: false,
      }));
    }
  };

  useEffect(() => {
    if (!availableTabs.length) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    let shouldReplace = false;

    if (standaloneMode) {
      ['tab', 'report', 'pos_report', 'accounting_report'].forEach(param => {
        if (nextParams.has(param)) {
          nextParams.delete(param);
          shouldReplace = true;
        }
      });
    } else {
      if (nextParams.get('tab') !== activeTab) {
        nextParams.set('tab', activeTab);
        shouldReplace = true;
      }

      if (activeTab === 'business') {
        if (nextParams.get('report') !== activeBusinessReport) {
          nextParams.set('report', activeBusinessReport);
          shouldReplace = true;
        }
        if (nextParams.has('pos_report')) {
          nextParams.delete('pos_report');
          shouldReplace = true;
        }
      } else if (nextParams.has('report')) {
        nextParams.delete('report');
        shouldReplace = true;
      }

      if (activeTab === 'pos') {
        if (nextParams.get('pos_report') !== activePosReport) {
          nextParams.set('pos_report', activePosReport);
          shouldReplace = true;
        }
      } else if (nextParams.has('pos_report')) {
        nextParams.delete('pos_report');
        shouldReplace = true;
      }

      if (activeTab === 'accounting') {
        if (nextParams.get('accounting_report') !== activeAccountingReport) {
          nextParams.set('accounting_report', activeAccountingReport);
          shouldReplace = true;
        }
      } else if (nextParams.has('accounting_report')) {
        nextParams.delete('accounting_report');
        shouldReplace = true;
      }
    }

    if (appliedFilters.outlet) {
      if (nextParams.get('outlet_id') !== appliedFilters.outlet) {
        nextParams.set('outlet_id', appliedFilters.outlet);
        shouldReplace = true;
      }
    } else if (nextParams.has('outlet_id')) {
      nextParams.delete('outlet_id');
      shouldReplace = true;
    }

    if (appliedFilters.dateFrom) {
      if (nextParams.get('date_from') !== appliedFilters.dateFrom) {
        nextParams.set('date_from', appliedFilters.dateFrom);
        shouldReplace = true;
      }
    } else if (nextParams.has('date_from')) {
      nextParams.delete('date_from');
      shouldReplace = true;
    }

    if (appliedFilters.dateTo) {
      if (nextParams.get('date_to') !== appliedFilters.dateTo) {
        nextParams.set('date_to', appliedFilters.dateTo);
        shouldReplace = true;
      }
    } else if (nextParams.has('date_to')) {
      nextParams.delete('date_to');
      shouldReplace = true;
    }

    if (shouldReplace) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    activeAccountingReport,
    activeBusinessReport,
    activePosReport,
    activeTab,
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    appliedFilters.outlet,
    availableTabs.length,
    searchParams,
    setSearchParams,
    standaloneMode,
  ]);

  useEffect(() => {
    if (activeTab === 'pos' && activePosReport === 'purchase-report') {
      setActivePurchaseSection('summary');
    }
  }, [activePosReport, activeTab]);

  useEffect(() => {
    if (activeTab === 'pos' && activePosReport === 'vendor-price-comparison') {
      setActiveVendorPriceSection('summary');
    }
  }, [activePosReport, activeTab]);

  useEffect(() => {
    if (activeTab === 'accounting' && activeAccountingReport === 'journal-report') {
      setActiveAccountingJournalSection('summary');
    }
  }, [activeAccountingReport, activeTab]);

  useEffect(() => {
    if (activeTab === 'accounting' && activeAccountingReport === 'general-ledger') {
      setActiveAccountingGeneralLedgerSection('summary');
    }
  }, [activeAccountingReport, activeTab]);

  useEffect(() => {
    if (activeTab === 'accounting' && activeAccountingReport === 'journal-report') {
      setActiveAccountingJournalDetailSection('overview');
    }
  }, [activeAccountingReport, activeTab, selectedAccountingJournalId]);

  useEffect(() => {
    const rows = Array.isArray(accountingJournalData.results) ? accountingJournalData.results : [];
    if (!rows.length) {
      setSelectedAccountingJournalId(null);
      return;
    }

    const hasSelectedRow = rows.some(row => Number(row?.id) === Number(selectedAccountingJournalId));
    if (!hasSelectedRow) {
      setSelectedAccountingJournalId(rows[0].id);
    }
  }, [accountingJournalData.results, selectedAccountingJournalId]);

  useEffect(() => {
    const rows = Array.isArray(accountingGeneralLedgerData.results) ? accountingGeneralLedgerData.results : [];
    if (!rows.length) {
      setSelectedAccountingGeneralLedgerAccountId(null);
      return;
    }

    const hasSelectedRow = rows.some(row => String(row?.account_id || '') === String(selectedAccountingGeneralLedgerAccountId || ''));
    if (!hasSelectedRow) {
      setSelectedAccountingGeneralLedgerAccountId(rows[0].account_id);
    }
  }, [accountingGeneralLedgerData.results, selectedAccountingGeneralLedgerAccountId]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return;
    }

    let isMounted = true;
    const loadBootstrap = async () => {
      setBootstrapping(true);
      try {
        const response = await fetch(`${API_BASE_URL}outletlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ limit: 200, offset: 0 }),
        });
        const data = await parseJsonResponse(response);
        if (!response.ok || !isSuccessResponse(data?.error) || !Array.isArray(data?.results)) {
          throw new Error(data?.message || 'Gagal memuat outlet');
        }

        if (isMounted) {
          setOutlets(
            data.results.map(outlet => ({
              id: String(outlet?.id ?? outlet?.ID ?? ''),
              name: outlet?.outlet_name || outlet?.OutletName || `Outlet #${outlet?.id || outlet?.ID || '-'}`,
              type: normalizeOutletTypeValue(outlet?.outlet_type ?? outlet?.OutletType ?? ''),
            }))
          );
        }
      } catch (error) {
        if (isMounted) {
          Swal.fire('Error', error.message || 'Gagal memuat outlet', 'error');
        }
      } finally {
        if (isMounted) {
          setBootstrapping(false);
        }
      }
    };

    loadBootstrap();
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business') {
      return;
    }

    let isMounted = true;
    const loadMemberships = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}membership/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            limit: 200,
            offset: 0,
            outlet: Number(filters.outlet || 0),
          }),
        });
        const data = await parseJsonResponse(response);
        if (!response.ok || !isSuccessResponse(data?.error) || !Array.isArray(data?.results)) {
          throw new Error(data?.message || 'Gagal memuat paket membership');
        }

        if (isMounted) {
          setMemberships(
            data.results.map(item => ({
              id: String(item?.id ?? ''),
              name: item?.name || `Membership #${item?.id || '-'}`,
            }))
          );
        }
      } catch (error) {
        if (isMounted) {
          setMemberships([]);
        }
      }
    };

    loadMemberships();
    return () => {
      isMounted = false;
    };
  }, [activeTab, filters.outlet]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || !['pt-sales', 'pt-session', 'pt-trainer'].includes(activeBusinessReport)) {
      return;
    }

    let isMounted = true;
    const loadPTReference = async () => {
      try {
        const [ptUserResponse, ptProductResponse, ptClientResponse] = await Promise.all([
          fetch(`${API_BASE_URL}pt-user/list`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              limit: 200,
              offset: 0,
              is_active: true,
            }),
          }),
          fetch(`${API_BASE_URL}pt-product/list`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              limit: 200,
              offset: 0,
              outlet_id: Number(filters.outlet || 0),
              pt_user_id: Number(filters.pt_user_id || 0),
            }),
          }),
          fetch(`${API_BASE_URL}pt-client/list`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              limit: 200,
              offset: 0,
              outlet_id: Number(filters.outlet || 0),
              is_active: true,
            }),
          }),
        ]);

        const [ptUserData, ptProductData, ptClientData] = await Promise.all([
          parseJsonResponse(ptUserResponse),
          parseJsonResponse(ptProductResponse),
          parseJsonResponse(ptClientResponse),
        ]);

        if (!ptUserResponse.ok || !isSuccessResponse(ptUserData?.error)) {
          throw new Error(ptUserData?.message || 'Gagal memuat staff layanan');
        }
        if (!ptProductResponse.ok || !isSuccessResponse(ptProductData?.error)) {
          throw new Error(ptProductData?.message || 'Gagal memuat layanan session');
        }
        if (!ptClientResponse.ok || !isSuccessResponse(ptClientData?.error)) {
          throw new Error(ptClientData?.message || 'Gagal memuat session client');
        }

        if (isMounted) {
          setPTUsers(Array.isArray(ptUserData?.results) ? ptUserData.results : []);
          setPTProducts(Array.isArray(ptProductData?.results) ? ptProductData.results : []);
          setPTClients(Array.isArray(ptClientData?.results) ? ptClientData.results : []);
        }
      } catch (error) {
        if (isMounted) {
          setPTUsers([]);
          setPTProducts([]);
          setPTClients([]);
        }
      }
    };

    loadPTReference();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, filters.outlet, filters.pt_user_id]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || activeBusinessReport !== 'membership-sales') {
      return;
    }

    let isMounted = true;
    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestMembershipSalesReport(token, {
          limit: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          outlet: Number(appliedFilters.outlet || 0),
          membership_id: Number(appliedFilters.membership_id || 0),
          payment_method: appliedFilters.payment_method,
          status: appliedFilters.status === '' ? null : Number(appliedFilters.status),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setReportData({
            summary: data?.summary || emptySummary,
            status_breakdown: Array.isArray(data?.status_breakdown) ? data.status_breakdown : [],
            payment_method_breakdown: Array.isArray(data?.payment_method_breakdown) ? data.payment_method_breakdown : [],
            source_breakdown: Array.isArray(data?.source_breakdown) ? data.source_breakdown : [],
            membership_breakdown: Array.isArray(data?.membership_breakdown) ? data.membership_breakdown : [],
            outlet_breakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
            processor_breakdown: Array.isArray(data?.processor_breakdown) ? data.processor_breakdown : [],
            trends: Array.isArray(data?.trends) ? data.trends : [],
            results: Array.isArray(data?.results) ? data.results : [],
          });
          setTotalRows(Number(data?.total || 0));
        }
      } catch (error) {
        if (isMounted) {
          setReportData(emptyReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Membership Sales Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, appliedFilters, currentPage, rowsPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || activeBusinessReport !== 'membership-sales') {
      return;
    }

    let isMounted = true;
    const loadAssistant = async () => {
      setMembershipGrowthAILoading(true);
      setMembershipGrowthAIError('');
      try {
        const data = await requestMembershipGrowthAssistant(token, {
          outlet: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          limit: 4,
        });

        if (isMounted) {
          setMembershipGrowthAIData({
            summary: data?.summary || emptyMembershipGrowthAISummary,
            upsell_suggestions: Array.isArray(data?.upsell_suggestions) ? data.upsell_suggestions : [],
            promo_candidates: Array.isArray(data?.promo_candidates) ? data.promo_candidates : [],
          });
        }
      } catch (error) {
        if (isMounted) {
          setMembershipGrowthAIData(emptyMembershipGrowthAIReport);
          setMembershipGrowthAIError(error.message || 'Gagal memuat AI membership growth');
        }
      } finally {
        if (isMounted) {
          setMembershipGrowthAILoading(false);
        }
      }
    };

    loadAssistant();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, appliedFilters.outlet, appliedFilters.dateFrom, appliedFilters.dateTo]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || activeBusinessReport !== 'staff-transaction-summary') {
      return;
    }

    let isMounted = true;
    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestStaffTransactionSummaryReport(token, {
          limit: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          outlet: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setStaffTransactionData({
            summary: data?.summary || emptyStaffTransactionSummary,
            outlet_breakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
            position_breakdown: Array.isArray(data?.position_breakdown) ? data.position_breakdown : [],
            channel_breakdown: Array.isArray(data?.channel_breakdown) ? data.channel_breakdown : [],
            top_staff_breakdown: Array.isArray(data?.top_staff_breakdown) ? data.top_staff_breakdown : [],
            trends: Array.isArray(data?.trends) ? data.trends : [],
            results: Array.isArray(data?.results) ? data.results : [],
          });
          setTotalRows(Number(data?.total || 0));
        }
      } catch (error) {
        if (isMounted) {
          setStaffTransactionData(emptyStaffTransactionSummaryReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Staff Transaction Summary');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, appliedFilters, currentPage, rowsPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || activeBusinessReport !== 'member-activity') {
      return;
    }

    let isMounted = true;
    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestMemberActivityReport(token, {
          limit: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          outlet: Number(appliedFilters.outlet || 0),
          membership_id: Number(appliedFilters.membership_id || 0),
          lifecycle: appliedFilters.lifecycle,
          activity: appliedFilters.activity,
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setMemberActivityData({
            summary: data?.summary || emptyMemberActivitySummary,
            lifecycle_breakdown: Array.isArray(data?.lifecycle_breakdown) ? data.lifecycle_breakdown : [],
            activity_breakdown: Array.isArray(data?.activity_breakdown) ? data.activity_breakdown : [],
            membership_breakdown: Array.isArray(data?.membership_breakdown) ? data.membership_breakdown : [],
            outlet_breakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
            results: Array.isArray(data?.results) ? data.results : [],
          });
          setTotalRows(Number(data?.total || 0));
        }
      } catch (error) {
        if (isMounted) {
          setMemberActivityData(emptyMemberActivityReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Member Activity Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, appliedFilters, currentPage, rowsPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || activeBusinessReport !== 'check-in') {
      return;
    }

    let isMounted = true;
    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestCheckinReport(token, {
          limit: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          outlet: Number(appliedFilters.outlet || 0),
          membership_id: Number(appliedFilters.membership_id || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setCheckinData({
            summary: data?.summary || emptyCheckinSummary,
            membership_breakdown: Array.isArray(data?.membership_breakdown) ? data.membership_breakdown : [],
            outlet_breakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
            weekday_breakdown: Array.isArray(data?.weekday_breakdown) ? data.weekday_breakdown : [],
            hour_breakdown: Array.isArray(data?.hour_breakdown) ? data.hour_breakdown : [],
            top_member_breakdown: Array.isArray(data?.top_member_breakdown) ? data.top_member_breakdown : [],
            trends: Array.isArray(data?.trends) ? data.trends : [],
            results: Array.isArray(data?.results) ? data.results : [],
          });
          setTotalRows(Number(data?.total || 0));
        }
      } catch (error) {
        if (isMounted) {
          setCheckinData(emptyCheckinReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Check-in Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, appliedFilters, currentPage, rowsPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || activeBusinessReport !== 'pt-sales') {
      return;
    }

    let isMounted = true;
    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPTSalesReport(token, {
          limit: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          outlet_id: Number(appliedFilters.outlet || 0),
          pt_user_id: Number(appliedFilters.pt_user_id || 0),
          product_id: Number(appliedFilters.product_id || 0),
          client_type: appliedFilters.client_type,
          payment_status: appliedFilters.pt_payment_status,
          contract_status: appliedFilters.contract_status,
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setPTSalesData({
            summary: data?.summary || emptyPTSalesSummary,
            trainer_breakdown: Array.isArray(data?.trainer_breakdown) ? data.trainer_breakdown : [],
            product_breakdown: Array.isArray(data?.product_breakdown) ? data.product_breakdown : [],
            client_type_breakdown: Array.isArray(data?.client_type_breakdown) ? data.client_type_breakdown : [],
            outlet_breakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
            payment_status_breakdown: Array.isArray(data?.payment_status_breakdown) ? data.payment_status_breakdown : [],
            contract_breakdown: Array.isArray(data?.contract_breakdown) ? data.contract_breakdown : [],
            trends: Array.isArray(data?.trends) ? data.trends : [],
            results: Array.isArray(data?.results) ? data.results : [],
          });
          setTotalRows(Number(data?.total || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPTSalesData(emptyPTSalesReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Private Session Sales Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, appliedFilters, currentPage, rowsPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || activeBusinessReport !== 'pt-session') {
      return;
    }

    let isMounted = true;
    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPTSessionReport(token, {
          limit: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          outlet_id: Number(appliedFilters.outlet || 0),
          client_id: Number(appliedFilters.client_id || 0),
          pt_user_id: Number(appliedFilters.pt_user_id || 0),
          product_id: Number(appliedFilters.product_id || 0),
          booking_status: appliedFilters.booking_status,
          attendance_status: appliedFilters.attendance_status,
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setPTSessionData({
            summary: data?.summary || emptyPTSessionSummary,
            trainer_breakdown: Array.isArray(data?.trainer_breakdown) ? data.trainer_breakdown : [],
            client_breakdown: Array.isArray(data?.client_breakdown) ? data.client_breakdown : [],
            product_breakdown: Array.isArray(data?.product_breakdown) ? data.product_breakdown : [],
            outlet_breakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
            booking_status_breakdown: Array.isArray(data?.booking_status_breakdown) ? data.booking_status_breakdown : [],
            attendance_status_breakdown: Array.isArray(data?.attendance_status_breakdown) ? data.attendance_status_breakdown : [],
            trends: Array.isArray(data?.trends) ? data.trends : [],
            results: Array.isArray(data?.results) ? data.results : [],
          });
          setTotalRows(Number(data?.total || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPTSessionData(emptyPTSessionReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Private Session Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, appliedFilters, currentPage, rowsPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'business' || activeBusinessReport !== 'pt-trainer') {
      return;
    }

    let isMounted = true;
    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPTTrainerPerformanceReport(token, {
          limit: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          outlet_id: Number(appliedFilters.outlet || 0),
          pt_user_id: Number(appliedFilters.pt_user_id || 0),
          payment_status: appliedFilters.pt_payment_status,
          attendance_status: appliedFilters.attendance_status,
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setPTTrainerData({
            summary: data?.summary || emptyPTTrainerPerformanceSummary,
            trainer_breakdown: Array.isArray(data?.trainer_breakdown) ? data.trainer_breakdown : [],
            outlet_breakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
            product_type_breakdown: Array.isArray(data?.product_type_breakdown) ? data.product_type_breakdown : [],
            trends: Array.isArray(data?.trends) ? data.trends : [],
            results: Array.isArray(data?.results) ? data.results : [],
          });
          setTotalRows(Number(data?.total || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPTTrainerData(emptyPTTrainerPerformanceReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Private Session Staff Performance Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeBusinessReport, activeTab, appliedFilters, currentPage, rowsPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || !['sales-summary', 'cashier-sales-summary', 'product-sales-report'].includes(activePosReport)) {
      return;
    }

    let isMounted = true;
    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const rows = await requestPOSSalesReport(token, {
          outlet: Number(appliedFilters.outlet || 0),
          payment_method: appliedFilters.payment_method,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
        });
        const nextSalesReport = buildPOSSalesReport(rows, resolveOutletName, appliedFilters.search);
        const nextCashierReport = buildPOSCashierSalesReport(rows, resolveOutletName, appliedFilters.search);
        const nextProductReport = buildPOSProductSalesReport(rows, resolveOutletName, {
          product_id: appliedFilters.product_id,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setPOSSalesData(nextSalesReport);
          setPOSCashierSalesData(nextCashierReport);
          setPOSProductSalesData(nextProductReport);
          setTotalRows(Number(
            activePosReport === 'product-sales-report'
              ? nextProductReport.results.length
              : activePosReport === 'cashier-sales-summary'
                ? nextCashierReport.results.length
              : nextSalesReport.results.length
          ) || 0);
        }
      } catch (error) {
        if (isMounted) {
          setPOSSalesData(emptyPOSSalesReport);
          setPOSCashierSalesData(emptyPOSCashierSalesReport);
          setPOSProductSalesData(emptyPOSProductSalesReport);
          setTotalRows(0);
          setBackendError(error.message || (activePosReport === 'product-sales-report'
            ? 'Gagal memuat Product Sales Report'
            : activePosReport === 'cashier-sales-summary'
              ? 'Gagal memuat Cashier Sales Summary'
              : 'Gagal memuat POS Sales Summary'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters, outlets]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || activePosReport !== 'sales-summary') {
      return;
    }

    let isMounted = true;
    const loadAssistant = async () => {
      setPOSPricingAILoading(true);
      setPOSPricingAIError('');
      try {
        const data = await requestPOSPricingAssistant(token, {
          outlet: Number(appliedFilters.outlet || 0),
          payment_method: appliedFilters.payment_method,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          product_id: Number(appliedFilters.product_id || 0),
        });

        if (isMounted) {
          setPOSPricingAIData({
            summary: data?.summary || emptyPOSPricingAISummary,
            bundling_recommendations: Array.isArray(data?.bundling_recommendations) ? data.bundling_recommendations : [],
            promo_candidates: Array.isArray(data?.promo_candidates) ? data.promo_candidates : [],
          });
        }
      } catch (error) {
        if (isMounted) {
          setPOSPricingAIData(emptyPOSPricingAIReport);
          setPOSPricingAIError(error.message || 'Gagal memuat AI pricing POS');
        }
      } finally {
        if (isMounted) {
          setPOSPricingAILoading(false);
        }
      }
    };

    loadAssistant();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters.outlet, appliedFilters.payment_method, appliedFilters.product_id, appliedFilters.dateFrom, appliedFilters.dateTo]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || activePosReport !== 'refund-void-report') {
      return;
    }

    let isMounted = true;
    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPOSRefundVoidReport(token, {
          outlet: Number(appliedFilters.outlet || 0),
          request_type: appliedFilters.approval_request_type,
          approval_status: appliedFilters.approval_status,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          search: appliedFilters.search,
        });
        const nextReport = buildPOSRefundVoidReport(data, resolveOutletName, {
          payment_method: appliedFilters.payment_method,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setPOSRefundVoidData(nextReport);
          setTotalRows(Number(nextReport.results.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPOSRefundVoidData(emptyPOSRefundVoidReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Refund / Void Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters, outlets]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || activePosReport !== 'purchase-report') {
      return;
    }

    let isMounted = true;
    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPOSPurchaseReport(token, {
          outlet: Number(appliedFilters.outlet || 0),
          payment_method: appliedFilters.payment_method,
          payment_status: appliedFilters.purchase_payment_status,
          supplier_name: appliedFilters.vendor_name,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          search: appliedFilters.search,
        });
        const nextReport = normalizePOSPurchaseReport(data, resolveOutletName);

        if (isMounted) {
          setPOSPurchaseData(nextReport);
          setTotalRows(Number(nextReport.results.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPOSPurchaseData(emptyPOSPurchaseReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Purchase Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters, outlets]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || activePosReport !== 'vendor-aging') {
      return;
    }

    let isMounted = true;
    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPOSVendorAgingReport(token, {
          outlet: Number(appliedFilters.outlet || 0),
          vendor_name: appliedFilters.vendor_name,
          vendor_bill_type: appliedFilters.vendor_bill_type,
          vendor_bill_status: appliedFilters.vendor_bill_status,
          aging_bucket: appliedFilters.aging_bucket,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          search: appliedFilters.search,
        });
        const nextReport = normalizePOSVendorAgingReport(data, resolveOutletName);

        if (isMounted) {
          setPOSVendorAgingData(nextReport);
          setTotalRows(Number(nextReport.results.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPOSVendorAgingData(emptyPOSVendorAgingReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Vendor Aging');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters, outlets]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || activePosReport !== 'stock-movement') {
      return;
    }

    let isMounted = true;
    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPOSStockMovementReport(token, {
          outlet: Number(appliedFilters.outlet || 0),
          product_id: Number(appliedFilters.product_id || 0),
          movement_type: appliedFilters.movement_type,
          reference_type: appliedFilters.reference_type,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          search: appliedFilters.search,
        });
        const nextReport = normalizePOSStockMovementReport(data, resolveOutletName);

        if (isMounted) {
          setPOSStockMovementData(nextReport);
          setTotalRows(Number(nextReport.results.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPOSStockMovementData(emptyPOSStockMovementReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Stock Movement');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters, outlets]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || activePosReport !== 'bom-consumption-report') {
      return;
    }

    let isMounted = true;
    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPOSBOMConsumptionReport(token, {
          outlet: Number(appliedFilters.outlet || 0),
          product_id: Number(appliedFilters.product_id || 0),
          ingredient_product_id: Number(appliedFilters.ingredient_product_id || 0),
          payment_method: appliedFilters.payment_method,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          search: appliedFilters.search,
        });
        const nextReport = normalizePOSBOMConsumptionReport(data, resolveOutletName);

        if (isMounted) {
          setPOSBOMConsumptionData(nextReport);
          setTotalRows(Number(nextReport.results.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPOSBOMConsumptionData(emptyPOSBOMConsumptionReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat BOM Consumption');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters, outlets]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || activePosReport !== 'stock-opname-variance') {
      return;
    }

    let isMounted = true;
    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPOSStockOpnameVarianceReport(token, {
          outlet: Number(appliedFilters.outlet || 0),
          product_id: Number(appliedFilters.product_id || 0),
          variance_type: appliedFilters.variance_type,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          search: appliedFilters.search,
        });
        const nextReport = normalizePOSStockOpnameVarianceReport(data, resolveOutletName);

        if (isMounted) {
          setPOSStockOpnameVarianceData(nextReport);
          setTotalRows(Number(nextReport.results.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPOSStockOpnameVarianceData(emptyPOSStockOpnameVarianceReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Stock Opname Variance');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters, outlets]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'pos' || activePosReport !== 'vendor-price-comparison') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestPOSVendorPriceComparisonReport(token, {
          outlet: Number(appliedFilters.outlet || 0),
          category_id: Number(appliedFilters.category_id || 0),
          product_id: Number(appliedFilters.product_id || 0),
          vendor_name: appliedFilters.vendor_name,
          item_type: appliedFilters.item_type,
          payment_method: appliedFilters.payment_method,
          purchase_payment_status: appliedFilters.purchase_payment_status,
          comparison_mode: appliedFilters.comparison_mode,
          min_purchase_count: Number(appliedFilters.min_purchase_count || 1),
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
          search: appliedFilters.search,
        });
        const nextReport = normalizePOSVendorPriceComparisonReport(data);

        if (isMounted) {
          setPOSVendorPriceComparisonData(nextReport);
          setTotalRows(Number(nextReport.results.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setPOSVendorPriceComparisonData(emptyPOSVendorPriceComparisonReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Vendor Price Comparison');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activePosReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'journal-report') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingJournalReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          source_system: appliedFilters.source_system,
          business_unit: appliedFilters.business_unit,
          posting_status: appliedFilters.accounting_posting_status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingJournalData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingJournalData(emptyAccountingJournalReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Journal Report');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'general-ledger') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingGeneralLedgerReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          source_system: appliedFilters.source_system,
          business_unit: appliedFilters.business_unit,
          posting_status: appliedFilters.accounting_posting_status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingGeneralLedgerData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingGeneralLedgerData(emptyAccountingGeneralLedgerReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat General Ledger');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'trial-balance') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingTrialBalanceReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          source_system: appliedFilters.source_system,
          business_unit: appliedFilters.business_unit,
          posting_status: appliedFilters.accounting_posting_status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingTrialBalanceData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingTrialBalanceData(emptyAccountingTrialBalanceReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Trial Balance');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'profit-loss') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingProfitLossReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          source_system: appliedFilters.source_system,
          business_unit: appliedFilters.business_unit,
          posting_status: appliedFilters.accounting_posting_status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingProfitLossData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingProfitLossData(emptyAccountingProfitLossReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Profit & Loss');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'balance-sheet') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingBalanceSheetReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          source_system: appliedFilters.source_system,
          business_unit: appliedFilters.business_unit,
          posting_status: appliedFilters.accounting_posting_status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingBalanceSheetData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingBalanceSheetData(emptyAccountingBalanceSheetReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Balance Sheet');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'cash-flow') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingCashFlowReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          source_system: appliedFilters.source_system,
          business_unit: appliedFilters.business_unit,
          posting_status: appliedFilters.accounting_posting_status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingCashFlowData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingCashFlowData(emptyAccountingCashFlowReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Cash Flow');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'accounts-payable-aging') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingAccountsPayableAgingReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          document_scope: appliedFilters.accounting_document_scope,
          status: appliedFilters.status,
          aging_bucket: appliedFilters.aging_bucket,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingAccountsPayableAgingData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingAccountsPayableAgingData(emptyAccountingAccountsPayableAgingReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat AP Aging');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'expense-breakdown') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingExpenseBreakdownReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          document_scope: appliedFilters.accounting_document_scope,
          status: appliedFilters.status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingExpenseBreakdownData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingExpenseBreakdownData(emptyAccountingExpenseBreakdownReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Expense Breakdown');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'asset-register') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingAssetRegisterReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          status: appliedFilters.status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingAssetRegisterData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingAssetRegisterData(emptyAccountingAssetRegisterReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Asset Register');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    if (!token || activeTab !== 'accounting' || activeAccountingReport !== 'depreciation-schedule') {
      return;
    }

    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setBackendError('');
      try {
        const data = await requestAccountingDepreciationScheduleReport(token, {
          outlet_id: Number(appliedFilters.outlet || 0),
          date_from: appliedFilters.dateFrom,
          date_to: appliedFilters.dateTo,
          status: appliedFilters.status,
          search: appliedFilters.search,
        });

        if (isMounted) {
          setAccountingDepreciationScheduleData(data);
          setTotalRows(Number(data?.results?.length || 0));
        }
      } catch (error) {
        if (isMounted) {
          setAccountingDepreciationScheduleData(emptyAccountingDepreciationScheduleReport);
          setTotalRows(0);
          setBackendError(error.message || 'Gagal memuat Depreciation Schedule');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadReport();
    return () => {
      isMounted = false;
    };
  }, [activeAccountingReport, activeTab, appliedFilters]);

  const fetchAllAccountingJournalRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingJournalReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      source_system: appliedFilters.source_system,
      business_unit: appliedFilters.business_unit,
      posting_status: appliedFilters.accounting_posting_status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingJournalReport.summary,
      sourceBreakdown: Array.isArray(data?.source_breakdown) ? data.source_breakdown : [],
      accountBreakdown: Array.isArray(data?.account_breakdown) ? data.account_breakdown : [],
      trends: Array.isArray(data?.trends) ? data.trends : [],
    };
  };

  const fetchAllAccountingGeneralLedgerRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingGeneralLedgerReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      source_system: appliedFilters.source_system,
      business_unit: appliedFilters.business_unit,
      posting_status: appliedFilters.accounting_posting_status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingGeneralLedgerReport.summary,
      accountTypeBreakdown: Array.isArray(data?.account_type_breakdown) ? data.account_type_breakdown : [],
      sourceBreakdown: Array.isArray(data?.source_breakdown) ? data.source_breakdown : [],
      outletBreakdown: Array.isArray(data?.outlet_breakdown) ? data.outlet_breakdown : [],
      trends: Array.isArray(data?.trends) ? data.trends : [],
    };
  };

  const fetchAllAccountingTrialBalanceRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingTrialBalanceReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      source_system: appliedFilters.source_system,
      business_unit: appliedFilters.business_unit,
      posting_status: appliedFilters.accounting_posting_status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingTrialBalanceReport.summary,
      accountTypeBreakdown: Array.isArray(data?.account_type_breakdown) ? data.account_type_breakdown : [],
    };
  };

  const fetchAllAccountingProfitLossRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingProfitLossReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      source_system: appliedFilters.source_system,
      business_unit: appliedFilters.business_unit,
      posting_status: appliedFilters.accounting_posting_status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingProfitLossReport.summary,
      accountTypeBreakdown: Array.isArray(data?.account_type_breakdown) ? data.account_type_breakdown : [],
    };
  };

  const fetchAllAccountingBalanceSheetRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingBalanceSheetReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      source_system: appliedFilters.source_system,
      business_unit: appliedFilters.business_unit,
      posting_status: appliedFilters.accounting_posting_status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingBalanceSheetReport.summary,
      accountTypeBreakdown: Array.isArray(data?.account_type_breakdown) ? data.account_type_breakdown : [],
    };
  };

  const fetchAllAccountingCashFlowRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingCashFlowReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      source_system: appliedFilters.source_system,
      business_unit: appliedFilters.business_unit,
      posting_status: appliedFilters.accounting_posting_status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingCashFlowReport.summary,
      sectionBreakdown: Array.isArray(data?.section_breakdown) ? data.section_breakdown : [],
      cashAccountBreakdown: Array.isArray(data?.cash_account_breakdown) ? data.cash_account_breakdown : [],
      trends: Array.isArray(data?.trends) ? data.trends : [],
    };
  };

  const fetchAllAccountingAccountsPayableAgingRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingAccountsPayableAgingReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      document_scope: appliedFilters.accounting_document_scope,
      status: appliedFilters.status,
      aging_bucket: appliedFilters.aging_bucket,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingAccountsPayableAgingReport.summary,
      agingBucketBreakdown: Array.isArray(data?.aging_bucket_breakdown) ? data.aging_bucket_breakdown : [],
      vendorBreakdown: Array.isArray(data?.vendor_breakdown) ? data.vendor_breakdown : [],
      scopeBreakdown: Array.isArray(data?.scope_breakdown) ? data.scope_breakdown : [],
      trends: Array.isArray(data?.trends) ? data.trends : [],
    };
  };

  const fetchAllAccountingExpenseBreakdownRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingExpenseBreakdownReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      document_scope: appliedFilters.accounting_document_scope,
      status: appliedFilters.status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingExpenseBreakdownReport.summary,
      categoryBreakdown: Array.isArray(data?.category_breakdown) ? data.category_breakdown : [],
      vendorBreakdown: Array.isArray(data?.vendor_breakdown) ? data.vendor_breakdown : [],
      scopeBreakdown: Array.isArray(data?.scope_breakdown) ? data.scope_breakdown : [],
      trends: Array.isArray(data?.trends) ? data.trends : [],
    };
  };

  const fetchAllAccountingAssetRegisterRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingAssetRegisterReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      status: appliedFilters.status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingAssetRegisterReport.summary,
      categoryBreakdown: Array.isArray(data?.category_breakdown) ? data.category_breakdown : [],
      vendorBreakdown: Array.isArray(data?.vendor_breakdown) ? data.vendor_breakdown : [],
      accountBreakdown: Array.isArray(data?.account_breakdown) ? data.account_breakdown : [],
      trends: Array.isArray(data?.trends) ? data.trends : [],
      policy: data?.policy || emptyAccountingAssetRegisterReport.policy,
    };
  };

  const fetchAllAccountingDepreciationScheduleRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestAccountingDepreciationScheduleReport(token, {
      outlet_id: Number(appliedFilters.outlet || 0),
      date_from: appliedFilters.dateFrom,
      date_to: appliedFilters.dateTo,
      status: appliedFilters.status,
      search: appliedFilters.search,
    });

    return {
      rows: Array.isArray(data?.results) ? data.results : [],
      summary: data?.summary || emptyAccountingDepreciationScheduleReport.summary,
      categoryBreakdown: Array.isArray(data?.category_breakdown) ? data.category_breakdown : [],
      usefulLifeBreakdown: Array.isArray(data?.useful_life_breakdown) ? data.useful_life_breakdown : [],
      trends: Array.isArray(data?.trends) ? data.trends : [],
      policy: data?.policy || emptyAccountingDepreciationScheduleReport.policy,
    };
  };

  const fetchAllMembershipSalesRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const collected = [];
    let offset = 0;
    let total = 0;
    let latestSummary = reportData.summary;
    let latestTrends = reportData.trends;
    const pageSize = 100;

    do {
      const data = await requestMembershipSalesReport(token, {
        limit: pageSize,
        offset,
        outlet: Number(appliedFilters.outlet || 0),
        membership_id: Number(appliedFilters.membership_id || 0),
        payment_method: appliedFilters.payment_method,
        status: appliedFilters.status === '' ? null : Number(appliedFilters.status),
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        search: appliedFilters.search,
      });
      total = Number(data?.total || 0);
      latestSummary = data?.summary || latestSummary;
      latestTrends = Array.isArray(data?.trends) ? data.trends : latestTrends;
      const chunk = Array.isArray(data?.results) ? data.results : [];
      collected.push(...chunk);
      offset += pageSize;
      if (chunk.length === 0) {
        break;
      }
    } while (offset < total);

    return {
      rows: collected,
      summary: latestSummary,
      trends: latestTrends,
    };
  };

  const fetchAllStaffTransactionSummaryRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const collected = [];
    let offset = 0;
    let total = 0;
    let latestSummary = staffTransactionData.summary;
    let latestTrends = staffTransactionData.trends;
    const pageSize = 100;

    do {
      const data = await requestStaffTransactionSummaryReport(token, {
        limit: pageSize,
        offset,
        outlet: Number(appliedFilters.outlet || 0),
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        search: appliedFilters.search,
      });
      total = Number(data?.total || 0);
      latestSummary = data?.summary || latestSummary;
      latestTrends = Array.isArray(data?.trends) ? data.trends : latestTrends;
      const chunk = Array.isArray(data?.results) ? data.results : [];
      collected.push(...chunk);
      offset += pageSize;
      if (chunk.length === 0) {
        break;
      }
    } while (offset < total);

    return {
      rows: collected,
      summary: latestSummary,
      trends: latestTrends,
    };
  };

  const fetchAllMemberActivityRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const collected = [];
    let offset = 0;
    let total = 0;
    let latestSummary = memberActivityData.summary;
    const pageSize = 100;

    do {
      const data = await requestMemberActivityReport(token, {
        limit: pageSize,
        offset,
        outlet: Number(appliedFilters.outlet || 0),
        membership_id: Number(appliedFilters.membership_id || 0),
        lifecycle: appliedFilters.lifecycle,
        activity: appliedFilters.activity,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        search: appliedFilters.search,
      });
      total = Number(data?.total || 0);
      latestSummary = data?.summary || latestSummary;
      const chunk = Array.isArray(data?.results) ? data.results : [];
      collected.push(...chunk);
      offset += pageSize;
      if (chunk.length === 0) {
        break;
      }
    } while (offset < total);

    return {
      rows: collected,
      summary: latestSummary,
    };
  };

  const fetchAllCheckinRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const collected = [];
    let offset = 0;
    let total = 0;
    let latestSummary = checkinData.summary;
    let latestTrends = checkinData.trends;
    const pageSize = 100;

    do {
      const data = await requestCheckinReport(token, {
        limit: pageSize,
        offset,
        outlet: Number(appliedFilters.outlet || 0),
        membership_id: Number(appliedFilters.membership_id || 0),
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        search: appliedFilters.search,
      });
      total = Number(data?.total || 0);
      latestSummary = data?.summary || latestSummary;
      latestTrends = Array.isArray(data?.trends) ? data.trends : latestTrends;
      const chunk = Array.isArray(data?.results) ? data.results : [];
      collected.push(...chunk);
      offset += pageSize;
      if (chunk.length === 0) {
        break;
      }
    } while (offset < total);

    return {
      rows: collected,
      summary: latestSummary,
      trends: latestTrends,
    };
  };

  const fetchAllPTSalesRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const collected = [];
    let offset = 0;
    let total = 0;
    let latestSummary = ptSalesData.summary;
    let latestTrends = ptSalesData.trends;
    const pageSize = 100;

    do {
      const data = await requestPTSalesReport(token, {
        limit: pageSize,
        offset,
        outlet_id: Number(appliedFilters.outlet || 0),
        pt_user_id: Number(appliedFilters.pt_user_id || 0),
        product_id: Number(appliedFilters.product_id || 0),
        client_type: appliedFilters.client_type,
        payment_status: appliedFilters.pt_payment_status,
        contract_status: appliedFilters.contract_status,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        search: appliedFilters.search,
      });
      total = Number(data?.total || 0);
      latestSummary = data?.summary || latestSummary;
      latestTrends = Array.isArray(data?.trends) ? data.trends : latestTrends;
      const chunk = Array.isArray(data?.results) ? data.results : [];
      collected.push(...chunk);
      offset += pageSize;
      if (chunk.length === 0) {
        break;
      }
    } while (offset < total);

    return {
      rows: collected,
      summary: latestSummary,
      trends: latestTrends,
    };
  };

  const fetchAllPTSessionRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const collected = [];
    let offset = 0;
    let total = 0;
    let latestSummary = ptSessionData.summary;
    let latestTrends = ptSessionData.trends;
    const pageSize = 100;

    do {
      const data = await requestPTSessionReport(token, {
        limit: pageSize,
        offset,
        outlet_id: Number(appliedFilters.outlet || 0),
        client_id: Number(appliedFilters.client_id || 0),
        pt_user_id: Number(appliedFilters.pt_user_id || 0),
        product_id: Number(appliedFilters.product_id || 0),
        booking_status: appliedFilters.booking_status,
        attendance_status: appliedFilters.attendance_status,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        search: appliedFilters.search,
      });
      total = Number(data?.total || 0);
      latestSummary = data?.summary || latestSummary;
      latestTrends = Array.isArray(data?.trends) ? data.trends : latestTrends;
      const chunk = Array.isArray(data?.results) ? data.results : [];
      collected.push(...chunk);
      offset += pageSize;
      if (chunk.length === 0) {
        break;
      }
    } while (offset < total);

    return {
      rows: collected,
      summary: latestSummary,
      trends: latestTrends,
    };
  };

  const fetchAllPTTrainerRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const collected = [];
    let offset = 0;
    let total = 0;
    let latestSummary = ptTrainerData.summary;
    let latestTrends = ptTrainerData.trends;
    const pageSize = 100;

    do {
      const data = await requestPTTrainerPerformanceReport(token, {
        limit: pageSize,
        offset,
        outlet_id: Number(appliedFilters.outlet || 0),
        pt_user_id: Number(appliedFilters.pt_user_id || 0),
        payment_status: appliedFilters.pt_payment_status,
        attendance_status: appliedFilters.attendance_status,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        search: appliedFilters.search,
      });
      total = Number(data?.total || 0);
      latestSummary = data?.summary || latestSummary;
      latestTrends = Array.isArray(data?.trends) ? data.trends : latestTrends;
      const chunk = Array.isArray(data?.results) ? data.results : [];
      collected.push(...chunk);
      offset += pageSize;
      if (chunk.length === 0) {
        break;
      }
    } while (offset < total);

    return {
      rows: collected,
      summary: latestSummary,
      trends: latestTrends,
    };
  };

  const fetchAllPOSSalesRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;
    const rows = await requestPOSSalesReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      payment_method: appliedFilters.payment_method,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
    });
    const report = buildPOSSalesReport(rows, resolveOutletName, appliedFilters.search);

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.trends,
    };
  };

  const fetchAllPOSCashierSalesRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;
    const rows = await requestPOSSalesReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      payment_method: appliedFilters.payment_method,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
    });
    const report = buildPOSCashierSalesReport(rows, resolveOutletName, appliedFilters.search);

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.trends,
    };
  };

  const fetchAllPOSProductSalesRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;
    const rows = await requestPOSSalesReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      payment_method: appliedFilters.payment_method,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
    });
    const report = buildPOSProductSalesReport(rows, resolveOutletName, {
      product_id: appliedFilters.product_id,
      search: appliedFilters.search,
    });

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.trends,
    };
  };

  const fetchAllPOSRefundVoidRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;
    const data = await requestPOSRefundVoidReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      request_type: appliedFilters.approval_request_type,
      approval_status: appliedFilters.approval_status,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
      search: appliedFilters.search,
    });
    const report = buildPOSRefundVoidReport(data, resolveOutletName, {
      payment_method: appliedFilters.payment_method,
      search: appliedFilters.search,
    });

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.trends,
    };
  };

  const fetchAllPOSPurchaseRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const data = await requestPOSPurchaseReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      payment_method: appliedFilters.payment_method,
      payment_status: appliedFilters.purchase_payment_status,
      supplier_name: appliedFilters.vendor_name,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
      search: appliedFilters.search,
    });
    const report = normalizePOSPurchaseReport(data, resolveOutletName);

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.trends,
    };
  };

  const fetchAllPOSVendorAgingRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const data = await requestPOSVendorAgingReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      vendor_name: appliedFilters.vendor_name,
      vendor_bill_type: appliedFilters.vendor_bill_type,
      vendor_bill_status: appliedFilters.vendor_bill_status,
      aging_bucket: appliedFilters.aging_bucket,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
      search: appliedFilters.search,
    });
    const report = normalizePOSVendorAgingReport(data, resolveOutletName);

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.due_timeline,
    };
  };

  const fetchAllPOSStockMovementRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const data = await requestPOSStockMovementReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      product_id: Number(appliedFilters.product_id || 0),
      movement_type: appliedFilters.movement_type,
      reference_type: appliedFilters.reference_type,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
      search: appliedFilters.search,
    });
    const report = normalizePOSStockMovementReport(data, resolveOutletName);

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.trends,
    };
  };

  const fetchAllPOSBOMConsumptionRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const data = await requestPOSBOMConsumptionReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      product_id: Number(appliedFilters.product_id || 0),
      ingredient_product_id: Number(appliedFilters.ingredient_product_id || 0),
      payment_method: appliedFilters.payment_method,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
      search: appliedFilters.search,
    });
    const report = normalizePOSBOMConsumptionReport(data, resolveOutletName);

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.trends,
    };
  };

  const fetchAllPOSStockOpnameVarianceRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const resolveOutletName = outletId =>
      outlets.find(item => String(item.id) === String(outletId))?.name || `Outlet #${outletId || '-'}`;

    const data = await requestPOSStockOpnameVarianceReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      product_id: Number(appliedFilters.product_id || 0),
      variance_type: appliedFilters.variance_type,
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
      search: appliedFilters.search,
    });
    const report = normalizePOSStockOpnameVarianceReport(data, resolveOutletName);

    return {
      rows: report.results,
      summary: report.summary,
      trends: report.trends,
    };
  };

  const fetchAllPOSVendorPriceComparisonRows = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return null;
    }

    const data = await requestPOSVendorPriceComparisonReport(token, {
      outlet: Number(appliedFilters.outlet || 0),
      category_id: Number(appliedFilters.category_id || 0),
      product_id: Number(appliedFilters.product_id || 0),
      vendor_name: appliedFilters.vendor_name,
      item_type: appliedFilters.item_type,
      payment_method: appliedFilters.payment_method,
      purchase_payment_status: appliedFilters.purchase_payment_status,
      comparison_mode: appliedFilters.comparison_mode,
      min_purchase_count: Number(appliedFilters.min_purchase_count || 1),
      dateFrom: appliedFilters.dateFrom,
      dateTo: appliedFilters.dateTo,
      search: appliedFilters.search,
    });
    const report = normalizePOSVendorPriceComparisonReport(data);

    return {
      rows: report.results,
      summary: report.summary,
      vendorDetails: report.vendor_details,
      comparisonMode: report.comparison_mode,
    };
  };

  const buildMembershipSalesExportRows = rows =>
    rows.map(row => ({
      Tanggal: formatDateTime(row.created_at),
      Jenis: row.transaction_label || 'Membership Sale',
      Member: row.member_name || '-',
      MemberID: row.member_id || '-',
      Paket: row.membership_name || 'Tanpa paket',
      Outlet: row.outlet || '-',
      Payment: row.payment_method_label || row.payment_method || '-',
      Status: row.status_label || '-',
      Petugas: row.processed_by_name || 'System',
      Actor: row.processed_by_actor_type || '-',
      Nilai: Number(row.amount || 0),
    }));

  const buildStaffTransactionSummaryExportRows = rows =>
    rows.map(row => ({
      StaffID: row.staff_id || '-',
      Staff: row.staff_name || '-',
      Username: row.username || '-',
      Jabatan: row.job_title || 'Tanpa jabatan',
      Outlet: row.outlet_name || '-',
      MembershipTransactions: Number(row.membership_transaction_count || 0),
      MembershipSales: Number(row.membership_sales_amount || 0),
      MembershipLastSaleAt: formatDateTime(row.membership_last_sale_at),
      ClassTransactions: Number(row.class_transaction_count || 0),
      ClassSales: Number(row.class_sales_amount || 0),
      ClassLastSaleAt: formatDateTime(row.class_last_sale_at),
      PTTransactions: Number(row.pt_transaction_count || 0),
      PTSales: Number(row.pt_sales_amount || 0),
      PTLastSaleAt: formatDateTime(row.pt_last_sale_at),
      POSTransactions: Number(row.pos_transaction_count || 0),
      POSGrossSales: Number(row.pos_sales_amount || 0),
      POSClosingShiftSales: Number(row.pos_closing_shift_sales_amount || 0),
      POSLastSaleAt: formatDateTime(row.pos_last_sale_at),
      TotalTransactions: Number(row.total_transaction_count || 0),
      TotalSales: Number(row.total_sales_amount || 0),
      AverageTicket: Number(row.average_ticket || 0),
      ActiveDays: Number(row.active_days || 0),
      SalesMix: row.sales_mix_label || '-',
      FirstTransactionAt: formatDateTime(row.first_transaction_at),
      LastTransactionAt: formatDateTime(row.last_transaction_at),
    }));

  const buildMemberActivityExportRows = rows =>
    rows.map(row => ({
      Member: row.member_name || '-',
      Outlet: row.outlet || '-',
      PaketAktif: row.membership_name || 'Belum ada membership',
      Lifecycle: row.lifecycle_label || '-',
      Aktivitas: row.activity_label || '-',
      Risiko: row.risk_label || '-',
      CheckinPeriode: Number(row.checkin_count_period || 0),
      LastCheckin: formatDateTime(row.last_checkin_at),
      HariSejakCheckin: row.days_since_last_checkin ?? '-',
      ExpiredAt: formatDateTime(row.membership_expired_at),
      HariMenujuExpired: row.days_to_expire ?? '-',
      LastPayment: formatDateTime(row.last_payment_at),
      NilaiPembayaranTerakhir: Number(row.last_payment_amount || 0),
      Email: row.email || '-',
      HP: row.hp || '-',
    }));

  const buildCheckinExportRows = rows =>
    rows.map(row => ({
      TanggalCheckin: formatDateTime(row.checkin_at),
      Member: row.member_name || '-',
      MemberID: row.member_id || '-',
      Outlet: row.outlet || '-',
      PaketSaatCheckin: row.membership_name || 'Belum ada membership',
      VisitPeriode: Number(row.visit_count_period || 0),
      Sequence: row.visit_sequence_label || '-',
      ExpiredAt: formatDateTime(row.membership_expired_at),
      HariMenujuExpired: row.days_to_expire ?? '-',
      Email: row.email || '-',
      HP: row.hp || '-',
    }));

  const buildPTSalesExportRows = rows =>
    rows.map(row => ({
      TanggalSale: formatDateTime(row.sale_date),
      Invoice: row.invoice_no || '-',
      Client: row.client_name || '-',
      ClientType: formatPTClientTypeLabel(row.client_type),
      Trainer: row.pt_name || '-',
      Produk: row.product_name || '-',
      ProductType: formatPTProductTypeLabel(row.product_type),
      Sessions: Number(row.session_count || 0),
      PaymentMethod: row.payment_method || '-',
      PaymentStatus: formatPTPaymentStatusLabel(row.payment_status),
      ContractStatus: formatPTContractStatusLabel(row.contract_status),
      Amount: Number(row.net_amount || 0),
      PaidAmount: Number(row.paid_amount || 0),
      Outstanding: Number(row.outstanding_amount || 0),
      Outlet: row.outlet || '-',
    }));

  const buildPTSessionExportRows = rows =>
    rows.map(row => ({
      TanggalSesi: formatDateTime(row.start_at),
      BookingCode: row.booking_code || '-',
      Client: row.client_name || '-',
      ClientType: formatPTClientTypeLabel(row.client_type),
      Trainer: row.pt_name || '-',
      Produk: row.product_name || '-',
      Contract: row.contract_no || '-',
      Invoice: row.invoice_no || '-',
      BookingStatus: formatPTBookingStatusLabel(row.booking_status),
      AttendanceStatus: formatPTAttendanceStatusLabel(row.attendance_status),
      SessionConsumed: row.session_consumed ? 'Yes' : 'No',
      ChargeableNoShow: row.is_chargeable_no_show ? 'Yes' : 'No',
      RemainingSessions: Number(row.remaining_sessions || 0),
      Outlet: row.outlet || '-',
    }));

  const buildPTTrainerExportRows = rows =>
    rows.map(row => ({
      Trainer: row.pt_name || '-',
      Outlet: row.outlet || '-',
      ActiveStatus: row.is_active ? 'Active' : 'Inactive',
      ActiveClients: Number(row.active_clients || 0),
      PackagesSold: Number(row.packages_sold || 0),
      SalesAmount: Number(row.sales_amount || 0),
      PaidSalesAmount: Number(row.paid_sales_amount || 0),
      SessionsScheduled: Number(row.sessions_scheduled || 0),
      SessionsCompleted: Number(row.sessions_completed || 0),
      SessionsCancelled: Number(row.sessions_cancelled || 0),
      SessionsNoShow: Number(row.sessions_no_show || 0),
      CompletionRate: Number(row.completion_rate || 0),
      SettlementEligible: Number(row.settlement_eligible_amount || 0),
    }));

  const buildPOSSalesExportRows = rows =>
    rows.map(row => ({
      Tanggal: formatDateTime(row.created_at),
      TransaksiID: row.id || '-',
      Outlet: row.outlet_name || '-',
      Cashier: row.cashier_name || (row.cashier_id ? `Cashier #${row.cashier_id}` : '-'),
      PaymentMethod: row.payment_method || '-',
      ItemQty: Number(row.item_quantity || 0),
      ItemLines: Number(row.item_count || 0),
      Service: getPOSTransactionServiceAmount(row),
      Tax: Number(row.tax || 0),
      Discount: Number(row.discount || 0),
      Total: getPOSTransactionFinalTotal(row),
      Produk: Array.isArray(row.product_names) && row.product_names.length
        ? row.product_names.join(', ')
        : '-',
      SyncStatus: row.accounting_sync_status || '-',
      Catatan: row.note || '-',
    }));

  const buildPOSCashierSalesExportRows = rows =>
    rows.map(row => ({
      Cashier: row.cashier_name || '-',
      TransactionCount: Number(row.transaction_count || 0),
      GrossSales: Number(row.gross_sales || 0),
      AverageTicket: Number(row.average_ticket || 0),
      ItemQuantity: Number(row.item_quantity || 0),
      AverageItemsPerTransaction: Number(row.average_items_per_transaction || 0),
      Service: Number(row.total_service || 0),
      Discount: Number(row.total_discount || 0),
      Tax: Number(row.total_tax || 0),
      OutletCount: Number(row.outlet_count || 0),
      DominantOutlet: row.dominant_outlet || '-',
      PaymentMethodCount: Number(row.payment_method_count || 0),
      TopPaymentMethod: row.top_payment_method || '-',
      TopPaymentSales: Number(row.top_payment_sales || 0),
      TopPaymentShare: Number(row.top_payment_share || 0),
      DominantTimeSlot: row.dominant_time_slot || '-',
      ActiveDays: Number(row.active_days || 0),
      FirstSaleAt: formatDateTime(row.first_sale_at),
      LastSaleAt: formatDateTime(row.last_sale_at),
    }));

  const buildPOSProductSalesExportRows = rows =>
    rows.map(row => ({
      ProductID: row.product_id || '-',
      Produk: row.product_name || '-',
      ItemType: formatProductItemTypeLabel(row.item_type),
      Unit: row.unit || '-',
      QuantitySold: Number(row.quantity_sold || 0),
      GrossSales: Number(row.gross_sales || 0),
      AverageSellingPrice: Number(row.average_selling_price || 0),
      TransactionCount: Number(row.transaction_count || 0),
      OutletCount: Number(row.outlet_count || 0),
      CashierCount: Number(row.cashier_count || 0),
      TopPaymentMethod: row.top_payment_method || '-',
      TopPaymentAmount: Number(row.top_payment_amount || 0),
      LastSoldAt: formatDateTime(row.last_sold_at),
    }));

  const buildPOSRefundVoidExportRows = rows =>
    rows.map(row => ({
      TanggalPengajuan: formatDateTime(row.requested_at),
      RequestID: row.id || '-',
      Tipe: formatPOSApprovalRequestTypeLabel(row.request_type),
      Status: formatPOSApprovalStatusLabel(row.status),
      Outlet: row.outlet_name || '-',
      TransactionID: row.transaction_id || '-',
      TransactionDate: formatDateTime(row.transaction_created_at),
      PaymentMethod: row.payment_method || '-',
      Cashier: row.cashier_name || '-',
      TransactionTotal: Number(row.transaction_total || 0),
      RequestTotal: Number(row.request_total || 0),
      RefundRealized: Number(row.refund_total || 0),
      ItemLines: Number(row.item_count || 0),
      ItemQty: Number(row.item_quantity || 0),
      Reason: row.reason || '-',
      RequestNote: row.request_note || '-',
      RequestedBy: row.requested_by_name || '-',
      RequestedAt: formatDateTime(row.requested_at),
      ReviewedBy: row.reviewed_by_name || '-',
      ReviewedAt: formatDateTime(row.reviewed_at),
      ReviewNote: row.review_note || '-',
      RefundID: row.refund_id || '-',
      DocumentStatus: formatPOSTransactionDocumentStatusLabel(row.transaction_document_status),
      SyncStatus: row.accounting_sync_status || '-',
      Produk: Array.isArray(row.item_names) && row.item_names.length
        ? row.item_names.join(', ')
        : '-',
    }));

  const buildPOSPurchaseExportRows = rows =>
    rows.map(row => ({
      TanggalPurchase: formatDateTime(row.purchase_date),
      PurchaseID: row.id || '-',
      Invoice: row.invoice_number || '-',
      Outlet: row.outlet_name || '-',
      Vendor: row.supplier_name || '-',
      PaymentMethod: row.payment_method || '-',
      PaymentStatus: formatPurchasePaymentStatusLabel(row.payment_status),
      ItemQty: Number(row.item_quantity || 0),
      ItemLines: Number(row.item_count || 0),
      Total: Number(row.total || 0),
      PaidAmount: Number(row.paid_amount || 0),
      Outstanding: Number(row.outstanding_amount || 0),
      DueDate: formatDateTime(row.due_date),
      VendorBillID: row.linked_vendor_bill_id || '-',
      Produk: Array.isArray(row.product_names) && row.product_names.length
        ? row.product_names.join(', ')
        : '-',
      SyncStatus: row.accounting_sync_status || '-',
      Catatan: row.note || '-',
    }));

  const buildPOSVendorAgingExportRows = rows =>
    rows.map(row => ({
      TanggalBill: formatDateTime(row.bill_date),
      BillNo: row.bill_no || `#${row.id || '-'}`,
      Outlet: row.outlet_name || '-',
      Vendor: row.vendor_name || '-',
      BillType: formatVendorBillTypeLabel(row.bill_type),
      Status: formatPurchasePaymentStatusLabel(row.status),
      DueDate: formatDateTime(row.due_date),
      DueStatus: row.due_status_label || formatVendorDueStatusLabel(row.due_status),
      AgingBucket: row.aging_bucket_label || formatVendorAgingBucketLabel(row.aging_bucket),
      AgeDays: Number(row.age_days || 0),
      TotalAmount: Number(row.total_amount || 0),
      PaidAmount: Number(row.paid_amount || 0),
      Outstanding: Number(row.outstanding_amount || 0),
      PurchaseID: row.purchase_id || '-',
      SyncStatus: row.accounting_sync_status || '-',
      Catatan: row.note || '-',
    }));

  const buildPOSStockMovementExportRows = rows =>
    rows.map(row => ({
      Tanggal: formatDateTime(row.created_at),
      LedgerID: row.id || '-',
      Outlet: row.outlet_name || '-',
      Product: row.product_name || '-',
      ItemType: formatProductItemTypeLabel(row.item_type),
      MovementType: formatInventoryMovementTypeLabel(row.movement_type),
      ReferenceType: formatInventoryReferenceLabel(row.reference_type),
      ReferenceID: row.reference_id || '-',
      QuantityIn: Number(row.quantity_in || 0),
      QuantityOut: Number(row.quantity_out || 0),
      NetQuantity: Number(row.net_quantity || 0),
      StockBefore: Number(row.stock_before || 0),
      StockAfter: Number(row.stock_after || 0),
      UnitCost: Number(row.unit_cost || 0),
      TotalCost: Number(row.total_cost || 0),
      Catatan: row.notes || '-',
    }));

  const buildPOSBOMConsumptionExportRows = rows =>
    rows.map(row => ({
      TanggalTransaksi: formatDateTime(row.transaction_created_at),
      TransactionID: row.transaction_id || '-',
      Outlet: row.outlet_name || '-',
      SoldProductID: row.sold_product_id || '-',
      ProdukJadi: row.sold_product_name || '-',
      IngredientProductID: row.ingredient_product_id || '-',
      BahanBaku: row.ingredient_product_name || '-',
      IngredientType: formatProductItemTypeLabel(row.ingredient_item_type),
      Unit: row.ingredient_unit || '-',
      SoldQuantity: Number(row.sold_quantity || 0),
      QuantityPerUnit: Number(row.quantity_per_unit || 0),
      QuantityConsumed: Number(row.quantity_consumed || 0),
      UnitCost: Number(row.unit_cost || 0),
      TotalCost: Number(row.total_cost || 0),
      PaymentMethod: row.payment_method || '-',
      Cashier: row.cashier_name || '-',
      TransactionTotal: Number(row.transaction_total || 0),
      CatatanKonsumsi: row.note || '-',
      CatatanTransaksi: row.transaction_note || '-',
    }));

  const buildPOSStockOpnameVarianceExportRows = rows =>
    rows.map(row => ({
      TanggalOpname: formatDateTime(row.opname_date),
      OpnameID: row.opname_id || '-',
      ItemID: row.id || '-',
      Outlet: row.outlet_name || '-',
      ProductID: row.product_id || '-',
      Product: row.product_name || '-',
      ItemType: formatProductItemTypeLabel(row.item_type),
      Unit: row.unit || '-',
      VarianceType: formatStockOpnameVarianceLabel(row.variance_type),
      SystemStock: Number(row.system_stock || 0),
      ActualStock: Number(row.actual_stock || 0),
      DifferenceQty: Number(row.difference_qty || 0),
      UnitCost: Number(row.unit_cost || 0),
      TotalVarianceCost: Number(row.total_cost || 0),
      Status: row.opname_status || '-',
      SyncStatus: row.accounting_sync_status || '-',
      CatatanItem: row.note || '-',
      CatatanDokumen: row.opname_note || '-',
    }));

  const buildPOSVendorPriceComparisonExportRows = rows =>
    rows.map(row => ({
      Product: row.product_name || '-',
      Category: row.category_name || 'Tanpa kategori',
      ItemType: formatProductItemTypeLabel(row.item_type),
      Unit: row.unit || '-',
      PreferredVendor: row.preferred_vendor || '-',
      PreferredPrice: Number(row.preferred_price || 0),
      SecondVendor: row.second_vendor || '-',
      SecondPrice: Number(row.second_price || 0),
      PriceSpread: Number(row.price_spread || 0),
      SpreadPercentage: Number(row.spread_percentage || 0),
      LastPurchasedVendor: row.last_purchased_vendor || '-',
      LastPurchasedPrice: Number(row.last_purchased_price || 0),
      AverageMarketPrice: Number(row.average_market_price || 0),
      VendorCount: Number(row.vendor_count || 0),
      PurchaseCount: Number(row.purchase_count || 0),
      TotalQuantity: Number(row.total_quantity_bought || 0),
      LastPurchaseDate: formatDateTime(row.last_purchase_date),
      PotentialSavings: Number(row.potential_savings || 0),
      Risk: row.risk_label || formatVendorPriceRiskLabel(row.risk_flag),
    }));

  const buildPOSVendorPriceComparisonVendorExportRows = rows =>
    rows.map(row => ({
      Product: row.product_name || '-',
      Category: row.category_name || 'Tanpa kategori',
      ItemType: formatProductItemTypeLabel(row.item_type),
      Unit: row.unit || '-',
      Vendor: row.vendor_name || '-',
      Rank: Number(row.rank || 0),
      Preferred: row.is_preferred ? 'Yes' : 'No',
      ComparisonPrice: Number(row.comparison_price || 0),
      LatestPrice: Number(row.latest_price || 0),
      AveragePrice: Number(row.average_price || 0),
      LowestPrice: Number(row.lowest_price || 0),
      HighestPrice: Number(row.highest_price || 0),
      PurchaseCount: Number(row.purchase_count || 0),
      TotalQuantity: Number(row.total_quantity || 0),
      LastPurchaseDate: formatDateTime(row.last_purchase_date),
      PaymentMix: row.payment_mix_label || '-',
      OutletCoverage: Number(row.outlet_coverage_count || 0),
    }));

  const buildAccountingJournalExportRows = rows =>
    rows.map(row => ({
      JournalNumber: formatAccountingJournalNumber(row.id, row.occurred_at),
      TanggalJurnal: formatDateTime(row.occurred_at),
      JournalID: row.id || '-',
      Outlet: row.outlet_name || '-',
      Description: row.description || '-',
      SourceSystem: formatAccountingSourceSystemLabel(row.source_system),
      SourceType: formatAccountingSourceTypeLabel(row.source_type),
      SourceID: row.source_id || '-',
      ExternalReference: row.external_reference || '-',
      BusinessUnit: formatAccountingBusinessUnitLabel(row.business_unit),
      PostingStatus: formatAccountingPostingStatusLabel(row.posting_status),
      PostedAt: formatDateTime(row.posted_at),
      TotalDebit: Number(row.total_debit || 0),
      TotalCredit: Number(row.total_credit || 0),
      LineCount: Number(row.line_count || 0),
      DebitAccountCount: Number(getAccountingSideLines(row.lines, 'debit').length || 0),
      CreditAccountCount: Number(getAccountingSideLines(row.lines, 'credit').length || 0),
      DebitAccounts: buildAccountingSidePreview(row.lines, 'debit'),
      CreditAccounts: buildAccountingSidePreview(row.lines, 'credit'),
    }));

  const buildAccountingGeneralLedgerExportRows = rows =>
    rows.flatMap(row => {
      const baseRow = {
        AccountID: row.account_id || '-',
        AccountName: row.account_name || row.account_id || '-',
        AccountType: row.account_type || 'Uncategorized',
        OpeningBalance: Number(row.opening_balance || 0),
        TotalDebit: Number(row.total_debit || 0),
        TotalCredit: Number(row.total_credit || 0),
        ClosingBalance: Number(row.closing_balance || 0),
        EntryCount: Number(row.entry_count || 0),
        LastOccurredAt: formatDateTime(row.last_occurred_at),
      };

      const entries = Array.isArray(row.entries) ? row.entries : [];
      if (!entries.length) {
        return [{
          ...baseRow,
          TanggalJurnal: '-',
          JournalID: '-',
          Outlet: '-',
          SourceSystem: '-',
          SourceType: '-',
          SourceID: '-',
          ExternalReference: '-',
          Description: 'Tidak ada mutasi pada periode terpilih',
          PostingStatus: '-',
          Debit: 0,
          Credit: 0,
          RunningBalance: Number(row.opening_balance || 0),
        }];
      }

      return entries.map(entry => ({
        ...baseRow,
        JournalNumber: formatAccountingJournalNumber(entry.journal_id, entry.occurred_at),
        TanggalJurnal: formatDateTime(entry.occurred_at),
        JournalID: entry.journal_id || '-',
        Outlet: entry.outlet_name || '-',
        SourceSystem: formatAccountingSourceSystemLabel(entry.source_system),
        SourceType: formatAccountingSourceTypeLabel(entry.source_type),
        SourceID: entry.source_id || '-',
        ExternalReference: entry.external_reference || '-',
        Description: entry.description || '-',
        PostingStatus: formatAccountingPostingStatusLabel(entry.posting_status),
        Debit: Number(entry.debit || 0),
        Credit: Number(entry.credit || 0),
        RunningBalance: Number(entry.running_balance || 0),
      }));
    });

  const buildAccountingTrialBalanceExportRows = rows =>
    rows.map(row => ({
      AccountID: row.account_id || '-',
      AccountName: row.account_name || row.account_id || '-',
      AccountType: row.account_type || 'Uncategorized',
      OpeningBalance: Number(row.opening_balance || 0),
      OpeningDebit: Number(row.opening_debit || 0),
      OpeningCredit: Number(row.opening_credit || 0),
      PeriodDebit: Number(row.period_debit || 0),
      PeriodCredit: Number(row.period_credit || 0),
      ClosingBalance: Number(row.closing_balance || 0),
      ClosingDebit: Number(row.closing_debit || 0),
      ClosingCredit: Number(row.closing_credit || 0),
      EntryCount: Number(row.entry_count || 0),
      LastOccurredAt: formatDateTime(row.last_occurred_at),
    }));

  const buildAccountingProfitLossExportRows = rows =>
    rows.map(row => ({
      AccountID: row.account_id || '-',
      AccountName: row.account_name || row.account_id || '-',
      AccountType: row.account_type || 'Uncategorized',
      TotalDebit: Number(row.total_debit || 0),
      TotalCredit: Number(row.total_credit || 0),
      NetAmount: Number(row.net_amount || 0),
      EntryCount: Number(row.entry_count || 0),
      LastOccurredAt: formatDateTime(row.last_occurred_at),
    }));

  const buildAccountingBalanceSheetExportRows = rows =>
    rows.map(row => ({
      AccountID: row.account_id || '-',
      AccountName: row.account_name || row.account_id || '-',
      AccountType: row.account_type || 'Uncategorized',
      OpeningBalance: Number(row.opening_balance || 0),
      TotalDebit: Number(row.total_debit || 0),
      TotalCredit: Number(row.total_credit || 0),
      ClosingBalance: Number(row.closing_balance || 0),
      EntryCount: Number(row.entry_count || 0),
      LastOccurredAt: formatDateTime(row.last_occurred_at),
    }));

  const buildAccountingCashFlowTrendExportRows = rows =>
    rows.map(row => ({
      PeriodKey: row.period_key || '-',
      PeriodLabel: row.period_label || '-',
      JournalCount: Number(row.journal_count || 0),
      InflowAmount: Number(row.inflow_amount || 0),
      OutflowAmount: Number(row.outflow_amount || 0),
      NetCashFlow: Number(row.net_cash_flow || 0),
    }));

  const buildAccountingCashFlowExportRows = rows =>
    rows.map(row => ({
      JournalID: Number(row.journal_id || 0),
      JournalNumber: formatAccountingJournalNumber(row.journal_id, row.occurred_at),
      TanggalJurnal: formatDateTime(row.occurred_at),
      Outlet: row.outlet_name || '-',
      CashAccountID: row.cash_account_id || '-',
      CashAccountName: row.cash_account_name || row.cash_account_id || '-',
      CounterAccountID: row.counter_account_id || '-',
      CounterAccountName: row.counter_account_name || row.counter_account_id || '-',
      CounterAccountType: row.counter_account_type || 'Uncategorized',
      CounterAccountDetail: row.description || '-',
      Section: row.section_label || row.section_key || '-',
      Direction: row.direction || '-',
      InflowAmount: Number(row.inflow_amount || 0),
      OutflowAmount: Number(row.outflow_amount || 0),
      NetCashFlow: Number(row.net_cash_flow || 0),
      SourceSystem: formatAccountingSourceSystemLabel(row.source_system),
      SourceType: formatAccountingSourceTypeLabel(row.source_type),
      SourceID: row.source_id || '-',
      ExternalReference: row.external_reference || '-',
      PostingStatus: formatAccountingPostingStatusLabel(row.posting_status),
      Description: row.description || '-',
    }));

  const buildAccountingAccountsPayableAgingTrendExportRows = rows =>
    rows.map(row => ({
      PeriodKey: row.period_key || '-',
      PeriodLabel: row.period_label || '-',
      BillCount: Number(row.bill_count || 0),
      OutstandingAmount: Number(row.outstanding_amount || 0),
      OverdueAmount: Number(row.overdue_amount || 0),
    }));

  const buildAccountingAccountsPayableAgingExportRows = rows =>
    rows.map(row => ({
      TanggalDokumen: formatDateTime(row.expense_date),
      DueDate: formatDateTime(row.due_date),
      DocumentNo: row.document_no || '-',
      Scope: row.document_scope_label || formatAccountingFinanceDocumentScopeLabel(row.document_scope),
      DocumentType: row.document_type_label || row.document_type || '-',
      Outlet: row.outlet_name || '-',
      Vendor: row.vendor_name || '-',
      Category: row.category || '-',
      Status: row.status_label || formatAccountingFinanceStatusLabel(row.status),
      AgingBucket: row.aging_bucket_label || formatAccountingFinanceAgingBucketLabel(row.aging_bucket),
      DueStatus: row.due_status_label || formatAccountingFinanceDueStatusLabel(row.due_status),
      AgeDays: Number(row.age_days || 0),
      DaysOverdue: Number(row.days_overdue || 0),
      Amount: Number(row.amount || 0),
      PaidAmount: Number(row.paid_amount || 0),
      OutstandingAmount: Number(row.outstanding_amount || 0),
      ExpenseAccount: row.expense_account_name || row.expense_account_id || '-',
      PayableAccount: row.payable_account_name || row.payable_account_id || '-',
      AccountingStatus: formatAccountingPostingStatusLabel(row.accounting_status),
      Description: row.description || '-',
    }));

  const buildAccountingExpenseBreakdownTrendExportRows = rows =>
    rows.map(row => ({
      PeriodKey: row.period_key || '-',
      PeriodLabel: row.period_label || '-',
      DocumentCount: Number(row.document_count || 0),
      TotalAmount: Number(row.total_amount || 0),
      PaidAmount: Number(row.paid_amount || 0),
      OutstandingAmount: Number(row.outstanding_amount || 0),
    }));

  const buildAccountingExpenseBreakdownExportRows = rows =>
    rows.map(row => ({
      TanggalDokumen: formatDateTime(row.expense_date),
      DueDate: formatDateTime(row.due_date),
      DocumentNo: row.document_no || '-',
      Scope: row.document_scope_label || formatAccountingFinanceDocumentScopeLabel(row.document_scope),
      DocumentType: row.document_type_label || row.document_type || '-',
      Outlet: row.outlet_name || '-',
      Vendor: row.vendor_name || '-',
      Category: row.category || '-',
      SettlementMode: row.settlement_mode || '-',
      Status: row.status_label || formatAccountingFinanceStatusLabel(row.status),
      Amount: Number(row.amount || 0),
      PaidAmount: Number(row.paid_amount || 0),
      OutstandingAmount: Number(row.outstanding_amount || 0),
      ExpenseAccount: row.expense_account_name || row.expense_account_id || '-',
      PayableAccount: row.payable_account_name || row.payable_account_id || '-',
      AccountingStatus: formatAccountingPostingStatusLabel(row.accounting_status),
      Description: row.description || '-',
    }));

  const buildAccountingAssetRegisterTrendExportRows = rows =>
    rows.map(row => ({
      PeriodKey: row.period_key || '-',
      PeriodLabel: row.period_label || '-',
      AssetCount: Number(row.asset_count || 0),
      AcquisitionCost: Number(row.acquisition_cost || 0),
      OutstandingAmount: Number(row.outstanding_amount || 0),
    }));

  const buildAccountingAssetRegisterExportRows = rows =>
    rows.map(row => ({
      TanggalPerolehan: formatDateTime(row.expense_date),
      DueDate: formatDateTime(row.due_date),
      DocumentNo: row.document_no || '-',
      DocumentType: row.document_type_label || row.document_type || '-',
      Outlet: row.outlet_name || '-',
      Vendor: row.vendor_name || '-',
      Category: row.category_label || formatAccountingAssetCategoryLabel(row.category),
      Quantity: Number(row.quantity || 1),
      Status: row.status_label || formatAccountingFinanceStatusLabel(row.status),
      Amount: Number(row.amount || 0),
      PaidAmount: Number(row.paid_amount || 0),
      OutstandingAmount: Number(row.outstanding_amount || 0),
      AssetAccount: row.asset_account_name || row.asset_account_id || '-',
      UsefulLifeMonths: Number(row.useful_life_months || 0),
      UsefulLife: row.useful_life_label || '-',
      DepreciationMethod: row.depreciation_method_label || formatAccountingDepreciationMethodLabel(row.depreciation_method),
      EstimatedMonthlyDepreciation: Number(row.estimated_monthly_depreciation || 0),
      EstimatedBookValue: Number(row.estimated_book_value || 0),
      AccountingStatus: formatAccountingPostingStatusLabel(row.accounting_status),
      Description: row.description || '-',
    }));

  const buildAccountingDepreciationScheduleTrendExportRows = rows =>
    rows.map(row => ({
      PeriodKey: row.period_key || '-',
      PeriodLabel: row.period_label || '-',
      AssetCount: Number(row.asset_count || 0),
      DepreciationAmount: Number(row.depreciation_amount || 0),
      AccumulatedDepreciation: Number(row.accumulated_depreciation || 0),
      EndingBookValue: Number(row.ending_book_value || 0),
    }));

  const buildAccountingDepreciationScheduleExportRows = rows =>
    rows.map(row => ({
      TanggalPerolehan: formatDateTime(row.expense_date),
      DepreciationStartDate: formatDateTime(row.depreciation_start_date),
      DepreciationEndDate: formatDateTime(row.depreciation_end_date),
      DocumentNo: row.document_no || '-',
      Outlet: row.outlet_name || '-',
      Vendor: row.vendor_name || '-',
      Category: row.category_label || formatAccountingAssetCategoryLabel(row.category),
      Quantity: Number(row.quantity || 1),
      Status: row.status_label || formatAccountingFinanceStatusLabel(row.status),
      AcquisitionCost: Number(row.amount || 0),
      CurrentBookValue: Number(row.current_book_value || 0),
      UsefulLifeMonths: Number(row.useful_life_months || 0),
      UsefulLife: row.useful_life_label || '-',
      MonthsElapsed: Number(row.months_elapsed || 0),
      MonthsRemaining: Number(row.months_remaining || 0),
      MonthlyDepreciation: Number(row.monthly_depreciation || 0),
      PeriodDepreciation: Number(row.period_depreciation || 0),
      AccumulatedDepreciation: Number(row.accumulated_depreciation || 0),
      FullyDepreciated: row.fully_depreciated ? 'Ya' : 'Belum',
      AssetAccount: row.asset_account_name || row.asset_account_id || '-',
      Description: row.description || '-',
    }));

  const buildAccountingDepreciationPolicyExportRows = policy =>
    (Array.isArray(policy?.useful_life_defaults) ? policy.useful_life_defaults : []).map(item => ({
      Category: item?.category_label || formatAccountingAssetCategoryLabel(item?.category),
      CategoryKey: item?.category || '-',
      UsefulLifeMonths: Number(item?.useful_life_months || 0),
      UsefulLife: Number(item?.useful_life_months || 0) > 0
        ? `${formatDecimalNumber(Number(item?.useful_life_months || 0) / 12)} tahun`
        : '-',
      Method: policy?.method_label || formatAccountingDepreciationMethodLabel(policy?.method),
      ResidualValue: Number(policy?.residual_value_default || 0),
      StartRule: policy?.depreciation_start_rule_label || policy?.depreciation_start_rule || '-',
    }));

  const buildInfoExportRows = message => ([
    {
      Info: message || 'Belum ada data untuk diexport.',
    },
  ]);

  const buildMetricExportRows = cards =>
    (Array.isArray(cards) ? cards : []).map(item => ({
      Metric: item?.label || item?.key || '-',
      Value: item?.value || '-',
    }));

  const buildKeyValueExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map(item => ({
      Field: item?.label || item?.key || '-',
      Value: item?.value || '-',
    }));

  const buildBreakdownExportRows = (groupLabel, rows) =>
    (Array.isArray(rows) ? rows : []).map(item => ({
      Group: groupLabel || '-',
      Label: item?.label || item?.key || '-',
      Key: item?.key || '-',
      Count: Number(item?.count || 0),
      Amount: Number(item?.amount || 0),
    }));

  const buildPOSPurchaseTrendExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map(row => ({
      Period: row.period_label || '-',
      PeriodKey: row.period_key || '-',
      TotalDocuments: Number(row.total_documents || 0),
      TotalQuantity: Number(row.total_quantity || 0),
      TotalAmount: Number(row.total_amount || 0),
      OutstandingAmount: Number(row.outstanding_amount || 0),
    }));

  const buildPOSVendorPriceGapExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map(row => ({
      Product: row.product_name || '-',
      Category: row.category_name || 'Tanpa kategori',
      ItemType: formatProductItemTypeLabel(row.item_type),
      PreferredVendor: row.preferred_vendor || '-',
      PreferredPrice: Number(row.preferred_price || 0),
      SecondVendor: row.second_vendor || '-',
      SecondPrice: Number(row.second_price || 0),
      PriceSpread: Number(row.price_spread || 0),
      SpreadPercentage: Number(row.spread_percentage || 0),
      PotentialSavings: Number(row.potential_savings || 0),
    }));

  const buildAccountingJournalTrendExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map(row => ({
      Period: row.period_label || '-',
      PeriodKey: row.period_key || '-',
      JournalCount: Number(row.journal_count || 0),
      TotalDebit: Number(row.total_debit || 0),
      TotalCredit: Number(row.total_credit || 0),
    }));

  const buildAccountingJournalAccountExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map(row => ({
      Account: row.label || row.key || '-',
      AccountCode: row.key || '-',
      Category: row.category || 'Uncategorized',
      JournalCount: Number(row.count || 0),
      Debit: Number(row.debit || 0),
      Credit: Number(row.credit || 0),
      NetMovement: Number(row.net_movement || 0),
    }));

  const findAccountingPairedJournal = (selectedRow, rows) => {
    if (!selectedRow) {
      return null;
    }

    return (Array.isArray(rows) ? rows : []).find(row =>
      Number(row?.id || 0) !== Number(selectedRow.id || 0) &&
      String(row?.source_system || '').trim().toLowerCase() === String(selectedRow.source_system || '').trim().toLowerCase() &&
      String(row?.source_id || '').trim() !== '' &&
      String(row?.source_id || '').trim() === String(selectedRow.source_id || '').trim() &&
      String(row?.external_reference || '').trim() === String(selectedRow.external_reference || '').trim()
    ) || null;
  };

  const buildAccountingJournalOverviewExportRows = (selectedRow, pairedJournal) => {
    if (!selectedRow) {
      return buildInfoExportRows('Pilih salah satu jurnal di tab detail untuk export inspector jurnal.');
    }

    const journalLines = Array.isArray(selectedRow?.lines) ? selectedRow.lines : [];

    return [
      {
        Field: 'Nomor jurnal',
        Value: formatAccountingJournalNumber(selectedRow.id, selectedRow.occurred_at),
      },
      {
        Field: 'Tanggal jurnal',
        Value: formatDateTime(selectedRow.occurred_at),
      },
      {
        Field: 'Keterangan',
        Value: selectedRow.description || '-',
      },
      {
        Field: 'Reference / Invoice',
        Value: selectedRow.external_reference || '-',
      },
      {
        Field: 'Source system',
        Value: formatAccountingSourceSystemLabel(selectedRow.source_system),
      },
      {
        Field: 'Jenis jurnal',
        Value: formatAccountingSourceTypeLabel(selectedRow.source_type),
      },
      {
        Field: 'Source ID',
        Value: selectedRow.source_id || '-',
      },
      {
        Field: 'Outlet',
        Value: selectedRow.outlet_name || '-',
      },
      {
        Field: 'Business unit',
        Value: formatAccountingBusinessUnitLabel(selectedRow.business_unit),
      },
      {
        Field: 'Posting status',
        Value: formatAccountingPostingStatusLabel(selectedRow.posting_status),
      },
      {
        Field: 'Posted at',
        Value: formatDateTime(selectedRow.posted_at),
      },
      {
        Field: 'Total debit',
        Value: Number(selectedRow.total_debit || 0),
      },
      {
        Field: 'Total credit',
        Value: Number(selectedRow.total_credit || 0),
      },
      {
        Field: 'Line count',
        Value: Number(selectedRow.line_count || journalLines.length || 0),
      },
      {
        Field: 'Debit lines',
        Value: Number(getAccountingSideLines(journalLines, 'debit').length || 0),
      },
      {
        Field: 'Credit lines',
        Value: Number(getAccountingSideLines(journalLines, 'credit').length || 0),
      },
      {
        Field: 'Jurnal pasangan',
        Value: pairedJournal
          ? `${formatAccountingJournalNumber(pairedJournal.id, pairedJournal.occurred_at)} - ${formatAccountingSourceTypeLabel(pairedJournal.source_type)}`
          : '-',
      },
    ];
  };

  const buildAccountingJournalLineExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map((row, index) => ({
      No: index + 1,
      COA: row.account_id || '-',
      Account: row.account_name || row.account_id || '-',
      AccountType: row.account_type || 'Uncategorized',
      Entry: formatAccountingLineSideLabel(row),
      Debit: Number(row.debit || 0),
      Credit: Number(row.credit || 0),
    }));

  const buildAccountingGeneralLedgerTrendExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map(row => ({
      Period: row.period_label || '-',
      PeriodKey: row.period_key || '-',
      EntryCount: Number(row.entry_count || 0),
      TotalDebit: Number(row.total_debit || 0),
      TotalCredit: Number(row.total_credit || 0),
    }));

  const buildAccountingGeneralLedgerAccountExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map(row => ({
      AccountID: row.account_id || '-',
      AccountName: row.account_name || row.account_id || '-',
      AccountType: row.account_type || 'Uncategorized',
      OpeningBalance: Number(row.opening_balance || 0),
      TotalDebit: Number(row.total_debit || 0),
      TotalCredit: Number(row.total_credit || 0),
      ClosingBalance: Number(row.closing_balance || 0),
      EntryCount: Number(row.entry_count || 0),
      LastOccurredAt: formatDateTime(row.last_occurred_at),
    }));

  const buildAccountingGeneralLedgerOverviewExportRows = selectedRow => {
    if (!selectedRow) {
      return buildInfoExportRows('Pilih salah satu akun di tab detail untuk export inspector ledger.');
    }

    return [
      {
        Field: 'Kode akun',
        Value: selectedRow.account_id || '-',
      },
      {
        Field: 'Nama akun',
        Value: selectedRow.account_name || selectedRow.account_id || '-',
      },
      {
        Field: 'Kategori akun',
        Value: selectedRow.account_type || 'Uncategorized',
      },
      {
        Field: 'Saldo awal',
        Value: Number(selectedRow.opening_balance || 0),
      },
      {
        Field: 'Total debit',
        Value: Number(selectedRow.total_debit || 0),
      },
      {
        Field: 'Total credit',
        Value: Number(selectedRow.total_credit || 0),
      },
      {
        Field: 'Saldo akhir',
        Value: Number(selectedRow.closing_balance || 0),
      },
      {
        Field: 'Jumlah line',
        Value: Number(selectedRow.entry_count || 0),
      },
      {
        Field: 'Aktivitas terakhir',
        Value: formatDateTime(selectedRow.last_occurred_at),
      },
    ];
  };

  const buildAccountingGeneralLedgerEntryExportRows = rows =>
    (Array.isArray(rows) ? rows : []).map(row => ({
      JournalNumber: formatAccountingJournalNumber(row.journal_id, row.occurred_at),
      TanggalJurnal: formatDateTime(row.occurred_at),
      Description: row.description || '-',
      Reference: row.external_reference || row.source_id || '-',
      Outlet: row.outlet_name || '-',
      SourceType: formatAccountingSourceTypeLabel(row.source_type),
      PostingStatus: formatAccountingPostingStatusLabel(row.posting_status),
      Debit: Number(row.debit || 0),
      Credit: Number(row.credit || 0),
      RunningBalance: Number(row.running_balance || 0),
    }));

  const buildCurrentReportExportMeta = () => {
    if (activeTab === 'accounting') {
      if (activeAccountingReport === 'journal-report') {
        return {
          title: 'Accounting Journal Report',
          sheetName: 'Journal Report',
          slug: 'accounting-journal-report',
        };
      }
      if (activeAccountingReport === 'general-ledger') {
        return {
          title: 'Accounting General Ledger',
          sheetName: 'General Ledger',
          slug: 'accounting-general-ledger',
        };
      }
      if (activeAccountingReport === 'trial-balance') {
        return {
          title: 'Accounting Trial Balance',
          sheetName: 'Trial Balance',
          slug: 'accounting-trial-balance',
        };
      }
      if (activeAccountingReport === 'profit-loss') {
        return {
          title: 'Accounting Profit Loss',
          sheetName: 'Profit Loss',
          slug: 'accounting-profit-loss',
        };
      }
      if (activeAccountingReport === 'balance-sheet') {
        return {
          title: 'Accounting Balance Sheet',
          sheetName: 'Balance Sheet',
          slug: 'accounting-balance-sheet',
        };
      }
      if (activeAccountingReport === 'cash-flow') {
        return {
          title: 'Accounting Cash Flow',
          sheetName: 'Cash Flow',
          slug: 'accounting-cash-flow',
        };
      }
      if (activeAccountingReport === 'accounts-payable-aging') {
        return {
          title: 'Accounting Accounts Payable Aging',
          sheetName: 'AP Aging',
          slug: 'accounting-ap-aging',
        };
      }
      if (activeAccountingReport === 'expense-breakdown') {
        return {
          title: 'Accounting Expense Breakdown',
          sheetName: 'Expense Breakdown',
          slug: 'accounting-expense-breakdown',
        };
      }
      if (activeAccountingReport === 'asset-register') {
        return {
          title: 'Accounting Asset Register',
          sheetName: 'Asset Register',
          slug: 'accounting-asset-register',
        };
      }
      if (activeAccountingReport === 'depreciation-schedule') {
        return {
          title: 'Accounting Depreciation Schedule',
          sheetName: 'Depreciation Schedule',
          slug: 'accounting-depreciation-schedule',
        };
      }
      return {
        title: 'Accounting Report',
        sheetName: 'Accounting',
        slug: 'accounting-report',
      };
    }

    if (activeTab === 'pos') {
      if (activePosReport === 'purchase-report') {
        return {
          title: 'POS Purchase Report',
          sheetName: 'POS Purchase',
          slug: 'pos-purchase-report',
        };
      }
      if (activePosReport === 'cashier-sales-summary') {
        return {
          title: 'Cashier Sales Summary Report',
          sheetName: 'Cashier Sales',
          slug: 'pos-cashier-sales-summary',
        };
      }
      if (activePosReport === 'product-sales-report') {
        return {
          title: 'Product Sales Report',
          sheetName: 'Product Sales',
          slug: 'pos-product-sales-report',
        };
      }
      if (activePosReport === 'refund-void-report') {
        return {
          title: 'Refund / Void Report',
          sheetName: 'Refund Void',
          slug: 'pos-refund-void-report',
        };
      }
      if (activePosReport === 'vendor-aging') {
        return {
          title: 'Vendor Aging Report',
          sheetName: 'Vendor Aging',
          slug: 'pos-vendor-aging',
        };
      }
      if (activePosReport === 'stock-movement') {
        return {
          title: 'Stock Movement Report',
          sheetName: 'Stock Movement',
          slug: 'pos-stock-movement',
        };
      }
      if (activePosReport === 'bom-consumption-report') {
        return {
          title: 'BOM Consumption Report',
          sheetName: 'BOM Consumption',
          slug: 'pos-bom-consumption-report',
        };
      }
      if (activePosReport === 'stock-opname-variance') {
        return {
          title: 'Stock Opname Variance Report',
          sheetName: 'Opname Variance',
          slug: 'pos-stock-opname-variance',
        };
      }
      if (activePosReport === 'vendor-price-comparison') {
        return {
          title: 'Vendor Price Comparison Report',
          sheetName: 'Vendor Price',
          slug: 'pos-vendor-price-comparison',
        };
      }
      return {
        title: 'POS Sales Summary Report',
        sheetName: 'POS Sales',
        slug: 'pos-sales-summary',
      };
    }

    if (activeBusinessReport === 'staff-transaction-summary') {
      return {
        title: 'Staff Transaction Summary Report',
        sheetName: 'Staff Transaction Summary',
        slug: 'staff-transaction-summary',
      };
    }
    if (activeBusinessReport === 'member-activity') {
      return {
        title: 'Member Activity Report',
        sheetName: 'Member Activity',
        slug: 'member-activity',
      };
    }
    if (activeBusinessReport === 'check-in') {
      return {
        title: 'Check-in Report',
        sheetName: 'Check-in',
        slug: 'check-in',
      };
    }
    if (activeBusinessReport === 'pt-sales') {
      return {
        title: 'Private Session Sales Report',
        sheetName: 'Private Session Sales',
        slug: 'pt-sales',
      };
    }
    if (activeBusinessReport === 'pt-session') {
      return {
        title: 'Private Session Report',
        sheetName: 'Private Session',
        slug: 'pt-session',
      };
    }
    if (activeBusinessReport === 'pt-trainer') {
      return {
        title: 'Private Session Staff Performance Report',
        sheetName: 'Private Session Staff',
        slug: 'pt-trainer',
      };
    }
    return {
      title: 'Membership Sales Report',
      sheetName: 'Membership Sales',
      slug: 'membership-sales',
    };
  };

  const getActiveExportPageMeta = () => {
    if (activeTab === 'pos' && activePosReport === 'purchase-report') {
      return {
        isScoped: true,
        label: activePurchaseSectionMeta.label,
        slug: activePurchaseSection,
      };
    }

    if (activeTab === 'pos' && activePosReport === 'vendor-price-comparison') {
      return {
        isScoped: true,
        label: activeVendorPriceSectionMeta.label,
        slug: activeVendorPriceSection,
      };
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'journal-report') {
      if (activeAccountingJournalSection === 'detail') {
        return {
          isScoped: true,
          label: `${activeAccountingJournalSectionMeta.label} - ${activeAccountingJournalDetailSectionMeta.label}`,
          slug: `${activeAccountingJournalSection}-${activeAccountingJournalDetailSection}`,
        };
      }

      return {
        isScoped: true,
        label: activeAccountingJournalSectionMeta.label,
        slug: activeAccountingJournalSection,
      };
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'general-ledger') {
      return {
        isScoped: true,
        label: activeAccountingGeneralLedgerSectionMeta.label,
        slug: activeAccountingGeneralLedgerSection,
      };
    }

    return {
      isScoped: false,
      label: 'Full report',
      slug: 'full',
    };
  };

  const normalizeExportRows = (rows, emptyMessage) =>
    Array.isArray(rows) && rows.length
      ? rows
      : buildInfoExportRows(emptyMessage || 'Belum ada data untuk halaman aktif.');

  const sanitizeSheetName = value =>
    String(value || 'Sheet')
      .replace(/[\\/*?:[\]]/g, ' ')
      .slice(0, 31);

  const formatPdfCellValue = (key, value, row) => {
    const normalizedValue = formatExportCellValue(key, value, row);
    if (normalizedValue === null || normalizedValue === undefined || normalizedValue === '') {
      return '-';
    }
    if (typeof normalizedValue === 'boolean') {
      return normalizedValue ? 'Yes' : 'No';
    }
    return String(normalizedValue);
  };

  const renderPdfTableSections = (doc, tables, startY = 84) => {
    const normalizedTables = Array.isArray(tables) ? tables : [];
    let currentY = startY;
    const pageHeight = doc.internal.pageSize.getHeight();

    normalizedTables.forEach((table, index) => {
      const rows = normalizeExportRows(table?.rows, table?.emptyMessage);
      const headers = Object.keys(rows[0] || {});
      const title = table?.title || `Bagian ${index + 1}`;

      if (currentY > pageHeight - 120) {
        doc.addPage();
        currentY = 40;
      }

      doc.setFontSize(11);
      doc.text(title, 40, currentY);

      autoTable(doc, {
        startY: currentY + 8,
        head: [headers],
        body: rows.map(row => headers.map(header => formatPdfCellValue(header, row[header], row))),
        styles: {
          fontSize: 8,
          cellPadding: 4,
        },
        headStyles: {
          fillColor: [72, 96, 64],
        },
        margin: {
          left: 28,
          right: 28,
        },
      });

      currentY = (doc.lastAutoTable?.finalY || currentY + 8) + 18;
    });
  };

  const buildSectionSpecificExportTables = exported => {
    if (activeTab === 'pos' && activePosReport === 'purchase-report') {
      if (activePurchaseSection === 'summary') {
        return [
          {
            name: 'Purchase KPI',
            title: 'KPI purchase aktif',
            rows: buildMetricExportRows(posPurchaseSummaryCards),
          },
          {
            name: 'Purchase Breakdown',
            title: 'Breakdown purchase aktif',
            rows: [
              ...buildBreakdownExportRows('Vendor mix', posPurchaseData.vendor_breakdown),
              ...buildBreakdownExportRows('Payment method mix', posPurchaseData.payment_method_breakdown),
              ...buildBreakdownExportRows('Payment status mix', posPurchaseData.payment_status_breakdown),
            ],
            emptyMessage: 'Belum ada breakdown purchase pada halaman ringkasan.',
          },
        ];
      }

      if (activePurchaseSection === 'analysis') {
        return [
          {
            name: 'Purchase Trend',
            title: 'Tren purchase harian',
            rows: buildPOSPurchaseTrendExportRows(posPurchaseData.trends),
            emptyMessage: 'Belum ada tren purchase pada halaman analisa.',
          },
          {
            name: 'Item Breakdown',
            title: 'Top produk dibeli',
            rows: buildBreakdownExportRows('Top produk dibeli', posPurchaseData.item_breakdown),
            emptyMessage: 'Belum ada breakdown produk purchase pada halaman analisa.',
          },
        ];
      }

      return [
        {
          name: 'Purchase Detail',
          title: 'Daftar purchase POS',
          rows: buildPOSPurchaseExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada data purchase pada halaman detail.',
        },
      ];
    }

    if (activeTab === 'pos' && activePosReport === 'vendor-price-comparison') {
      if (activeVendorPriceSection === 'summary') {
        return [
          {
            name: 'Vendor KPI',
            title: 'KPI vendor price comparison',
            rows: buildMetricExportRows(posVendorPriceComparisonSummaryCards),
          },
          {
            name: 'Vendor Breakdown',
            title: 'Breakdown vendor price comparison',
            rows: [
              ...buildBreakdownExportRows('Vendor wins', posVendorPriceComparisonData.vendor_win_breakdown),
              ...buildBreakdownExportRows('Category savings', posVendorPriceComparisonData.category_spread_breakdown),
            ],
            emptyMessage: 'Belum ada breakdown vendor price comparison pada halaman ringkasan.',
          },
        ];
      }

      if (activeVendorPriceSection === 'analysis') {
        return [
          {
            name: 'Top Price Gap',
            title: 'Product dengan spread harga terbesar',
            rows: buildPOSVendorPriceGapExportRows(posVendorPriceComparisonData.top_price_gap_products),
            emptyMessage: 'Belum ada product spread di halaman analisa vendor.',
          },
          {
            name: 'Vendor Benchmark',
            title: 'Benchmark vendor per product',
            rows: buildPOSVendorPriceComparisonVendorExportRows(
              Array.isArray(exported?.vendorDetails) ? exported.vendorDetails : posVendorPriceComparisonData.vendor_details
            ),
            emptyMessage: 'Belum ada benchmark vendor di halaman analisa vendor.',
          },
        ];
      }

      return [
        {
          name: 'Vendor Detail',
          title: 'Daftar vendor price comparison',
          rows: buildPOSVendorPriceComparisonExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada detail vendor price comparison pada halaman aktif.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'journal-report') {
      const journalRows = Array.isArray(exported?.rows) ? exported.rows : [];
      const selectedRow =
        journalRows.find(row => Number(row?.id || 0) === Number(selectedAccountingJournalId || 0)) ||
        journalRows[0] ||
        null;
      const pairedJournal = findAccountingPairedJournal(selectedRow, journalRows);

      if (activeAccountingJournalSection === 'summary') {
        return [
          {
            name: 'Audit Scope',
            title: 'Scope audit journal report',
            rows: buildKeyValueExportRows([
              { label: 'Outlet', value: selectedOutletLabel },
              { label: 'Periode', value: selectedAccountingPeriodLabel },
              { label: 'Source system', value: selectedAccountingSourceSystemLabel },
              { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
              { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
              { label: 'Kata kunci', value: selectedAccountingSearchLabel },
            ]),
          },
          {
            name: 'Journal KPI',
            title: 'KPI journal report',
            rows: buildMetricExportRows(accountingJournalSummaryCards),
          },
          {
            name: 'Journal Breakdown',
            title: 'Breakdown journal report',
            rows: [
              ...buildBreakdownExportRows('Source system mix', accountingJournalData.source_breakdown),
              ...buildBreakdownExportRows('Business unit mix', accountingJournalData.business_unit_breakdown),
              ...buildBreakdownExportRows('Posting status', accountingJournalData.posting_status_breakdown),
              ...buildBreakdownExportRows('Sebaran outlet', accountingJournalData.outlet_breakdown),
            ],
            emptyMessage: 'Belum ada breakdown journal report pada halaman ringkasan.',
          },
        ];
      }

      if (activeAccountingJournalSection === 'analysis') {
        return [
          {
            name: 'Journal Trend',
            title: 'Tren jurnal harian',
            rows: buildAccountingJournalTrendExportRows(Array.isArray(exported?.trends) ? exported.trends : accountingJournalData.trends),
            emptyMessage: 'Belum ada tren jurnal pada halaman analisa.',
          },
          {
            name: 'Account Breakdown',
            title: 'Breakdown akun ledger',
            rows: buildAccountingJournalAccountExportRows(
              Array.isArray(exported?.accountBreakdown) ? exported.accountBreakdown : accountingJournalData.account_breakdown
            ),
            emptyMessage: 'Belum ada breakdown akun pada halaman analisa.',
          },
        ];
      }

      const overviewRows = buildAccountingJournalOverviewExportRows(selectedRow, pairedJournal);
      if (activeAccountingJournalDetailSection === 'overview') {
        return [
          {
            name: 'Journal Overview',
            title: 'Ringkasan jurnal terpilih',
            rows: overviewRows,
          },
        ];
      }

      return [
        {
          name: 'Journal Overview',
          title: 'Ringkasan jurnal terpilih',
          rows: overviewRows,
        },
        {
          name: activeAccountingJournalDetailSection === 'general' ? 'Journal Umum' : 'Journal Audit',
          title: activeAccountingJournalDetailSection === 'general' ? 'Format jurnal umum' : 'Detail line jurnal',
          rows: buildAccountingJournalLineExportRows(
            activeAccountingJournalDetailSection === 'general'
              ? sortAccountingJournalLinesForDisplay(selectedRow?.lines)
              : selectedRow?.lines
          ),
          emptyMessage: 'Belum ada line jurnal untuk jurnal yang sedang dipilih.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'general-ledger') {
      const ledgerRows = Array.isArray(exported?.rows) ? exported.rows : [];
      const selectedRow =
        ledgerRows.find(row => String(row?.account_id || '') === String(selectedAccountingGeneralLedgerAccountId || '')) ||
        ledgerRows[0] ||
        null;

      if (activeAccountingGeneralLedgerSection === 'summary') {
        return [
          {
            name: 'Ledger Scope',
            title: 'Scope audit general ledger',
            rows: buildKeyValueExportRows([
              { label: 'Outlet', value: selectedOutletLabel },
              { label: 'Periode', value: selectedAccountingPeriodLabel },
              { label: 'Source system', value: selectedAccountingSourceSystemLabel },
              { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
              { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
              { label: 'Kata kunci', value: selectedAccountingSearchLabel },
            ]),
          },
          {
            name: 'Ledger KPI',
            title: 'KPI general ledger',
            rows: buildMetricExportRows(accountingGeneralLedgerSummaryCards),
          },
          {
            name: 'Ledger Breakdown',
            title: 'Breakdown general ledger',
            rows: [
              ...buildBreakdownExportRows('Kategori akun ledger', accountingGeneralLedgerData.account_type_breakdown),
              ...buildBreakdownExportRows('Source system mix', accountingGeneralLedgerData.source_breakdown),
              ...buildBreakdownExportRows('Sebaran outlet', accountingGeneralLedgerData.outlet_breakdown),
            ],
            emptyMessage: 'Belum ada breakdown general ledger pada halaman ringkasan.',
          },
        ];
      }

      if (activeAccountingGeneralLedgerSection === 'analysis') {
        return [
          {
            name: 'Ledger Trend',
            title: 'Tren line ledger harian',
            rows: buildAccountingGeneralLedgerTrendExportRows(
              Array.isArray(exported?.trends) ? exported.trends : accountingGeneralLedgerData.trends
            ),
            emptyMessage: 'Belum ada tren general ledger pada halaman analisa.',
          },
          {
            name: 'Ledger Accounts',
            title: 'Daftar akun dan mutasinya',
            rows: buildAccountingGeneralLedgerAccountExportRows(ledgerRows),
            emptyMessage: 'Belum ada akun ledger pada halaman analisa.',
          },
        ];
      }

      return [
        {
          name: 'Ledger Overview',
          title: 'Ringkasan akun terpilih',
          rows: buildAccountingGeneralLedgerOverviewExportRows(selectedRow),
        },
        {
          name: 'Ledger Entries',
          title: 'Mutasi jurnal pembentuk saldo akun',
          rows: buildAccountingGeneralLedgerEntryExportRows(selectedRow?.entries),
          emptyMessage: 'Belum ada line ledger untuk akun yang sedang dipilih.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'trial-balance') {
      return [
        {
          name: 'Trial Scope',
          title: 'Scope trial balance',
          rows: buildKeyValueExportRows([
            { label: 'Outlet', value: selectedOutletLabel },
            { label: 'Periode', value: selectedAccountingPeriodLabel },
            { label: 'Source system', value: selectedAccountingSourceSystemLabel },
            { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
            { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
            { label: 'Kata kunci', value: selectedAccountingSearchLabel },
          ]),
        },
        {
          name: 'Trial KPI',
          title: 'KPI trial balance',
          rows: buildMetricExportRows(accountingTrialBalanceSummaryCards),
        },
        {
          name: 'Trial Breakdown',
          title: 'Breakdown kategori akun trial balance',
          rows: buildBreakdownExportRows('Kategori akun', accountingTrialBalanceData.account_type_breakdown),
          emptyMessage: 'Belum ada breakdown trial balance pada periode ini.',
        },
        {
          name: 'Trial Detail',
          title: 'Daftar trial balance',
          rows: buildAccountingTrialBalanceExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada akun trial balance pada periode ini.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'profit-loss') {
      return [
        {
          name: 'PL Scope',
          title: 'Scope profit & loss',
          rows: buildKeyValueExportRows([
            { label: 'Outlet', value: selectedOutletLabel },
            { label: 'Periode', value: selectedAccountingPeriodLabel },
            { label: 'Source system', value: selectedAccountingSourceSystemLabel },
            { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
            { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
            { label: 'Kata kunci', value: selectedAccountingSearchLabel },
          ]),
        },
        {
          name: 'PL KPI',
          title: 'KPI profit & loss',
          rows: buildMetricExportRows(accountingProfitLossSummaryCards),
        },
        {
          name: 'PL Breakdown',
          title: 'Breakdown kategori profit & loss',
          rows: buildBreakdownExportRows('Kategori akun', accountingProfitLossData.account_type_breakdown),
          emptyMessage: 'Belum ada breakdown profit & loss pada periode ini.',
        },
        {
          name: 'PL Detail',
          title: 'Daftar akun profit & loss',
          rows: buildAccountingProfitLossExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada akun profit & loss pada periode ini.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'balance-sheet') {
      return [
        {
          name: 'BS Scope',
          title: 'Scope balance sheet',
          rows: buildKeyValueExportRows([
            { label: 'Outlet', value: selectedOutletLabel },
            { label: 'Periode', value: selectedAccountingPeriodLabel },
            { label: 'Source system', value: selectedAccountingSourceSystemLabel },
            { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
            { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
            { label: 'Kata kunci', value: selectedAccountingSearchLabel },
          ]),
        },
        {
          name: 'BS KPI',
          title: 'KPI balance sheet',
          rows: buildMetricExportRows(accountingBalanceSheetSummaryCards),
        },
        {
          name: 'BS Breakdown',
          title: 'Breakdown kategori balance sheet',
          rows: buildBreakdownExportRows('Kategori akun', accountingBalanceSheetData.account_type_breakdown),
          emptyMessage: 'Belum ada breakdown balance sheet pada periode ini.',
        },
        {
          name: 'BS Detail',
          title: 'Daftar akun balance sheet',
          rows: buildAccountingBalanceSheetExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada akun balance sheet pada periode ini.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'cash-flow') {
      return [
        {
          name: 'CF Scope',
          title: 'Scope cash flow',
          rows: buildKeyValueExportRows([
            { label: 'Outlet', value: selectedOutletLabel },
            { label: 'Periode', value: selectedAccountingPeriodLabel },
            { label: 'Source system', value: selectedAccountingSourceSystemLabel },
            { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
            { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
            { label: 'Kata kunci', value: selectedAccountingSearchLabel },
          ]),
        },
        {
          name: 'CF KPI',
          title: 'KPI cash flow',
          rows: buildMetricExportRows(accountingCashFlowSummaryCards),
        },
        {
          name: 'CF Section',
          title: 'Breakdown arus kas per section',
          rows: buildBreakdownExportRows('Section', accountingCashFlowData.section_breakdown),
          emptyMessage: 'Belum ada breakdown cash flow pada periode ini.',
        },
        {
          name: 'CF Cash Accounts',
          title: 'Breakdown akun kas',
          rows: buildBreakdownExportRows('Cash account', accountingCashFlowData.cash_account_breakdown),
          emptyMessage: 'Belum ada akun kas yang terdeteksi pada periode ini.',
        },
        {
          name: 'CF Trends',
          title: 'Tren arus kas harian',
          rows: buildAccountingCashFlowTrendExportRows(
            Array.isArray(exported?.trends) ? exported.trends : accountingCashFlowData.trends
          ),
          emptyMessage: 'Belum ada tren cash flow pada periode ini.',
        },
        {
          name: 'CF Detail',
          title: 'Detail arus kas',
          rows: buildAccountingCashFlowExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada detail cash flow pada periode ini.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'accounts-payable-aging') {
      return [
        {
          name: 'AP Scope',
          title: 'Scope AP aging',
          rows: buildKeyValueExportRows([
            { label: 'Outlet', value: selectedOutletLabel },
            { label: 'Periode dokumen', value: selectedAccountingPeriodLabel },
            { label: 'Scope dokumen', value: selectedAccountingFinanceDocumentScopeLabel },
            { label: 'Status dokumen', value: selectedAccountingFinanceStatusLabel },
            { label: 'Bucket aging', value: selectedAccountingFinanceAgingBucketLabel },
            { label: 'Kata kunci', value: selectedAccountingSearchLabel },
          ]),
        },
        {
          name: 'AP KPI',
          title: 'KPI AP aging',
          rows: buildMetricExportRows(accountingAccountsPayableAgingSummaryCards),
        },
        {
          name: 'AP Bucket',
          title: 'Breakdown aging bucket',
          rows: buildBreakdownExportRows('Aging bucket', accountingAccountsPayableAgingData.aging_bucket_breakdown),
          emptyMessage: 'Belum ada bucket aging pada periode ini.',
        },
        {
          name: 'AP Vendor',
          title: 'Breakdown vendor payable',
          rows: buildBreakdownExportRows('Vendor', accountingAccountsPayableAgingData.vendor_breakdown),
          emptyMessage: 'Belum ada vendor payable pada periode ini.',
        },
        {
          name: 'AP Scope Breakdown',
          title: 'Breakdown scope dokumen',
          rows: buildBreakdownExportRows('Scope', accountingAccountsPayableAgingData.scope_breakdown),
          emptyMessage: 'Belum ada scope dokumen payable pada periode ini.',
        },
        {
          name: 'AP Trend',
          title: 'Tren payable harian',
          rows: buildAccountingAccountsPayableAgingTrendExportRows(
            Array.isArray(exported?.trends) ? exported.trends : accountingAccountsPayableAgingData.trends
          ),
          emptyMessage: 'Belum ada tren AP aging pada periode ini.',
        },
        {
          name: 'AP Detail',
          title: 'Detail AP aging',
          rows: buildAccountingAccountsPayableAgingExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada detail AP aging pada periode ini.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'expense-breakdown') {
      return [
        {
          name: 'EXP Scope',
          title: 'Scope expense breakdown',
          rows: buildKeyValueExportRows([
            { label: 'Outlet', value: selectedOutletLabel },
            { label: 'Periode dokumen', value: selectedAccountingPeriodLabel },
            { label: 'Scope dokumen', value: selectedAccountingFinanceDocumentScopeLabel },
            { label: 'Status dokumen', value: selectedAccountingFinanceStatusLabel },
            { label: 'Kata kunci', value: selectedAccountingSearchLabel },
          ]),
        },
        {
          name: 'EXP KPI',
          title: 'KPI expense breakdown',
          rows: buildMetricExportRows(accountingExpenseBreakdownSummaryCards),
        },
        {
          name: 'EXP Category',
          title: 'Breakdown kategori biaya',
          rows: buildBreakdownExportRows('Category', accountingExpenseBreakdownData.category_breakdown),
          emptyMessage: 'Belum ada kategori biaya pada periode ini.',
        },
        {
          name: 'EXP Vendor',
          title: 'Breakdown vendor',
          rows: buildBreakdownExportRows('Vendor', accountingExpenseBreakdownData.vendor_breakdown),
          emptyMessage: 'Belum ada vendor pada periode ini.',
        },
        {
          name: 'EXP Scope Breakdown',
          title: 'Breakdown scope dokumen',
          rows: buildBreakdownExportRows('Scope', accountingExpenseBreakdownData.scope_breakdown),
          emptyMessage: 'Belum ada scope dokumen pada periode ini.',
        },
        {
          name: 'EXP Trend',
          title: 'Tren biaya harian',
          rows: buildAccountingExpenseBreakdownTrendExportRows(
            Array.isArray(exported?.trends) ? exported.trends : accountingExpenseBreakdownData.trends
          ),
          emptyMessage: 'Belum ada tren expense breakdown pada periode ini.',
        },
        {
          name: 'EXP Detail',
          title: 'Detail expense breakdown',
          rows: buildAccountingExpenseBreakdownExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada detail expense breakdown pada periode ini.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'asset-register') {
      return [
        {
          name: 'AR Scope',
          title: 'Scope asset register',
          rows: buildKeyValueExportRows([
            { label: 'Outlet', value: selectedOutletLabel },
            { label: 'Periode perolehan', value: selectedAccountingPeriodLabel },
            { label: 'Scope dokumen', value: selectedAccountingFixedAssetScopeLabel },
            { label: 'Status dokumen', value: selectedAccountingFinanceStatusLabel },
            { label: 'Kata kunci', value: selectedAccountingSearchLabel },
          ]),
        },
        {
          name: 'AR KPI',
          title: 'KPI asset register',
          rows: buildMetricExportRows(accountingAssetRegisterSummaryCards),
        },
        {
          name: 'AR Policy',
          title: 'Policy estimasi depresiasi',
          rows: buildAccountingDepreciationPolicyExportRows(exported?.policy || accountingAssetRegisterData.policy),
          emptyMessage: 'Belum ada policy default depresiasi.',
        },
        {
          name: 'AR Category',
          title: 'Breakdown kategori aset',
          rows: buildBreakdownExportRows('Category', accountingAssetRegisterData.category_breakdown),
          emptyMessage: 'Belum ada kategori aset pada periode ini.',
        },
        {
          name: 'AR Vendor',
          title: 'Breakdown vendor aset',
          rows: buildBreakdownExportRows('Vendor', accountingAssetRegisterData.vendor_breakdown),
          emptyMessage: 'Belum ada vendor aset pada periode ini.',
        },
        {
          name: 'AR Account',
          title: 'Breakdown akun aset',
          rows: buildBreakdownExportRows('Asset account', accountingAssetRegisterData.account_breakdown),
          emptyMessage: 'Belum ada akun aset pada periode ini.',
        },
        {
          name: 'AR Trend',
          title: 'Tren akuisisi aset',
          rows: buildAccountingAssetRegisterTrendExportRows(
            Array.isArray(exported?.trends) ? exported.trends : accountingAssetRegisterData.trends
          ),
          emptyMessage: 'Belum ada tren akuisisi aset pada periode ini.',
        },
        {
          name: 'AR Detail',
          title: 'Detail asset register',
          rows: buildAccountingAssetRegisterExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada detail asset register pada periode ini.',
        },
      ];
    }

    if (activeTab === 'accounting' && activeAccountingReport === 'depreciation-schedule') {
      return [
        {
          name: 'DEP Scope',
          title: 'Scope depreciation schedule',
          rows: buildKeyValueExportRows([
            { label: 'Outlet', value: selectedOutletLabel },
            { label: 'Periode report', value: selectedAccountingPeriodLabel },
            { label: 'Scope dokumen', value: selectedAccountingFixedAssetScopeLabel },
            { label: 'Status dokumen', value: selectedAccountingFinanceStatusLabel },
            { label: 'Kata kunci', value: selectedAccountingSearchLabel },
          ]),
        },
        {
          name: 'DEP KPI',
          title: 'KPI depreciation schedule',
          rows: buildMetricExportRows(accountingDepreciationScheduleSummaryCards),
        },
        {
          name: 'DEP Policy',
          title: 'Policy default penyusutan',
          rows: buildAccountingDepreciationPolicyExportRows(exported?.policy || accountingDepreciationScheduleData.policy),
          emptyMessage: 'Belum ada policy default penyusutan.',
        },
        {
          name: 'DEP Category',
          title: 'Breakdown nilai buku per kategori',
          rows: buildBreakdownExportRows('Category', accountingDepreciationScheduleData.category_breakdown),
          emptyMessage: 'Belum ada kategori depresiasi pada periode ini.',
        },
        {
          name: 'DEP Useful Life',
          title: 'Breakdown umur manfaat',
          rows: buildBreakdownExportRows('Useful life bucket', accountingDepreciationScheduleData.useful_life_breakdown),
          emptyMessage: 'Belum ada bucket umur manfaat pada periode ini.',
        },
        {
          name: 'DEP Trend',
          title: 'Tren penyusutan bulanan',
          rows: buildAccountingDepreciationScheduleTrendExportRows(
            Array.isArray(exported?.trends) ? exported.trends : accountingDepreciationScheduleData.trends
          ),
          emptyMessage: 'Belum ada tren penyusutan pada periode ini.',
        },
        {
          name: 'DEP Detail',
          title: 'Detail depreciation schedule',
          rows: buildAccountingDepreciationScheduleExportRows(Array.isArray(exported?.rows) ? exported.rows : []),
          emptyMessage: 'Belum ada detail penyusutan pada periode ini.',
        },
      ];
    }

    return null;
  };

  const handleExportExcel = async () => {
    if (loading || exporting) {
      return;
    }
    setExporting(true);
    try {
      const reportExportMeta = buildCurrentReportExportMeta();
      const activeExportPageMeta = getActiveExportPageMeta();
      const exported = activeTab === 'accounting'
        ? activeAccountingReport === 'journal-report'
          ? await fetchAllAccountingJournalRows()
        : activeAccountingReport === 'general-ledger'
            ? await fetchAllAccountingGeneralLedgerRows()
          : activeAccountingReport === 'trial-balance'
            ? await fetchAllAccountingTrialBalanceRows()
          : activeAccountingReport === 'profit-loss'
            ? await fetchAllAccountingProfitLossRows()
          : activeAccountingReport === 'balance-sheet'
            ? await fetchAllAccountingBalanceSheetRows()
        : activeAccountingReport === 'cash-flow'
            ? await fetchAllAccountingCashFlowRows()
          : activeAccountingReport === 'accounts-payable-aging'
            ? await fetchAllAccountingAccountsPayableAgingRows()
          : activeAccountingReport === 'expense-breakdown'
            ? await fetchAllAccountingExpenseBreakdownRows()
          : activeAccountingReport === 'asset-register'
            ? await fetchAllAccountingAssetRegisterRows()
          : activeAccountingReport === 'depreciation-schedule'
            ? await fetchAllAccountingDepreciationScheduleRows()
          : null
        : activeTab === 'pos'
        ? activePosReport === 'purchase-report'
          ? await fetchAllPOSPurchaseRows()
          : activePosReport === 'cashier-sales-summary'
            ? await fetchAllPOSCashierSalesRows()
          : activePosReport === 'product-sales-report'
            ? await fetchAllPOSProductSalesRows()
          : activePosReport === 'refund-void-report'
            ? await fetchAllPOSRefundVoidRows()
          : activePosReport === 'vendor-aging'
            ? await fetchAllPOSVendorAgingRows()
            : activePosReport === 'stock-movement'
              ? await fetchAllPOSStockMovementRows()
              : activePosReport === 'bom-consumption-report'
                ? await fetchAllPOSBOMConsumptionRows()
              : activePosReport === 'stock-opname-variance'
                ? await fetchAllPOSStockOpnameVarianceRows()
              : activePosReport === 'vendor-price-comparison'
                ? await fetchAllPOSVendorPriceComparisonRows()
            : await fetchAllPOSSalesRows()
        : activeBusinessReport === 'staff-transaction-summary'
          ? await fetchAllStaffTransactionSummaryRows()
          : activeBusinessReport === 'member-activity'
          ? await fetchAllMemberActivityRows()
          : activeBusinessReport === 'check-in'
            ? await fetchAllCheckinRows()
            : activeBusinessReport === 'pt-sales'
              ? await fetchAllPTSalesRows()
              : activeBusinessReport === 'pt-session'
                ? await fetchAllPTSessionRows()
                : activeBusinessReport === 'pt-trainer'
                  ? await fetchAllPTTrainerRows()
            : await fetchAllMembershipSalesRows();
      if (!exported || exported.rows.length === 0) {
        Swal.fire('Info', 'Belum ada data untuk diexport.', 'info');
        return;
      }

      const summarySheetRows = activeTab === 'accounting'
        ? activeAccountingReport === 'journal-report'
          ? [{
              Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
              Outlet: selectedOutletLabel,
              SourceSystem: selectedAccountingSourceSystemLabel,
              BusinessUnit: selectedAccountingBusinessUnitLabel,
              PostingStatus: selectedAccountingPostingStatusLabel,
              TotalJournals: Number(exported.summary?.total_journals || 0),
              TotalLines: Number(exported.summary?.total_lines || 0),
              TotalDebit: Number(exported.summary?.total_debit || 0),
              TotalCredit: Number(exported.summary?.total_credit || 0),
              PostedJournals: Number(exported.summary?.posted_journals || 0),
              PendingJournals: Number(exported.summary?.pending_journals || 0),
              UniqueAccounts: Number(exported.summary?.unique_accounts || 0),
              UniqueOutlets: Number(exported.summary?.unique_outlets || 0),
              UniqueSources: Number(exported.summary?.unique_sources || 0),
            }]
          : activeAccountingReport === 'general-ledger'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                SourceSystem: selectedAccountingSourceSystemLabel,
                BusinessUnit: selectedAccountingBusinessUnitLabel,
                PostingStatus: selectedAccountingPostingStatusLabel,
                TotalAccounts: Number(exported.summary?.total_accounts || 0),
                AccountsWithOpening: Number(exported.summary?.accounts_with_opening || 0),
                AccountsWithMovement: Number(exported.summary?.accounts_with_movement || 0),
                TotalEntries: Number(exported.summary?.total_entries || 0),
                TotalDebit: Number(exported.summary?.total_debit || 0),
                TotalCredit: Number(exported.summary?.total_credit || 0),
                UniqueOutlets: Number(exported.summary?.unique_outlets || 0),
                UniqueSources: Number(exported.summary?.unique_sources || 0),
              }]
          : activeAccountingReport === 'trial-balance'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                SourceSystem: selectedAccountingSourceSystemLabel,
                BusinessUnit: selectedAccountingBusinessUnitLabel,
                PostingStatus: selectedAccountingPostingStatusLabel,
                TotalAccounts: Number(exported.summary?.total_accounts || 0),
                AccountsWithOpening: Number(exported.summary?.accounts_with_opening || 0),
                AccountsWithMovement: Number(exported.summary?.accounts_with_movement || 0),
                OpeningDebit: Number(exported.summary?.opening_debit || 0),
                OpeningCredit: Number(exported.summary?.opening_credit || 0),
                PeriodDebit: Number(exported.summary?.period_debit || 0),
                PeriodCredit: Number(exported.summary?.period_credit || 0),
                ClosingDebit: Number(exported.summary?.closing_debit || 0),
                ClosingCredit: Number(exported.summary?.closing_credit || 0),
                NetDifference: Number(exported.summary?.net_difference || 0),
              }]
          : activeAccountingReport === 'profit-loss'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                SourceSystem: selectedAccountingSourceSystemLabel,
                BusinessUnit: selectedAccountingBusinessUnitLabel,
                PostingStatus: selectedAccountingPostingStatusLabel,
                TotalRevenue: Number(exported.summary?.total_revenue || 0),
                TotalExpenses: Number(exported.summary?.total_expenses || 0),
                NetProfit: Number(exported.summary?.net_profit || 0),
                TotalAccounts: Number(exported.summary?.total_accounts || 0),
                RevenueAccounts: Number(exported.summary?.revenue_accounts || 0),
                ExpenseAccounts: Number(exported.summary?.expense_accounts || 0),
                AccountsWithMovement: Number(exported.summary?.accounts_with_movement || 0),
              }]
          : activeAccountingReport === 'balance-sheet'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                SourceSystem: selectedAccountingSourceSystemLabel,
                BusinessUnit: selectedAccountingBusinessUnitLabel,
                PostingStatus: selectedAccountingPostingStatusLabel,
                TotalAssets: Number(exported.summary?.total_assets || 0),
                TotalLiabilities: Number(exported.summary?.total_liabilities || 0),
                TotalEquity: Number(exported.summary?.total_equity || 0),
                TotalLiabilitiesAndEquity: Number(exported.summary?.total_liabilities_and_equity || 0),
                BalanceGap: Number(exported.summary?.balance_gap || 0),
                TotalAccounts: Number(exported.summary?.total_accounts || 0),
                AccountsWithOpening: Number(exported.summary?.accounts_with_opening || 0),
                AccountsWithMovement: Number(exported.summary?.accounts_with_movement || 0),
              }]
          : activeAccountingReport === 'cash-flow'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                SourceSystem: selectedAccountingSourceSystemLabel,
                BusinessUnit: selectedAccountingBusinessUnitLabel,
                PostingStatus: selectedAccountingPostingStatusLabel,
                OpeningCashBalance: Number(exported.summary?.opening_cash_balance || 0),
                CashInflow: Number(exported.summary?.cash_inflow || 0),
                CashOutflow: Number(exported.summary?.cash_outflow || 0),
                NetCashFlow: Number(exported.summary?.net_cash_flow || 0),
                ClosingCashBalance: Number(exported.summary?.closing_cash_balance || 0),
                OperatingCashFlow: Number(exported.summary?.operating_cash_flow || 0),
                InvestingCashFlow: Number(exported.summary?.investing_cash_flow || 0),
                FinancingCashFlow: Number(exported.summary?.financing_cash_flow || 0),
                InternalTransferFlow: Number(exported.summary?.internal_transfer_flow || 0),
                CashAccounts: Number(exported.summary?.cash_accounts || 0),
                JournalsWithCash: Number(exported.summary?.journals_with_cash || 0),
                EntriesWithCash: Number(exported.summary?.entries_with_cash || 0),
              }]
          : activeAccountingReport === 'accounts-payable-aging'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                DocumentScope: selectedAccountingFinanceDocumentScopeLabel,
                Status: selectedAccountingFinanceStatusLabel,
                AgingBucket: selectedAccountingFinanceAgingBucketLabel,
                TotalBills: Number(exported.summary?.total_bills || 0),
                OpenBills: Number(exported.summary?.open_bills || 0),
                OverdueBills: Number(exported.summary?.overdue_bills || 0),
                OutstandingTotal: Number(exported.summary?.outstanding_total || 0),
                OverdueTotal: Number(exported.summary?.overdue_total || 0),
                CurrentTotal: Number(exported.summary?.current_total || 0),
                Within30DaysTotal: Number(exported.summary?.within_30_days_total || 0),
                Within60DaysTotal: Number(exported.summary?.within_60_days_total || 0),
                Above60DaysTotal: Number(exported.summary?.above_60_days_total || 0),
                AverageOutstandingPerBill: Number(exported.summary?.average_outstanding_per_bill || 0),
                UniqueVendors: Number(exported.summary?.unique_vendors || 0),
              }]
          : activeAccountingReport === 'expense-breakdown'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                DocumentScope: selectedAccountingFinanceDocumentScopeLabel,
                Status: selectedAccountingFinanceStatusLabel,
                TotalDocuments: Number(exported.summary?.total_documents || 0),
                PayableDocuments: Number(exported.summary?.payable_documents || 0),
                CashDocuments: Number(exported.summary?.cash_documents || 0),
                TotalAmount: Number(exported.summary?.total_amount || 0),
                PaidAmount: Number(exported.summary?.paid_amount || 0),
                OutstandingAmount: Number(exported.summary?.outstanding_amount || 0),
                AverageDocumentAmount: Number(exported.summary?.average_document_amount || 0),
                UniqueCategories: Number(exported.summary?.unique_categories || 0),
                UniqueVendors: Number(exported.summary?.unique_vendors || 0),
              }]
          : activeAccountingReport === 'asset-register'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                DocumentScope: selectedAccountingFixedAssetScopeLabel,
                Status: selectedAccountingFinanceStatusLabel,
                TotalAssets: Number(exported.summary?.total_assets || 0),
                PayableAssets: Number(exported.summary?.payable_assets || 0),
                CashAssets: Number(exported.summary?.cash_assets || 0),
                PostedAssets: Number(exported.summary?.posted_assets || 0),
                TotalAcquisitionCost: Number(exported.summary?.total_acquisition_cost || 0),
                PaidAmount: Number(exported.summary?.paid_amount || 0),
                OutstandingAmount: Number(exported.summary?.outstanding_amount || 0),
                AverageAcquisitionCost: Number(exported.summary?.average_acquisition_cost || 0),
                EstimatedMonthlyDepreciation: Number(exported.summary?.estimated_monthly_depreciation || 0),
                EstimatedBookValue: Number(exported.summary?.estimated_book_value || 0),
                UniqueCategories: Number(exported.summary?.unique_categories || 0),
                UniqueVendors: Number(exported.summary?.unique_vendors || 0),
              }]
          : activeAccountingReport === 'depreciation-schedule'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: selectedOutletLabel,
                DocumentScope: selectedAccountingFixedAssetScopeLabel,
                Status: selectedAccountingFinanceStatusLabel,
                TotalAssets: Number(exported.summary?.total_assets || 0),
                DepreciableAssets: Number(exported.summary?.depreciable_assets || 0),
                TotalAcquisitionCost: Number(exported.summary?.total_acquisition_cost || 0),
                DepreciableBase: Number(exported.summary?.depreciable_base || 0),
                MonthlyDepreciation: Number(exported.summary?.monthly_depreciation || 0),
                PeriodDepreciation: Number(exported.summary?.period_depreciation || 0),
                AccumulatedDepreciation: Number(exported.summary?.accumulated_depreciation || 0),
                CurrentBookValue: Number(exported.summary?.current_book_value || 0),
                FullyDepreciatedAssets: Number(exported.summary?.fully_depreciated_assets || 0),
                AverageRemainingLifeMonths: Number(exported.summary?.average_remaining_life_months || 0),
              }]
          : []
        : activeTab === 'pos'
        ? activePosReport === 'purchase-report'
          ? [{
              Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
              Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
              TotalDocuments: Number(exported.summary?.total_documents || 0),
              TotalAmount: Number(exported.summary?.total_amount || 0),
              PaidAmount: Number(exported.summary?.paid_amount || 0),
              OutstandingAmount: Number(exported.summary?.outstanding_amount || 0),
              AverageDocument: Number(exported.summary?.average_document || 0),
              ItemQty: Number(exported.summary?.total_quantity || 0),
              UniqueVendors: Number(exported.summary?.unique_vendors || 0),
              UniqueProducts: Number(exported.summary?.unique_products || 0),
              CreditDocuments: Number(exported.summary?.credit_documents || 0),
              OpenDocuments: Number(exported.summary?.open_documents || 0),
            }]
          : activePosReport === 'cashier-sales-summary'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                PaymentMethod: appliedFilters.payment_method ? POS_SALES_PAYMENT_METHOD_OPTIONS.find(item => item.value === appliedFilters.payment_method)?.label || appliedFilters.payment_method : 'Semua metode',
                TotalCashiers: Number(exported.summary?.total_cashiers || 0),
                TotalTransactions: Number(exported.summary?.total_transactions || 0),
                GrossSales: Number(exported.summary?.gross_sales || 0),
                Service: Number(exported.summary?.total_service || 0),
                AverageSalesPerCashier: Number(exported.summary?.average_sales_per_cashier || 0),
                AverageTicket: Number(exported.summary?.average_ticket || 0),
                TotalItemQuantity: Number(exported.summary?.total_item_quantity || 0),
                ActiveOutlets: Number(exported.summary?.active_outlets || 0),
                TopCashier: exported.summary?.top_cashier_name || '-',
                TopCashierSales: Number(exported.summary?.top_cashier_sales || 0),
                TopCashierTransactions: Number(exported.summary?.top_cashier_transactions || 0),
              }]
          : activePosReport === 'product-sales-report'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                Product: posSalesProductOptions.find(item => item.value === appliedFilters.product_id)?.label || 'Semua produk',
                PaymentMethod: appliedFilters.payment_method ? POS_SALES_PAYMENT_METHOD_OPTIONS.find(item => item.value === appliedFilters.payment_method)?.label || appliedFilters.payment_method : 'Semua metode',
                TotalProducts: Number(exported.summary?.total_products || 0),
                TotalQuantity: Number(exported.summary?.total_quantity || 0),
                GrossSales: Number(exported.summary?.gross_sales || 0),
                AverageSellingPrice: Number(exported.summary?.average_selling_price || 0),
                AverageSalesPerProduct: Number(exported.summary?.average_sales_per_product || 0),
                UniqueOutlets: Number(exported.summary?.unique_outlets || 0),
                UniqueCashiers: Number(exported.summary?.unique_cashiers || 0),
                UniqueItemTypes: Number(exported.summary?.unique_item_types || 0),
                TopProduct: exported.summary?.top_product_name || '-',
                TopProductSales: Number(exported.summary?.top_product_sales || 0),
                TopProductQuantity: Number(exported.summary?.top_product_quantity || 0),
              }]
          : activePosReport === 'refund-void-report'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                RequestType: appliedFilters.approval_request_type ? formatPOSApprovalRequestTypeLabel(appliedFilters.approval_request_type) : 'Semua tipe',
                ApprovalStatus: appliedFilters.approval_status ? formatPOSApprovalStatusLabel(appliedFilters.approval_status) : 'Semua status',
                PaymentMethod: appliedFilters.payment_method ? POS_SALES_PAYMENT_METHOD_OPTIONS.find(item => item.value === appliedFilters.payment_method)?.label || appliedFilters.payment_method : 'Semua metode',
                TotalRequests: Number(exported.summary?.total_requests || 0),
                ApprovedRequests: Number(exported.summary?.approved_requests || 0),
                PendingRequests: Number(exported.summary?.pending_requests || 0),
                RejectedRequests: Number(exported.summary?.rejected_requests || 0),
                RefundRequests: Number(exported.summary?.refund_requests || 0),
                VoidRequests: Number(exported.summary?.void_requests || 0),
                RequestAmount: Number(exported.summary?.total_request_amount || 0),
                ApprovedAmount: Number(exported.summary?.approved_amount || 0),
                RefundRealized: Number(exported.summary?.executed_refund_amount || 0),
                UniqueRequesters: Number(exported.summary?.unique_requesters || 0),
                UniqueReviewers: Number(exported.summary?.unique_reviewers || 0),
              }]
          : activePosReport === 'vendor-aging'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                TotalBills: Number(exported.summary?.total_bills || 0),
                TotalOutstanding: Number(exported.summary?.total_outstanding_amount || 0),
                OverdueBills: Number(exported.summary?.overdue_bills || 0),
                OverdueAmount: Number(exported.summary?.overdue_amount || 0),
                DueTodayAmount: Number(exported.summary?.due_today_amount || 0),
                CurrentAmount: Number(exported.summary?.current_amount || 0),
                UniqueVendors: Number(exported.summary?.unique_vendors || 0),
                AverageAgeDays: Number(exported.summary?.average_age_days || 0),
                MaxAgeDays: Number(exported.summary?.max_age_days || 0),
              }]
          : activePosReport === 'stock-movement'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                TotalMovements: Number(exported.summary?.total_movements || 0),
                QuantityIn: Number(exported.summary?.total_quantity_in || 0),
                QuantityOut: Number(exported.summary?.total_quantity_out || 0),
                NetQuantity: Number(exported.summary?.net_quantity || 0),
                TotalCostIn: Number(exported.summary?.total_cost_in || 0),
                TotalCostOut: Number(exported.summary?.total_cost_out || 0),
                NetCost: Number(exported.summary?.net_cost || 0),
                UniqueProducts: Number(exported.summary?.unique_products || 0),
                UniqueReferences: Number(exported.summary?.unique_references || 0),
              }]
          : activePosReport === 'bom-consumption-report'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                ProdukJadi: posBOMSoldProductOptions.find(item => item.value === appliedFilters.product_id)?.label || 'Semua produk jadi',
                BahanBaku: posBOMIngredientOptions.find(item => item.value === appliedFilters.ingredient_product_id)?.label || 'Semua bahan baku',
                PaymentMethod: appliedFilters.payment_method ? POS_SALES_PAYMENT_METHOD_OPTIONS.find(item => item.value === appliedFilters.payment_method)?.label || appliedFilters.payment_method : 'Semua metode',
                TotalTransactions: Number(exported.summary?.total_transactions || 0),
                TotalRows: Number(exported.summary?.total_rows || 0),
                TotalSoldQuantity: Number(exported.summary?.total_sold_quantity || 0),
                TotalConsumedQuantity: Number(exported.summary?.total_consumed_quantity || 0),
                TotalConsumptionCost: Number(exported.summary?.total_consumption_cost || 0),
                ActiveFinishedGoods: Number(exported.summary?.active_finished_goods || 0),
                ActiveIngredients: Number(exported.summary?.active_ingredients || 0),
                ActiveOutlets: Number(exported.summary?.active_outlets || 0),
                AverageCostPerSale: Number(exported.summary?.average_cost_per_sale || 0),
              }]
          : activePosReport === 'stock-opname-variance'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                Product: posStockOpnameProductOptions.find(item => item.value === appliedFilters.product_id)?.label || 'Semua produk',
                VarianceType: formatStockOpnameVarianceLabel(appliedFilters.variance_type || 'variance_only'),
                TotalOpnames: Number(exported.summary?.total_opnames || 0),
                TotalVarianceItems: Number(exported.summary?.total_variance_items || 0),
                GainItems: Number(exported.summary?.gain_items || 0),
                LossItems: Number(exported.summary?.loss_items || 0),
                MatchedItems: Number(exported.summary?.matched_items || 0),
                TotalGainQuantity: Number(exported.summary?.total_gain_quantity || 0),
                TotalLossQuantity: Number(exported.summary?.total_loss_quantity || 0),
                NetVarianceQuantity: Number(exported.summary?.net_variance_quantity || 0),
                TotalVarianceCost: Number(exported.summary?.total_variance_cost || 0),
                AffectedProducts: Number(exported.summary?.affected_products || 0),
                AffectedOutlets: Number(exported.summary?.affected_outlets || 0),
              }]
          : activePosReport === 'vendor-price-comparison'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                Category: selectedVendorPriceCategoryLabel,
                Product: selectedVendorPriceProductLabel,
                Vendor: appliedFilters.vendor_name || 'Semua vendor',
                ItemType: appliedFilters.item_type ? formatProductItemTypeLabel(appliedFilters.item_type) : 'Semua tipe',
                PaymentMethod: appliedFilters.payment_method ? POS_PURCHASE_PAYMENT_METHOD_OPTIONS.find(item => item.value === appliedFilters.payment_method)?.label || appliedFilters.payment_method : 'Semua metode',
                PaymentStatus: appliedFilters.purchase_payment_status ? formatPurchasePaymentStatusLabel(appliedFilters.purchase_payment_status) : 'Semua status',
                ComparisonMode: formatVendorPriceComparisonModeLabel(exported.comparisonMode || appliedFilters.comparison_mode),
                MinPurchaseCount: Number(appliedFilters.min_purchase_count || 1),
                ProductsCompared: Number(exported.summary?.products_compared || 0),
                ActiveVendors: Number(exported.summary?.active_vendors || 0),
                CheapestVendorWins: Number(exported.summary?.cheapest_vendor_wins || 0),
                PotentialSavings: Number(exported.summary?.potential_savings || 0),
                SingleSourceProducts: Number(exported.summary?.single_source_products || 0),
                HighSpreadProducts: Number(exported.summary?.high_spread_products || 0),
              }]
          : [{
              Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
              Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
              TotalTransaksi: Number(exported.summary?.total_transactions || 0),
              GrossSales: Number(exported.summary?.gross_sales || 0),
              Service: Number(exported.summary?.total_service || 0),
              Tax: Number(exported.summary?.total_tax || 0),
              Discount: Number(exported.summary?.total_discount || 0),
              AverageTicket: Number(exported.summary?.average_ticket || 0),
              ItemQty: Number(exported.summary?.total_item_quantity || 0),
              UniqueCashiers: Number(exported.summary?.unique_cashiers || 0),
              UniqueProducts: Number(exported.summary?.unique_products || 0),
            }]
        : activeBusinessReport === 'staff-transaction-summary'
        ? [{
            Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
            Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
            TotalActiveStaff: Number(exported.summary?.total_active_staff || 0),
            StaffWithTransactions: Number(exported.summary?.staff_with_transactions || 0),
            StaffWithoutTransactions: Number(exported.summary?.staff_without_transactions || 0),
            MembershipTransactions: Number(exported.summary?.total_membership_transactions || 0),
            MembershipSales: Number(exported.summary?.total_membership_sales || 0),
            ClassTransactions: Number(exported.summary?.total_class_transactions || 0),
            ClassSales: Number(exported.summary?.total_class_sales || 0),
            PTTransactions: Number(exported.summary?.total_pt_transactions || 0),
            PTSales: Number(exported.summary?.total_pt_sales || 0),
            POSTransactions: Number(exported.summary?.total_pos_transactions || 0),
            POSGrossSales: Number(exported.summary?.total_pos_sales || 0),
            POSClosingShiftSales: Number(exported.summary?.total_pos_closing_shift_sales || 0),
            TotalTransactions: Number(exported.summary?.total_transactions || 0),
            TotalSales: Number(exported.summary?.total_sales_amount || 0),
            AverageSalesPerStaff: Number(exported.summary?.average_sales_per_staff || 0),
            AverageTicket: Number(exported.summary?.average_ticket || 0),
            TopStaff: exported.summary?.top_staff_name || '-',
          }]
        : activeBusinessReport === 'member-activity'
        ? [{
            Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
            Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
            TotalMembers: Number(exported.summary?.total_members || 0),
            MembershipAktif: Number(exported.summary?.active_memberships || 0),
            ExpiringSoon: Number(exported.summary?.expiring_soon || 0),
            Expired: Number(exported.summary?.expired_memberships || 0),
            CheckinPeriode: Number(exported.summary?.checked_in_period || 0),
            NeverCheckin: Number(exported.summary?.never_checkin || 0),
          }]
        : activeBusinessReport === 'check-in'
          ? [{
              Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
              Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
              TotalCheckin: Number(exported.summary?.total_checkins || 0),
              MemberUnik: Number(exported.summary?.unique_members || 0),
              RepeatMember: Number(exported.summary?.repeat_members || 0),
              CheckinHariIni: Number(exported.summary?.today_checkins || 0),
              AverageHarian: Number(exported.summary?.average_daily_checkin || 0),
            }]
        : activeBusinessReport === 'pt-sales'
          ? [{
              Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
              Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
              Trainer: ptUsers.find(item => String(item.id) === appliedFilters.pt_user_id)?.pt_name || 'Semua trainer',
              TotalPTSales: Number(exported.summary?.total_sales_amount || 0),
              TotalInvoices: Number(exported.summary?.total_invoices || 0),
              PaidAmount: Number(exported.summary?.paid_amount || 0),
              OutstandingAmount: Number(exported.summary?.outstanding_amount || 0),
              AverageTicket: Number(exported.summary?.average_ticket || 0),
              NewClients: Number(exported.summary?.new_clients || 0),
              RepeatClients: Number(exported.summary?.repeat_clients || 0),
            }]
          : activeBusinessReport === 'pt-session'
            ? [{
                Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                Trainer: ptUsers.find(item => String(item.id) === appliedFilters.pt_user_id)?.pt_name || 'Semua trainer',
                Client: ptClients.find(item => String(item.id) === appliedFilters.client_id)?.full_name || 'Semua client',
                Scheduled: Number(exported.summary?.total_sessions_scheduled || 0),
                Completed: Number(exported.summary?.total_sessions_completed || 0),
                Cancelled: Number(exported.summary?.total_sessions_cancelled || 0),
                Rescheduled: Number(exported.summary?.total_sessions_rescheduled || 0),
                NoShow: Number(exported.summary?.no_show_count || 0),
                RemainingSessions: Number(exported.summary?.sessions_remaining || 0),
                CompletionRate: Number(exported.summary?.completion_rate || 0),
              }]
            : activeBusinessReport === 'pt-trainer'
              ? [{
                  Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
                  Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
                  Trainer: ptUsers.find(item => String(item.id) === appliedFilters.pt_user_id)?.pt_name || 'Semua trainer',
                  ActiveTrainers: Number(exported.summary?.active_trainers || 0),
                  ClientsHandled: Number(exported.summary?.clients_handled || 0),
                  SessionsCompleted: Number(exported.summary?.sessions_completed || 0),
                  SalesAttributed: Number(exported.summary?.sales_attributed || 0),
                  PaidSalesAmount: Number(exported.summary?.paid_sales_amount || 0),
                  CompletionRate: Number(exported.summary?.completion_rate || 0),
                  AvgSessionsPerTrainer: Number(exported.summary?.average_sessions_per_trainer || 0),
                  SettlementEligible: Number(exported.summary?.settlement_eligible_amount || 0),
                }]
        : [{
            Period: `${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'}`,
            Outlet: outlets.find(item => item.id === appliedFilters.outlet)?.name || 'Semua outlet',
            TotalRevenue: Number(exported.summary?.total_amount || 0),
            TotalTransactions: Number(exported.summary?.total_transactions || 0),
            TotalMembers: Number(exported.summary?.total_members || 0),
            AverageTicket: Number(exported.summary?.average_ticket || 0),
          }];
      const summarySheet = XLSX.utils.json_to_sheet(formatExportRowsForDisplay(summarySheetRows));
      const detailSheetRows =
        activeTab === 'accounting'
          ? activeAccountingReport === 'journal-report'
            ? buildAccountingJournalExportRows(exported.rows)
            : activeAccountingReport === 'general-ledger'
              ? buildAccountingGeneralLedgerExportRows(exported.rows)
            : activeAccountingReport === 'trial-balance'
              ? buildAccountingTrialBalanceExportRows(exported.rows)
            : activeAccountingReport === 'profit-loss'
              ? buildAccountingProfitLossExportRows(exported.rows)
            : activeAccountingReport === 'balance-sheet'
              ? buildAccountingBalanceSheetExportRows(exported.rows)
            : activeAccountingReport === 'cash-flow'
              ? buildAccountingCashFlowExportRows(exported.rows)
            : activeAccountingReport === 'accounts-payable-aging'
              ? buildAccountingAccountsPayableAgingExportRows(exported.rows)
            : activeAccountingReport === 'expense-breakdown'
              ? buildAccountingExpenseBreakdownExportRows(exported.rows)
            : activeAccountingReport === 'asset-register'
              ? buildAccountingAssetRegisterExportRows(exported.rows)
            : activeAccountingReport === 'depreciation-schedule'
              ? buildAccountingDepreciationScheduleExportRows(exported.rows)
            : []
          : activeTab === 'pos'
          ? activePosReport === 'purchase-report'
            ? buildPOSPurchaseExportRows(exported.rows)
            : activePosReport === 'cashier-sales-summary'
              ? buildPOSCashierSalesExportRows(exported.rows)
            : activePosReport === 'product-sales-report'
              ? buildPOSProductSalesExportRows(exported.rows)
            : activePosReport === 'refund-void-report'
              ? buildPOSRefundVoidExportRows(exported.rows)
            : activePosReport === 'vendor-aging'
              ? buildPOSVendorAgingExportRows(exported.rows)
              : activePosReport === 'stock-movement'
                ? buildPOSStockMovementExportRows(exported.rows)
                : activePosReport === 'bom-consumption-report'
                  ? buildPOSBOMConsumptionExportRows(exported.rows)
                : activePosReport === 'stock-opname-variance'
                  ? buildPOSStockOpnameVarianceExportRows(exported.rows)
                : activePosReport === 'vendor-price-comparison'
                  ? buildPOSVendorPriceComparisonExportRows(exported.rows)
              : buildPOSSalesExportRows(exported.rows)
          : activeBusinessReport === 'staff-transaction-summary'
          ? buildStaffTransactionSummaryExportRows(exported.rows)
          : activeBusinessReport === 'member-activity'
          ? buildMemberActivityExportRows(exported.rows)
          : activeBusinessReport === 'check-in'
            ? buildCheckinExportRows(exported.rows)
            : activeBusinessReport === 'pt-sales'
              ? buildPTSalesExportRows(exported.rows)
              : activeBusinessReport === 'pt-session'
                ? buildPTSessionExportRows(exported.rows)
                : activeBusinessReport === 'pt-trainer'
                  ? buildPTTrainerExportRows(exported.rows)
            : buildMembershipSalesExportRows(exported.rows);
      const detailSheet = XLSX.utils.json_to_sheet(formatExportRowsForDisplay(detailSheetRows));
      const sectionSpecificTables = buildSectionSpecificExportTables(exported);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
      if (Array.isArray(sectionSpecificTables) && sectionSpecificTables.length) {
        sectionSpecificTables.forEach((table, index) => {
          const rows = formatExportRowsForDisplay(normalizeExportRows(table?.rows, table?.emptyMessage));
          XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(rows),
            sanitizeSheetName(table?.name || `${reportExportMeta.sheetName} ${index + 1}`)
          );
        });
      } else {
        XLSX.utils.book_append_sheet(workbook, detailSheet, sanitizeSheetName(reportExportMeta.sheetName));
      }
      if (!sectionSpecificTables && activeTab === 'pos' && activePosReport === 'vendor-price-comparison') {
        const vendorDetailSheet = XLSX.utils.json_to_sheet(
          formatExportRowsForDisplay(
            buildPOSVendorPriceComparisonVendorExportRows(Array.isArray(exported.vendorDetails) ? exported.vendorDetails : [])
          )
        );
        XLSX.utils.book_append_sheet(workbook, vendorDetailSheet, 'Vendor Detail');
      }
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([excelBuffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `${reportExportMeta.slug}${activeExportPageMeta.isScoped ? `-${activeExportPageMeta.slug}` : ''}-report-${appliedFilters.dateFrom || 'all'}-${appliedFilters.dateTo || 'all'}.xlsx`
      );
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal export Excel', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (loading || exporting) {
      return;
    }
    setExporting(true);
    try {
      const reportExportMeta = buildCurrentReportExportMeta();
      const activeExportPageMeta = getActiveExportPageMeta();
      const exported = activeTab === 'accounting'
        ? activeAccountingReport === 'journal-report'
          ? await fetchAllAccountingJournalRows()
        : activeAccountingReport === 'general-ledger'
            ? await fetchAllAccountingGeneralLedgerRows()
          : activeAccountingReport === 'trial-balance'
            ? await fetchAllAccountingTrialBalanceRows()
          : activeAccountingReport === 'profit-loss'
            ? await fetchAllAccountingProfitLossRows()
          : activeAccountingReport === 'balance-sheet'
            ? await fetchAllAccountingBalanceSheetRows()
        : activeAccountingReport === 'cash-flow'
            ? await fetchAllAccountingCashFlowRows()
          : activeAccountingReport === 'accounts-payable-aging'
            ? await fetchAllAccountingAccountsPayableAgingRows()
          : activeAccountingReport === 'expense-breakdown'
            ? await fetchAllAccountingExpenseBreakdownRows()
          : activeAccountingReport === 'asset-register'
            ? await fetchAllAccountingAssetRegisterRows()
          : activeAccountingReport === 'depreciation-schedule'
            ? await fetchAllAccountingDepreciationScheduleRows()
          : null
        : activeTab === 'pos'
        ? activePosReport === 'purchase-report'
          ? await fetchAllPOSPurchaseRows()
          : activePosReport === 'cashier-sales-summary'
            ? await fetchAllPOSCashierSalesRows()
          : activePosReport === 'product-sales-report'
            ? await fetchAllPOSProductSalesRows()
          : activePosReport === 'refund-void-report'
            ? await fetchAllPOSRefundVoidRows()
          : activePosReport === 'vendor-aging'
            ? await fetchAllPOSVendorAgingRows()
            : activePosReport === 'stock-movement'
              ? await fetchAllPOSStockMovementRows()
              : activePosReport === 'bom-consumption-report'
                ? await fetchAllPOSBOMConsumptionRows()
              : activePosReport === 'stock-opname-variance'
                ? await fetchAllPOSStockOpnameVarianceRows()
              : activePosReport === 'vendor-price-comparison'
                ? await fetchAllPOSVendorPriceComparisonRows()
            : await fetchAllPOSSalesRows()
        : activeBusinessReport === 'staff-transaction-summary'
          ? await fetchAllStaffTransactionSummaryRows()
          : activeBusinessReport === 'member-activity'
          ? await fetchAllMemberActivityRows()
          : activeBusinessReport === 'check-in'
            ? await fetchAllCheckinRows()
            : activeBusinessReport === 'pt-sales'
              ? await fetchAllPTSalesRows()
              : activeBusinessReport === 'pt-session'
                ? await fetchAllPTSessionRows()
                : activeBusinessReport === 'pt-trainer'
                  ? await fetchAllPTTrainerRows()
            : await fetchAllMembershipSalesRows();
      if (!exported || exported.rows.length === 0) {
        Swal.fire('Info', 'Belum ada data untuk diexport.', 'info');
        return;
      }

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });
      doc.setFontSize(14);
      doc.text(
        `${reportExportMeta.title}${activeExportPageMeta.isScoped ? ` - ${activeExportPageMeta.label}` : ''}`,
        40,
        34
      );
      doc.setFontSize(10);
      doc.text(
        `Periode: ${appliedFilters.dateFrom || '-'} s/d ${appliedFilters.dateTo || '-'} | Outlet: ${selectedOutletLabel}${activeExportPageMeta.isScoped ? ` | Halaman: ${activeExportPageMeta.label}` : ''}`,
        40,
        52
      );
      if (activeTab === 'accounting' && activeAccountingReport === 'journal-report') {
        doc.text(
          `Jurnal: ${formatCount(exported.summary?.total_journals)} | Lines: ${formatCount(exported.summary?.total_lines)} | Debit: ${formatCurrency(exported.summary?.total_debit)} | Credit: ${formatCurrency(exported.summary?.total_credit)} | Posted: ${formatCount(exported.summary?.posted_journals)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'general-ledger') {
        doc.text(
          `Akun: ${formatCount(exported.summary?.total_accounts)} | Saldo awal: ${formatCount(exported.summary?.accounts_with_opening)} | Bergerak: ${formatCount(exported.summary?.accounts_with_movement)} | Entries: ${formatCount(exported.summary?.total_entries)} | Debit: ${formatCurrency(exported.summary?.total_debit)} | Credit: ${formatCurrency(exported.summary?.total_credit)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'trial-balance') {
        doc.text(
          `Akun: ${formatCount(exported.summary?.total_accounts)} | Opening D/C: ${formatCurrency(exported.summary?.opening_debit)} / ${formatCurrency(exported.summary?.opening_credit)} | Period D/C: ${formatCurrency(exported.summary?.period_debit)} / ${formatCurrency(exported.summary?.period_credit)} | Selisih: ${formatCurrency(exported.summary?.net_difference)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'profit-loss') {
        doc.text(
          `Revenue: ${formatCurrency(exported.summary?.total_revenue)} | Expenses: ${formatCurrency(exported.summary?.total_expenses)} | Net profit: ${formatCurrency(exported.summary?.net_profit)} | Akun: ${formatCount(exported.summary?.total_accounts)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'balance-sheet') {
        doc.text(
          `Assets: ${formatCurrency(exported.summary?.total_assets)} | Liabilities: ${formatCurrency(exported.summary?.total_liabilities)} | Equity: ${formatCurrency(exported.summary?.total_equity)} | Gap: ${formatCurrency(exported.summary?.balance_gap)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'cash-flow') {
        doc.text(
          `Opening: ${formatCurrency(exported.summary?.opening_cash_balance)} | Inflow: ${formatCurrency(exported.summary?.cash_inflow)} | Outflow: ${formatCurrency(exported.summary?.cash_outflow)} | Net: ${formatCurrency(exported.summary?.net_cash_flow)} | Closing: ${formatCurrency(exported.summary?.closing_cash_balance)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'accounts-payable-aging') {
        doc.text(
          `Outstanding: ${formatCurrency(exported.summary?.outstanding_total)} | Overdue: ${formatCurrency(exported.summary?.overdue_total)} | Open bills: ${formatCount(exported.summary?.open_bills)} | Vendors: ${formatCount(exported.summary?.unique_vendors)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'expense-breakdown') {
        doc.text(
          `Total: ${formatCurrency(exported.summary?.total_amount)} | Paid: ${formatCurrency(exported.summary?.paid_amount)} | Outstanding: ${formatCurrency(exported.summary?.outstanding_amount)} | Dokumen: ${formatCount(exported.summary?.total_documents)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'asset-register') {
        doc.text(
          `Aset: ${formatCount(exported.summary?.total_assets)} | Nilai perolehan: ${formatCurrency(exported.summary?.total_acquisition_cost)} | Outstanding: ${formatCurrency(exported.summary?.outstanding_amount)} | Est. depr/bln: ${formatCurrency(exported.summary?.estimated_monthly_depreciation)}`,
          40,
          68
        );
      } else if (activeTab === 'accounting' && activeAccountingReport === 'depreciation-schedule') {
        doc.text(
          `Aset: ${formatCount(exported.summary?.total_assets)} | Depr periode: ${formatCurrency(exported.summary?.period_depreciation)} | Akumulasi: ${formatCurrency(exported.summary?.accumulated_depreciation)} | Book value: ${formatCurrency(exported.summary?.current_book_value)}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'purchase-report') {
        doc.text(
          `Dokumen: ${formatCount(exported.summary?.total_documents)} | Purchase: ${formatCurrency(exported.summary?.total_amount)} | Paid: ${formatCurrency(exported.summary?.paid_amount)} | Outstanding: ${formatCurrency(exported.summary?.outstanding_amount)} | Qty: ${formatCount(exported.summary?.total_quantity)}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'cashier-sales-summary') {
        doc.text(
          `Kasir: ${formatCount(exported.summary?.total_cashiers)} | Transaksi: ${formatCount(exported.summary?.total_transactions)} | Sales: ${formatCurrency(exported.summary?.gross_sales)} | Service: ${formatCurrency(exported.summary?.total_service)} | Avg / kasir: ${formatCurrency(exported.summary?.average_sales_per_cashier)} | Top cashier: ${exported.summary?.top_cashier_name || '-'}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'product-sales-report') {
        doc.text(
          `Produk: ${formatCount(exported.summary?.total_products)} | Qty: ${formatCount(exported.summary?.total_quantity)} | Sales: ${formatCurrency(exported.summary?.gross_sales)} | Avg price: ${formatCurrency(exported.summary?.average_selling_price)} | Top product: ${exported.summary?.top_product_name || '-'}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'refund-void-report') {
        doc.text(
          `Requests: ${formatCount(exported.summary?.total_requests)} | Approved: ${formatCount(exported.summary?.approved_requests)} | Pending: ${formatCount(exported.summary?.pending_requests)} | Exposure: ${formatCurrency(exported.summary?.total_request_amount)} | Refund realized: ${formatCurrency(exported.summary?.executed_refund_amount)}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'vendor-aging') {
        doc.text(
          `Bills: ${formatCount(exported.summary?.total_bills)} | Outstanding: ${formatCurrency(exported.summary?.total_outstanding_amount)} | Overdue: ${formatCurrency(exported.summary?.overdue_amount)} | Vendor aktif: ${formatCount(exported.summary?.unique_vendors)}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'stock-movement') {
        doc.text(
          `Movements: ${formatCount(exported.summary?.total_movements)} | In: ${formatCount(exported.summary?.total_quantity_in)} | Out: ${formatCount(exported.summary?.total_quantity_out)} | Net: ${formatCount(exported.summary?.net_quantity)} | Produk: ${formatCount(exported.summary?.unique_products)}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'bom-consumption-report') {
        doc.text(
          `Transaksi: ${formatCount(exported.summary?.total_transactions)} | Rows: ${formatCount(exported.summary?.total_rows)} | Sold qty: ${formatCount(exported.summary?.total_sold_quantity)} | Consumed qty: ${formatCount(exported.summary?.total_consumed_quantity)} | Cost: ${formatCurrency(exported.summary?.total_consumption_cost)}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'stock-opname-variance') {
        doc.text(
          `Opnames: ${formatCount(exported.summary?.total_opnames)} | Variance items: ${formatCount(exported.summary?.total_variance_items)} | Gain qty: ${formatCount(exported.summary?.total_gain_quantity)} | Loss qty: ${formatCount(exported.summary?.total_loss_quantity)} | Variance cost: ${formatCurrency(exported.summary?.total_variance_cost)}`,
          40,
          68
        );
      } else if (activeTab === 'pos' && activePosReport === 'vendor-price-comparison') {
        doc.text(
          `Products: ${formatCount(exported.summary?.products_compared)} | Vendors: ${formatCount(exported.summary?.active_vendors)} | Savings: ${formatCurrency(exported.summary?.potential_savings)} | Single source: ${formatCount(exported.summary?.single_source_products)} | Mode: ${formatVendorPriceComparisonModeLabel(exported.comparisonMode)}`,
          40,
          68
        );
      } else if (activeTab === 'pos') {
        doc.text(
          `Transaksi: ${formatCount(exported.summary?.total_transactions)} | Sales: ${formatCurrency(exported.summary?.gross_sales)} | Service: ${formatCurrency(exported.summary?.total_service)} | Tax: ${formatCurrency(exported.summary?.total_tax)} | Discount: ${formatCurrency(exported.summary?.total_discount)} | Item Qty: ${formatCount(exported.summary?.total_item_quantity)}`,
          40,
          68
        );
      } else if (activeBusinessReport === 'staff-transaction-summary') {
        doc.text(
          `Staff aktif: ${formatCount(exported.summary?.total_active_staff)} | Staff bertransaksi: ${formatCount(exported.summary?.staff_with_transactions)} | Membership: ${formatCurrency(exported.summary?.total_membership_sales)} | Class sale: ${formatCurrency(exported.summary?.total_class_sales)} | PT sales: ${formatCurrency(exported.summary?.total_pt_sales)} | POS sales: ${formatCurrency(exported.summary?.total_pos_sales)} | POS closing shift: ${formatCurrency(exported.summary?.total_pos_closing_shift_sales)} | Total sales: ${formatCurrency(exported.summary?.total_sales_amount)}`,
          40,
          68
        );
      } else if (activeBusinessReport === 'member-activity') {
        doc.text(
          `Member: ${formatCount(exported.summary?.total_members)} | Aktif: ${formatCount(exported.summary?.active_memberships)} | Expiring Soon: ${formatCount(exported.summary?.expiring_soon)} | Check-in Periode: ${formatCount(exported.summary?.checked_in_period)}`,
          40,
          68
        );
      } else if (activeBusinessReport === 'check-in') {
        doc.text(
          `Total check-in: ${formatCount(exported.summary?.total_checkins)} | Member unik: ${formatCount(exported.summary?.unique_members)} | Repeat member: ${formatCount(exported.summary?.repeat_members)} | Avg harian: ${formatCount(exported.summary?.average_daily_checkin)}`,
          40,
          68
        );
      } else if (activeBusinessReport === 'pt-sales') {
        doc.text(
          `Private session sales: ${formatCurrency(exported.summary?.total_sales_amount)} | Invoice: ${formatCount(exported.summary?.total_invoices)} | Paid: ${formatCurrency(exported.summary?.paid_amount)} | Outstanding: ${formatCurrency(exported.summary?.outstanding_amount)}`,
          40,
          68
        );
      } else if (activeBusinessReport === 'pt-session') {
        doc.text(
          `Scheduled: ${formatCount(exported.summary?.total_sessions_scheduled)} | Completed: ${formatCount(exported.summary?.total_sessions_completed)} | Cancelled: ${formatCount(exported.summary?.total_sessions_cancelled)} | Remaining: ${formatCount(exported.summary?.sessions_remaining)}`,
          40,
          68
        );
      } else if (activeBusinessReport === 'pt-trainer') {
        doc.text(
          `Trainer aktif: ${formatCount(exported.summary?.active_trainers)} | Completed: ${formatCount(exported.summary?.sessions_completed)} | Sales: ${formatCurrency(exported.summary?.sales_attributed)} | Settlement: ${formatCurrency(exported.summary?.settlement_eligible_amount)}`,
          40,
          68
        );
      } else {
        doc.text(
          `Revenue: ${formatCurrency(exported.summary?.total_amount)} | Transaksi: ${formatCount(exported.summary?.total_transactions)} | Member unik: ${formatCount(exported.summary?.total_members)}`,
          40,
          68
        );
      }

      const sectionSpecificTables = buildSectionSpecificExportTables(exported);
      if (Array.isArray(sectionSpecificTables) && sectionSpecificTables.length) {
        renderPdfTableSections(doc, sectionSpecificTables, 84);
      } else {
        autoTable(doc, {
        startY: 84,
        head: [activeTab === 'accounting'
          ? activeAccountingReport === 'journal-report'
            ? [
                'Journal Number',
                'Tanggal',
                'Outlet',
                'Reference / Invoice',
                'Jenis',
                'Deskripsi',
                'COA Debit',
                'COA Credit',
                'Debit',
                'Credit',
                'Status',
              ]
            : activeAccountingReport === 'general-ledger'
              ? [
                  'Akun',
                  'Kategori',
                  'Saldo Awal',
                  'Tanggal',
                  'Journal Number',
                  'Outlet',
                  'Deskripsi',
                  'Debit',
                  'Credit',
                  'Saldo Berjalan',
                ]
            : []
          : activeTab === 'pos'
          ? activePosReport === 'purchase-report'
            ? [
                'Tanggal',
                'Invoice',
                'Outlet',
                'Vendor',
                'Payment',
                'Status',
                'Item Qty',
                'Total',
                'Outstanding',
              ]
            : activePosReport === 'cashier-sales-summary'
              ? [
                  'Kasir',
                  'Transaksi',
                  'Gross Sales',
                  'Service',
                  'Avg Ticket',
                  'Item Qty',
                  'Outlet',
                  'Payment Dominan',
                  'Slot Dominan',
                  'Last Sale',
                ]
            : activePosReport === 'product-sales-report'
              ? [
                  'Produk',
                  'Tipe',
                  'Qty',
                  'Sales',
                  'Avg Price',
                  'Transaksi',
                  'Outlet',
                  'Top Payment',
                  'Last Sold',
                ]
            : activePosReport === 'refund-void-report'
              ? [
                  'Tanggal',
                  'Request',
                  'Outlet / TXN',
                  'Pembayaran',
                  'Status',
                  'Nilai',
                  'Pengaju',
                  'Refund',
                ]
            : activePosReport === 'vendor-aging'
              ? [
                  'Bill Date',
                  'Bill No',
                  'Vendor',
                  'Type',
                  'Due Date',
                  'Aging',
                  'Status',
                'Outstanding',
                'Sync',
              ]
            : activePosReport === 'stock-movement'
              ? [
                  'Tanggal',
                  'Product',
                  'Movement',
                  'Source',
                  'Qty In',
                  'Qty Out',
                  'Net',
                  'Stock After',
                  'Cost',
                ]
              : activePosReport === 'bom-consumption-report'
                ? [
                    'Tanggal',
                    'TXN / Outlet',
                    'Produk Jadi',
                    'Bahan Baku',
                    'Sold Qty',
                    'Qty / Unit',
                    'Qty Consume',
                    'Cost',
                    'Payment',
                  ]
              : activePosReport === 'stock-opname-variance'
                ? [
                    'Tanggal',
                    'Product',
                    'Outlet / Tipe',
                    'System / Actual',
                    'Variance',
                    'Qty',
                    'Cost',
                    'Sync',
                    'Opname',
                  ]
              : activePosReport === 'vendor-price-comparison'
                ? [
                    'Product',
                    'Category',
                    'Preferred Vendor',
                    'Preferred Price',
                    'Second Vendor',
                    'Spread',
                    'Spread %',
                    'Last Vendor',
                    'Savings',
                  ]
            : [
                'Tanggal',
                'Transaksi',
                'Outlet',
                'Cashier',
                'Payment',
                'Item Qty',
                'Service / Tax / Discount',
                'Total',
                'Sync',
              ]
          : activeBusinessReport === 'staff-transaction-summary'
          ? [
              'Staff',
              'Outlet / Jabatan',
              'Membership',
              'Class Sale',
              'PT Sales',
              'POS Closing Shift',
              'Sales Mix',
              'Total',
              'Last Activity',
            ]
          : activeBusinessReport === 'member-activity'
          ? [
              'Member',
              'Outlet',
              'Paket',
              'Lifecycle',
              'Aktivitas',
              'Last Check-in',
              'Hari Sejak Check-in',
              'Expired',
              'Risiko',
            ]
          : activeBusinessReport === 'check-in'
            ? [
                'Tanggal Check-in',
                'Member',
                'Outlet',
                'Paket Saat Check-in',
                'Visit Periode',
                'Sequence',
                'Expired',
              ]
          : activeBusinessReport === 'pt-sales'
            ? [
                'Tanggal Sale',
                'Invoice',
                'Client',
                'Trainer',
                'Produk',
                'Status Bayar',
                'Contract',
                'Nilai',
                'Outstanding',
              ]
            : activeBusinessReport === 'pt-session'
              ? [
                  'Tanggal Sesi',
                  'Booking',
                  'Client',
                  'Trainer',
                  'Produk',
                  'Booking Status',
                  'Attendance',
                  'Consumed',
                  'Remaining',
                ]
              : activeBusinessReport === 'pt-trainer'
                ? [
                    'Trainer',
                    'Outlet',
                    'Client',
                    'Package Sold',
                    'Sales',
                    'Completed',
                    'Cancelled',
                    'No Show',
                    'Settlement',
                  ]
          : [
              'Tanggal',
              'Jenis',
              'Member',
              'Paket',
              'Outlet',
              'Payment',
              'Status',
              'Petugas',
              'Nilai',
            ]],
        body: activeTab === 'accounting'
          ? activeAccountingReport === 'journal-report'
            ? exported.rows.map(row => ([
                formatAccountingJournalNumber(row.id, row.occurred_at),
                formatDateTime(row.occurred_at),
                row.outlet_name || '-',
                row.external_reference || row.source_id || '-',
                `${formatAccountingSourceSystemLabel(row.source_system)} / ${formatAccountingSourceTypeLabel(row.source_type)}`,
                row.description || row.external_reference || '-',
                buildAccountingSidePreview(row.lines, 'debit'),
                buildAccountingSidePreview(row.lines, 'credit'),
                formatCurrency(row.total_debit),
                formatCurrency(row.total_credit),
                `${formatAccountingPostingStatusLabel(row.posting_status)} / ${formatDateTime(row.posted_at)}`,
              ]))
            : activeAccountingReport === 'general-ledger'
              ? buildAccountingGeneralLedgerExportRows(exported.rows).map(row => ([
                  `${row.AccountName || '-'} (${row.AccountID || '-'})`,
                  row.AccountType || 'Uncategorized',
                  formatCurrency(row.OpeningBalance),
                  row.TanggalJurnal || '-',
                  row.JournalNumber || '-',
                  row.Outlet || '-',
                  row.Description || '-',
                  formatCurrency(row.Debit),
                  formatCurrency(row.Credit),
                  formatCurrency(row.RunningBalance),
                ]))
            : []
          : activeTab === 'pos'
          ? activePosReport === 'purchase-report'
            ? exported.rows.map(row => ([
                formatDateTime(row.purchase_date),
                row.invoice_number || `#${row.id || '-'}`,
                row.outlet_name || '-',
                row.supplier_name || '-',
                row.payment_method || '-',
                formatPurchasePaymentStatusLabel(row.payment_status),
                formatCount(row.item_quantity),
                formatCurrency(row.total),
                formatCurrency(row.outstanding_amount),
              ]))
            : activePosReport === 'cashier-sales-summary'
              ? exported.rows.map(row => ([
                  row.cashier_name || '-',
                  formatCount(row.transaction_count),
                  formatCurrency(row.gross_sales),
                  formatCurrency(row.total_service),
                  formatCurrency(row.average_ticket),
                  `${formatCount(row.item_quantity)} / ${formatCount(row.average_items_per_transaction)}`,
                  `${row.dominant_outlet || '-'} / ${formatCount(row.outlet_count)}`,
                  `${row.top_payment_method || '-'} / ${Number(row.top_payment_share || 0).toFixed(1)}%`,
                  row.dominant_time_slot || '-',
                  formatDateTime(row.last_sale_at),
                ]))
            : activePosReport === 'product-sales-report'
              ? exported.rows.map(row => ([
                  row.product_name || '-',
                  `${formatProductItemTypeLabel(row.item_type)} / ${row.unit || '-'}`,
                  formatCount(row.quantity_sold),
                  formatCurrency(row.gross_sales),
                  formatCurrency(row.average_selling_price),
                  formatCount(row.transaction_count),
                  formatCount(row.outlet_count),
                  row.top_payment_method || '-',
                  formatDateTime(row.last_sold_at),
                ]))
            : activePosReport === 'refund-void-report'
              ? exported.rows.map(row => ([
                  formatDateTime(row.requested_at),
                  `${formatPOSApprovalRequestTypeLabel(row.request_type)} #${row.id || '-'}`,
                  `${row.outlet_name || '-'} / TXN #${row.transaction_id || '-'}`,
                  `${row.payment_method || '-'} / ${row.cashier_name || '-'}`,
                  formatPOSApprovalStatusLabel(row.status),
                  `${formatCurrency(row.request_total)} / ${formatCount(row.item_count)} item`,
                  row.requested_by_name || '-',
                  `${formatCurrency(row.refund_total)} / ${row.accounting_sync_status || formatPOSTransactionDocumentStatusLabel(row.transaction_document_status)}`,
                ]))
            : activePosReport === 'vendor-aging'
              ? exported.rows.map(row => ([
                  formatDateTime(row.bill_date),
                  row.bill_no || `#${row.id || '-'}`,
                  row.vendor_name || '-',
                  formatVendorBillTypeLabel(row.bill_type),
                  formatDateTime(row.due_date),
                  row.aging_bucket_label || formatVendorAgingBucketLabel(row.aging_bucket),
                  formatPurchasePaymentStatusLabel(row.status),
                  formatCurrency(row.outstanding_amount),
                  row.accounting_sync_status || '-',
                ]))
            : activePosReport === 'stock-movement'
              ? exported.rows.map(row => ([
                  formatDateTime(row.created_at),
                  row.product_name || '-',
                  formatInventoryMovementTypeLabel(row.movement_type),
                  `${formatInventoryReferenceLabel(row.reference_type)} #${row.reference_id || '-'}`,
                  formatCount(row.quantity_in),
                  formatCount(row.quantity_out),
                  formatCount(row.net_quantity),
                  formatCount(row.stock_after),
                  formatCurrency(row.total_cost),
                ]))
              : activePosReport === 'bom-consumption-report'
                ? exported.rows.map(row => ([
                    formatDateTime(row.transaction_created_at),
                    `#${row.transaction_id || '-'} / ${row.outlet_name || '-'}`,
                    row.sold_product_name || '-',
                    `${row.ingredient_product_name || '-'} / ${formatProductItemTypeLabel(row.ingredient_item_type)}`,
                    formatCount(row.sold_quantity),
                    formatCount(row.quantity_per_unit),
                    formatCount(row.quantity_consumed),
                    formatCurrency(row.total_cost),
                    `${row.payment_method || '-'} / ${row.cashier_name || '-'}`,
                  ]))
              : activePosReport === 'stock-opname-variance'
                ? exported.rows.map(row => ([
                    formatDateTime(row.opname_date),
                    row.product_name || '-',
                    `${row.outlet_name || '-'} / ${formatProductItemTypeLabel(row.item_type)}`,
                    `${formatCount(row.system_stock)} / ${formatCount(row.actual_stock)}`,
                    formatStockOpnameVarianceLabel(row.variance_type),
                    formatCount(row.difference_qty),
                    formatCurrency(row.total_cost),
                    row.accounting_sync_status || '-',
                    `#${row.opname_id || '-'} / ${row.opname_status || '-'}`,
                  ]))
              : activePosReport === 'vendor-price-comparison'
                ? exported.rows.map(row => ([
                    row.product_name || '-',
                    row.category_name || 'Tanpa kategori',
                    row.preferred_vendor || '-',
                    formatCurrency(row.preferred_price),
                    row.second_vendor || '-',
                    formatCurrency(row.price_spread),
                    `${Number(row.spread_percentage || 0).toFixed(2)}%`,
                    row.last_purchased_vendor || '-',
                    formatCurrency(row.potential_savings),
                  ]))
            : exported.rows.map(row => ([
                formatDateTime(row.created_at),
                `#${row.id || '-'}`,
                row.outlet_name || '-',
                row.cashier_name || (row.cashier_id ? `Cashier #${row.cashier_id}` : '-'),
                row.payment_method || '-',
                formatCount(row.item_quantity),
                `${formatCurrency(getPOSTransactionServiceAmount(row))} / ${formatCurrency(row.tax)} / ${formatCurrency(row.discount)}`,
                formatCurrency(getPOSTransactionFinalTotal(row)),
                row.accounting_sync_status || '-',
              ]))
          : activeBusinessReport === 'staff-transaction-summary'
          ? exported.rows.map(row => ([
              row.staff_name || '-',
              `${row.outlet_name || '-'} (${row.job_title || 'Tanpa jabatan'})`,
              `${formatCount(row.membership_transaction_count)} trx / ${formatCurrency(row.membership_sales_amount)}`,
              `${formatCount(row.class_transaction_count)} trx / ${formatCurrency(row.class_sales_amount)}`,
              `${formatCount(row.pt_transaction_count)} trx / ${formatCurrency(row.pt_sales_amount)}`,
              `${formatCount(row.pos_transaction_count)} trx / ${formatCurrency(row.pos_closing_shift_sales_amount)}`,
              row.sales_mix_label || '-',
              `${formatCurrency(row.total_sales_amount)} / ${formatCount(row.total_transaction_count)} trx`,
              formatDateTime(row.last_transaction_at),
            ]))
          : activeBusinessReport === 'member-activity'
          ? exported.rows.map(row => ([
              row.member_name || '-',
              row.outlet || '-',
              row.membership_name || 'Belum ada membership',
              row.lifecycle_label || '-',
              row.activity_label || '-',
              formatDateTime(row.last_checkin_at),
              row.days_since_last_checkin ?? '-',
              formatDateTime(row.membership_expired_at),
              row.risk_label || '-',
            ]))
          : activeBusinessReport === 'check-in'
            ? exported.rows.map(row => ([
                formatDateTime(row.checkin_at),
                row.member_name || '-',
                row.outlet || '-',
                row.membership_name || 'Belum ada membership',
                formatCount(row.visit_count_period),
                row.visit_sequence_label || '-',
                formatDateTime(row.membership_expired_at),
              ]))
          : activeBusinessReport === 'pt-sales'
            ? exported.rows.map(row => ([
                formatDateTime(row.sale_date),
                row.invoice_no || '-',
                `${row.client_name || '-'} (${formatPTClientTypeLabel(row.client_type)})`,
                row.pt_name || '-',
                row.product_name || '-',
                formatPTPaymentStatusLabel(row.payment_status),
                formatPTContractStatusLabel(row.contract_status),
                formatCurrency(row.net_amount),
                formatCurrency(row.outstanding_amount),
              ]))
            : activeBusinessReport === 'pt-session'
              ? exported.rows.map(row => ([
                  formatDateTime(row.start_at),
                  row.booking_code || '-',
                  `${row.client_name || '-'} (${formatPTClientTypeLabel(row.client_type)})`,
                  row.pt_name || '-',
                  row.product_name || '-',
                  formatPTBookingStatusLabel(row.booking_status),
                  formatPTAttendanceStatusLabel(row.attendance_status),
                  row.session_consumed ? 'Yes' : 'No',
                  formatCount(row.remaining_sessions),
                ]))
              : activeBusinessReport === 'pt-trainer'
                ? exported.rows.map(row => ([
                    row.pt_name || '-',
                    row.outlet || '-',
                    formatCount(row.active_clients),
                    formatCount(row.packages_sold),
                    formatCurrency(row.sales_amount),
                    formatCount(row.sessions_completed),
                    formatCount(row.sessions_cancelled),
                    formatCount(row.sessions_no_show),
                    formatCurrency(row.settlement_eligible_amount),
                  ]))
          : exported.rows.map(row => ([
              formatDateTime(row.created_at),
              row.transaction_label || 'Membership Sale',
              row.member_name || '-',
              row.membership_name || 'Tanpa paket',
              row.outlet || '-',
              row.payment_method_label || row.payment_method || '-',
              row.status_label || '-',
              row.processed_by_name || 'System',
              formatCurrency(row.amount),
            ])),
        styles: {
          fontSize: 8,
          cellPadding: 4,
        },
        headStyles: {
          fillColor: [72, 96, 64],
        },
        margin: {
          left: 28,
          right: 28,
        },
        });
      }

      doc.save(`${reportExportMeta.slug}${activeExportPageMeta.isScoped ? `-${activeExportPageMeta.slug}` : ''}-report-${appliedFilters.dateFrom || 'all'}-${appliedFilters.dateTo || 'all'}.pdf`);
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal export PDF', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleTabChange = nextTab => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', nextTab);
    if (nextTab === 'business' && defaultBusinessReport) {
      nextParams.set('report', defaultBusinessReport);
      nextParams.delete('pos_report');
      nextParams.delete('accounting_report');
    }
    if (nextTab === 'pos' && defaultPosReport) {
      nextParams.set('pos_report', defaultPosReport);
      nextParams.delete('report');
      nextParams.delete('accounting_report');
    }
    if (nextTab === 'accounting' && defaultAccountingReport) {
      nextParams.set('accounting_report', defaultAccountingReport);
      nextParams.delete('report');
      nextParams.delete('pos_report');
    }
    setCurrentPage(1);
    setSearchParams(nextParams);
  };

  const handleRefreshReport = () => {
    setCurrentPage(1);
    setAppliedFilters({ ...filters });
  };

  const handleBusinessReportChange = reportKey => {
    const selected = availableBusinessReports.find(item => item.key === reportKey);
    if (!selected?.available) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'business');
    nextParams.set('report', reportKey);
    setCurrentPage(1);
    setSearchParams(nextParams);
  };

  const handlePosReportChange = reportKey => {
    const selected = availablePosReports.find(item => item.key === reportKey);
    if (!selected?.available) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'pos');
    nextParams.set('pos_report', reportKey);
    setCurrentPage(1);
    setSearchParams(nextParams);
  };

  const handleAccountingReportChange = reportKey => {
    const selected = availableAccountingReports.find(item => item.key === reportKey);
    if (!selected?.available) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'accounting');
    nextParams.set('accounting_report', reportKey);
    setCurrentPage(1);
    setSearchParams(nextParams);
  };

  const applyQuickRange = preset => {
    const range = buildDefaultRange(preset);
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
    }));
    setAppliedFilters(prev => ({
      ...prev,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
    }));
  };

  const applyFilters = () => {
    if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
      Swal.fire('Filter tidak valid', 'Tanggal awal tidak boleh lebih besar dari tanggal akhir.', 'warning');
      return;
    }

    setCurrentPage(1);
    setAppliedFilters({ ...filters });
  };

  const resetFilters = () => {
    const range = buildDefaultRange('last-30-days');
    const nextFilters = buildReportFilterState({
      scopedOutletId,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
    });

    setCurrentPage(1);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
  };

  const summaryCards = [
    {
      key: 'revenue',
      label: 'Total revenue',
      value: formatCurrency(reportData.summary.total_amount),
      icon: FaWallet,
    },
    {
      key: 'transactions',
      label: 'Total transaksi',
      value: formatCount(reportData.summary.total_transactions),
      icon: FaClipboardCheck,
    },
    {
      key: 'members',
      label: 'Member unik',
      value: formatCount(reportData.summary.total_members),
      icon: FaUsers,
    },
    {
      key: 'average-ticket',
      label: 'Average ticket',
      value: formatCurrency(reportData.summary.average_ticket),
      icon: FaMoneyBillWave,
    },
  ];

  const staffTransactionSummaryCards = [
    {
      key: 'staff-active',
      label: 'Staff aktif',
      value: formatCount(staffTransactionData.summary.total_active_staff),
      icon: FaUsers,
    },
    {
      key: 'staff-selling',
      label: 'Staff bertransaksi',
      value: formatCount(staffTransactionData.summary.staff_with_transactions),
      icon: FaClipboardCheck,
    },
    {
      key: 'staff-membership-sales',
      label: 'Sales membership',
      value: formatCurrency(staffTransactionData.summary.total_membership_sales),
      icon: FaWallet,
    },
    {
      key: 'staff-class-sales',
      label: 'Sales class',
      value: formatCurrency(staffTransactionData.summary.total_class_sales),
      icon: FaDumbbell,
    },
    {
      key: 'staff-pt-sales',
      label: 'Sales PT',
      value: formatCurrency(staffTransactionData.summary.total_pt_sales),
      icon: FaLayerGroup,
    },
    {
      key: 'staff-pos-sales',
      label: 'POS sales',
      value: formatCurrency(staffTransactionData.summary.total_pos_sales),
      icon: FaStore,
    },
    {
      key: 'staff-total-transactions',
      label: 'Total transaksi',
      value: formatCount(staffTransactionData.summary.total_transactions),
      icon: FaReceipt,
    },
    {
      key: 'staff-total-sales',
      label: 'Total sales gabungan',
      value: formatCurrency(staffTransactionData.summary.total_sales_amount),
      icon: FaMoneyBillWave,
    },
    {
      key: 'staff-average-sales',
      label: 'Rata-rata per staff',
      value: formatCurrency(staffTransactionData.summary.average_sales_per_staff),
      icon: FaChartLine,
    },
    {
      key: 'staff-average-ticket',
      label: 'Average ticket',
      value: formatCurrency(staffTransactionData.summary.average_ticket),
      icon: FaLayerGroup,
    },
  ];

  const memberActivitySummaryCards = [
    {
      key: 'members',
      label: 'Total member',
      value: formatCount(memberActivityData.summary.total_members),
      icon: FaUsers,
    },
    {
      key: 'active-membership',
      label: 'Membership aktif',
      value: formatCount(memberActivityData.summary.active_memberships),
      icon: FaWallet,
    },
    {
      key: 'expiring',
      label: 'Segera expired',
      value: formatCount(memberActivityData.summary.expiring_soon),
      icon: FaCalendarAlt,
    },
    {
      key: 'checked-in',
      label: 'Check-in periode',
      value: formatCount(memberActivityData.summary.checked_in_period),
      icon: FaClipboardCheck,
    },
    {
      key: 'inactive-14-days',
      label: 'Tidak datang >14 hari',
      value: formatCount(memberActivityData.summary.inactive_more_than_14_days),
      icon: FaChartLine,
    },
    {
      key: 'never-checkin',
      label: 'Belum pernah check-in',
      value: formatCount(memberActivityData.summary.never_checkin),
      icon: FaMoneyBillWave,
    },
  ];

  const checkinSummaryCards = [
    {
      key: 'total-checkin',
      label: 'Total check-in',
      value: formatCount(checkinData.summary.total_checkins),
      icon: FaClipboardCheck,
    },
    {
      key: 'unique-member',
      label: 'Member unik',
      value: formatCount(checkinData.summary.unique_members),
      icon: FaUsers,
    },
    {
      key: 'repeat-member',
      label: 'Repeat member',
      value: formatCount(checkinData.summary.repeat_members),
      icon: FaChartLine,
    },
    {
      key: 'today-checkin',
      label: 'Check-in hari ini',
      value: formatCount(checkinData.summary.today_checkins),
      icon: FaCalendarAlt,
    },
    {
      key: 'average-daily',
      label: 'Rata-rata harian',
      value: formatCount(checkinData.summary.average_daily_checkin),
      icon: FaLayerGroup,
    },
  ];

  const ptSalesSummaryCards = [
    {
      key: 'pt-sales-total',
      label: 'Total private session sales',
      value: formatCurrency(ptSalesData.summary.total_sales_amount),
      icon: FaWallet,
    },
    {
      key: 'pt-sales-invoices',
      label: 'Total invoice',
      value: formatCount(ptSalesData.summary.total_invoices),
      icon: FaReceipt,
    },
    {
      key: 'pt-sales-paid',
      label: 'Paid amount',
      value: formatCurrency(ptSalesData.summary.paid_amount),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pt-sales-outstanding',
      label: 'Outstanding',
      value: formatCurrency(ptSalesData.summary.outstanding_amount),
      icon: FaClipboardCheck,
    },
    {
      key: 'pt-sales-average',
      label: 'Average session ticket',
      value: formatCurrency(ptSalesData.summary.average_ticket),
      icon: FaChartLine,
    },
    {
      key: 'pt-sales-new-clients',
      label: 'New session clients',
      value: formatCount(ptSalesData.summary.new_clients),
      icon: FaUsers,
    },
    {
      key: 'pt-sales-repeat-clients',
      label: 'Repeat session clients',
      value: formatCount(ptSalesData.summary.repeat_clients),
      icon: FaLayerGroup,
    },
  ];

  const ptSessionSummaryCards = [
    {
      key: 'pt-session-scheduled',
      label: 'Total sesi dijadwalkan',
      value: formatCount(ptSessionData.summary.total_sessions_scheduled),
      icon: FaCalendarAlt,
    },
    {
      key: 'pt-session-completed',
      label: 'Sesi selesai',
      value: formatCount(ptSessionData.summary.total_sessions_completed),
      icon: FaClipboardCheck,
    },
    {
      key: 'pt-session-cancelled',
      label: 'Sesi dibatalkan',
      value: formatCount(ptSessionData.summary.total_sessions_cancelled),
      icon: FaRedoAlt,
    },
    {
      key: 'pt-session-rescheduled',
      label: 'Sesi reschedule',
      value: formatCount(ptSessionData.summary.total_sessions_rescheduled),
      icon: FaLayerGroup,
    },
    {
      key: 'pt-session-no-show',
      label: 'No-show',
      value: formatCount(ptSessionData.summary.no_show_count),
      icon: FaUsers,
    },
    {
      key: 'pt-session-remaining',
      label: 'Sisa sesi kontrak',
      value: formatCount(ptSessionData.summary.sessions_remaining),
      icon: FaWallet,
    },
    {
      key: 'pt-session-completion-rate',
      label: 'Completion rate',
      value: `${Number(ptSessionData.summary.completion_rate || 0).toFixed(2)}%`,
      icon: FaChartLine,
    },
  ];

  const ptTrainerSummaryCards = [
    {
      key: 'pt-trainer-active',
      label: 'Trainer aktif',
      value: formatCount(ptTrainerData.summary.active_trainers),
      icon: FaUsers,
    },
    {
      key: 'pt-trainer-clients',
      label: 'Client handled',
      value: formatCount(ptTrainerData.summary.clients_handled),
      icon: FaLayerGroup,
    },
    {
      key: 'pt-trainer-completed',
      label: 'Sesi selesai',
      value: formatCount(ptTrainerData.summary.sessions_completed),
      icon: FaClipboardCheck,
    },
    {
      key: 'pt-trainer-sales',
      label: 'Sales attributed',
      value: formatCurrency(ptTrainerData.summary.sales_attributed),
      icon: FaWallet,
    },
    {
      key: 'pt-trainer-paid',
      label: 'Paid sales',
      value: formatCurrency(ptTrainerData.summary.paid_sales_amount),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pt-trainer-rate',
      label: 'Completion rate',
      value: `${Number(ptTrainerData.summary.completion_rate || 0).toFixed(2)}%`,
      icon: FaChartLine,
    },
    {
      key: 'pt-trainer-average',
      label: 'Avg sesi per trainer',
      value: Number(ptTrainerData.summary.average_sessions_per_trainer || 0).toFixed(2),
      icon: FaCalendarAlt,
    },
    {
      key: 'pt-trainer-settlement',
      label: 'Settlement eligible',
      value: formatCurrency(ptTrainerData.summary.settlement_eligible_amount),
      icon: FaReceipt,
    },
  ];

  const posSalesSummaryCards = [
    {
      key: 'pos-sales-total',
      label: 'Gross sales',
      value: formatCurrency(posSalesData.summary.gross_sales),
      icon: FaWallet,
    },
    {
      key: 'pos-sales-transactions',
      label: 'Total transaksi',
      value: formatCount(posSalesData.summary.total_transactions),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-sales-average-ticket',
      label: 'Average ticket',
      value: formatCurrency(posSalesData.summary.average_ticket),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-sales-tax',
      label: 'Total tax',
      value: formatCurrency(posSalesData.summary.total_tax),
      icon: FaReceipt,
    },
    {
      key: 'pos-sales-service',
      label: 'Total service',
      value: formatCurrency(posSalesData.summary.total_service),
      icon: FaConciergeBell,
    },
    {
      key: 'pos-sales-discount',
      label: 'Total discount',
      value: formatCurrency(posSalesData.summary.total_discount),
      icon: FaChartLine,
    },
    {
      key: 'pos-sales-items',
      label: 'Item terjual',
      value: formatCount(posSalesData.summary.total_item_quantity),
      icon: FaLayerGroup,
    },
    {
      key: 'pos-sales-cashiers',
      label: 'Kasir aktif',
      value: formatCount(posSalesData.summary.unique_cashiers),
      icon: FaUsers,
    },
    {
      key: 'pos-sales-products',
      label: 'Produk terjual',
      value: formatCount(posSalesData.summary.unique_products),
      icon: FaClipboardCheck,
    },
  ];

  const renderMembershipGrowthAISection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">AI growth assistant</span>
          <h2>Rekomendasi upsell membership</h2>
          <p>Panel ini membaca paket yang sering diulang, peluang upgrade ke durasi lebih panjang, dan promo renewal yang paling layak dicoba.</p>
        </div>
        <span className="soft-stat">
          <FaChartLine /> {membershipGrowthAIData.upsell_suggestions.length} upsell
        </span>
      </div>

      {membershipGrowthAILoading ? (
        <div className="report-center-empty-copy">
          Menyusun rekomendasi AI membership dari histori penjualan dan pola repeat buyer...
        </div>
      ) : membershipGrowthAIError ? (
        <div className="report-center-empty-copy">
          {membershipGrowthAIError}
        </div>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={3} sm={6}>
              <div className="report-center-journal-meta-card">
                <span>Paket dianalisis</span>
                <strong>{formatCount(membershipGrowthAIData.summary.memberships_analyzed)}</strong>
                <small>Semua paket aktif di scope report</small>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="report-center-journal-meta-card">
                <span>Transaksi dibaca</span>
                <strong>{formatCount(membershipGrowthAIData.summary.transactions_analyzed)}</strong>
                <small>Penjualan membership sukses</small>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="report-center-journal-meta-card">
                <span>Total revenue</span>
                <strong>{formatCurrency(membershipGrowthAIData.summary.total_revenue)}</strong>
                <small>Basis rekomendasi AI saat ini</small>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="report-center-journal-meta-card">
                <span>Entry package utama</span>
                <strong>{membershipGrowthAIData.summary.top_entry_package || '-'}</strong>
                <small>{formatCount(membershipGrowthAIData.summary.repeat_buyers)} repeat buyer terbaca</small>
              </div>
            </Col>
          </Row>

          {!canCaptureAIOutletFeedback ? (
            <div className="report-center-empty-copy mb-3">
              Pilih satu outlet spesifik jika Anda ingin menyimpan feedback `diterima / diabaikan` agar AI belajar dari perilaku outlet tersebut.
            </div>
          ) : null}

          <Row className="g-3 mb-3">
            {(membershipGrowthAIData.upsell_suggestions || []).map(item => (
              <Col lg={6} key={`${item.source_membership_id}-${item.target_membership_id}`}>
                <div className="report-center-journal-meta-card report-center-journal-meta-card--wide h-100">
                  <div className="d-flex justify-content-between gap-2 flex-wrap">
                    <strong>{item.source_membership_name} → {item.target_membership_name}</strong>
                    <span className="soft-stat">{item.confidence_label}</span>
                  </div>
                  <small>{item.reason}</small>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <span className="soft-stat">Repeat buyer {formatCount(item.repeat_buyer_count)}</span>
                    <span className="soft-stat">Gap {formatCurrency(item.price_gap)}</span>
                    <span className="soft-stat">Efisiensi {formatPercentage(item.efficiency_gain_percent)}</span>
                    <span className="soft-stat">Lift {formatCurrency(item.potential_revenue_lift)}</span>
                    {(item.feedback_accepted_count || item.feedback_ignored_count || item.feedback_campaign_used_count || item.feedback_sales_improved_count) ? (
                      <span className="soft-stat">
                        Outlet: {formatCount(item.feedback_accepted_count)} diterima • {formatCount(item.feedback_ignored_count)} diabaikan
                      </span>
                    ) : null}
                    {(item.feedback_campaign_used_count || item.feedback_sales_improved_count) ? (
                      <span className="soft-stat">
                        Campaign {formatCount(item.feedback_campaign_used_count)}x • Sales lift {formatCount(item.feedback_sales_improved_count)}x
                      </span>
                    ) : null}
                    {(item.feedback_estimated_revenue_lift_total || item.feedback_observed_revenue_lift_total || item.feedback_average_transaction_lift_percent) ? (
                      <span className="soft-stat">
                        Est {formatCurrency(item.feedback_estimated_revenue_lift_total)} • Realisasi {formatCurrency(item.feedback_observed_revenue_lift_total)} • Lift trx {formatPercentage(item.feedback_average_transaction_lift_percent)}
                      </span>
                    ) : null}
                    {(item.feedback_auto_attributed_campaign_count || item.feedback_auto_observed_revenue_lift_total || item.feedback_auto_average_transaction_lift_percent) ? (
                      <span className="soft-stat">
                        Auto {formatCount(item.feedback_auto_attributed_campaign_count)} campaign • Realisasi {formatCurrency(item.feedback_auto_observed_revenue_lift_total)} • Lift trx {formatPercentage(item.feedback_auto_average_transaction_lift_percent)}
                      </span>
                    ) : null}
                    {item.feedback_last_action ? (
                      <span className="soft-stat">Terakhir {formatAIFeedbackActionLabel(item.feedback_last_action).toLowerCase()}</span>
                    ) : null}
                  </div>
                  <div className="mt-3">
                    <strong>Pitch</strong>
                    <div>{item.recommended_pitch}</div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      variant={item.feedback_last_action === 'accepted' ? 'success' : 'outline-success'}
                      disabled={!canCaptureAIOutletFeedback || membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('upsell', item.recommendation_key, 'accepted')]}
                      onClick={() => handleMembershipGrowthFeedback('upsell', item.recommendation_key, 'accepted')}
                    >
                      {membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('upsell', item.recommendation_key, 'accepted')] ? 'Menyimpan...' : 'Tandai diterima'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_last_action === 'ignored' ? 'warning' : 'outline-secondary'}
                      disabled={!canCaptureAIOutletFeedback || membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('upsell', item.recommendation_key, 'ignored')]}
                      onClick={() => handleMembershipGrowthFeedback('upsell', item.recommendation_key, 'ignored')}
                    >
                      {membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('upsell', item.recommendation_key, 'ignored')] ? 'Menyimpan...' : 'Abaikan'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_campaign_used_count > 0 ? 'primary' : 'outline-primary'}
                      disabled={!canCaptureAIOutletFeedback || membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('upsell', item.recommendation_key, 'campaign_used')]}
                      onClick={() => handleMembershipGrowthFeedback('upsell', item.recommendation_key, 'campaign_used')}
                    >
                      {membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('upsell', item.recommendation_key, 'campaign_used')] ? 'Menyimpan...' : 'Dipakai jadi campaign'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_sales_improved_count > 0 ? 'dark' : 'outline-dark'}
                      disabled={!canCaptureAIOutletFeedback || membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('upsell', item.recommendation_key, 'sales_improved')]}
                      onClick={() => handleMembershipGrowthFeedback('upsell', item.recommendation_key, 'sales_improved')}
                    >
                      {membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('upsell', item.recommendation_key, 'sales_improved')] ? 'Menyimpan...' : 'Berhasil naikkan sales'}
                    </Button>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <Row className="g-3">
            {(membershipGrowthAIData.promo_candidates || []).map(item => (
              <Col lg={6} key={`${item.strategy_key}-${item.membership_id}`}>
                <div className="report-center-journal-meta-card h-100">
                  <div className="d-flex justify-content-between gap-2 flex-wrap">
                    <strong>{item.strategy_label}</strong>
                    <span className="soft-stat">{item.confidence_label}</span>
                  </div>
                  <small>{item.membership_name}</small>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <span className="soft-stat">Harga {formatCurrency(item.current_price)}</span>
                    <span className="soft-stat">Promo {formatCurrency(item.suggested_promo_price)}</span>
                    <span className="soft-stat">Diskon {formatPercentage(item.suggested_discount_percent)}</span>
                    <span className="soft-stat">Member pendukung {formatCount(item.supporting_members)}</span>
                    {(item.feedback_accepted_count || item.feedback_ignored_count || item.feedback_campaign_used_count || item.feedback_sales_improved_count) ? (
                      <span className="soft-stat">
                        Outlet: {formatCount(item.feedback_accepted_count)} diterima • {formatCount(item.feedback_ignored_count)} diabaikan
                      </span>
                    ) : null}
                    {(item.feedback_campaign_used_count || item.feedback_sales_improved_count) ? (
                      <span className="soft-stat">
                        Campaign {formatCount(item.feedback_campaign_used_count)}x • Sales lift {formatCount(item.feedback_sales_improved_count)}x
                      </span>
                    ) : null}
                    {(item.feedback_estimated_revenue_lift_total || item.feedback_observed_revenue_lift_total || item.feedback_average_transaction_lift_percent) ? (
                      <span className="soft-stat">
                        Est {formatCurrency(item.feedback_estimated_revenue_lift_total)} • Realisasi {formatCurrency(item.feedback_observed_revenue_lift_total)} • Lift trx {formatPercentage(item.feedback_average_transaction_lift_percent)}
                      </span>
                    ) : null}
                    {(item.feedback_auto_attributed_campaign_count || item.feedback_auto_observed_revenue_lift_total || item.feedback_auto_average_transaction_lift_percent) ? (
                      <span className="soft-stat">
                        Auto {formatCount(item.feedback_auto_attributed_campaign_count)} campaign • Realisasi {formatCurrency(item.feedback_auto_observed_revenue_lift_total)} • Lift trx {formatPercentage(item.feedback_auto_average_transaction_lift_percent)}
                      </span>
                    ) : null}
                    {item.feedback_last_action ? (
                      <span className="soft-stat">Terakhir {formatAIFeedbackActionLabel(item.feedback_last_action).toLowerCase()}</span>
                    ) : null}
                  </div>
                  <div className="mt-3">
                    {item.reason}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      variant={item.feedback_last_action === 'accepted' ? 'success' : 'outline-success'}
                      disabled={!canCaptureAIOutletFeedback || membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'accepted')]}
                      onClick={() => handleMembershipGrowthFeedback('promo', item.recommendation_key, 'accepted')}
                    >
                      {membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'accepted')] ? 'Menyimpan...' : 'Tandai diterima'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_last_action === 'ignored' ? 'warning' : 'outline-secondary'}
                      disabled={!canCaptureAIOutletFeedback || membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'ignored')]}
                      onClick={() => handleMembershipGrowthFeedback('promo', item.recommendation_key, 'ignored')}
                    >
                      {membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'ignored')] ? 'Menyimpan...' : 'Abaikan'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_campaign_used_count > 0 ? 'primary' : 'outline-primary'}
                      disabled={!canCaptureAIOutletFeedback || membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'campaign_used')]}
                      onClick={() => handleMembershipGrowthFeedback('promo', item.recommendation_key, 'campaign_used')}
                    >
                      {membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'campaign_used')] ? 'Menyimpan...' : 'Dipakai jadi campaign'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_sales_improved_count > 0 ? 'dark' : 'outline-dark'}
                      disabled={!canCaptureAIOutletFeedback || membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'sales_improved')]}
                      onClick={() => handleMembershipGrowthFeedback('promo', item.recommendation_key, 'sales_improved')}
                    >
                      {membershipGrowthFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'sales_improved')] ? 'Menyimpan...' : 'Berhasil naikkan sales'}
                    </Button>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {!membershipGrowthAIData.upsell_suggestions.length && !membershipGrowthAIData.promo_candidates.length ? (
            <div className="report-center-empty-copy">
              Belum ada pola yang cukup kuat untuk menyusun upsell atau promo membership pada periode ini.
            </div>
          ) : null}
        </>
      )}
    </section>
  );

  const renderPOSPricingAISection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">AI pricing assistant</span>
          <h2>Rekomendasi bundling dan promo POS</h2>
          <p>Panel ini membaca pair produk yang sering muncul bersama, jam transaksi yang masih lemah, dan kandidat promo ringan yang masih aman untuk operasional.</p>
        </div>
        <span className="soft-stat">
          <FaStore /> {posPricingAIData.bundling_recommendations.length} bundling
        </span>
      </div>

      {posPricingAILoading ? (
        <div className="report-center-empty-copy">
          Menyusun rekomendasi bundling dan promo dari histori transaksi POS...
        </div>
      ) : posPricingAIError ? (
        <div className="report-center-empty-copy">
          {posPricingAIError}
        </div>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={3} sm={6}>
              <div className="report-center-journal-meta-card">
                <span>Transaksi dibaca</span>
                <strong>{formatCount(posPricingAIData.summary.transactions_analyzed)}</strong>
                <small>Basis analisis bundling</small>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="report-center-journal-meta-card">
                <span>Produk unik</span>
                <strong>{formatCount(posPricingAIData.summary.unique_products)}</strong>
                <small>SKU aktif pada periode ini</small>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="report-center-journal-meta-card">
                <span>Gross sales</span>
                <strong>{formatCurrency(posPricingAIData.summary.gross_sales)}</strong>
                <small>Nilai transaksi yang dibaca</small>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="report-center-journal-meta-card">
                <span>Slot dominan</span>
                <strong>{posPricingAIData.summary.dominant_time_slot || '-'}</strong>
                <small>Avg ticket {formatCurrency(posPricingAIData.summary.average_ticket)}</small>
              </div>
            </Col>
          </Row>

          {String(appliedFilters.product_id || '').trim() ? (
            <div className="report-center-filter-notice mb-3">
              <div>
                <strong>Produk fokus AI: {posPricingAIData.summary.focus_product_name || selectedPOSPricingFocusProductLabel}</strong>
                <p>
                  AI hanya menilai bundling dan promo yang berkaitan dengan produk ini.
                  {Number(posPricingAIData.summary.focus_product_unit_cost || 0) > 0 ? ` Estimasi HPP dasar ${formatCurrency(posPricingAIData.summary.focus_product_unit_cost)}.` : ''}
                  {Number(posPricingAIData.summary.focus_product_cost_fill || 0) > 0 ? ` Coverage HPP ${formatPercentage(posPricingAIData.summary.focus_product_cost_fill)}.` : ''}
                </p>
              </div>
            </div>
          ) : null}

          {!canCaptureAIOutletFeedback ? (
            <div className="report-center-empty-copy mb-3">
              Pilih satu outlet spesifik jika Anda ingin menyimpan feedback `diterima / diabaikan` agar rekomendasi bundling dan promo belajar dari outlet itu.
            </div>
          ) : null}

          <Row className="g-3 mb-3">
            {(posPricingAIData.bundling_recommendations || []).map(item => (
              <Col lg={6} key={`${item.primary_product_id}-${item.paired_product_id}`}>
                <div className="report-center-journal-meta-card report-center-journal-meta-card--wide h-100">
                  <div className="d-flex justify-content-between gap-2 flex-wrap">
                    <strong>{item.primary_product_name} + {item.paired_product_name}</strong>
                    <span className="soft-stat">{item.confidence_label}</span>
                  </div>
                  <small>{item.reason}</small>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <span className="soft-stat">Joint trx {formatCount(item.joint_transaction_count)}</span>
                    <span className="soft-stat">Attach rate {formatPercentage(item.attach_rate_percent)}</span>
                    <span className="soft-stat">Avg value {formatCurrency(item.average_bundle_value)}</span>
                    <span className="soft-stat">Bundle {formatCurrency(item.suggested_bundle_price)}</span>
                    {(item.feedback_accepted_count || item.feedback_ignored_count || item.feedback_campaign_used_count || item.feedback_sales_improved_count) ? (
                      <span className="soft-stat">
                        Outlet: {formatCount(item.feedback_accepted_count)} diterima • {formatCount(item.feedback_ignored_count)} diabaikan
                      </span>
                    ) : null}
                    {(item.feedback_campaign_used_count || item.feedback_sales_improved_count) ? (
                      <span className="soft-stat">
                        Campaign {formatCount(item.feedback_campaign_used_count)}x • Sales lift {formatCount(item.feedback_sales_improved_count)}x
                      </span>
                    ) : null}
                    {(item.feedback_estimated_revenue_lift_total || item.feedback_observed_revenue_lift_total || item.feedback_average_transaction_lift_percent) ? (
                      <span className="soft-stat">
                        Est {formatCurrency(item.feedback_estimated_revenue_lift_total)} • Realisasi {formatCurrency(item.feedback_observed_revenue_lift_total)} • Lift trx {formatPercentage(item.feedback_average_transaction_lift_percent)}
                      </span>
                    ) : null}
                    {(item.feedback_auto_attributed_campaign_count || item.feedback_auto_observed_revenue_lift_total || item.feedback_auto_average_transaction_lift_percent) ? (
                      <span className="soft-stat">
                        Auto {formatCount(item.feedback_auto_attributed_campaign_count)} campaign • Realisasi {formatCurrency(item.feedback_auto_observed_revenue_lift_total)} • Lift trx {formatPercentage(item.feedback_auto_average_transaction_lift_percent)}
                      </span>
                    ) : null}
                    {item.feedback_last_action ? (
                      <span className="soft-stat">Terakhir {formatAIFeedbackActionLabel(item.feedback_last_action).toLowerCase()}</span>
                    ) : null}
                  </div>
                  <div className="report-center-ai-margin-card mt-3">
                    <div className="report-center-ai-margin-card__head">
                      <strong>Analisa margin promo</strong>
                      <span className={`report-center-ai-margin-badge is-${resolveAIMarginTone(item?.margin_analysis?.safety_label)}`}>
                        {item?.margin_analysis?.safety_label || 'Cek HPP'}
                      </span>
                    </div>
                    <div className="report-center-ai-margin-grid">
                      <div className="report-center-ai-margin-stat">
                        <span>HPP estimasi</span>
                        <strong>{formatCurrency(item?.margin_analysis?.estimated_hpp || 0)}</strong>
                        <small>{item?.margin_analysis?.cost_coverage_label || 'HPP belum lengkap'} • Coverage {formatPercentage(item?.margin_analysis?.cost_coverage_percent || 0)}</small>
                      </div>
                      <div className="report-center-ai-margin-stat">
                        <span>Harga aman</span>
                        <strong>{formatCurrency(item?.margin_analysis?.minimum_safe_price || 0)}</strong>
                        <small>Break-even {formatCurrency(item?.margin_analysis?.break_even_price || 0)}</small>
                      </div>
                      <div className="report-center-ai-margin-stat">
                        <span>Margin promo</span>
                        <strong>{formatCurrency(item?.margin_analysis?.suggested_margin_amount || 0)}</strong>
                        <small>{formatPercentage(item?.margin_analysis?.suggested_margin_percent || 0)} dari harga promo</small>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      variant={item.feedback_last_action === 'accepted' ? 'success' : 'outline-success'}
                      disabled={!canCaptureAIOutletFeedback || posPricingFeedbackSubmitting[buildAIFeedbackStateKey('bundling', item.recommendation_key, 'accepted')]}
                      onClick={() => handlePOSPricingFeedback('bundling', item.recommendation_key, 'accepted')}
                    >
                      {posPricingFeedbackSubmitting[buildAIFeedbackStateKey('bundling', item.recommendation_key, 'accepted')] ? 'Menyimpan...' : 'Tandai diterima'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_last_action === 'ignored' ? 'warning' : 'outline-secondary'}
                      disabled={!canCaptureAIOutletFeedback || posPricingFeedbackSubmitting[buildAIFeedbackStateKey('bundling', item.recommendation_key, 'ignored')]}
                      onClick={() => handlePOSPricingFeedback('bundling', item.recommendation_key, 'ignored')}
                    >
                      {posPricingFeedbackSubmitting[buildAIFeedbackStateKey('bundling', item.recommendation_key, 'ignored')] ? 'Menyimpan...' : 'Abaikan'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_campaign_used_count > 0 ? 'primary' : 'outline-primary'}
                      disabled={!canCaptureAIOutletFeedback || posPricingFeedbackSubmitting[buildAIFeedbackStateKey('bundling', item.recommendation_key, 'campaign_used')]}
                      onClick={() => handlePOSPricingFeedback('bundling', item.recommendation_key, 'campaign_used')}
                    >
                      {posPricingFeedbackSubmitting[buildAIFeedbackStateKey('bundling', item.recommendation_key, 'campaign_used')] ? 'Menyimpan...' : 'Dipakai jadi campaign'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_sales_improved_count > 0 ? 'dark' : 'outline-dark'}
                      disabled={!canCaptureAIOutletFeedback || posPricingFeedbackSubmitting[buildAIFeedbackStateKey('bundling', item.recommendation_key, 'sales_improved')]}
                      onClick={() => handlePOSPricingFeedback('bundling', item.recommendation_key, 'sales_improved')}
                    >
                      {posPricingFeedbackSubmitting[buildAIFeedbackStateKey('bundling', item.recommendation_key, 'sales_improved')] ? 'Menyimpan...' : 'Berhasil naikkan sales'}
                    </Button>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <Row className="g-3">
            {(posPricingAIData.promo_candidates || []).map(item => (
              <Col lg={6} key={`${item.strategy_key}-${item.focus_product_id || item.time_slot_key || item.focus_product_name}`}>
                <div className="report-center-journal-meta-card h-100">
                  <div className="d-flex justify-content-between gap-2 flex-wrap">
                    <strong>{item.strategy_label}</strong>
                    <span className="soft-stat">{item.confidence_label}</span>
                  </div>
                  <small>{item.focus_product_name || item.time_slot_label || 'Promo outlet'}</small>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {item.time_slot_label ? <span className="soft-stat">Slot {item.time_slot_label}</span> : null}
                    <span className="soft-stat">Pendukung {formatCount(item.supporting_transactions)} trx</span>
                    <span className="soft-stat">Diskon {formatPercentage(item.suggested_discount_percent)}</span>
                    <span className="soft-stat">Harga saran {formatCurrency(item.suggested_price)}</span>
                    <span className="soft-stat">Lift {formatPercentage(item.expected_lift_percent)}</span>
                    {(item.feedback_accepted_count || item.feedback_ignored_count || item.feedback_campaign_used_count || item.feedback_sales_improved_count) ? (
                      <span className="soft-stat">
                        Outlet: {formatCount(item.feedback_accepted_count)} diterima • {formatCount(item.feedback_ignored_count)} diabaikan
                      </span>
                    ) : null}
                    {(item.feedback_campaign_used_count || item.feedback_sales_improved_count) ? (
                      <span className="soft-stat">
                        Campaign {formatCount(item.feedback_campaign_used_count)}x • Sales lift {formatCount(item.feedback_sales_improved_count)}x
                      </span>
                    ) : null}
                    {(item.feedback_estimated_revenue_lift_total || item.feedback_observed_revenue_lift_total || item.feedback_average_transaction_lift_percent) ? (
                      <span className="soft-stat">
                        Est {formatCurrency(item.feedback_estimated_revenue_lift_total)} • Realisasi {formatCurrency(item.feedback_observed_revenue_lift_total)} • Lift trx {formatPercentage(item.feedback_average_transaction_lift_percent)}
                      </span>
                    ) : null}
                    {(item.feedback_auto_attributed_campaign_count || item.feedback_auto_observed_revenue_lift_total || item.feedback_auto_average_transaction_lift_percent) ? (
                      <span className="soft-stat">
                        Auto {formatCount(item.feedback_auto_attributed_campaign_count)} campaign • Realisasi {formatCurrency(item.feedback_auto_observed_revenue_lift_total)} • Lift trx {formatPercentage(item.feedback_auto_average_transaction_lift_percent)}
                      </span>
                    ) : null}
                    {item.feedback_last_action ? (
                      <span className="soft-stat">Terakhir {formatAIFeedbackActionLabel(item.feedback_last_action).toLowerCase()}</span>
                    ) : null}
                  </div>
                  <div className="mt-3">
                    {item.reason}
                  </div>
                  <div className="report-center-ai-margin-card mt-3">
                    <div className="report-center-ai-margin-card__head">
                      <strong>Analisa margin promo</strong>
                      <span className={`report-center-ai-margin-badge is-${resolveAIMarginTone(item?.margin_analysis?.safety_label)}`}>
                        {item?.margin_analysis?.safety_label || 'Cek HPP'}
                      </span>
                    </div>
                    <div className="report-center-ai-margin-grid">
                      <div className="report-center-ai-margin-stat">
                        <span>HPP estimasi</span>
                        <strong>{formatCurrency(item?.margin_analysis?.estimated_hpp || 0)}</strong>
                        <small>{item?.margin_analysis?.cost_coverage_label || 'HPP belum lengkap'} • Coverage {formatPercentage(item?.margin_analysis?.cost_coverage_percent || 0)}</small>
                      </div>
                      <div className="report-center-ai-margin-stat">
                        <span>Harga aman</span>
                        <strong>{formatCurrency(item?.margin_analysis?.minimum_safe_price || 0)}</strong>
                        <small>Break-even {formatCurrency(item?.margin_analysis?.break_even_price || 0)}</small>
                      </div>
                      <div className="report-center-ai-margin-stat">
                        <span>Margin promo</span>
                        <strong>{formatCurrency(item?.margin_analysis?.suggested_margin_amount || 0)}</strong>
                        <small>{formatPercentage(item?.margin_analysis?.suggested_margin_percent || 0)} dari harga promo</small>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      variant={item.feedback_last_action === 'accepted' ? 'success' : 'outline-success'}
                      disabled={!canCaptureAIOutletFeedback || posPricingFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'accepted')]}
                      onClick={() => handlePOSPricingFeedback('promo', item.recommendation_key, 'accepted')}
                    >
                      {posPricingFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'accepted')] ? 'Menyimpan...' : 'Tandai diterima'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_last_action === 'ignored' ? 'warning' : 'outline-secondary'}
                      disabled={!canCaptureAIOutletFeedback || posPricingFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'ignored')]}
                      onClick={() => handlePOSPricingFeedback('promo', item.recommendation_key, 'ignored')}
                    >
                      {posPricingFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'ignored')] ? 'Menyimpan...' : 'Abaikan'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_campaign_used_count > 0 ? 'primary' : 'outline-primary'}
                      disabled={!canCaptureAIOutletFeedback || posPricingFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'campaign_used')]}
                      onClick={() => handlePOSPricingFeedback('promo', item.recommendation_key, 'campaign_used')}
                    >
                      {posPricingFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'campaign_used')] ? 'Menyimpan...' : 'Dipakai jadi campaign'}
                    </Button>
                    <Button
                      size="sm"
                      variant={item.feedback_sales_improved_count > 0 ? 'dark' : 'outline-dark'}
                      disabled={!canCaptureAIOutletFeedback || posPricingFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'sales_improved')]}
                      onClick={() => handlePOSPricingFeedback('promo', item.recommendation_key, 'sales_improved')}
                    >
                      {posPricingFeedbackSubmitting[buildAIFeedbackStateKey('promo', item.recommendation_key, 'sales_improved')] ? 'Menyimpan...' : 'Berhasil naikkan sales'}
                    </Button>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {!posPricingAIData.bundling_recommendations.length && !posPricingAIData.promo_candidates.length ? (
            <div className="report-center-empty-copy">
              Belum ada pola transaksi yang cukup stabil untuk menyusun bundling atau promo POS pada periode ini.
            </div>
          ) : null}
        </>
      )}
    </section>
  );

  const posCashierSalesSummaryCards = [
    {
      key: 'pos-cashier-sales-cashiers',
      label: 'Kasir aktif',
      value: formatCount(posCashierSalesData.summary.total_cashiers),
      icon: FaUsers,
    },
    {
      key: 'pos-cashier-sales-gross',
      label: 'Gross sales',
      value: formatCurrency(posCashierSalesData.summary.gross_sales),
      icon: FaWallet,
    },
    {
      key: 'pos-cashier-sales-service',
      label: 'Total service',
      value: formatCurrency(posCashierSalesData.summary.total_service),
      icon: FaConciergeBell,
    },
    {
      key: 'pos-cashier-sales-transactions',
      label: 'Total transaksi',
      value: formatCount(posCashierSalesData.summary.total_transactions),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-cashier-sales-average-per-cashier',
      label: 'Avg sales / kasir',
      value: formatCurrency(posCashierSalesData.summary.average_sales_per_cashier),
      icon: FaChartLine,
    },
    {
      key: 'pos-cashier-sales-average-ticket',
      label: 'Average ticket',
      value: formatCurrency(posCashierSalesData.summary.average_ticket),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-cashier-sales-items',
      label: 'Qty terjual',
      value: formatCount(posCashierSalesData.summary.total_item_quantity),
      icon: FaLayerGroup,
    },
    {
      key: 'pos-cashier-sales-top-cashier',
      label: 'Top cashier',
      value: posCashierSalesData.summary.top_cashier_name || '-',
      icon: FaReceipt,
    },
    {
      key: 'pos-cashier-sales-outlets',
      label: 'Outlet aktif',
      value: formatCount(posCashierSalesData.summary.active_outlets),
      icon: FaStore,
    },
  ];

  const posProductSalesSummaryCards = [
    {
      key: 'pos-product-sales-products',
      label: 'Produk terjual',
      value: formatCount(posProductSalesData.summary.total_products),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-product-sales-qty',
      label: 'Qty terjual',
      value: formatCount(posProductSalesData.summary.total_quantity),
      icon: FaLayerGroup,
    },
    {
      key: 'pos-product-sales-gross',
      label: 'Gross sales',
      value: formatCurrency(posProductSalesData.summary.gross_sales),
      icon: FaWallet,
    },
    {
      key: 'pos-product-sales-average-price',
      label: 'Avg harga jual',
      value: formatCurrency(posProductSalesData.summary.average_selling_price),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-product-sales-average-revenue',
      label: 'Avg revenue / produk',
      value: formatCurrency(posProductSalesData.summary.average_sales_per_product),
      icon: FaChartLine,
    },
    {
      key: 'pos-product-sales-outlets',
      label: 'Outlet aktif',
      value: formatCount(posProductSalesData.summary.unique_outlets),
      icon: FaStore,
    },
    {
      key: 'pos-product-sales-cashiers',
      label: 'Kasir aktif',
      value: formatCount(posProductSalesData.summary.unique_cashiers),
      icon: FaUsers,
    },
    {
      key: 'pos-product-sales-top',
      label: 'Top product revenue',
      value: formatCurrency(posProductSalesData.summary.top_product_sales),
      icon: FaReceipt,
    },
  ];

  const posRefundVoidSummaryCards = [
    {
      key: 'pos-refund-void-total',
      label: 'Total pengajuan',
      value: formatCount(posRefundVoidData.summary.total_requests),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-refund-void-approved',
      label: 'Approved',
      value: formatCount(posRefundVoidData.summary.approved_requests),
      icon: FaCheckCircle,
    },
    {
      key: 'pos-refund-void-pending',
      label: 'Pending',
      value: formatCount(posRefundVoidData.summary.pending_requests),
      icon: FaClock,
    },
    {
      key: 'pos-refund-void-rejected',
      label: 'Rejected',
      value: formatCount(posRefundVoidData.summary.rejected_requests),
      icon: FaTimesCircle,
    },
    {
      key: 'pos-refund-void-amount',
      label: 'Nilai pengajuan',
      value: formatCurrency(posRefundVoidData.summary.total_request_amount),
      icon: FaWallet,
    },
    {
      key: 'pos-refund-void-realized',
      label: 'Refund terealisasi',
      value: formatCurrency(posRefundVoidData.summary.executed_refund_amount),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-refund-void-refund',
      label: 'Refund request',
      value: formatCount(posRefundVoidData.summary.refund_requests),
      icon: FaUndoAlt,
    },
    {
      key: 'pos-refund-void-void',
      label: 'Void request',
      value: formatCount(posRefundVoidData.summary.void_requests),
      icon: FaBan,
    },
  ];

  const posPurchaseSummaryCards = [
    {
      key: 'pos-purchase-documents',
      label: 'Total dokumen purchase',
      value: formatCount(posPurchaseData.summary.total_documents),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-purchase-total',
      label: 'Total pembelian',
      value: formatCurrency(posPurchaseData.summary.total_amount),
      icon: FaWallet,
    },
    {
      key: 'pos-purchase-paid',
      label: 'Sudah dibayar',
      value: formatCurrency(posPurchaseData.summary.paid_amount),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-purchase-outstanding',
      label: 'Outstanding',
      value: formatCurrency(posPurchaseData.summary.outstanding_amount),
      icon: FaReceipt,
    },
    {
      key: 'pos-purchase-average',
      label: 'Rata-rata per dokumen',
      value: formatCurrency(posPurchaseData.summary.average_document),
      icon: FaChartLine,
    },
    {
      key: 'pos-purchase-qty',
      label: 'Qty dibeli',
      value: formatCount(posPurchaseData.summary.total_quantity),
      icon: FaLayerGroup,
    },
    {
      key: 'pos-purchase-vendors',
      label: 'Vendor aktif',
      value: formatCount(posPurchaseData.summary.unique_vendors),
      icon: FaUsers,
    },
    {
      key: 'pos-purchase-credit',
      label: 'Purchase tempo',
      value: formatCount(posPurchaseData.summary.credit_documents),
      icon: FaClipboardCheck,
    },
  ];

  const posVendorAgingSummaryCards = [
    {
      key: 'pos-vendor-aging-bills',
      label: 'Total bill outstanding',
      value: formatCount(posVendorAgingData.summary.total_bills),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-vendor-aging-outstanding',
      label: 'Total outstanding',
      value: formatCurrency(posVendorAgingData.summary.total_outstanding_amount),
      icon: FaWallet,
    },
    {
      key: 'pos-vendor-aging-overdue',
      label: 'Overdue amount',
      value: formatCurrency(posVendorAgingData.summary.overdue_amount),
      icon: FaReceipt,
    },
    {
      key: 'pos-vendor-aging-current',
      label: 'Belum jatuh tempo',
      value: formatCurrency(posVendorAgingData.summary.current_amount),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-vendor-aging-vendors',
      label: 'Vendor aktif',
      value: formatCount(posVendorAgingData.summary.unique_vendors),
      icon: FaUsers,
    },
    {
      key: 'pos-vendor-aging-due-today',
      label: 'Jatuh tempo hari ini',
      value: formatCurrency(posVendorAgingData.summary.due_today_amount),
      icon: FaCalendarAlt,
    },
    {
      key: 'pos-vendor-aging-average-days',
      label: 'Rata-rata umur hutang',
      value: formatDayValue(posVendorAgingData.summary.average_age_days),
      icon: FaChartLine,
    },
    {
      key: 'pos-vendor-aging-max-days',
      label: 'Umur tertinggi',
      value: formatDayValue(posVendorAgingData.summary.max_age_days),
      icon: FaLayerGroup,
    },
  ];

  const posStockMovementSummaryCards = [
    {
      key: 'pos-stock-movement-total',
      label: 'Total movement',
      value: formatCount(posStockMovementData.summary.total_movements),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-stock-movement-in',
      label: 'Qty masuk',
      value: formatCount(posStockMovementData.summary.total_quantity_in),
      icon: FaWallet,
    },
    {
      key: 'pos-stock-movement-out',
      label: 'Qty keluar',
      value: formatCount(posStockMovementData.summary.total_quantity_out),
      icon: FaReceipt,
    },
    {
      key: 'pos-stock-movement-net',
      label: 'Net movement',
      value: formatCount(posStockMovementData.summary.net_quantity),
      icon: FaChartLine,
    },
    {
      key: 'pos-stock-movement-cost-in',
      label: 'Nilai masuk',
      value: formatCurrency(posStockMovementData.summary.total_cost_in),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-stock-movement-cost-out',
      label: 'Nilai keluar',
      value: formatCurrency(posStockMovementData.summary.total_cost_out),
      icon: FaLayerGroup,
    },
    {
      key: 'pos-stock-movement-products',
      label: 'Produk aktif',
      value: formatCount(posStockMovementData.summary.unique_products),
      icon: FaUsers,
    },
    {
      key: 'pos-stock-movement-references',
      label: 'Dokumen sumber',
      value: formatCount(posStockMovementData.summary.unique_references),
      icon: FaCalendarAlt,
    },
  ];

  const posBOMConsumptionSummaryCards = [
    {
      key: 'pos-bom-transactions',
      label: 'Transaksi BOM',
      value: formatCount(posBOMConsumptionData.summary.total_transactions),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-bom-rows',
      label: 'Baris konsumsi',
      value: formatCount(posBOMConsumptionData.summary.total_rows),
      icon: FaLayerGroup,
    },
    {
      key: 'pos-bom-sold-qty',
      label: 'Qty produk jadi',
      value: formatCount(posBOMConsumptionData.summary.total_sold_quantity),
      icon: FaWallet,
    },
    {
      key: 'pos-bom-consumed-qty',
      label: 'Qty bahan keluar',
      value: formatCount(posBOMConsumptionData.summary.total_consumed_quantity),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-bom-cost',
      label: 'Nilai consumption',
      value: formatCurrency(posBOMConsumptionData.summary.total_consumption_cost),
      icon: FaReceipt,
    },
    {
      key: 'pos-bom-average-cost',
      label: 'Avg cost / sale',
      value: formatCurrency(posBOMConsumptionData.summary.average_cost_per_sale),
      icon: FaChartLine,
    },
    {
      key: 'pos-bom-finished-goods',
      label: 'Produk jadi aktif',
      value: formatCount(posBOMConsumptionData.summary.active_finished_goods),
      icon: FaStore,
    },
    {
      key: 'pos-bom-ingredients',
      label: 'Bahan baku aktif',
      value: formatCount(posBOMConsumptionData.summary.active_ingredients),
      icon: FaUsers,
    },
  ];

  const posStockOpnameVarianceSummaryCards = [
    {
      key: 'pos-stock-opname-total',
      label: 'Dokumen opname',
      value: formatCount(posStockOpnameVarianceData.summary.total_opnames),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-stock-opname-items',
      label: 'Item berselisih',
      value: formatCount(posStockOpnameVarianceData.summary.total_variance_items),
      icon: FaLayerGroup,
    },
    {
      key: 'pos-stock-opname-gain',
      label: 'Gain items',
      value: formatCount(posStockOpnameVarianceData.summary.gain_items),
      icon: FaWallet,
    },
    {
      key: 'pos-stock-opname-loss',
      label: 'Loss items',
      value: formatCount(posStockOpnameVarianceData.summary.loss_items),
      icon: FaReceipt,
    },
    {
      key: 'pos-stock-opname-gain-qty',
      label: 'Qty surplus',
      value: formatCount(posStockOpnameVarianceData.summary.total_gain_quantity),
      icon: FaMoneyBillWave,
    },
    {
      key: 'pos-stock-opname-loss-qty',
      label: 'Qty shortage',
      value: formatCount(posStockOpnameVarianceData.summary.total_loss_quantity),
      icon: FaChartLine,
    },
    {
      key: 'pos-stock-opname-cost',
      label: 'Nilai variance',
      value: formatCurrency(posStockOpnameVarianceData.summary.total_variance_cost),
      icon: FaReceipt,
    },
    {
      key: 'pos-stock-opname-products',
      label: 'Produk terdampak',
      value: formatCount(posStockOpnameVarianceData.summary.affected_products),
      icon: FaUsers,
    },
  ];

  const posVendorPriceComparisonSummaryCards = [
    {
      key: 'pos-vendor-price-products',
      label: 'Produk dibandingkan',
      value: formatCount(posVendorPriceComparisonData.summary.products_compared),
      icon: FaClipboardCheck,
    },
    {
      key: 'pos-vendor-price-vendors',
      label: 'Vendor aktif',
      value: formatCount(posVendorPriceComparisonData.summary.active_vendors),
      icon: FaUsers,
    },
    {
      key: 'pos-vendor-price-wins',
      label: 'Cheapest vendor wins',
      value: formatCount(posVendorPriceComparisonData.summary.cheapest_vendor_wins),
      icon: FaLayerGroup,
    },
    {
      key: 'pos-vendor-price-savings',
      label: 'Potential savings',
      value: formatCurrency(posVendorPriceComparisonData.summary.potential_savings),
      icon: FaWallet,
    },
    {
      key: 'pos-vendor-price-single-source',
      label: 'Single-source products',
      value: formatCount(posVendorPriceComparisonData.summary.single_source_products),
      icon: FaReceipt,
    },
    {
      key: 'pos-vendor-price-high-spread',
      label: 'High spread products',
      value: formatCount(posVendorPriceComparisonData.summary.high_spread_products),
      icon: FaChartLine,
    },
  ];

  const accountingJournalSummaryCards = [
    {
      key: 'accounting-journal-total',
      label: 'Total jurnal',
      value: formatCount(accountingJournalData.summary.total_journals),
      icon: FaReceipt,
    },
    {
      key: 'accounting-journal-lines',
      label: 'Total line',
      value: formatCount(accountingJournalData.summary.total_lines),
      icon: FaLayerGroup,
    },
    {
      key: 'accounting-journal-debit',
      label: 'Total debit',
      value: formatCurrency(accountingJournalData.summary.total_debit),
      icon: FaWallet,
    },
    {
      key: 'accounting-journal-credit',
      label: 'Total credit',
      value: formatCurrency(accountingJournalData.summary.total_credit),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-journal-posted',
      label: 'Posted journals',
      value: formatCount(accountingJournalData.summary.posted_journals),
      icon: FaCheckCircle,
    },
    {
      key: 'accounting-journal-pending',
      label: 'Pending journals',
      value: formatCount(accountingJournalData.summary.pending_journals),
      icon: FaClock,
    },
    {
      key: 'accounting-journal-accounts',
      label: 'Akun unik',
      value: formatCount(accountingJournalData.summary.unique_accounts),
      icon: FaClipboardCheck,
    },
    {
      key: 'accounting-journal-outlets',
      label: 'Outlet tercakup',
      value: formatCount(accountingJournalData.summary.unique_outlets),
      icon: FaStore,
    },
  ];

  const accountingGeneralLedgerSummaryCards = [
    {
      key: 'accounting-ledger-total-accounts',
      label: 'Total akun',
      value: formatCount(accountingGeneralLedgerData.summary.total_accounts),
      icon: FaReceipt,
    },
    {
      key: 'accounting-ledger-opening',
      label: 'Akun saldo awal',
      value: formatCount(accountingGeneralLedgerData.summary.accounts_with_opening),
      icon: FaLayerGroup,
    },
    {
      key: 'accounting-ledger-moving',
      label: 'Akun bergerak',
      value: formatCount(accountingGeneralLedgerData.summary.accounts_with_movement),
      icon: FaClipboardCheck,
    },
    {
      key: 'accounting-ledger-entries',
      label: 'Total line',
      value: formatCount(accountingGeneralLedgerData.summary.total_entries),
      icon: FaClock,
    },
    {
      key: 'accounting-ledger-debit',
      label: 'Debit periode',
      value: formatCurrency(accountingGeneralLedgerData.summary.total_debit),
      icon: FaWallet,
    },
    {
      key: 'accounting-ledger-credit',
      label: 'Credit periode',
      value: formatCurrency(accountingGeneralLedgerData.summary.total_credit),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-ledger-sources',
      label: 'Source tercakup',
      value: formatCount(accountingGeneralLedgerData.summary.unique_sources),
      icon: FaCheckCircle,
    },
    {
      key: 'accounting-ledger-outlets',
      label: 'Outlet tercakup',
      value: formatCount(accountingGeneralLedgerData.summary.unique_outlets),
      icon: FaStore,
    },
  ];

  const accountingTrialBalanceSummaryCards = [
    {
      key: 'accounting-trial-total',
      label: 'Total akun',
      value: formatCount(accountingTrialBalanceData.summary.total_accounts),
      icon: FaReceipt,
    },
    {
      key: 'accounting-trial-opening',
      label: 'Akun saldo awal',
      value: formatCount(accountingTrialBalanceData.summary.accounts_with_opening),
      icon: FaLayerGroup,
    },
    {
      key: 'accounting-trial-moving',
      label: 'Akun bergerak',
      value: formatCount(accountingTrialBalanceData.summary.accounts_with_movement),
      icon: FaClipboardCheck,
    },
    {
      key: 'accounting-trial-opening-debit',
      label: 'Opening debit',
      value: formatCurrency(accountingTrialBalanceData.summary.opening_debit),
      icon: FaWallet,
    },
    {
      key: 'accounting-trial-opening-credit',
      label: 'Opening credit',
      value: formatCurrency(accountingTrialBalanceData.summary.opening_credit),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-trial-closing-debit',
      label: 'Closing debit',
      value: formatCurrency(accountingTrialBalanceData.summary.closing_debit),
      icon: FaWallet,
    },
    {
      key: 'accounting-trial-closing-credit',
      label: 'Closing credit',
      value: formatCurrency(accountingTrialBalanceData.summary.closing_credit),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-trial-gap',
      label: 'Selisih TB',
      value: formatCurrency(accountingTrialBalanceData.summary.net_difference),
      icon: FaChartLine,
    },
  ];

  const accountingProfitLossSummaryCards = [
    {
      key: 'accounting-pl-revenue',
      label: 'Total revenue',
      value: formatCurrency(accountingProfitLossData.summary.total_revenue),
      icon: FaWallet,
    },
    {
      key: 'accounting-pl-expenses',
      label: 'Total expenses',
      value: formatCurrency(accountingProfitLossData.summary.total_expenses),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-pl-net',
      label: 'Net profit',
      value: formatCurrency(accountingProfitLossData.summary.net_profit),
      icon: FaChartLine,
    },
    {
      key: 'accounting-pl-accounts',
      label: 'Total akun',
      value: formatCount(accountingProfitLossData.summary.total_accounts),
      icon: FaReceipt,
    },
    {
      key: 'accounting-pl-revenue-accounts',
      label: 'Akun revenue',
      value: formatCount(accountingProfitLossData.summary.revenue_accounts),
      icon: FaCheckCircle,
    },
    {
      key: 'accounting-pl-expense-accounts',
      label: 'Akun expense',
      value: formatCount(accountingProfitLossData.summary.expense_accounts),
      icon: FaClipboardCheck,
    },
    {
      key: 'accounting-pl-moving',
      label: 'Akun bergerak',
      value: formatCount(accountingProfitLossData.summary.accounts_with_movement),
      icon: FaClock,
    },
  ];

  const accountingBalanceSheetSummaryCards = [
    {
      key: 'accounting-bs-assets',
      label: 'Total assets',
      value: formatCurrency(accountingBalanceSheetData.summary.total_assets),
      icon: FaWallet,
    },
    {
      key: 'accounting-bs-liabilities',
      label: 'Total liabilities',
      value: formatCurrency(accountingBalanceSheetData.summary.total_liabilities),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-bs-equity',
      label: 'Total equity',
      value: formatCurrency(accountingBalanceSheetData.summary.total_equity),
      icon: FaChartLine,
    },
    {
      key: 'accounting-bs-le',
      label: 'Liability + equity',
      value: formatCurrency(accountingBalanceSheetData.summary.total_liabilities_and_equity),
      icon: FaLayerGroup,
    },
    {
      key: 'accounting-bs-gap',
      label: 'Balance gap',
      value: formatCurrency(accountingBalanceSheetData.summary.balance_gap),
      icon: FaBan,
    },
    {
      key: 'accounting-bs-accounts',
      label: 'Total akun',
      value: formatCount(accountingBalanceSheetData.summary.total_accounts),
      icon: FaReceipt,
    },
    {
      key: 'accounting-bs-opening',
      label: 'Akun saldo awal',
      value: formatCount(accountingBalanceSheetData.summary.accounts_with_opening),
      icon: FaCheckCircle,
    },
    {
      key: 'accounting-bs-moving',
      label: 'Akun bergerak',
      value: formatCount(accountingBalanceSheetData.summary.accounts_with_movement),
      icon: FaClipboardCheck,
    },
  ];

  const accountingCashFlowSummaryCards = [
    {
      key: 'accounting-cf-opening',
      label: 'Opening cash',
      value: formatCurrency(accountingCashFlowData.summary.opening_cash_balance),
      icon: FaWallet,
    },
    {
      key: 'accounting-cf-inflow',
      label: 'Cash inflow',
      value: formatCurrency(accountingCashFlowData.summary.cash_inflow),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-cf-outflow',
      label: 'Cash outflow',
      value: formatCurrency(accountingCashFlowData.summary.cash_outflow),
      icon: FaReceipt,
    },
    {
      key: 'accounting-cf-net',
      label: 'Net cash flow',
      value: formatCurrency(accountingCashFlowData.summary.net_cash_flow),
      icon: FaChartLine,
    },
    {
      key: 'accounting-cf-closing',
      label: 'Closing cash',
      value: formatCurrency(accountingCashFlowData.summary.closing_cash_balance),
      icon: FaLayerGroup,
    },
    {
      key: 'accounting-cf-operating',
      label: 'Operating',
      value: formatCurrency(accountingCashFlowData.summary.operating_cash_flow),
      icon: FaCheckCircle,
    },
    {
      key: 'accounting-cf-investing',
      label: 'Investing',
      value: formatCurrency(accountingCashFlowData.summary.investing_cash_flow),
      icon: FaClipboardCheck,
    },
    {
      key: 'accounting-cf-financing',
      label: 'Financing',
      value: formatCurrency(accountingCashFlowData.summary.financing_cash_flow),
      icon: FaClock,
    },
    {
      key: 'accounting-cf-journals',
      label: 'Jurnal kas',
      value: formatCount(accountingCashFlowData.summary.journals_with_cash),
      icon: FaStore,
    },
  ];

  const accountingAccountsPayableAgingSummaryCards = [
    {
      key: 'accounting-ap-outstanding',
      label: 'Outstanding AP',
      value: formatCurrency(accountingAccountsPayableAgingData.summary.outstanding_total),
      icon: FaWallet,
    },
    {
      key: 'accounting-ap-overdue',
      label: 'Overdue AP',
      value: formatCurrency(accountingAccountsPayableAgingData.summary.overdue_total),
      icon: FaReceipt,
    },
    {
      key: 'accounting-ap-open-bills',
      label: 'Open bills',
      value: formatCount(accountingAccountsPayableAgingData.summary.open_bills),
      icon: FaLayerGroup,
    },
    {
      key: 'accounting-ap-overdue-bills',
      label: 'Overdue bills',
      value: formatCount(accountingAccountsPayableAgingData.summary.overdue_bills),
      icon: FaClock,
    },
    {
      key: 'accounting-ap-vendors',
      label: 'Vendor aktif',
      value: formatCount(accountingAccountsPayableAgingData.summary.unique_vendors),
      icon: FaUsers,
    },
    {
      key: 'accounting-ap-current',
      label: 'Current bucket',
      value: formatCurrency(accountingAccountsPayableAgingData.summary.current_total),
      icon: FaCheckCircle,
    },
    {
      key: 'accounting-ap-operational',
      label: 'Operational AP',
      value: formatCurrency(accountingAccountsPayableAgingData.summary.operational_outstanding_total),
      icon: FaClipboardCheck,
    },
    {
      key: 'accounting-ap-asset',
      label: 'Asset AP',
      value: formatCurrency(accountingAccountsPayableAgingData.summary.asset_outstanding_total),
      icon: FaBuilding,
    },
  ];

  const accountingExpenseBreakdownSummaryCards = [
    {
      key: 'accounting-exp-total',
      label: 'Total amount',
      value: formatCurrency(accountingExpenseBreakdownData.summary.total_amount),
      icon: FaWallet,
    },
    {
      key: 'accounting-exp-paid',
      label: 'Paid amount',
      value: formatCurrency(accountingExpenseBreakdownData.summary.paid_amount),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-exp-outstanding',
      label: 'Outstanding',
      value: formatCurrency(accountingExpenseBreakdownData.summary.outstanding_amount),
      icon: FaReceipt,
    },
    {
      key: 'accounting-exp-documents',
      label: 'Dokumen',
      value: formatCount(accountingExpenseBreakdownData.summary.total_documents),
      icon: FaLayerGroup,
    },
    {
      key: 'accounting-exp-payable',
      label: 'Payable docs',
      value: formatCount(accountingExpenseBreakdownData.summary.payable_documents),
      icon: FaClock,
    },
    {
      key: 'accounting-exp-categories',
      label: 'Kategori',
      value: formatCount(accountingExpenseBreakdownData.summary.unique_categories),
      icon: FaClipboardCheck,
    },
    {
      key: 'accounting-exp-vendors',
      label: 'Vendor',
      value: formatCount(accountingExpenseBreakdownData.summary.unique_vendors),
      icon: FaUsers,
    },
    {
      key: 'accounting-exp-average',
      label: 'Avg dokumen',
      value: formatCurrency(accountingExpenseBreakdownData.summary.average_document_amount),
      icon: FaChartLine,
    },
  ];

  const accountingAssetRegisterSummaryCards = [
    {
      key: 'accounting-asset-total',
      label: 'Total aset',
      value: formatCount(accountingAssetRegisterData.summary.total_assets),
      icon: FaBuilding,
    },
    {
      key: 'accounting-asset-acquisition',
      label: 'Nilai perolehan',
      value: formatCurrency(accountingAssetRegisterData.summary.total_acquisition_cost),
      icon: FaWallet,
    },
    {
      key: 'accounting-asset-paid',
      label: 'Sudah dibayar',
      value: formatCurrency(accountingAssetRegisterData.summary.paid_amount),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-asset-outstanding',
      label: 'Outstanding',
      value: formatCurrency(accountingAssetRegisterData.summary.outstanding_amount),
      icon: FaReceipt,
    },
    {
      key: 'accounting-asset-average',
      label: 'Avg per aset',
      value: formatCurrency(accountingAssetRegisterData.summary.average_acquisition_cost),
      icon: FaChartLine,
    },
    {
      key: 'accounting-asset-categories',
      label: 'Kategori',
      value: formatCount(accountingAssetRegisterData.summary.unique_categories),
      icon: FaClipboardCheck,
    },
    {
      key: 'accounting-asset-vendors',
      label: 'Vendor',
      value: formatCount(accountingAssetRegisterData.summary.unique_vendors),
      icon: FaUsers,
    },
    {
      key: 'accounting-asset-estimated-depr',
      label: 'Estimasi depr / bulan',
      value: formatCurrency(accountingAssetRegisterData.summary.estimated_monthly_depreciation),
      icon: FaClock,
    },
  ];

  const accountingDepreciationScheduleSummaryCards = [
    {
      key: 'accounting-depr-assets',
      label: 'Aset terjadwal',
      value: formatCount(accountingDepreciationScheduleData.summary.total_assets),
      icon: FaBuilding,
    },
    {
      key: 'accounting-depr-base',
      label: 'Depreciable base',
      value: formatCurrency(accountingDepreciationScheduleData.summary.depreciable_base),
      icon: FaWallet,
    },
    {
      key: 'accounting-depr-monthly',
      label: 'Depr / bulan aktif',
      value: formatCurrency(accountingDepreciationScheduleData.summary.monthly_depreciation),
      icon: FaClock,
    },
    {
      key: 'accounting-depr-period',
      label: 'Depr periode',
      value: formatCurrency(accountingDepreciationScheduleData.summary.period_depreciation),
      icon: FaMoneyBillWave,
    },
    {
      key: 'accounting-depr-accumulated',
      label: 'Akumulasi depr',
      value: formatCurrency(accountingDepreciationScheduleData.summary.accumulated_depreciation),
      icon: FaReceipt,
    },
    {
      key: 'accounting-depr-book-value',
      label: 'Book value',
      value: formatCurrency(accountingDepreciationScheduleData.summary.current_book_value),
      icon: FaLayerGroup,
    },
    {
      key: 'accounting-depr-fully',
      label: 'Fully depreciated',
      value: formatCount(accountingDepreciationScheduleData.summary.fully_depreciated_assets),
      icon: FaCheckCircle,
    },
    {
      key: 'accounting-depr-remaining',
      label: 'Avg sisa umur',
      value: formatMonthValue(accountingDepreciationScheduleData.summary.average_remaining_life_months),
      icon: FaChartLine,
    },
  ];

  const accountingTrialBalanceColumns = [
    {
      name: 'Akun',
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.account_name || row.account_id || '-'}</strong>
          <small>{row.account_id || '-'} • {row.account_type || 'Uncategorized'}</small>
        </span>
      ),
    },
    {
      name: 'Opening',
      sortable: true,
      right: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.opening_balance || 0))}</strong>
          <small>
            D {formatCurrency(Number(row.opening_debit || 0))} • C {formatCurrency(Number(row.opening_credit || 0))}
          </small>
        </span>
      ),
    },
    {
      name: 'Mutasi',
      sortable: true,
      right: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>D {formatCurrency(Number(row.period_debit || 0))}</strong>
          <small>C {formatCurrency(Number(row.period_credit || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Closing',
      sortable: true,
      right: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.closing_balance || 0))}</strong>
          <small>
            D {formatCurrency(Number(row.closing_debit || 0))} • C {formatCurrency(Number(row.closing_credit || 0))}
          </small>
        </span>
      ),
    },
    {
      name: 'Aktivitas',
      sortable: true,
      width: '140px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(Number(row.entry_count || 0))} line</strong>
          <small>{formatDateTime(row.last_occurred_at)}</small>
        </span>
      ),
    },
  ];

  const accountingProfitLossColumns = [
    {
      name: 'Akun',
      sortable: true,
      grow: 1.5,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.account_name || row.account_id || '-'}</strong>
          <small>{row.account_id || '-'} • {row.account_type || 'Uncategorized'}</small>
        </span>
      ),
    },
    {
      name: 'Debit',
      selector: row => row.total_debit,
      sortable: true,
      right: true,
      width: '130px',
      cell: row => <strong>{formatCurrency(Number(row.total_debit || 0))}</strong>,
    },
    {
      name: 'Credit',
      selector: row => row.total_credit,
      sortable: true,
      right: true,
      width: '130px',
      cell: row => <strong>{formatCurrency(Number(row.total_credit || 0))}</strong>,
    },
    {
      name: 'Net amount',
      selector: row => row.net_amount,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(Number(row.net_amount || 0))}</strong>,
    },
    {
      name: 'Aktivitas',
      sortable: true,
      width: '140px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(Number(row.entry_count || 0))} line</strong>
          <small>{formatDateTime(row.last_occurred_at)}</small>
        </span>
      ),
    },
  ];

  const accountingBalanceSheetColumns = [
    {
      name: 'Akun',
      sortable: true,
      grow: 1.5,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.account_name || row.account_id || '-'}</strong>
          <small>{row.account_id || '-'} • {row.account_type || 'Uncategorized'}</small>
        </span>
      ),
    },
    {
      name: 'Opening',
      selector: row => row.opening_balance,
      sortable: true,
      right: true,
      width: '140px',
      cell: row => <strong>{formatCurrency(Number(row.opening_balance || 0))}</strong>,
    },
    {
      name: 'Debit',
      selector: row => row.total_debit,
      sortable: true,
      right: true,
      width: '125px',
      cell: row => formatCurrency(Number(row.total_debit || 0)),
    },
    {
      name: 'Credit',
      selector: row => row.total_credit,
      sortable: true,
      right: true,
      width: '125px',
      cell: row => formatCurrency(Number(row.total_credit || 0)),
    },
    {
      name: 'Closing',
      selector: row => row.closing_balance,
      sortable: true,
      right: true,
      width: '140px',
      cell: row => <strong>{formatCurrency(Number(row.closing_balance || 0))}</strong>,
    },
    {
      name: 'Aktivitas',
      sortable: true,
      width: '140px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(Number(row.entry_count || 0))} line</strong>
          <small>{formatDateTime(row.last_occurred_at)}</small>
        </span>
      ),
    },
  ];

  const accountingCashFlowTrendColumns = [
    {
      name: 'Tanggal',
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || row.period_key || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Jurnal',
      selector: row => row.journal_count,
      sortable: true,
      right: true,
      width: '110px',
      cell: row => <strong>{formatCount(Number(row.journal_count || 0))}</strong>,
    },
    {
      name: 'Inflow',
      selector: row => row.inflow_amount,
      sortable: true,
      right: true,
      width: '140px',
      cell: row => <strong>{formatCurrency(Number(row.inflow_amount || 0))}</strong>,
    },
    {
      name: 'Outflow',
      selector: row => row.outflow_amount,
      sortable: true,
      right: true,
      width: '140px',
      cell: row => <strong>{formatCurrency(Number(row.outflow_amount || 0))}</strong>,
    },
    {
      name: 'Net flow',
      selector: row => row.net_cash_flow,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(Number(row.net_cash_flow || 0))}</strong>,
    },
  ];

  const accountingCashFlowColumns = [
    {
      name: 'Jurnal',
      sortable: true,
      grow: 1.3,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatAccountingJournalNumber(row.journal_id, row.occurred_at)}</strong>
          <small>{formatDateTime(row.occurred_at)} • {row.outlet_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Cash account',
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.cash_account_name || row.cash_account_id || '-'}</strong>
          <small>{row.cash_account_id || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Counter account',
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.counter_account_name || row.counter_account_id || '-'}</strong>
          <small>{row.counter_account_id || '-'} • {row.counter_account_type || 'Uncategorized'}</small>
          {row.description ? <small>{row.description}</small> : null}
        </span>
      ),
    },
    {
      name: 'Section',
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.section_label || '-'}</strong>
          <small>{row.direction || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Inflow',
      selector: row => row.inflow_amount,
      sortable: true,
      right: true,
      width: '130px',
      cell: row => formatCurrency(Number(row.inflow_amount || 0)),
    },
    {
      name: 'Outflow',
      selector: row => row.outflow_amount,
      sortable: true,
      right: true,
      width: '130px',
      cell: row => formatCurrency(Number(row.outflow_amount || 0)),
    },
    {
      name: 'Net flow',
      selector: row => row.net_cash_flow,
      sortable: true,
      right: true,
      width: '140px',
      cell: row => <strong>{formatCurrency(Number(row.net_cash_flow || 0))}</strong>,
    },
  ];

  const accountingAccountsPayableAgingTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Bill',
      selector: row => row.bill_count,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.bill_count)}</strong>,
    },
    {
      name: 'Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(Number(row.outstanding_amount || 0))}</strong>,
    },
    {
      name: 'Overdue',
      selector: row => row.overdue_amount,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(Number(row.overdue_amount || 0))}</strong>,
    },
  ];

  const accountingAccountsPayableAgingColumns = [
    {
      name: 'Dokumen',
      sortable: true,
      grow: 1.25,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.document_no || '-'}</strong>
          <small>{formatDateTime(row.expense_date)} • {row.outlet_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Vendor / Scope',
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.vendor_name || '-'}</strong>
          <small>{row.document_scope_label || formatAccountingFinanceDocumentScopeLabel(row.document_scope)} • {row.document_type_label || row.document_type || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Due / Aging',
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatDateTime(row.due_date)}</strong>
          <small>{row.aging_bucket_label || formatAccountingFinanceAgingBucketLabel(row.aging_bucket)} • {row.days_overdue > 0 ? `${formatCount(row.days_overdue)} hari overdue` : row.due_status_label || formatAccountingFinanceDueStatusLabel(row.due_status)}</small>
        </span>
      ),
    },
    {
      name: 'Kategori / Status',
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.category || '-'}</strong>
          <small>{row.status_label || formatAccountingFinanceStatusLabel(row.status)}</small>
        </span>
      ),
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      right: true,
      width: '135px',
      cell: row => formatCurrency(Number(row.amount || 0)),
    },
    {
      name: 'Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      right: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(Number(row.outstanding_amount || 0))}</strong>,
    },
    {
      name: 'Akun hutang',
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.payable_account_name || row.payable_account_id || '-'}</strong>
          <small>{formatAccountingPostingStatusLabel(row.accounting_status)}</small>
        </span>
      ),
    },
  ];

  const accountingExpenseBreakdownTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Dokumen',
      selector: row => row.document_count,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.document_count)}</strong>,
    },
    {
      name: 'Total',
      selector: row => row.total_amount,
      sortable: true,
      right: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(Number(row.total_amount || 0))}</strong>,
    },
    {
      name: 'Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      right: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(Number(row.outstanding_amount || 0))}</strong>,
    },
  ];

  const accountingExpenseBreakdownColumns = [
    {
      name: 'Dokumen',
      sortable: true,
      grow: 1.25,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.document_no || '-'}</strong>
          <small>{formatDateTime(row.expense_date)} • {row.outlet_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Kategori / Vendor',
      sortable: true,
      grow: 1.25,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.category || '-'}</strong>
          <small>{row.vendor_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Scope / Type',
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.document_scope_label || formatAccountingFinanceDocumentScopeLabel(row.document_scope)}</strong>
          <small>{row.document_type_label || row.document_type || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      right: true,
      width: '130px',
      cell: row => <strong>{formatCurrency(Number(row.amount || 0))}</strong>,
    },
    {
      name: 'Paid / Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>Paid {formatCurrency(Number(row.paid_amount || 0))}</strong>
          <small>OS {formatCurrency(Number(row.outstanding_amount || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Akun expense',
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.expense_account_name || row.expense_account_id || '-'}</strong>
          <small>{row.status_label || formatAccountingFinanceStatusLabel(row.status)} • {formatAccountingPostingStatusLabel(row.accounting_status)}</small>
        </span>
      ),
    },
  ];

  const accountingAssetRegisterTrendColumns = [
    {
      name: 'Periode',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Aset',
      selector: row => row.asset_count,
      sortable: true,
      width: '110px',
      cell: row => <strong>{formatCount(row.asset_count)}</strong>,
    },
    {
      name: 'Acquisition',
      selector: row => row.acquisition_cost,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(Number(row.acquisition_cost || 0))}</strong>,
    },
    {
      name: 'Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      right: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(Number(row.outstanding_amount || 0))}</strong>,
    },
  ];

  const accountingAssetRegisterColumns = [
    {
      name: 'Aset',
      sortable: true,
      grow: 1.3,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.description || row.document_no || '-'}</strong>
          <small>{row.document_no || '-'} • {formatDateTime(row.expense_date)}</small>
        </span>
      ),
    },
    {
      name: 'Qty',
      selector: row => Number(row.quantity || 1),
      sortable: true,
      width: '90px',
      cell: row => <strong>{formatCount(Number(row.quantity || 1))}</strong>,
    },
    {
      name: 'Kategori / Vendor',
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.category_label || formatAccountingAssetCategoryLabel(row.category)}</strong>
          <small>{row.vendor_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Akun aset',
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.asset_account_name || row.asset_account_id || '-'}</strong>
          <small>{row.status_label || formatAccountingFinanceStatusLabel(row.status)}</small>
        </span>
      ),
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      right: true,
      width: '135px',
      cell: row => <strong>{formatCurrency(Number(row.amount || 0))}</strong>,
    },
    {
      name: 'Paid / OS',
      selector: row => row.outstanding_amount,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>Paid {formatCurrency(Number(row.paid_amount || 0))}</strong>
          <small>OS {formatCurrency(Number(row.outstanding_amount || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Useful life',
      sortable: true,
      width: '160px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.useful_life_label || '-'}</strong>
          <small>{row.depreciation_method_label || formatAccountingDepreciationMethodLabel(row.depreciation_method)}</small>
        </span>
      ),
    },
    {
      name: 'Estimasi book value',
      selector: row => row.estimated_book_value,
      sortable: true,
      right: true,
      width: '165px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.estimated_book_value || 0))}</strong>
          <small>Dep / bln {formatCurrency(Number(row.estimated_monthly_depreciation || 0))}</small>
        </span>
      ),
    },
  ];

  const accountingDepreciationScheduleTrendColumns = [
    {
      name: 'Periode',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Aset',
      selector: row => row.asset_count,
      sortable: true,
      width: '110px',
      cell: row => <strong>{formatCount(row.asset_count)}</strong>,
    },
    {
      name: 'Depreciation',
      selector: row => row.depreciation_amount,
      sortable: true,
      right: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(Number(row.depreciation_amount || 0))}</strong>,
    },
    {
      name: 'Accumulated',
      selector: row => row.accumulated_depreciation,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(Number(row.accumulated_depreciation || 0))}</strong>,
    },
    {
      name: 'Ending book',
      selector: row => row.ending_book_value,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(Number(row.ending_book_value || 0))}</strong>,
    },
  ];

  const accountingDepreciationScheduleColumns = [
    {
      name: 'Aset',
      sortable: true,
      grow: 1.3,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.description || row.document_no || '-'}</strong>
          <small>{row.document_no || '-'} • {formatDateTime(row.expense_date)}</small>
        </span>
      ),
    },
    {
      name: 'Qty',
      selector: row => Number(row.quantity || 1),
      sortable: true,
      width: '90px',
      cell: row => <strong>{formatCount(Number(row.quantity || 1))}</strong>,
    },
    {
      name: 'Kategori / Vendor',
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.category_label || formatAccountingAssetCategoryLabel(row.category)}</strong>
          <small>{row.vendor_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Useful life',
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.useful_life_label || '-'}</strong>
          <small>{formatCount(Number(row.months_elapsed || 0))} terpakai • {formatCount(Number(row.months_remaining || 0))} sisa</small>
        </span>
      ),
    },
    {
      name: 'Periode susut',
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatDateTime(row.depreciation_start_date)}</strong>
          <small>s/d {formatDateTime(row.depreciation_end_date)}</small>
        </span>
      ),
    },
    {
      name: 'Depreciation',
      selector: row => row.period_depreciation,
      sortable: true,
      width: '175px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.period_depreciation || 0))}</strong>
          <small>Per bulan {formatCurrency(Number(row.monthly_depreciation || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Accum / Book value',
      selector: row => row.current_book_value,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>BV {formatCurrency(Number(row.current_book_value || 0))}</strong>
          <small>Acc {formatCurrency(Number(row.accumulated_depreciation || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      sortable: true,
      width: '140px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.fully_depreciated ? 'Fully depreciated' : 'Masih berjalan'}</strong>
          <small>{row.asset_account_name || row.asset_account_id || '-'}</small>
        </span>
      ),
    },
  ];

  const posVendorPriceGapColumns = [
    {
      name: 'Product',
      selector: row => row.product_name,
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.product_name || '-'}</strong>
          <small>{row.category_name || 'Tanpa kategori'} • {formatProductItemTypeLabel(row.item_type)}</small>
        </span>
      ),
    },
    {
      name: 'Preferred vendor',
      selector: row => row.preferred_vendor,
      sortable: true,
      grow: 1.1,
    },
    {
      name: 'Preferred price',
      selector: row => row.preferred_price,
      sortable: true,
      right: true,
      cell: row => formatCurrency(row.preferred_price),
    },
    {
      name: 'Second vendor',
      selector: row => row.second_vendor,
      sortable: true,
      grow: 1.1,
      cell: row => row.second_vendor || '-',
    },
    {
      name: 'Spread',
      selector: row => row.price_spread,
      sortable: true,
      right: true,
      cell: row => formatCurrency(row.price_spread),
    },
    {
      name: 'Spread %',
      selector: row => row.spread_percentage,
      sortable: true,
      right: true,
      cell: row => `${Number(row.spread_percentage || 0).toFixed(2)}%`,
    },
    {
      name: 'Potential savings',
      selector: row => row.potential_savings,
      sortable: true,
      right: true,
      cell: row => formatCurrency(row.potential_savings),
    },
  ];

  const posVendorPriceComparisonColumns = [
    {
      name: 'Product',
      selector: row => row.product_name,
      sortable: true,
      grow: 1.5,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.product_name || '-'}</strong>
          <small>{row.category_name || 'Tanpa kategori'} • {row.unit || '-'} • {formatProductItemTypeLabel(row.item_type)}</small>
        </span>
      ),
    },
    {
      name: 'Preferred vendor',
      selector: row => row.preferred_vendor,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.preferred_vendor || '-'}</strong>
          <small>{formatCurrency(row.preferred_price)}</small>
        </span>
      ),
    },
    {
      name: 'Second vendor',
      selector: row => row.second_vendor,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.second_vendor || '-'}</strong>
          <small>{row.second_vendor ? formatCurrency(row.second_price) : 'Tidak ada pembanding'}</small>
        </span>
      ),
    },
    {
      name: 'Market reference',
      selector: row => row.average_market_price,
      sortable: true,
      right: true,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.average_market_price)}</strong>
          <small>Last: {formatCurrency(row.last_purchased_price)}</small>
        </span>
      ),
    },
    {
      name: 'Spread',
      selector: row => row.spread_percentage,
      sortable: true,
      right: true,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.price_spread)}</strong>
          <small>{Number(row.spread_percentage || 0).toFixed(2)}%</small>
        </span>
      ),
    },
    {
      name: 'Volume',
      selector: row => row.total_quantity_bought,
      sortable: true,
      right: true,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.total_quantity_bought)}</strong>
          <small>{formatCount(row.purchase_count)} transaksi beli</small>
        </span>
      ),
    },
    {
      name: 'Savings',
      selector: row => row.potential_savings,
      sortable: true,
      right: true,
      cell: row => formatCurrency(row.potential_savings),
    },
    {
      name: 'Risk',
      selector: row => row.risk_label,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-transaction-pill">
          {row.risk_label || formatVendorPriceRiskLabel(row.risk_flag)}
        </span>
      ),
    },
  ];

  const posVendorPriceVendorColumns = [
    {
      name: 'Product',
      selector: row => row.product_name,
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.product_name || '-'}</strong>
          <small>{row.category_name || 'Tanpa kategori'} • {formatProductItemTypeLabel(row.item_type)}</small>
        </span>
      ),
    },
    {
      name: 'Vendor',
      selector: row => row.vendor_name,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.vendor_name || '-'}</strong>
          <small>Rank #{row.rank || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Comparison price',
      selector: row => row.comparison_price,
      sortable: true,
      right: true,
      cell: row => formatCurrency(row.comparison_price),
    },
    {
      name: 'Price range',
      selector: row => row.lowest_price,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.lowest_price)} - {formatCurrency(row.highest_price)}</strong>
          <small>Latest: {formatCurrency(row.latest_price)}</small>
        </span>
      ),
    },
    {
      name: 'Volume',
      selector: row => row.total_quantity,
      sortable: true,
      right: true,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.total_quantity)}</strong>
          <small>{formatCount(row.purchase_count)} transaksi</small>
        </span>
      ),
    },
    {
      name: 'Coverage',
      selector: row => row.outlet_coverage_count,
      sortable: true,
      right: true,
      cell: row => formatCount(row.outlet_coverage_count),
    },
    {
      name: 'Payment mix',
      selector: row => row.payment_mix_label,
      sortable: true,
      grow: 1.1,
      cell: row => row.payment_mix_label || '-',
    },
  ];

  const accountingJournalTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Jurnal',
      selector: row => row.journal_count,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.journal_count)}</strong>,
    },
    {
      name: 'Debit',
      selector: row => row.total_debit,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_debit)}</strong>,
    },
    {
      name: 'Credit',
      selector: row => row.total_credit,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_credit)}</strong>,
    },
  ];

  const accountingJournalAccountColumns = [
    {
      name: 'Akun',
      selector: row => row.label,
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.label || row.key || '-'}</strong>
          <small>{row.key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Kategori',
      selector: row => row.category,
      sortable: true,
      width: '180px',
      cell: row => row.category || 'Uncategorized',
    },
    {
      name: 'Jurnal',
      selector: row => row.count,
      sortable: true,
      width: '110px',
      cell: row => <strong>{formatCount(row.count)}</strong>,
    },
    {
      name: 'Debit',
      selector: row => row.debit,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.debit)}</strong>,
    },
    {
      name: 'Credit',
      selector: row => row.credit,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.credit)}</strong>,
    },
    {
      name: 'Net',
      selector: row => row.net_movement,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.net_movement)}</strong>,
    },
  ];

  const accountingJournalColumns = [
    {
      name: 'Journal Number',
      selector: row => row.occurred_at,
      sortable: true,
      width: '220px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatAccountingJournalNumber(row.id, row.occurred_at)}</strong>
          <small>{formatDateTime(row.occurred_at)}</small>
        </span>
      ),
    },
    {
      name: 'Keterangan',
      selector: row => row.description,
      sortable: true,
      grow: 1.55,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.description || '-'}</strong>
          <small>{formatCount(row.line_count)} line jurnal</small>
        </span>
      ),
    },
    {
      name: 'Reference / Invoice',
      selector: row => row.external_reference || row.source_id,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.external_reference || row.source_id || '-'}</strong>
          <small>{formatAccountingSourceSystemLabel(row.source_system)} • {formatAccountingSourceTypeLabel(row.source_type)}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Type',
      selector: row => row.posting_status,
      sortable: true,
      width: '220px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{formatAccountingBusinessUnitLabel(row.business_unit)} • {formatAccountingSourceTypeLabel(row.source_type)}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      selector: row => row.posting_status,
      sortable: true,
      width: '125px',
      cell: row => formatAccountingPostingStatusLabel(row.posting_status),
    },
    {
      name: 'Debit',
      selector: row => row.total_debit,
      sortable: true,
      width: '150px',
      cell: row => (
        <strong>{formatCurrency(row.total_debit)}</strong>
      ),
    },
    {
      name: 'Kredit',
      selector: row => row.total_credit,
      sortable: true,
      width: '150px',
      cell: row => (
        <strong>{formatCurrency(row.total_credit)}</strong>
      ),
    },
  ];

  const accountingJournalLineColumns = [
    {
      name: 'COA',
      selector: row => row.account_id,
      sortable: true,
      width: '130px',
      cell: row => (
        <span className="report-center-table__muted">{row.account_id || '-'}</span>
      ),
    },
    {
      name: 'Account / Description',
      selector: row => row.account_name || row.account_id,
      sortable: true,
      grow: 1.8,
      cell: row => (
        <span className={`report-center-table__identity report-center-table__journal-line ${getAccountingLineSideKey(row) === 'credit' ? 'is-credit' : ''}`}>
          <strong>{row.account_name || row.account_id || '-'}</strong>
          <small>COA {row.account_id || '-'} • {formatAccountingLineSideLabel(row)} • {row.account_type || 'Uncategorized'}</small>
        </span>
      ),
    },
    {
      name: 'Debit',
      selector: row => row.debit,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{Number(row.debit || 0) > 0 ? formatCurrency(row.debit) : '-'}</strong>,
    },
    {
      name: 'Kredit',
      selector: row => row.credit,
      sortable: true,
      right: true,
      width: '150px',
      cell: row => <strong>{Number(row.credit || 0) > 0 ? formatCurrency(row.credit) : '-'}</strong>,
    },
  ];

  const accountingGeneralLedgerTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Line',
      selector: row => row.entry_count,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.entry_count)}</strong>,
    },
    {
      name: 'Debit',
      selector: row => row.total_debit,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_debit)}</strong>,
    },
    {
      name: 'Credit',
      selector: row => row.total_credit,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_credit)}</strong>,
    },
  ];

  const accountingGeneralLedgerAccountColumns = [
    {
      name: 'Akun',
      selector: row => row.account_name || row.account_id,
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.account_name || row.account_id || '-'}</strong>
          <small>{row.account_id || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Kategori',
      selector: row => row.account_type,
      sortable: true,
      width: '160px',
      cell: row => row.account_type || 'Uncategorized',
    },
    {
      name: 'Saldo awal',
      selector: row => row.opening_balance,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.opening_balance)}</strong>,
    },
    {
      name: 'Debit',
      selector: row => row.total_debit,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.total_debit)}</strong>,
    },
    {
      name: 'Credit',
      selector: row => row.total_credit,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.total_credit)}</strong>,
    },
    {
      name: 'Saldo akhir',
      selector: row => row.closing_balance,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.closing_balance)}</strong>,
    },
    {
      name: 'Line',
      selector: row => row.entry_count,
      sortable: true,
      width: '100px',
      cell: row => <strong>{formatCount(row.entry_count)}</strong>,
    },
    {
      name: 'Aktivitas terakhir',
      selector: row => row.last_occurred_at,
      sortable: true,
      width: '180px',
      cell: row => <span className="report-center-table__muted">{formatDateTime(row.last_occurred_at)}</span>,
    },
  ];

  const accountingGeneralLedgerEntryColumns = [
    {
      name: 'Journal Number',
      selector: row => row.occurred_at,
      sortable: true,
      width: '210px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatAccountingJournalNumber(row.journal_id, row.occurred_at)}</strong>
          <small>{formatDateTime(row.occurred_at)}</small>
        </span>
      ),
    },
    {
      name: 'Deskripsi',
      selector: row => row.description,
      sortable: true,
      grow: 1.6,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.description || '-'}</strong>
          <small>{row.external_reference || row.source_id || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Type',
      selector: row => row.outlet_name,
      sortable: true,
      width: '210px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{formatAccountingSourceSystemLabel(row.source_system)} • {formatAccountingSourceTypeLabel(row.source_type)}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      selector: row => row.posting_status,
      sortable: true,
      width: '120px',
      cell: row => formatAccountingPostingStatusLabel(row.posting_status),
    },
    {
      name: 'Debit',
      selector: row => row.debit,
      sortable: true,
      width: '140px',
      cell: row => <strong>{Number(row.debit || 0) > 0 ? formatCurrency(row.debit) : '-'}</strong>,
    },
    {
      name: 'Credit',
      selector: row => row.credit,
      sortable: true,
      width: '140px',
      cell: row => <strong>{Number(row.credit || 0) > 0 ? formatCurrency(row.credit) : '-'}</strong>,
    },
    {
      name: 'Saldo',
      selector: row => row.running_balance,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.running_balance)}</strong>,
    },
  ];

  const detailColumns = [
    {
      name: 'Tanggal',
      selector: row => row.created_at,
      sortable: true,
      width: '165px',
      cell: row => <span className="report-center-table__muted">{formatDateTime(row.created_at)}</span>,
    },
    {
      name: 'Member',
      selector: row => row.member_name,
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.member_name || '-'}</strong>
          <small>Member #{row.member_id || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Paket',
      selector: row => row.membership_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.membership_name || 'Tanpa paket'}</strong>
          <small>{row.outlet || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Jenis',
      selector: row => row.transaction_label,
      sortable: true,
      width: '160px',
      cell: row => (
        <span className="report-center-transaction-pill">
          {row.transaction_label || 'Membership Sale'}
        </span>
      ),
    },
    {
      name: 'Payment',
      selector: row => row.payment_method_label,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.payment_method_label || 'Other'}</strong>
          <small>{row.payment_method || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status_label,
      sortable: true,
      width: '130px',
      cell: row => (
        <span className={`report-center-status ${getStatusVariantClass(row.status)}`}>
          {row.status_label || 'Pending'}
        </span>
      ),
    },
    {
      name: 'Diproses oleh',
      selector: row => row.processed_by_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.processed_by_name || 'System'}</strong>
          <small>{row.processed_by_actor_type || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Nilai',
      selector: row => row.amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.amount)}</strong>,
    },
  ];

  const trendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label}</strong>
          <small>{row.period_key}</small>
        </span>
      ),
    },
    {
      name: 'Transaksi',
      selector: row => row.total_transactions,
      sortable: true,
    },
    {
      name: 'Revenue',
      selector: row => row.total_amount,
      sortable: true,
      cell: row => <strong>{formatCurrency(row.total_amount)}</strong>,
    },
    {
      name: 'Success',
      selector: row => row.success_count,
      sortable: true,
    },
    {
      name: 'Pending',
      selector: row => row.pending_count,
      sortable: true,
    },
    {
      name: 'Failed',
      selector: row => row.failed_count,
      sortable: true,
    },
  ];

  const staffTransactionTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Membership',
      selector: row => row.membership_transaction_count,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(Number(row.membership_transaction_count || 0))} trx</strong>
          <small>{formatCurrency(Number(row.membership_sales_amount || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Class Sale',
      selector: row => row.class_transaction_count,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(Number(row.class_transaction_count || 0))} trx</strong>
          <small>{formatCurrency(Number(row.class_sales_amount || 0))}</small>
        </span>
      ),
    },
    {
      name: 'PT Sales',
      selector: row => row.pt_transaction_count,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(Number(row.pt_transaction_count || 0))} trx</strong>
          <small>{formatCurrency(Number(row.pt_sales_amount || 0))}</small>
        </span>
      ),
    },
    {
      name: 'POS Sales',
      selector: row => row.pos_sales_amount,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(Number(row.pos_transaction_count || 0))} trx</strong>
          <small>{formatCurrency(Number(row.pos_sales_amount || 0))} • Closing {formatCurrency(Number(row.pos_closing_shift_sales_amount || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Gabungan',
      selector: row => row.total_sales_amount,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.total_sales_amount || 0))}</strong>
          <small>{formatCount(Number(row.total_transaction_count || 0))} transaksi</small>
        </span>
      ),
    },
  ];

  const staffTransactionColumns = [
    {
      name: 'Staff',
      selector: row => row.staff_name,
      sortable: true,
      grow: 1.35,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.staff_name || '-'}</strong>
          <small>{row.username || '-'} • Staff #{row.staff_id || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Jabatan',
      selector: row => row.outlet_name,
      sortable: true,
      grow: 1.25,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{row.job_title || 'Tanpa jabatan'}</small>
        </span>
      ),
    },
    {
      name: 'Membership',
      selector: row => row.membership_sales_amount,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.membership_sales_amount || 0))}</strong>
          <small>{formatCount(Number(row.membership_transaction_count || 0))} trx</small>
        </span>
      ),
    },
    {
      name: 'Class Sale',
      selector: row => row.class_sales_amount,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.class_sales_amount || 0))}</strong>
          <small>{formatCount(Number(row.class_transaction_count || 0))} trx</small>
        </span>
      ),
    },
    {
      name: 'PT Sales',
      selector: row => row.pt_sales_amount,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.pt_sales_amount || 0))}</strong>
          <small>{formatCount(Number(row.pt_transaction_count || 0))} trx</small>
        </span>
      ),
    },
    {
      name: 'POS Sales',
      selector: row => row.pos_sales_amount,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.pos_sales_amount || 0))}</strong>
          <small>{formatCount(Number(row.pos_transaction_count || 0))} trx • Closing {formatCurrency(Number(row.pos_closing_shift_sales_amount || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Sales Mix',
      selector: row => row.sales_mix_label,
      sortable: true,
      width: '155px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.sales_mix_label || '-'}</strong>
          <small>{formatCount(Number(row.active_days || 0))} hari aktif</small>
        </span>
      ),
    },
    {
      name: 'Total',
      selector: row => row.total_sales_amount,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(Number(row.total_sales_amount || 0))}</strong>
          <small>{formatCount(Number(row.total_transaction_count || 0))} trx • Avg {formatCurrency(Number(row.average_ticket || 0))}</small>
        </span>
      ),
    },
    {
      name: 'Aktivitas terakhir',
      selector: row => row.last_transaction_at,
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatDateTime(row.last_transaction_at)}</strong>
          <small>Mulai {formatDateTime(row.first_transaction_at)}</small>
        </span>
      ),
    },
  ];

  const posSalesTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Transaksi',
      selector: row => row.total_transactions,
      sortable: true,
      width: '130px',
      cell: row => <strong>{formatCount(row.total_transactions)}</strong>,
    },
    {
      name: 'Item Qty',
      selector: row => row.total_item_quantity,
      sortable: true,
      width: '130px',
      cell: row => <strong>{formatCount(row.total_item_quantity)}</strong>,
    },
    {
      name: 'Sales',
      selector: row => row.total_amount,
      sortable: true,
      width: '160px',
      cell: row => <strong>{formatCurrency(row.total_amount)}</strong>,
    },
  ];

  const posSalesColumns = [
    {
      name: 'Transaksi / Tanggal',
      selector: row => row.created_at,
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>#{row.id || '-'}</strong>
          <small>{formatDateTime(row.created_at)}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Cashier',
      selector: row => row.outlet_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{formatCashierLabel(row)}</small>
        </span>
      ),
    },
    {
      name: 'Payment',
      selector: row => row.payment_method,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-transaction-pill">
          {row.payment_method || 'Other'}
        </span>
      ),
    },
    {
      name: 'Items',
      selector: row => row.item_quantity,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.item_quantity)} qty</strong>
          <small>{formatCount(row.item_count)} baris</small>
        </span>
      ),
    },
    {
      name: 'Produk',
      selector: row => (Array.isArray(row.product_names) ? row.product_names.join(', ') : ''),
      sortable: false,
      grow: 1.6,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{Array.isArray(row.product_names) && row.product_names.length ? row.product_names[0] : 'Produk tidak tercatat'}</strong>
          <small>
            {Array.isArray(row.product_names) && row.product_names.length > 1
              ? `${row.product_names.length} produk`
              : row.note || '-'}
          </small>
        </span>
      ),
    },
    {
      name: 'Charges',
      selector: row => row.tax,
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>Svc {formatCurrency(getPOSTransactionServiceAmount(row))} • Tax {formatCurrency(row.tax)}</strong>
          <small>Disc {formatCurrency(row.discount)}</small>
        </span>
      ),
    },
    {
      name: 'Total',
      selector: row => getPOSTransactionFinalTotal(row),
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(getPOSTransactionFinalTotal(row))}</strong>,
    },
    {
      name: 'Sync',
      selector: row => row.accounting_sync_status,
      sortable: true,
      width: '145px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.accounting_sync_status || '-'}</strong>
          <small>{row.accounting_synced_at ? formatDateTime(row.accounting_synced_at) : '-'}</small>
        </span>
      ),
    },
  ];

  const posCashierSalesTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Kasir aktif',
      selector: row => row.active_cashiers,
      sortable: true,
      width: '130px',
      cell: row => <strong>{formatCount(row.active_cashiers)}</strong>,
    },
    {
      name: 'Transaksi',
      selector: row => row.total_transactions,
      sortable: true,
      width: '130px',
      cell: row => <strong>{formatCount(row.total_transactions)}</strong>,
    },
    {
      name: 'Qty',
      selector: row => row.total_item_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_item_quantity)}</strong>,
    },
    {
      name: 'Sales',
      selector: row => row.total_amount,
      sortable: true,
      width: '160px',
      cell: row => <strong>{formatCurrency(row.total_amount)}</strong>,
    },
    {
      name: 'Avg ticket',
      selector: row => row.average_ticket,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.average_ticket)}</strong>,
    },
  ];

  const posCashierSalesColumns = [
    {
      name: 'Kasir',
      selector: row => row.cashier_name,
      sortable: true,
      grow: 1.25,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.cashier_name || '-'}</strong>
          <small>{formatCount(row.transaction_count)} transaksi • {formatCount(row.active_days)} hari aktif</small>
        </span>
      ),
    },
    {
      name: 'Sales / Avg ticket',
      selector: row => row.gross_sales,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.gross_sales)}</strong>
          <small>Avg {formatCurrency(row.average_ticket)}</small>
        </span>
      ),
    },
    {
      name: 'Qty / Charges',
      selector: row => row.item_quantity,
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.item_quantity)} qty</strong>
          <small>Svc {formatCurrency(row.total_service)} • Disc {formatCurrency(row.total_discount)} • Tax {formatCurrency(row.total_tax)}</small>
        </span>
      ),
    },
    {
      name: 'Payment dominan',
      selector: row => row.top_payment_method,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.top_payment_method || '-'}</strong>
          <small>{formatCount(row.payment_method_count)} metode • {Number(row.top_payment_share || 0).toFixed(1)}%</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Aktivitas',
      selector: row => row.last_sale_at,
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.dominant_outlet || '-'}</strong>
          <small>{row.dominant_time_slot || '-'} • {formatDateTime(row.last_sale_at)}</small>
        </span>
      ),
    },
  ];

  const posProductSalesTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Produk',
      selector: row => row.unique_products,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.unique_products)}</strong>,
    },
    {
      name: 'Transaksi',
      selector: row => row.total_transactions,
      sortable: true,
      width: '130px',
      cell: row => <strong>{formatCount(row.total_transactions)}</strong>,
    },
    {
      name: 'Qty',
      selector: row => row.total_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_quantity)}</strong>,
    },
    {
      name: 'Sales',
      selector: row => row.total_sales,
      sortable: true,
      width: '160px',
      cell: row => <strong>{formatCurrency(row.total_sales)}</strong>,
    },
  ];

  const posProductSalesColumns = [
    {
      name: 'Produk',
      selector: row => row.product_name,
      sortable: true,
      grow: 1.35,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.product_name || '-'}</strong>
          <small>{formatProductItemTypeLabel(row.item_type)} • {row.unit || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Qty / Sales',
      selector: row => row.gross_sales,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.quantity_sold)} qty</strong>
          <small>{formatCurrency(row.gross_sales)}</small>
        </span>
      ),
    },
    {
      name: 'Avg Price',
      selector: row => row.average_selling_price,
      sortable: true,
      width: '145px',
      cell: row => <strong>{formatCurrency(row.average_selling_price)}</strong>,
    },
    {
      name: 'Coverage',
      selector: row => row.transaction_count,
      sortable: true,
      width: '160px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.transaction_count)} transaksi</strong>
          <small>{formatCount(row.outlet_count)} outlet • {formatCount(row.cashier_count)} kasir</small>
        </span>
      ),
    },
    {
      name: 'Payment / Last sold',
      selector: row => row.last_sold_at,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.top_payment_method || '-'}</strong>
          <small>{formatDateTime(row.last_sold_at)}</small>
        </span>
      ),
    },
  ];

  const posRefundVoidTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Request',
      selector: row => row.total_requests,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_requests)}</strong>,
    },
    {
      name: 'Approved',
      selector: row => row.approved_requests,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.approved_requests)}</strong>,
    },
    {
      name: 'Pending',
      selector: row => row.pending_requests,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.pending_requests)}</strong>,
    },
    {
      name: 'Rejected',
      selector: row => row.rejected_requests,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.rejected_requests)}</strong>,
    },
    {
      name: 'Exposure',
      selector: row => row.request_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.request_amount)}</strong>,
    },
    {
      name: 'Refund realized',
      selector: row => row.refund_amount,
      sortable: true,
      width: '160px',
      cell: row => <strong>{formatCurrency(row.refund_amount)}</strong>,
    },
  ];

  const posRefundVoidColumns = [
    {
      name: 'Request / Tanggal',
      selector: row => row.requested_at,
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{`${formatPOSApprovalRequestTypeLabel(row.request_type)} #${row.id || '-'}`}</strong>
          <small>{formatDateTime(row.requested_at)}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatPOSApprovalStatusLabel(row.status)}</strong>
          <small>{formatPOSTransactionDocumentStatusLabel(row.transaction_document_status)}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Transaksi',
      selector: row => row.outlet_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{`TXN #${row.transaction_id || '-'} • ${formatDateTime(row.transaction_created_at)}`}</small>
        </span>
      ),
    },
    {
      name: 'Payment / Kasir',
      selector: row => row.payment_method,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.payment_method ? row.payment_method.toUpperCase() : '-'}</strong>
          <small>{row.cashier_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Nilai / Item',
      selector: row => row.request_total,
      sortable: true,
      width: '165px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.request_total)}</strong>
          <small>{`${formatCount(row.item_count)} baris • ${formatCount(row.item_quantity)} qty`}</small>
        </span>
      ),
    },
    {
      name: 'Pengaju / Reviewer',
      selector: row => row.requested_by_name,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.requested_by_name || '-'}</strong>
          <small>{row.reviewed_by_name || 'Belum direview'}</small>
        </span>
      ),
    },
    {
      name: 'Alasan / Produk',
      selector: row => row.reason,
      sortable: true,
      grow: 1.3,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.reason || '-'}</strong>
          <small>
            {row.request_note
              || (Array.isArray(row.item_names) && row.item_names.length
                ? `${row.item_names[0]}${row.item_names.length > 1 ? ` +${row.item_names.length - 1}` : ''}`
                : '-')}
          </small>
        </span>
      ),
    },
    {
      name: 'Refund / Sync',
      selector: row => row.refund_total,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.refund_total)}</strong>
          <small>{row.accounting_sync_status || '-'}</small>
        </span>
      ),
    },
  ];

  const posPurchaseTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Dokumen',
      selector: row => row.total_documents,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_documents)}</strong>,
    },
    {
      name: 'Qty',
      selector: row => row.total_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_quantity)}</strong>,
    },
    {
      name: 'Purchase',
      selector: row => row.total_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_amount)}</strong>,
    },
    {
      name: 'Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.outstanding_amount)}</strong>,
    },
  ];

  const posPurchaseColumns = [
    {
      name: 'Invoice / Tanggal',
      selector: row => row.purchase_date,
      sortable: true,
      width: '200px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.invoice_number || `#${row.id || '-'}`}</strong>
          <small>{formatDateTime(row.purchase_date)}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Vendor',
      selector: row => row.outlet_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{row.supplier_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Metode / Status',
      selector: row => row.payment_method,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{(row.payment_method || 'cash').toUpperCase()}</strong>
          <small>{formatPurchasePaymentStatusLabel(row.payment_status)}</small>
        </span>
      ),
    },
    {
      name: 'Qty / Baris',
      selector: row => row.item_quantity,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.item_quantity)} qty</strong>
          <small>{formatCount(row.item_count)} baris</small>
        </span>
      ),
    },
    {
      name: 'Produk',
      selector: row => (Array.isArray(row.product_names) ? row.product_names.join(', ') : ''),
      sortable: false,
      grow: 1.5,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{Array.isArray(row.product_names) && row.product_names.length ? row.product_names[0] : 'Produk tidak tercatat'}</strong>
          <small>
            {Array.isArray(row.product_names) && row.product_names.length > 1
              ? `${row.product_names.length} produk`
              : row.note || '-'}
          </small>
        </span>
      ),
    },
    {
      name: 'Total / Dibayar',
      selector: row => row.total,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.total)}</strong>
          <small>Paid {formatCurrency(row.paid_amount)}</small>
        </span>
      ),
    },
    {
      name: 'Sisa / Jatuh Tempo',
      selector: row => row.outstanding_amount,
      sortable: true,
      width: '175px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.outstanding_amount)}</strong>
          <small>{row.due_date ? `Due ${formatDateTime(row.due_date)}` : 'Tanpa due date'}</small>
        </span>
      ),
    },
    {
      name: 'Sync',
      selector: row => row.accounting_sync_status,
      sortable: true,
      width: '145px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.accounting_sync_status || '-'}</strong>
          <small>{row.accounting_synced_at ? formatDateTime(row.accounting_synced_at) : '-'}</small>
        </span>
      ),
    },
  ];

  const posVendorAgingTimelineColumns = [
    {
      name: 'Referensi jatuh tempo',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Bills',
      selector: row => row.total_bills,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_bills)}</strong>,
    },
    {
      name: 'Overdue bills',
      selector: row => row.overdue_bills,
      sortable: true,
      width: '140px',
      cell: row => <strong>{formatCount(row.overdue_bills)}</strong>,
    },
    {
      name: 'Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      width: '160px',
      cell: row => <strong>{formatCurrency(row.outstanding_amount)}</strong>,
    },
    {
      name: 'Overdue amount',
      selector: row => row.overdue_amount,
      sortable: true,
      width: '160px',
      cell: row => <strong>{formatCurrency(row.overdue_amount)}</strong>,
    },
  ];

  const posVendorAgingColumns = [
    {
      name: 'Bill / Tanggal',
      selector: row => row.bill_date,
      sortable: true,
      width: '205px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.bill_no || `#${row.id || '-'}`}</strong>
          <small>{formatDateTime(row.bill_date)}</small>
        </span>
      ),
    },
    {
      name: 'Vendor / Outlet',
      selector: row => row.vendor_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.vendor_name || '-'}</strong>
          <small>{row.outlet_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Tipe / Status',
      selector: row => row.bill_type,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatVendorBillTypeLabel(row.bill_type)}</strong>
          <small>{formatPurchasePaymentStatusLabel(row.status)}</small>
        </span>
      ),
    },
    {
      name: 'Jatuh Tempo / Aging',
      selector: row => row.age_days,
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.due_date ? formatDateTime(row.due_date) : 'Tanpa due date'}</strong>
          <small>{row.aging_bucket_label || formatVendorAgingBucketLabel(row.aging_bucket)}</small>
        </span>
      ),
    },
    {
      name: 'Total / Dibayar',
      selector: row => row.total_amount,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.total_amount)}</strong>
          <small>Paid {formatCurrency(row.paid_amount)}</small>
        </span>
      ),
    },
    {
      name: 'Outstanding / Umur',
      selector: row => row.outstanding_amount,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.outstanding_amount)}</strong>
          <small>{row.age_days > 0 ? `${formatCount(row.age_days)} hari overdue` : row.due_status_label || formatVendorDueStatusLabel(row.due_status)}</small>
        </span>
      ),
    },
    {
      name: 'Referensi',
      selector: row => row.purchase_id || row.note,
      sortable: false,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.purchase_id ? `Purchase #${row.purchase_id}` : 'Manual bill'}</strong>
          <small>{row.note || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Sync',
      selector: row => row.accounting_sync_status,
      sortable: true,
      width: '145px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.accounting_sync_status || '-'}</strong>
          <small>{row.accounting_synced_at ? formatDateTime(row.accounting_synced_at) : '-'}</small>
        </span>
      ),
    },
  ];

  const posStockMovementTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Movement',
      selector: row => row.total_movements,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_movements)}</strong>,
    },
    {
      name: 'Qty In',
      selector: row => row.total_quantity_in,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_quantity_in)}</strong>,
    },
    {
      name: 'Qty Out',
      selector: row => row.total_quantity_out,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_quantity_out)}</strong>,
    },
    {
      name: 'Net',
      selector: row => row.net_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.net_quantity)}</strong>,
    },
    {
      name: 'Nilai masuk',
      selector: row => row.total_cost_in,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_cost_in)}</strong>,
    },
    {
      name: 'Nilai keluar',
      selector: row => row.total_cost_out,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_cost_out)}</strong>,
    },
  ];

  const posStockMovementColumns = [
    {
      name: 'Tanggal / Product',
      selector: row => row.created_at,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.product_name || `Product #${row.product_id || '-'}`}</strong>
          <small>{formatDateTime(row.created_at)}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Tipe',
      selector: row => row.outlet_name,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{formatProductItemTypeLabel(row.item_type)}</small>
        </span>
      ),
    },
    {
      name: 'Movement / Source',
      selector: row => row.movement_type,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatInventoryMovementTypeLabel(row.movement_type)}</strong>
          <small>{formatInventoryReferenceLabel(row.reference_type)} #{row.reference_id || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Qty In / Out',
      selector: row => row.net_quantity,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>In {formatCount(row.quantity_in)}</strong>
          <small>Out {formatCount(row.quantity_out)}</small>
        </span>
      ),
    },
    {
      name: 'Net / Stock',
      selector: row => row.stock_after,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.net_quantity)} net</strong>
          <small>{formatCount(row.stock_before)} -> {formatCount(row.stock_after)}</small>
        </span>
      ),
    },
    {
      name: 'Cost',
      selector: row => row.total_cost,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.total_cost)}</strong>
          <small>Unit {formatCurrency(row.unit_cost)}</small>
        </span>
      ),
    },
    {
      name: 'Catatan',
      selector: row => row.notes,
      sortable: false,
      grow: 1.3,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.notes || '-'}</strong>
          <small>Ledger #{row.id || '-'}</small>
        </span>
      ),
    },
  ];

  const posBOMConsumptionTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Transaksi',
      selector: row => row.total_transactions,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_transactions)}</strong>,
    },
    {
      name: 'Rows',
      selector: row => row.total_rows,
      sortable: true,
      width: '110px',
      cell: row => <strong>{formatCount(row.total_rows)}</strong>,
    },
    {
      name: 'Qty jadi',
      selector: row => row.total_sold_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_sold_quantity)}</strong>,
    },
    {
      name: 'Qty bahan',
      selector: row => row.total_consumed_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_consumed_quantity)}</strong>,
    },
    {
      name: 'Nilai cost',
      selector: row => row.total_consumption_cost,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_consumption_cost)}</strong>,
    },
  ];

  const posBOMConsumptionColumns = [
    {
      name: 'Tanggal / TXN',
      selector: row => row.transaction_created_at,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>TXN #{row.transaction_id || '-'}</strong>
          <small>{formatDateTime(row.transaction_created_at)}</small>
        </span>
      ),
    },
    {
      name: 'Produk jadi',
      selector: row => row.sold_product_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.sold_product_name || `Product #${row.sold_product_id || '-'}`}</strong>
          <small>Qty jual {formatCount(row.sold_quantity)}</small>
        </span>
      ),
    },
    {
      name: 'Bahan baku',
      selector: row => row.ingredient_product_name,
      sortable: true,
      grow: 1.3,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.ingredient_product_name || `Product #${row.ingredient_product_id || '-'}`}</strong>
          <small>{formatProductItemTypeLabel(row.ingredient_item_type)}{row.ingredient_unit ? ` • ${row.ingredient_unit}` : ''}</small>
        </span>
      ),
    },
    {
      name: 'Rasio / Qty',
      selector: row => row.quantity_consumed,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.quantity_per_unit)} / unit</strong>
          <small>{formatCount(row.quantity_consumed)} total consume</small>
        </span>
      ),
    },
    {
      name: 'Cost',
      selector: row => row.total_cost,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.total_cost)}</strong>
          <small>Unit {formatCurrency(row.unit_cost)}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Payment',
      selector: row => row.outlet_name,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{row.payment_method || '-'} • {row.cashier_name || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Catatan',
      selector: row => row.note || row.transaction_note,
      sortable: false,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.note || row.transaction_note || '-'}</strong>
          <small>{formatCurrency(row.transaction_total)} total transaksi</small>
        </span>
      ),
    },
  ];

  const posStockOpnameVarianceTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Opname',
      selector: row => row.total_opnames,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_opnames)}</strong>,
    },
    {
      name: 'Variance item',
      selector: row => row.variance_items,
      sortable: true,
      width: '130px',
      cell: row => <strong>{formatCount(row.variance_items)}</strong>,
    },
    {
      name: 'Qty gain',
      selector: row => row.total_gain_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_gain_quantity)}</strong>,
    },
    {
      name: 'Qty loss',
      selector: row => row.total_loss_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_loss_quantity)}</strong>,
    },
    {
      name: 'Net',
      selector: row => row.net_variance_quantity,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.net_variance_quantity)}</strong>,
    },
    {
      name: 'Nilai variance',
      selector: row => row.total_variance_cost,
      sortable: true,
      width: '160px',
      cell: row => <strong>{formatCurrency(row.total_variance_cost)}</strong>,
    },
  ];

  const posStockOpnameVarianceColumns = [
    {
      name: 'Tanggal / Product',
      selector: row => row.opname_date,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.product_name || `Product #${row.product_id || '-'}`}</strong>
          <small>{formatDateTime(row.opname_date)}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Tipe',
      selector: row => row.outlet_name,
      sortable: true,
      grow: 1.1,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet_name || '-'}</strong>
          <small>{formatProductItemTypeLabel(row.item_type)}{row.unit ? ` • ${row.unit}` : ''}</small>
        </span>
      ),
    },
    {
      name: 'System / Aktual',
      selector: row => row.actual_stock,
      sortable: true,
      width: '160px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.system_stock)} -> {formatCount(row.actual_stock)}</strong>
          <small>Selisih {formatCount(row.difference_qty)}</small>
        </span>
      ),
    },
    {
      name: 'Variance',
      selector: row => row.variance_type,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatStockOpnameVarianceLabel(row.variance_type)}</strong>
          <small>{row.difference_qty > 0 ? '+' : ''}{formatCount(row.difference_qty)}</small>
        </span>
      ),
    },
    {
      name: 'Cost',
      selector: row => row.total_cost,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.total_cost)}</strong>
          <small>Unit {formatCurrency(row.unit_cost)}</small>
        </span>
      ),
    },
    {
      name: 'Dokumen / Sync',
      selector: row => row.opname_id,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>Opname #{row.opname_id || '-'}</strong>
          <small>{row.accounting_sync_status || row.opname_status || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Catatan',
      selector: row => row.note || row.opname_note,
      sortable: false,
      grow: 1.4,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.note || row.opname_note || '-'}</strong>
          <small>{row.opname_note && row.note && row.opname_note !== row.note ? row.opname_note : row.opname_status || '-'}</small>
        </span>
      ),
    },
  ];

  const memberActivityColumns = [
    {
      name: 'Member',
      selector: row => row.member_name,
      sortable: true,
      grow: 1.3,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.member_name || '-'}</strong>
          <small>{row.email || row.hp || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Paket',
      selector: row => row.outlet,
      sortable: true,
      grow: 1.15,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet || '-'}</strong>
          <small>{row.membership_name || 'Belum ada membership'}</small>
        </span>
      ),
    },
    {
      name: 'Lifecycle',
      selector: row => row.lifecycle_label,
      sortable: true,
      width: '165px',
      cell: row => (
        <span className="report-center-transaction-pill">
          {row.lifecycle_label || '-'}
        </span>
      ),
    },
    {
      name: 'Aktivitas',
      selector: row => row.activity_label,
      sortable: true,
      width: '185px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.activity_label || '-'}</strong>
          <small>{row.risk_label || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Check-in Periode',
      selector: row => row.checkin_count_period,
      sortable: true,
      width: '140px',
      cell: row => <strong>{formatCount(row.checkin_count_period)}</strong>,
    },
    {
      name: 'Last Check-in',
      selector: row => row.last_checkin_at,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatDateTime(row.last_checkin_at)}</strong>
          <small>{formatDayValue(row.days_since_last_checkin)}</small>
        </span>
      ),
    },
    {
      name: 'Membership Expired',
      selector: row => row.membership_expired_at,
      sortable: true,
      width: '175px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatDateTime(row.membership_expired_at)}</strong>
          <small>{row.days_to_expire === null || row.days_to_expire === undefined ? '-' : `${row.days_to_expire} hari`}</small>
        </span>
      ),
    },
    {
      name: 'Pembayaran Terakhir',
      selector: row => row.last_payment_amount,
      sortable: true,
      width: '165px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.last_payment_amount)}</strong>
          <small>{formatDateTime(row.last_payment_at)}</small>
        </span>
      ),
    },
  ];

  const checkinTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Total Check-in',
      selector: row => row.total_checkins,
      sortable: true,
      width: '140px',
      cell: row => <strong>{formatCount(row.total_checkins)}</strong>,
    },
    {
      name: 'Member Unik',
      selector: row => row.unique_members,
      sortable: true,
      width: '140px',
      cell: row => <strong>{formatCount(row.unique_members)}</strong>,
    },
  ];

  const checkinColumns = [
    {
      name: 'Tanggal Check-in',
      selector: row => row.checkin_at,
      sortable: true,
      width: '170px',
      cell: row => <span className="report-center-table__muted">{formatDateTime(row.checkin_at)}</span>,
    },
    {
      name: 'Member',
      selector: row => row.member_name,
      sortable: true,
      grow: 1.3,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.member_name || '-'}</strong>
          <small>{row.email || row.hp || `Member #${row.member_id || '-'}`}</small>
        </span>
      ),
    },
    {
      name: 'Outlet / Paket',
      selector: row => row.outlet,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.outlet || '-'}</strong>
          <small>{row.membership_name || 'Belum ada membership'}</small>
        </span>
      ),
    },
    {
      name: 'Visit Periode',
      selector: row => row.visit_count_period,
      sortable: true,
      width: '135px',
      cell: row => <strong>{formatCount(row.visit_count_period)}</strong>,
    },
    {
      name: 'Sequence',
      selector: row => row.visit_sequence_label,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-transaction-pill">
          {row.visit_sequence_label || '-'}
        </span>
      ),
    },
    {
      name: 'Membership Expired',
      selector: row => row.membership_expired_at,
      sortable: true,
      width: '185px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatDateTime(row.membership_expired_at)}</strong>
          <small>{row.days_to_expire === null || row.days_to_expire === undefined ? '-' : `${row.days_to_expire} hari`}</small>
        </span>
      ),
    },
  ];

  const ptSalesTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Invoice',
      selector: row => row.total_invoices,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_invoices)}</strong>,
    },
    {
      name: 'Private Session Sales',
      selector: row => row.total_sales_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.total_sales_amount)}</strong>,
    },
    {
      name: 'Paid',
      selector: row => row.paid_amount,
      sortable: true,
      width: '140px',
      cell: row => <strong>{formatCurrency(row.paid_amount)}</strong>,
    },
    {
      name: 'Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.outstanding_amount)}</strong>,
    },
  ];

  const ptSalesColumns = [
    {
      name: 'Invoice / Tanggal',
      selector: row => row.sale_date,
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.invoice_no || '-'}</strong>
          <small>{formatDateTime(row.sale_date)}</small>
        </span>
      ),
    },
    {
      name: 'Client',
      selector: row => row.client_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.client_name || '-'}</strong>
          <small>{formatPTClientTypeLabel(row.client_type)}</small>
        </span>
      ),
    },
    {
      name: 'Trainer / Produk',
      selector: row => row.pt_name,
      sortable: true,
      grow: 1.25,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.pt_name || '-'}</strong>
          <small>{row.product_name || '-'} • {formatPTProductTypeLabel(row.product_type)}</small>
        </span>
      ),
    },
    {
      name: 'Sessions',
      selector: row => row.session_count,
      sortable: true,
      width: '110px',
      cell: row => <strong>{formatCount(row.session_count)}</strong>,
    },
    {
      name: 'Payment',
      selector: row => row.payment_status,
      sortable: true,
      width: '170px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatPTPaymentStatusLabel(row.payment_status)}</strong>
          <small>{row.payment_method || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Contract',
      selector: row => row.contract_status,
      sortable: true,
      width: '155px',
      cell: row => (
        <span className="report-center-transaction-pill">
          {formatPTContractStatusLabel(row.contract_status)}
        </span>
      ),
    },
    {
      name: 'Nilai',
      selector: row => row.net_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.net_amount)}</strong>,
    },
    {
      name: 'Outstanding',
      selector: row => row.outstanding_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.outstanding_amount)}</strong>,
    },
  ];

  const ptSessionTrendColumns = [
    {
      name: 'Hari',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Scheduled',
      selector: row => row.total_sessions_scheduled,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_sessions_scheduled)}</strong>,
    },
    {
      name: 'Completed',
      selector: row => row.total_sessions_completed,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_sessions_completed)}</strong>,
    },
    {
      name: 'Cancelled',
      selector: row => row.total_sessions_cancelled,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.total_sessions_cancelled)}</strong>,
    },
    {
      name: 'Rescheduled',
      selector: row => row.total_sessions_rescheduled,
      sortable: true,
      width: '130px',
      cell: row => <strong>{formatCount(row.total_sessions_rescheduled)}</strong>,
    },
    {
      name: 'No Show',
      selector: row => row.no_show_count,
      sortable: true,
      width: '110px',
      cell: row => <strong>{formatCount(row.no_show_count)}</strong>,
    },
  ];

  const ptSessionColumns = [
    {
      name: 'Booking / Jadwal',
      selector: row => row.start_at,
      sortable: true,
      width: '190px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.booking_code || '-'}</strong>
          <small>{formatDateTime(row.start_at)} - {formatDateTime(row.end_at)}</small>
        </span>
      ),
    },
    {
      name: 'Client',
      selector: row => row.client_name,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.client_name || '-'}</strong>
          <small>{formatPTClientTypeLabel(row.client_type)}</small>
        </span>
      ),
    },
    {
      name: 'Trainer / Produk',
      selector: row => row.pt_name,
      sortable: true,
      grow: 1.25,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.pt_name || '-'}</strong>
          <small>{row.product_name || '-'} • {row.outlet || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Contract / Invoice',
      selector: row => row.contract_no,
      sortable: true,
      width: '180px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.contract_no || '-'}</strong>
          <small>{row.invoice_no || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Booking Status',
      selector: row => row.booking_status,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-transaction-pill">
          {formatPTBookingStatusLabel(row.booking_status)}
        </span>
      ),
    },
    {
      name: 'Attendance',
      selector: row => row.attendance_status,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatPTAttendanceStatusLabel(row.attendance_status)}</strong>
          <small>{row.is_chargeable_no_show ? 'Chargeable no-show' : row.session_consumed ? 'Session consumed' : 'No consume'}</small>
        </span>
      ),
    },
    {
      name: 'Consumed',
      selector: row => row.session_consumed,
      sortable: true,
      width: '110px',
      cell: row => <strong>{row.session_consumed ? 'Yes' : 'No'}</strong>,
    },
    {
      name: 'Sisa Sesi',
      selector: row => row.remaining_sessions,
      sortable: true,
      width: '110px',
      cell: row => <strong>{formatCount(row.remaining_sessions)}</strong>,
    },
  ];

  const ptTrainerTrendColumns = [
    {
      name: 'Periode',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.2,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.period_label || '-'}</strong>
          <small>{row.period_key || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Trainer Aktif',
      selector: row => row.active_trainers,
      sortable: true,
      width: '130px',
      cell: row => <strong>{formatCount(row.active_trainers)}</strong>,
    },
    {
      name: 'Completed',
      selector: row => row.sessions_completed,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.sessions_completed)}</strong>,
    },
    {
      name: 'Cancelled',
      selector: row => row.sessions_cancelled,
      sortable: true,
      width: '120px',
      cell: row => <strong>{formatCount(row.sessions_cancelled)}</strong>,
    },
    {
      name: 'No Show',
      selector: row => row.sessions_no_show,
      sortable: true,
      width: '110px',
      cell: row => <strong>{formatCount(row.sessions_no_show)}</strong>,
    },
    {
      name: 'Sales',
      selector: row => row.sales_attributed,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.sales_attributed)}</strong>,
    },
    {
      name: 'Settlement',
      selector: row => row.settlement_eligible_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.settlement_eligible_amount)}</strong>,
    },
  ];

  const ptTrainerColumns = [
    {
      name: 'Trainer',
      selector: row => row.pt_name,
      sortable: true,
      grow: 1.25,
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{row.pt_name || '-'}</strong>
          <small>{row.is_active ? 'Active trainer' : 'Inactive trainer'}</small>
        </span>
      ),
    },
    {
      name: 'Outlet',
      selector: row => row.outlet,
      sortable: true,
      width: '160px',
      cell: row => <span className="report-center-table__muted">{row.outlet || '-'}</span>,
    },
    {
      name: 'Client / Package',
      selector: row => row.active_clients,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.active_clients)} client</strong>
          <small>{formatCount(row.packages_sold)} package sold</small>
        </span>
      ),
    },
    {
      name: 'Sales',
      selector: row => row.sales_amount,
      sortable: true,
      width: '155px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCurrency(row.sales_amount)}</strong>
          <small>Paid {formatCurrency(row.paid_sales_amount)}</small>
        </span>
      ),
    },
    {
      name: 'Sesi',
      selector: row => row.sessions_completed,
      sortable: true,
      width: '165px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.sessions_completed)} completed</strong>
          <small>{formatCount(row.sessions_scheduled)} scheduled</small>
        </span>
      ),
    },
    {
      name: 'Cancel / No Show',
      selector: row => row.sessions_cancelled,
      sortable: true,
      width: '155px',
      cell: row => (
        <span className="report-center-table__identity">
          <strong>{formatCount(row.sessions_cancelled)} cancel</strong>
          <small>{formatCount(row.sessions_no_show)} no-show</small>
        </span>
      ),
    },
    {
      name: 'Completion Rate',
      selector: row => row.completion_rate,
      sortable: true,
      width: '140px',
      cell: row => <strong>{Number(row.completion_rate || 0).toFixed(2)}%</strong>,
    },
    {
      name: 'Settlement',
      selector: row => row.settlement_eligible_amount,
      sortable: true,
      width: '150px',
      cell: row => <strong>{formatCurrency(row.settlement_eligible_amount)}</strong>,
    },
  ];

  const renderBreakdownCard = (title, eyebrow, items, options = {}) => (
    <section className="section-card report-center-breakdown-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
        </div>
      </div>
      {items.length > 0 ? (
        <div className="report-center-breakdown-list">
          {items.map(item => (
            <div key={`${title}-${item.key}`} className="report-center-breakdown-row">
              <div className="report-center-breakdown-copy">
                <strong>{item.label || '-'}</strong>
                <small>{formatCount(item.count)} {options.countLabel || 'transaksi'}</small>
              </div>
              <strong>
                {options.valueMode === 'count'
                  ? formatCount(item.quantity || item.count)
                  : typeof item.amount === 'number'
                  ? formatCurrency(item.amount)
                  : formatCount(item.count)}
              </strong>
            </div>
          ))}
        </div>
      ) : (
        <p className="report-center-empty-copy">Belum ada data untuk breakdown ini.</p>
      )}
    </section>
  );

  const renderBusinessFilterCard = () => (
    <section className="section-card report-center-filter-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">
            {activeBusinessReport === 'staff-transaction-summary'
              ? 'Staff Transaction Summary'
              : activeBusinessReport === 'member-activity'
              ? 'Member Activity'
              : activeBusinessReport === 'check-in'
                ? 'Check-in'
                : activeBusinessReport === 'pt-sales'
                  ? 'Private Session Sales'
                  : activeBusinessReport === 'pt-session'
                    ? 'Private Session'
                    : activeBusinessReport === 'pt-trainer'
                      ? 'Private Session Staff'
                : 'Membership Sales'}
          </span>
          <h2>Filter report</h2>
          <p>
            {activeBusinessReport === 'staff-transaction-summary'
              ? 'Gunakan filter ini untuk membaca produktivitas staf aktif dari penjualan membership, class sale, PT sales, dan POS sales dalam satu periode.'
              : activeBusinessReport === 'member-activity'
                ? 'Gunakan filter ini untuk membaca lifecycle member, kunjungan, dan risiko churn sebelum audit tabel detail.'
              : activeBusinessReport === 'check-in'
                ? 'Gunakan filter ini untuk membaca pola kunjungan, paket saat check-in, dan member aktif per periode.'
                : activeBusinessReport === 'pt-sales'
                  ? 'Gunakan filter ini untuk membaca penjualan private session per staff, layanan, tipe client, dan status pembayaran.'
                  : activeBusinessReport === 'pt-session'
                    ? 'Gunakan filter ini untuk membaca booking private session, sesi selesai, cancel, no-show, dan sisa sesi per client atau staff.'
                    : activeBusinessReport === 'pt-trainer'
                      ? 'Gunakan filter ini untuk membaca performa staff layanan dari sisi sesi selesai, penjualan, dan estimasi settlement.'
                : 'Gunakan filter ini untuk membaca ringkasan penjualan membership sebelum audit transaksi detail.'}
          </p>
        </div>
        <span className="status-pill">
          <FaFilter /> Filter list
        </span>
      </div>

      <div className="report-center-quick-range">
        {[
          { key: 'today', label: 'Hari ini' },
          { key: 'last-7-days', label: '7 hari' },
          { key: 'last-30-days', label: '30 hari' },
          { key: 'this-month', label: 'Bulan ini' },
        ].map(item => (
          <button
            key={item.key}
            type="button"
            className={`report-center-quick-range__chip ${quickRange === item.key ? 'is-active' : ''}`}
            onClick={() => applyQuickRange(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {hasPendingFilterChanges ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Filter di form belum diterapkan.</strong>
            <p>Tabel masih memakai filter sebelumnya. Klik tampilkan report atau refresh untuk memakai filter yang sedang terlihat.</p>
          </div>
          <Button onClick={applyFilters} disabled={loading}>
            <FaSearch /> Terapkan sekarang
          </Button>
        </div>
      ) : null}

      <Row className="g-3 align-items-end">
        <Col xl={2} md={6}>
          <Form.Group>
            <Form.Label>Outlet</Form.Label>
            <Form.Select
              value={filters.outlet}
              onChange={event => {
                const nextOutlet = event.target.value;
                setFilters(prev => ({
                  ...prev,
                  outlet: nextOutlet,
                  membership_id: '',
                  pt_user_id: '',
                  product_id: '',
                  client_id: '',
                }));
              }}
            >
              <option value="">Semua outlet</option>
              {outlets.map(outlet => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xl={2} md={6}>
          <Form.Group>
            <Form.Label>
              {['pt-sales', 'pt-session', 'pt-trainer'].includes(activeBusinessReport)
                ? 'Session staff'
                : activeBusinessReport === 'staff-transaction-summary'
                  ? 'Staff scope'
                  : 'Paket membership'}
            </Form.Label>
            {['pt-sales', 'pt-session', 'pt-trainer'].includes(activeBusinessReport) ? (
              <Form.Select
                value={filters.pt_user_id}
                onChange={event => {
                  const nextPTUser = event.target.value;
                  setFilters(prev => ({
                    ...prev,
                    pt_user_id: nextPTUser,
                    product_id: '',
                  }));
                }}
              >
                  <option value="">Semua staff</option>
                  {ptUsers.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.pt_name || `Staff #${item.id || '-'}`}
                    </option>
                  ))}
                </Form.Select>
            ) : activeBusinessReport === 'staff-transaction-summary' ? (
              <Form.Control value="Semua staff aktif" disabled readOnly />
            ) : (
              <Form.Select
                value={filters.membership_id}
                onChange={event => setFilters(prev => ({ ...prev, membership_id: event.target.value }))}
              >
                <option value="">Semua paket</option>
                {memberships.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
        </Col>

        {activeBusinessReport === 'member-activity' ? (
          <>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Lifecycle</Form.Label>
                <Form.Select
                  value={filters.lifecycle}
                  onChange={event => setFilters(prev => ({ ...prev, lifecycle: event.target.value }))}
                >
                  <option value="">Semua lifecycle</option>
                  <option value="active">Aktif</option>
                  <option value="expiring_soon">Segera expired</option>
                  <option value="expired">Expired</option>
                  <option value="no_membership">Belum ada membership</option>
                  <option value="inactive_profile">Profil nonaktif</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Aktivitas</Form.Label>
                <Form.Select
                  value={filters.activity}
                  onChange={event => setFilters(prev => ({ ...prev, activity: event.target.value }))}
                >
                  <option value="">Semua aktivitas</option>
                  <option value="visited">Datang di periode</option>
                  <option value="no_visit">Tidak datang di periode</option>
                  <option value="inactive_14_days">Tidak datang &gt; 14 hari</option>
                  <option value="never_checkin">Belum pernah check-in</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </>
        ) : activeBusinessReport === 'pt-sales' ? (
          <>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Session service</Form.Label>
                <Form.Select
                  value={filters.product_id}
                  onChange={event => setFilters(prev => ({ ...prev, product_id: event.target.value }))}
                >
                  <option value="">Semua layanan</option>
                  {ptProducts.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.product_name || `Layanan #${item.id || '-'}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Tipe client</Form.Label>
                <Form.Select
                  value={filters.client_type}
                  onChange={event => setFilters(prev => ({ ...prev, client_type: event.target.value }))}
                >
                  <option value="">Semua client</option>
                  <option value="member">Member</option>
                  <option value="non_member">Non Member</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Status bayar</Form.Label>
                <Form.Select
                  value={filters.pt_payment_status}
                  onChange={event => setFilters(prev => ({ ...prev, pt_payment_status: event.target.value }))}
                >
                  <option value="">Semua status</option>
                  <option value="pending">Pending</option>
                  <option value="partial_paid">Partial Paid</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Status contract</Form.Label>
                <Form.Select
                  value={filters.contract_status}
                  onChange={event => setFilters(prev => ({ ...prev, contract_status: event.target.value }))}
                >
                  <option value="">Semua contract</option>
                  <option value="frozen">Frozen</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="not_created">Not Created</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </>
        ) : activeBusinessReport === 'pt-session' ? (
          <>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Session client</Form.Label>
                <Form.Select
                  value={filters.client_id}
                  onChange={event => setFilters(prev => ({ ...prev, client_id: event.target.value }))}
                >
                  <option value="">Semua client</option>
                  {ptClients.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.full_name || `Client #${item.id || '-'}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Session service</Form.Label>
                <Form.Select
                  value={filters.product_id}
                  onChange={event => setFilters(prev => ({ ...prev, product_id: event.target.value }))}
                >
                  <option value="">Semua layanan</option>
                  {ptProducts.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.product_name || `Layanan #${item.id || '-'}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Booking status</Form.Label>
                <Form.Select
                  value={filters.booking_status}
                  onChange={event => setFilters(prev => ({ ...prev, booking_status: event.target.value }))}
                >
                  <option value="">Semua booking</option>
                  <option value="requested">Requested</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="rescheduled">Rescheduled</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Attendance</Form.Label>
                <Form.Select
                  value={filters.attendance_status}
                  onChange={event => setFilters(prev => ({ ...prev, attendance_status: event.target.value }))}
                >
                  <option value="">Semua attendance</option>
                  <option value="pending">Pending</option>
                  <option value="checked_in">Checked In</option>
                  <option value="completed">Completed</option>
                  <option value="no_show">No Show</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </>
        ) : activeBusinessReport === 'pt-trainer' ? (
          <>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Status bayar</Form.Label>
                <Form.Select
                  value={filters.pt_payment_status}
                  onChange={event => setFilters(prev => ({ ...prev, pt_payment_status: event.target.value }))}
                >
                  <option value="">Semua status</option>
                  <option value="pending">Pending</option>
                  <option value="partial_paid">Partial Paid</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Status sesi</Form.Label>
                <Form.Select
                  value={filters.attendance_status}
                  onChange={event => setFilters(prev => ({ ...prev, attendance_status: event.target.value }))}
                >
                  <option value="">Semua sesi</option>
                  <option value="pending">Pending</option>
                  <option value="checked_in">Checked In</option>
                  <option value="completed">Completed</option>
                  <option value="no_show">No Show</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </>
        ) : activeBusinessReport === 'membership-sales' ? (
          <>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Metode bayar</Form.Label>
                <Form.Select
                  value={filters.payment_method}
                  onChange={event => setFilters(prev => ({ ...prev, payment_method: event.target.value }))}
                >
                  <option value="">Semua metode</option>
                  <option value="cash">Cash</option>
                  <option value="qris">QRIS</option>
                  <option value="transfer">Transfer / Bank</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={event => setFilters(prev => ({ ...prev, status: event.target.value }))}
                >
                  <option value="">Semua status</option>
                  <option value="1">Success</option>
                  <option value="0">Pending</option>
                  <option value="2">Failed</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </>
        ) : null}

        <Col xl={2} md={6}>
          <Form.Group>
            <Form.Label>Tanggal awal</Form.Label>
            <Form.Control
              type="date"
              value={filters.dateFrom}
              onChange={event => setFilters(prev => ({ ...prev, dateFrom: event.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col xl={2} md={6}>
          <Form.Group>
            <Form.Label>Tanggal akhir</Form.Label>
            <Form.Control
              type="date"
              value={filters.dateTo}
              onChange={event => setFilters(prev => ({ ...prev, dateTo: event.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col xl={8} md={12}>
          <Form.Group>
            <Form.Label>Cari cepat</Form.Label>
            <Form.Control
              value={filters.search}
              onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
              placeholder={activeBusinessReport === 'member-activity'
                ? 'Nama member, email, HP, ID member, atau paket aktif'
                : activeBusinessReport === 'staff-transaction-summary'
                  ? 'Nama staf, username, jabatan, ID staf, atau outlet'
                : activeBusinessReport === 'check-in'
                  ? 'Nama member, email, HP, ID member, paket saat check-in, atau outlet'
                  : activeBusinessReport === 'pt-sales'
                    ? 'Invoice, client, trainer, produk, atau metode bayar'
                    : activeBusinessReport === 'pt-session'
                      ? 'Booking code, contract, invoice, client, trainer, atau produk'
                      : activeBusinessReport === 'pt-trainer'
                        ? 'Nama trainer, invoice, client, produk, atau outlet'
                  : 'Nama member, nama paket, ID member, atau petugas'}
            />
          </Form.Group>
        </Col>
        <Col xl={4} md={12}>
          <div className="report-center-filter-actions">
            <Button onClick={applyFilters} disabled={loading}>
              <FaSearch /> Tampilkan report
            </Button>
            <Button variant="outline-secondary" onClick={resetFilters} disabled={loading}>
              <FaRedoAlt /> Reset
            </Button>
          </div>
        </Col>
      </Row>
    </section>
  );

  const posSalesPageRows = posSalesData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posCashierSalesPageRows = posCashierSalesData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posProductSalesPageRows = posProductSalesData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posRefundVoidPageRows = posRefundVoidData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posPurchasePageRows = posPurchaseData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posVendorAgingPageRows = posVendorAgingData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posStockMovementPageRows = posStockMovementData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posBOMConsumptionPageRows = posBOMConsumptionData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posStockOpnameVariancePageRows = posStockOpnameVarianceData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const posVendorPriceComparisonPageRows = posVendorPriceComparisonData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingJournalPageRows = accountingJournalData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingGeneralLedgerPageRows = accountingGeneralLedgerData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingTrialBalancePageRows = accountingTrialBalanceData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingProfitLossPageRows = accountingProfitLossData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingBalanceSheetPageRows = accountingBalanceSheetData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingCashFlowPageRows = accountingCashFlowData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingAccountsPayableAgingPageRows = accountingAccountsPayableAgingData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingExpenseBreakdownPageRows = accountingExpenseBreakdownData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingAssetRegisterPageRows = accountingAssetRegisterData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const accountingDepreciationSchedulePageRows = accountingDepreciationScheduleData.results.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );
  const isRefundVoidReport = activePosReport === 'refund-void-report';
  const isCashierSalesSummaryReport = activePosReport === 'cashier-sales-summary';
  const isProductSalesReport = activePosReport === 'product-sales-report';
  const isPurchaseReport = activePosReport === 'purchase-report';
  const isVendorAgingReport = activePosReport === 'vendor-aging';
  const isStockMovementReport = activePosReport === 'stock-movement';
  const isBOMConsumptionReport = activePosReport === 'bom-consumption-report';
  const isStockOpnameVarianceReport = activePosReport === 'stock-opname-variance';
  const isVendorPriceComparisonReport = activePosReport === 'vendor-price-comparison';
  const isJournalReport = activeAccountingReport === 'journal-report';
  const isGeneralLedgerReport = activeAccountingReport === 'general-ledger';
  const isTrialBalanceReport = activeAccountingReport === 'trial-balance';
  const isProfitLossReport = activeAccountingReport === 'profit-loss';
  const isBalanceSheetReport = activeAccountingReport === 'balance-sheet';
  const isCashFlowReport = activeAccountingReport === 'cash-flow';
  const isAccountsPayableAgingReport = activeAccountingReport === 'accounts-payable-aging';
  const isExpenseBreakdownReport = activeAccountingReport === 'expense-breakdown';
  const isAssetRegisterReport = activeAccountingReport === 'asset-register';
  const isDepreciationScheduleReport = activeAccountingReport === 'depreciation-schedule';
  const isFinanceDocumentAccountingReport =
    isAccountsPayableAgingReport ||
    isExpenseBreakdownReport ||
    isAssetRegisterReport ||
    isDepreciationScheduleReport;
  const isFixedAssetAccountingReport = isAssetRegisterReport || isDepreciationScheduleReport;
  const posPaymentMethodOptions = isPurchaseReport || isVendorPriceComparisonReport
    ? POS_PURCHASE_PAYMENT_METHOD_OPTIONS
    : POS_SALES_PAYMENT_METHOD_OPTIONS;
  const posVendorOptions = Array.from(
    new Set(
      (isVendorAgingReport
        ? posVendorAgingData.results
        : isVendorPriceComparisonReport
          ? posVendorPriceComparisonData.vendor_details
          : posPurchaseData.results)
        .map(item => String(item?.vendor_name || item?.supplier_name || '').trim())
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right));
  const posSalesProductOptions = Array.from(
    new Map(
      posProductSalesData.results
        .filter(item => item?.product_id || item?.product_name)
        .map(item => [String(item.product_id || item.product_name), {
          value: String(item.product_id || ''),
          label: item.product_name || `Product #${item.product_id}`,
        }])
    ).values()
  ).sort((left, right) => left.label.localeCompare(right.label));
  const posPricingFocusProductOptions = Array.from(
    new Map(
      posSalesData.results
        .flatMap(row => (Array.isArray(row?.items) ? row.items : []))
        .filter(item => item?.product_id || item?.product?.id)
        .map(item => {
          const productID = String(item?.product_id || item?.product?.id || '');
          const productName = item?.product?.name || `Product #${productID || '-'}`;
          return [productID || productName, { value: productID, label: productName }];
        })
    ).values()
  ).sort((left, right) => left.label.localeCompare(right.label));
  const posVendorPriceCategoryOptions = Array.from(
    new Map(
      posVendorPriceComparisonData.results
        .filter(item => item?.category_name)
        .map(item => [String(item.category_id || item.category_name), {
          value: String(item.category_id || ''),
          label: item.category_name || 'Tanpa kategori',
        }])
    ).values()
  ).sort((left, right) => left.label.localeCompare(right.label));
  const posVendorPriceProductOptions = Array.from(
    new Map(
      posVendorPriceComparisonData.results
        .filter(item => item?.product_id)
        .map(item => [String(item.product_id), {
          value: String(item.product_id),
          label: item.product_name || `Product #${item.product_id}`,
        }])
    ).values()
  ).sort((left, right) => left.label.localeCompare(right.label));
  const posStockProductOptions = Array.from(
    new Map(
      posStockMovementData.results
        .filter(item => item?.product_id)
        .map(item => [String(item.product_id), { value: String(item.product_id), label: item.product_name || `Product #${item.product_id}` }])
    ).values()
  ).sort((left, right) => left.label.localeCompare(right.label));
  const posBOMSoldProductOptions = Array.from(
    new Map(
      posBOMConsumptionData.results
        .filter(item => item?.sold_product_id)
        .map(item => [String(item.sold_product_id), { value: String(item.sold_product_id), label: item.sold_product_name || `Product #${item.sold_product_id}` }])
    ).values()
  ).sort((left, right) => left.label.localeCompare(right.label));
  const posBOMIngredientOptions = Array.from(
    new Map(
      posBOMConsumptionData.results
        .filter(item => item?.ingredient_product_id)
        .map(item => [String(item.ingredient_product_id), { value: String(item.ingredient_product_id), label: item.ingredient_product_name || `Product #${item.ingredient_product_id}` }])
    ).values()
  ).sort((left, right) => left.label.localeCompare(right.label));
  const posStockOpnameProductOptions = Array.from(
    new Map(
      posStockOpnameVarianceData.results
        .filter(item => item?.product_id)
        .map(item => [String(item.product_id), { value: String(item.product_id), label: item.product_name || `Product #${item.product_id}` }])
    ).values()
  ).sort((left, right) => left.label.localeCompare(right.label));
  const selectedVendorPriceCategoryLabel =
    posVendorPriceCategoryOptions.find(item => item.value === appliedFilters.category_id)?.label || 'Semua kategori';
  const selectedVendorPriceProductLabel =
    posVendorPriceProductOptions.find(item => item.value === appliedFilters.product_id)?.label || 'Semua produk';
  const selectedPOSPricingFocusProductLabel =
    posPricingFocusProductOptions.find(item => item.value === String(appliedFilters.product_id || ''))?.label ||
    (appliedFilters.product_id ? `Product #${appliedFilters.product_id}` : 'Semua produk');
  const selectedOutletLabel =
    outlets.find(item => String(item.id) === String(appliedFilters.outlet))?.name || 'Semua outlet';
  const buildScopedManagementPath = basePath => {
    const query = new URLSearchParams();
    const scopedOutlet = filters.outlet || appliedFilters.outlet;
    if (scopedOutlet) {
      query.set('outlet_id', scopedOutlet);
    }
    return query.toString() ? `${basePath}?${query.toString()}` : basePath;
  };
  const selectedAccountingSourceSystemLabel = appliedFilters.source_system
    ? formatAccountingSourceSystemLabel(appliedFilters.source_system)
    : 'Semua source';
  const selectedAccountingBusinessUnitLabel = appliedFilters.business_unit
    ? formatAccountingBusinessUnitLabel(appliedFilters.business_unit)
    : 'Semua business unit';
  const selectedAccountingPostingStatusLabel = appliedFilters.accounting_posting_status
    ? formatAccountingPostingStatusLabel(appliedFilters.accounting_posting_status)
    : 'Semua status';
  const selectedAccountingFinanceDocumentScopeLabel = appliedFilters.accounting_document_scope
    ? formatAccountingFinanceDocumentScopeLabel(appliedFilters.accounting_document_scope)
    : 'Semua scope';
  const selectedAccountingFixedAssetScopeLabel = 'Asset';
  const selectedAccountingFinanceStatusLabel = appliedFilters.status
    ? formatAccountingFinanceStatusLabel(appliedFilters.status)
    : 'Semua status aktif';
  const selectedAccountingFinanceAgingBucketLabel = appliedFilters.aging_bucket
    ? formatAccountingFinanceAgingBucketLabel(appliedFilters.aging_bucket)
    : 'Semua bucket';
  const selectedAccountingPeriodLabel = formatDateRangeLabel(appliedFilters.dateFrom, appliedFilters.dateTo);
  const selectedAccountingSearchLabel = appliedFilters.search
    ? `“${appliedFilters.search}”`
    : 'Tanpa kata kunci';
  const selectedAccountingJournalRow =
    accountingJournalData.results.find(row => Number(row?.id) === Number(selectedAccountingJournalId)) ||
    accountingJournalData.results[0] ||
    null;
  const selectedAccountingGeneralLedgerRow =
    accountingGeneralLedgerData.results.find(row => String(row?.account_id || '') === String(selectedAccountingGeneralLedgerAccountId || '')) ||
    accountingGeneralLedgerData.results[0] ||
    null;
  const accountingJournalRowStyles = selectedAccountingJournalId
    ? [
        {
          when: row => Number(row?.id) === Number(selectedAccountingJournalId),
          style: {
            background:
              'linear-gradient(145deg, rgba(217, 255, 99, 0.12), rgba(83, 240, 156, 0.06)), var(--surface-alt)',
            boxShadow: 'inset 3px 0 0 rgba(164, 207, 48, 0.52)',
          },
        },
      ]
    : [];
  const accountingGeneralLedgerRowStyles = selectedAccountingGeneralLedgerAccountId
    ? [
        {
          when: row => String(row?.account_id || '') === String(selectedAccountingGeneralLedgerAccountId || ''),
          style: {
            background:
              'linear-gradient(145deg, rgba(217, 255, 99, 0.12), rgba(83, 240, 156, 0.06)), var(--surface-alt)',
            boxShadow: 'inset 3px 0 0 rgba(164, 207, 48, 0.52)',
          },
        },
      ]
    : [];
  const posPurchaseStatusOptions = ['open', 'partial_paid', 'paid'];
  const purchaseReportSections = [
    {
      key: 'summary',
      label: 'Ringkasan',
      copy: 'KPI utama, vendor, metode bayar, dan status hutang.',
      icon: FaLayerGroup,
    },
    {
      key: 'analysis',
      label: 'Analisa',
      copy: 'Tren harian dan pola pembelian produk per periode.',
      icon: FaChartLine,
    },
    {
      key: 'detail',
      label: 'Detail',
      copy: 'Audit dokumen purchase satu per satu.',
      icon: FaReceipt,
    },
  ];
  const activePurchaseSectionMeta =
    purchaseReportSections.find(item => item.key === activePurchaseSection) || purchaseReportSections[0];
  const vendorPriceSections = [
    {
      key: 'summary',
      label: 'Ringkasan',
      copy: 'KPI utama, vendor terbaik, sebaran kategori, dan peluang penghematan.',
      icon: FaLayerGroup,
    },
    {
      key: 'analysis',
      label: 'Analisa',
      copy: 'Top price gap product dan benchmark vendor per product.',
      icon: FaChartLine,
    },
    {
      key: 'detail',
      label: 'Detail',
      copy: 'Audit per product untuk keputusan kerja sama vendor.',
      icon: FaReceipt,
    },
  ];
  const activeVendorPriceSectionMeta =
    vendorPriceSections.find(item => item.key === activeVendorPriceSection) || vendorPriceSections[0];
  const accountingJournalSections = [
    {
      key: 'summary',
      label: 'Ringkasan',
      copy: 'Konteks filter aktif, KPI utama, dan sebaran jurnal paling penting.',
      icon: FaLayerGroup,
    },
    {
      key: 'analysis',
      label: 'Analisa',
      copy: 'Tren harian dan breakdown akun untuk membaca pola ledger.',
      icon: FaChartLine,
    },
    {
      key: 'detail',
      label: 'Audit detail',
      copy: 'Daftar jurnal dan inspector line debit-credit per jurnal.',
      icon: FaClipboardCheck,
    },
  ];
  const activeAccountingJournalSectionMeta =
    accountingJournalSections.find(item => item.key === activeAccountingJournalSection) || accountingJournalSections[0];
  const accountingGeneralLedgerSections = [
    {
      key: 'summary',
      label: 'Ringkasan',
      copy: 'KPI akun, filter aktif, dan sebaran saldo ledger per kategori.',
      icon: FaLayerGroup,
    },
    {
      key: 'analysis',
      label: 'Analisa',
      copy: 'Tren per hari dan daftar akun untuk membaca pola mutasi ledger.',
      icon: FaChartLine,
    },
    {
      key: 'detail',
      label: 'Detail akun',
      copy: 'Pilih akun lalu audit mutasi jurnal dan saldo berjalan di periode terpilih.',
      icon: FaClipboardCheck,
    },
  ];
  const activeAccountingGeneralLedgerSectionMeta =
    accountingGeneralLedgerSections.find(item => item.key === activeAccountingGeneralLedgerSection) || accountingGeneralLedgerSections[0];
  const accountingJournalDetailSections = [
    {
      key: 'overview',
      label: 'Ringkasan',
      copy: 'Identitas jurnal, status posting, nilai total, dan konteks audit.',
      icon: FaLayerGroup,
    },
    {
      key: 'general',
      label: 'Jurnal umum',
      copy: 'Penyajian COA, account and description, debit, serta kredit.',
      icon: FaReceipt,
    },
    {
      key: 'audit',
      label: 'Audit teknis',
      copy: 'Tabel line jurnal untuk sortir cepat dan pemeriksaan detail.',
      icon: FaClipboardCheck,
    },
  ];
  const activeAccountingJournalDetailSectionMeta =
    accountingJournalDetailSections.find(item => item.key === activeAccountingJournalDetailSection) || accountingJournalDetailSections[0];

  const buildAccountingScopeItems = () => (
    isFixedAssetAccountingReport
      ? [
          { label: 'Outlet', value: selectedOutletLabel },
          { label: 'Periode', value: selectedAccountingPeriodLabel },
          { label: 'Scope dokumen', value: selectedAccountingFixedAssetScopeLabel },
          { label: 'Status dokumen', value: selectedAccountingFinanceStatusLabel },
          { label: 'Kata kunci', value: selectedAccountingSearchLabel },
        ]
      : isFinanceDocumentAccountingReport
      ? [
          { label: 'Outlet', value: selectedOutletLabel },
          { label: 'Periode', value: selectedAccountingPeriodLabel },
          { label: 'Scope dokumen', value: selectedAccountingFinanceDocumentScopeLabel },
          { label: 'Status dokumen', value: selectedAccountingFinanceStatusLabel },
          ...(isAccountsPayableAgingReport ? [{ label: 'Bucket aging', value: selectedAccountingFinanceAgingBucketLabel }] : []),
          { label: 'Kata kunci', value: selectedAccountingSearchLabel },
        ]
      : [
          { label: 'Outlet', value: selectedOutletLabel },
          { label: 'Periode', value: selectedAccountingPeriodLabel },
          { label: 'Source system', value: selectedAccountingSourceSystemLabel },
          { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
          { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
          { label: 'Kata kunci', value: selectedAccountingSearchLabel },
        ]
  );

  const renderAccountingScopeCard = (eyebrow, title, description) => (
    <section className="section-card report-center-journal-scope-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      <div className="report-center-journal-scope-grid">
        {buildAccountingScopeItems().map(item => (
          <article key={`${title}-${item.label}`} className="report-center-journal-scope-item">
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </article>
        ))}
      </div>
    </section>
  );

  const renderAccountingKpiGrid = cards => (
    <div className="report-center-kpi-grid report-center-kpi-grid--wide">
      {(Array.isArray(cards) ? cards : []).map(item => {
        const Icon = item.icon;
        return (
          <section key={item.key} className="section-card report-center-kpi-card">
            <span className="report-center-kpi-card__icon">
              <Icon />
            </span>
            <div className="report-center-kpi-card__copy">
              <p>{item.label}</p>
              <strong>{item.value}</strong>
            </div>
          </section>
        );
      })}
    </div>
  );

  const renderAccountingDepreciationPolicyCard = (policy, title, description) => (
    <section className="section-card report-center-filter-notice">
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
        <p>
          Metode: <strong>{policy?.method_label || formatAccountingDepreciationMethodLabel(policy?.method)}</strong> |
          Residu default: <strong>{formatCurrency(policy?.residual_value_default || 0)}</strong> |
          Start rule: <strong>{policy?.depreciation_start_rule_label || policy?.depreciation_start_rule || '-'}</strong>
        </p>
      </div>
      <div className="report-center-journal-scope-grid">
        {(Array.isArray(policy?.useful_life_defaults) ? policy.useful_life_defaults : []).map(item => (
          <article key={`${policy?.method || 'policy'}-${item.category || item.category_label}`} className="report-center-journal-scope-item">
            <span>{item.category_label || formatAccountingAssetCategoryLabel(item.category)}</span>
            <strong>{item.useful_life_months ? `${formatDecimalNumber(Number(item.useful_life_months || 0) / 12)} tahun` : '-'}</strong>
          </article>
        ))}
      </div>
    </section>
  );

  const renderCashierSalesSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posCashierSalesSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Top performa kasir', 'Cashier', posCashierSalesData.cashier_breakdown, { countLabel: 'trx' })}
        {renderBreakdownCard('Payment method mix', 'Payment', posCashierSalesData.payment_method_breakdown, { countLabel: 'trx' })}
        {renderBreakdownCard('Sebaran outlet', 'Outlet', posCashierSalesData.outlet_breakdown, { countLabel: 'trx' })}
        {renderBreakdownCard('Jam transaksi dominan', 'Time slot', posCashierSalesData.time_slot_breakdown, { countLabel: 'trx' })}
      </div>
    </>
  );

  const renderCashierSalesAnalysisSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Daily trend</span>
          <h2>Tren performa kasir harian</h2>
          <p>Lihat jumlah kasir aktif, transaksi, item terjual, dan omzet per hari untuk membaca ritme operasional kasir.</p>
        </div>
      </div>
      <AdminDataTable
        columns={posCashierSalesTrendColumns}
        data={posCashierSalesData.trends}
        progressPending={loading}
        pagination={false}
        highlightOnHover
        emptyTitle="Belum ada tren kasir"
        emptyDescription="Ubah outlet, metode bayar, atau periode untuk melihat tren performa kasir."
      />
    </section>
  );

  const renderCashierSalesDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail kasir</span>
          <h2>Daftar performa kasir</h2>
          <p>Audit performa kasir berdasarkan omzet, transaksi, average ticket, kuantitas item, metode bayar dominan, dan aktivitas terakhir.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} kasir</span>
      </div>
      <AdminDataTable
        columns={posCashierSalesColumns}
        data={posCashierSalesPageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada ringkasan kasir"
        emptyDescription="Ubah outlet, metode bayar, atau periode untuk melihat performa penjualan per kasir."
      />
    </section>
  );

  const renderProductSalesSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posProductSalesSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Top produk terjual', 'Product', posProductSalesData.product_breakdown, { countLabel: 'qty' })}
        {renderBreakdownCard('Payment method mix', 'Payment', posProductSalesData.payment_method_breakdown, { countLabel: 'qty' })}
        {renderBreakdownCard('Sebaran outlet', 'Outlet', posProductSalesData.outlet_breakdown, { countLabel: 'qty' })}
        {renderBreakdownCard('Penjualan per kasir', 'Cashier', posProductSalesData.cashier_breakdown, { countLabel: 'qty' })}
        {renderBreakdownCard('Item type mix', 'Item type', posProductSalesData.item_type_breakdown, { countLabel: 'qty' })}
      </div>
    </>
  );

  const renderProductSalesAnalysisSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Daily trend</span>
          <h2>Tren product sales harian</h2>
          <p>Lihat produk aktif, transaksi yang melibatkan produk, qty keluar, dan revenue per hari untuk membaca ritme penjualan per produk.</p>
        </div>
      </div>
      <AdminDataTable
        columns={posProductSalesTrendColumns}
        data={posProductSalesData.trends}
        progressPending={loading}
        pagination={false}
        highlightOnHover
        emptyTitle="Belum ada tren product sales"
        emptyDescription="Pilih outlet, produk, atau periode untuk melihat tren penjualan per produk."
      />
    </section>
  );

  const renderProductSalesDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail produk</span>
          <h2>Daftar product sales POS</h2>
          <p>Audit performa produk berdasarkan qty, omzet, harga jual rata-rata, coverage transaksi, outlet, kasir, dan payment method dominan.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} produk</span>
      </div>
      <AdminDataTable
        columns={posProductSalesColumns}
        data={posProductSalesPageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada data product sales"
        emptyDescription="Ubah produk, outlet, metode bayar, atau periode untuk melihat performa penjualan per produk."
      />
    </section>
  );

  const renderRefundVoidSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posRefundVoidSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Tipe pembatalan', 'Request type', posRefundVoidData.request_type_breakdown, { countLabel: 'request' })}
        {renderBreakdownCard('Status approval', 'Approval flow', posRefundVoidData.status_breakdown, { countLabel: 'request' })}
        {renderBreakdownCard('Pengaju paling aktif', 'Requester', posRefundVoidData.requester_breakdown, { countLabel: 'request' })}
        {renderBreakdownCard('Reviewer paling aktif', 'Reviewer', posRefundVoidData.reviewer_breakdown, { countLabel: 'request' })}
        {renderBreakdownCard('Alasan pembatalan', 'Reason', posRefundVoidData.reason_breakdown, { countLabel: 'request' })}
      </div>
    </>
  );

  const renderRefundVoidAnalysisSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Daily trend</span>
          <h2>Tren pengajuan pembatalan</h2>
          <p>Lihat ritme refund dan void per hari, status approval, exposure nominal, dan refund yang benar-benar terealisasi setelah approval.</p>
        </div>
      </div>
      <AdminDataTable
        columns={posRefundVoidTrendColumns}
        data={posRefundVoidData.trends}
        progressPending={loading}
        pagination={false}
        highlightOnHover
        emptyTitle="Belum ada tren refund / void"
        emptyDescription="Pilih periode, tipe pembatalan, atau status approval untuk melihat tren pengajuan refund dan void."
      />
    </section>
  );

  const renderRefundVoidDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail pembatalan transaksi</span>
          <h2>Daftar refund / void POS</h2>
          <p>Audit pengajuan refund dan void berdasarkan transaksi sumber, metode bayar, pengaju, reviewer, alasan, nominal, dan hasil refund document.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} request</span>
      </div>
      <AdminDataTable
        columns={posRefundVoidColumns}
        data={posRefundVoidPageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada data refund / void"
        emptyDescription="Ubah tipe pembatalan, status approval, metode bayar, atau periode untuk melihat audit pembatalan transaksi."
      />
    </section>
  );

  const renderPurchaseSectionTabs = () => (
    <section className="section-card report-center-subtab-shell">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Purchase workspace</span>
          <h2>Pilih fokus tampilan</h2>
          <p>Gunakan tab ini agar user bisa fokus ke ringkasan, analisa, atau audit detail tanpa harus scroll panjang.</p>
        </div>
        <span className="status-pill">
          <FaLayerGroup /> 3 tampilan
        </span>
      </div>
      <div className="report-center-subtab-nav" role="tablist" aria-label="Tab purchase report">
        {purchaseReportSections.map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={activePurchaseSection === item.key}
              className={`report-center-subtab-button ${activePurchaseSection === item.key ? 'is-active' : ''}`}
              onClick={() => setActivePurchaseSection(item.key)}
            >
              <span className="report-center-subtab-button__icon">
                <Icon />
              </span>
              <span className="report-center-subtab-button__copy">
                <strong>{item.label}</strong>
                <small>{item.copy}</small>
              </span>
            </button>
          );
        })}
      </div>
      <div className="report-center-subtab-active-copy">
        <strong>Tab aktif: {activePurchaseSectionMeta.label}</strong>
        <p>{activePurchaseSectionMeta.copy}</p>
      </div>
    </section>
  );

  const renderPurchaseSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posPurchaseSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Vendor mix', 'Vendor', posPurchaseData.vendor_breakdown, { countLabel: 'dokumen' })}
        {renderBreakdownCard('Payment method mix', 'Payment', posPurchaseData.payment_method_breakdown, { countLabel: 'dokumen' })}
        {renderBreakdownCard('Payment status mix', 'Status', posPurchaseData.payment_status_breakdown, { countLabel: 'dokumen' })}
      </div>
    </>
  );

  const renderPurchaseAnalysisSection = () => (
    <>
      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Top produk dibeli', 'Product', posPurchaseData.item_breakdown, { countLabel: 'kemunculan', valueMode: 'count' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Daily trend</span>
            <h2>Tren purchase harian</h2>
            <p>Lihat jumlah dokumen, qty dibeli, nilai purchase, dan outstanding per hari untuk membaca ritme pembelian outlet.</p>
          </div>
        </div>
        <AdminDataTable
          columns={posPurchaseTrendColumns}
          data={posPurchaseData.trends}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada tren purchase"
          emptyDescription="Pilih outlet atau periode untuk melihat tren purchase outlet."
        />
      </section>
    </>
  );

  const renderPurchaseDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail purchase</span>
          <h2>Daftar purchase POS</h2>
          <p>Audit dokumen purchase berdasarkan invoice, vendor, metode bayar, status hutang, item dibeli, total, dan status sync accounting.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} dokumen</span>
      </div>
      <AdminDataTable
        columns={posPurchaseColumns}
        data={posPurchasePageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada data purchase"
        emptyDescription="Ubah outlet, metode bayar, status bayar, atau periode untuk melihat dokumen purchase."
      />
    </section>
  );

  const renderVendorAgingSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posVendorAgingSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Vendor exposure', 'Vendor', posVendorAgingData.vendor_breakdown, { countLabel: 'bill' })}
        {renderBreakdownCard('Aging bucket', 'Bucket', posVendorAgingData.aging_bucket_breakdown, { countLabel: 'bill' })}
        {renderBreakdownCard('Bill type mix', 'Bill type', posVendorAgingData.bill_type_breakdown, { countLabel: 'bill' })}
        {renderBreakdownCard('Status hutang', 'Status', posVendorAgingData.status_breakdown, { countLabel: 'bill' })}
      </div>
    </>
  );

  const renderVendorAgingAnalysisSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Due timeline</span>
          <h2>Timeline jatuh tempo vendor</h2>
          <p>Lihat sebaran bill berdasarkan tanggal jatuh tempo untuk membaca beban hutang yang mendekat dan yang sudah overdue.</p>
        </div>
      </div>
      <AdminDataTable
        columns={posVendorAgingTimelineColumns}
        data={posVendorAgingData.due_timeline}
        progressPending={loading}
        pagination={false}
        highlightOnHover
        emptyTitle="Belum ada timeline vendor aging"
        emptyDescription="Pilih outlet atau periode untuk melihat sebaran jatuh tempo hutang vendor."
      />
    </section>
  );

  const renderVendorAgingDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail hutang vendor</span>
          <h2>Daftar vendor aging</h2>
          <p>Audit bill outstanding berdasarkan vendor, jenis bill, jatuh tempo, umur hutang, nilai outstanding, dan status sync accounting.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} bill</span>
      </div>
      <AdminDataTable
        columns={posVendorAgingColumns}
        data={posVendorAgingPageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada vendor aging"
        emptyDescription="Ubah vendor, status hutang, bucket aging, atau periode untuk melihat bill hutang vendor."
      />
    </section>
  );

  const renderStockMovementSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posStockMovementSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Movement mix', 'Movement', posStockMovementData.movement_breakdown, { countLabel: 'ledger' })}
        {renderBreakdownCard('Source mix', 'Source', posStockMovementData.reference_breakdown, { countLabel: 'ledger' })}
        {renderBreakdownCard('Item type mix', 'Item type', posStockMovementData.item_type_breakdown, { countLabel: 'ledger' })}
        {renderBreakdownCard('Top produk bergerak', 'Product', posStockMovementData.product_breakdown, { countLabel: 'ledger' })}
      </div>
    </>
  );

  const renderStockMovementAnalysisSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Daily trend</span>
          <h2>Tren mutasi stok harian</h2>
          <p>Lihat ritme stok masuk dan keluar per hari untuk membaca pengaruh purchase, penjualan, refund, adjustment, opname, dan BOM.</p>
        </div>
      </div>
      <AdminDataTable
        columns={posStockMovementTrendColumns}
        data={posStockMovementData.trends}
        progressPending={loading}
        pagination={false}
        highlightOnHover
        emptyTitle="Belum ada tren stock movement"
        emptyDescription="Pilih outlet atau periode untuk melihat mutasi stok harian."
      />
    </section>
  );

  const renderStockMovementDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail mutasi stok</span>
          <h2>Daftar stock movement</h2>
          <p>Audit pergerakan stok dari inventory ledger berdasarkan produk, jenis movement, dokumen sumber, qty, stock before/after, dan nilai cost.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} ledger</span>
      </div>
      <AdminDataTable
        columns={posStockMovementColumns}
        data={posStockMovementPageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada data stock movement"
        emptyDescription="Ubah produk, movement type, source, atau periode untuk melihat mutasi stok."
      />
    </section>
  );

  const renderBOMConsumptionSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posBOMConsumptionSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Top produk jadi', 'Finished good', posBOMConsumptionData.finished_good_breakdown, { countLabel: 'row' })}
        {renderBreakdownCard('Top bahan baku', 'Ingredient', posBOMConsumptionData.ingredient_breakdown, { countLabel: 'row' })}
        {renderBreakdownCard('Sebaran outlet', 'Outlet', posBOMConsumptionData.outlet_breakdown, { countLabel: 'row' })}
        {renderBreakdownCard('Tipe ingredient', 'Item type', posBOMConsumptionData.ingredient_type_breakdown, { countLabel: 'row' })}
      </div>
    </>
  );

  const renderBOMConsumptionAnalysisSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Daily trend</span>
          <h2>Tren BOM consumption harian</h2>
          <p>Lihat transaksi yang mengonsumsi BOM, qty produk jadi terjual, qty bahan baku keluar, dan nilai cost per hari untuk membaca ritme produksi outlet.</p>
        </div>
      </div>
      <AdminDataTable
        columns={posBOMConsumptionTrendColumns}
        data={posBOMConsumptionData.trends}
        progressPending={loading}
        pagination={false}
        highlightOnHover
        emptyTitle="Belum ada tren BOM consumption"
        emptyDescription="Pilih outlet, produk jadi, bahan baku, atau periode untuk melihat tren konsumsi bahan baku."
      />
    </section>
  );

  const renderBOMConsumptionDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail BOM consumption</span>
          <h2>Daftar konsumsi bahan baku</h2>
          <p>Audit konsumsi BOM per transaksi berdasarkan produk jadi, bahan baku, rasio recipe, qty consume, cost, outlet, dan pembayaran transaksi sumber.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} row</span>
      </div>
      <AdminDataTable
        columns={posBOMConsumptionColumns}
        data={posBOMConsumptionPageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada data BOM consumption"
        emptyDescription="Ubah outlet, payment method, produk jadi, bahan baku, atau periode untuk melihat detail konsumsi recipe."
      />
    </section>
  );

  const renderStockOpnameVarianceSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posStockOpnameVarianceSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Arah variance', 'Variance', posStockOpnameVarianceData.variance_breakdown, { countLabel: 'item' })}
        {renderBreakdownCard('Outlet terdampak', 'Outlet', posStockOpnameVarianceData.outlet_breakdown, { countLabel: 'item' })}
        {renderBreakdownCard('Top produk berselisih', 'Product', posStockOpnameVarianceData.product_breakdown, { countLabel: 'item' })}
        {renderBreakdownCard('Item type mix', 'Item type', posStockOpnameVarianceData.item_type_breakdown, { countLabel: 'item' })}
      </div>
    </>
  );

  const renderStockOpnameVarianceAnalysisSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Daily trend</span>
          <h2>Tren variance stock opname</h2>
          <p>Lihat jumlah dokumen opname, item berselisih, qty gain, qty loss, dan nilai variance per hari untuk membaca kualitas kontrol stok.</p>
        </div>
      </div>
      <AdminDataTable
        columns={posStockOpnameVarianceTrendColumns}
        data={posStockOpnameVarianceData.trends}
        progressPending={loading}
        pagination={false}
        highlightOnHover
        emptyTitle="Belum ada tren stock opname variance"
        emptyDescription="Pilih outlet, produk, tipe variance, atau periode untuk melihat tren variance hasil opname."
      />
    </section>
  );

  const renderStockOpnameVarianceDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail variance opname</span>
          <h2>Daftar stock opname variance</h2>
          <p>Audit hasil opname per produk berdasarkan stok sistem, stok aktual, arah selisih, nilai variance, dan status sinkronisasi dokumen.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} item</span>
      </div>
      <AdminDataTable
        columns={posStockOpnameVarianceColumns}
        data={posStockOpnameVariancePageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada data stock opname variance"
        emptyDescription="Ubah produk, tipe variance, outlet, atau periode untuk melihat item opname yang ingin diaudit."
      />
    </section>
  );

  const renderVendorPriceSectionTabs = () => (
    <section className="section-card report-center-subtab-shell">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Procurement workspace</span>
          <h2>Pilih fokus analisa vendor</h2>
          <p>Gunakan tab ini agar owner dan purchasing bisa fokus ke ringkasan, analisa perbandingan harga, atau audit detail per product.</p>
        </div>
        <span className="status-pill">
          <FaLayerGroup /> 3 tampilan
        </span>
      </div>
      <div className="report-center-subtab-nav" role="tablist" aria-label="Tab vendor price comparison">
        {vendorPriceSections.map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={activeVendorPriceSection === item.key}
              className={`report-center-subtab-button ${activeVendorPriceSection === item.key ? 'is-active' : ''}`}
              onClick={() => setActiveVendorPriceSection(item.key)}
            >
              <span className="report-center-subtab-button__icon">
                <Icon />
              </span>
              <span className="report-center-subtab-button__copy">
                <strong>{item.label}</strong>
                <small>{item.copy}</small>
              </span>
            </button>
          );
        })}
      </div>
      <div className="report-center-subtab-active-copy">
        <strong>Tab aktif: {activeVendorPriceSectionMeta.label}</strong>
        <p>{activeVendorPriceSectionMeta.copy}</p>
      </div>
    </section>
  );

  const renderVendorPriceSummarySection = () => (
    <>
      <div className="report-center-kpi-grid report-center-kpi-grid--wide">
        {posVendorPriceComparisonSummaryCards.map(item => {
          const Icon = item.icon;
          return (
            <section key={item.key} className="section-card report-center-kpi-card">
              <span className="report-center-kpi-card__icon">
                <Icon />
              </span>
              <div className="report-center-kpi-card__copy">
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </section>
          );
        })}
      </div>

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Vendor wins', 'Preferred vendor', posVendorPriceComparisonData.vendor_win_breakdown, { countLabel: 'produk' })}
        {renderBreakdownCard('Category savings', 'Category', posVendorPriceComparisonData.category_spread_breakdown, { countLabel: 'produk' })}
      </div>
    </>
  );

  const renderVendorPriceAnalysisSection = () => (
    <>
      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Top price gap</span>
            <h2>Product dengan spread harga terbesar</h2>
            <p>Lihat product yang punya selisih harga vendor paling besar agar tim purchasing cepat tahu titik penghematan yang paling potensial.</p>
          </div>
        </div>
        <AdminDataTable
          columns={posVendorPriceGapColumns}
          data={posVendorPriceComparisonData.top_price_gap_products}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada top gap product"
          emptyDescription="Ubah filter outlet, kategori, vendor, atau periode untuk melihat product dengan spread harga terbesar."
        />
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Vendor benchmark</span>
            <h2>Benchmark vendor per product</h2>
            <p>Bandingkan harga vendor, purchase count, volume, payment mix, dan outlet coverage untuk membaca vendor terbaik secara lebih menyeluruh.</p>
          </div>
          <span className="soft-stat">{formatCount(posVendorPriceComparisonData.vendor_details.length)} vendor-product</span>
        </div>
        <AdminDataTable
          columns={posVendorPriceVendorColumns}
          data={posVendorPriceComparisonData.vendor_details.slice(0, 50)}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada benchmark vendor"
          emptyDescription="Gunakan export Excel jika ingin melihat semua detail vendor-product."
        />
      </section>
    </>
  );

  const renderVendorPriceDetailSection = () => (
    <section className="section-card table-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Detail per product</span>
          <h2>Daftar vendor price comparison</h2>
          <p>Audit preferred vendor, spread harga, harga beli terakhir, volume pembelian, dan risk flag per product sebelum menetapkan vendor kerja sama.</p>
        </div>
        <span className="soft-stat">{formatCount(totalRows)} product</span>
      </div>
      <AdminDataTable
        columns={posVendorPriceComparisonColumns}
        data={posVendorPriceComparisonPageRows}
        progressPending={loading || bootstrapping}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangePage={page => setCurrentPage(page)}
        onChangeRowsPerPage={perPage => {
          setRowsPerPage(perPage);
          setCurrentPage(1);
        }}
        highlightOnHover
        emptyTitle="Belum ada data vendor price comparison"
        emptyDescription="Ubah filter produk, kategori, vendor, tipe item, atau periode untuk melihat perbandingan vendor."
      />
    </section>
  );

  const renderPosFilterCard = () => (
    <section className="section-card report-center-filter-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">{isCashierSalesSummaryReport
            ? 'Cashier Sales Summary'
            : isProductSalesReport
            ? 'Product Sales Report'
            : isBOMConsumptionReport
            ? 'BOM Consumption Report'
            : isPurchaseReport
            ? 'Purchase Report'
            : isRefundVoidReport
              ? 'Refund / Void Report'
              : isVendorAgingReport
                ? 'Vendor Aging'
                : isStockMovementReport
                  ? 'Stock Movement'
                  : isStockOpnameVarianceReport
                    ? 'Stock Opname Variance'
                  : isVendorPriceComparisonReport
                    ? 'Vendor Price Comparison'
                    : 'POS Sales Summary'}</span>
          <h2>Filter report POS</h2>
          <p>{isCashierSalesSummaryReport
            ? 'Gunakan filter ini untuk membaca performa kasir berdasarkan omzet, transaksi, average ticket, metode bayar dominan, dan ritme aktivitas per periode.'
            : isProductSalesReport
            ? 'Gunakan filter ini untuk membaca produk paling laku, qty terjual, harga jual rata-rata, channel pembayaran dominan, dan coverage outlet per periode.'
            : isBOMConsumptionReport
            ? 'Gunakan filter ini untuk membaca konsumsi bahan baku dari penjualan finished good, rasio BOM, qty keluar, dan nilai cost per periode.'
            : isPurchaseReport
            ? 'Gunakan filter ini untuk membaca pembelian stok, vendor, metode bayar, status hutang, dan audit dokumen purchase per periode.'
            : isRefundVoidReport
              ? 'Gunakan filter ini untuk membaca pengajuan refund dan void berdasarkan status approval, metode bayar transaksi, pengaju, reviewer, serta nilai pembatalan per periode.'
              : isVendorAgingReport
                ? 'Gunakan filter ini untuk membaca outstanding hutang vendor berdasarkan jatuh tempo, jenis bill, bucket aging, dan vendor terbesar.'
                : isStockMovementReport
                  ? 'Gunakan filter ini untuk membaca mutasi stok berdasarkan produk, movement type, dokumen sumber, dan nilai cost per periode.'
                  : isStockOpnameVarianceReport
                    ? 'Gunakan filter ini untuk membaca selisih stok hasil opname berdasarkan produk, arah variance, nilai selisih, dan outlet terdampak.'
                  : isVendorPriceComparisonReport
                    ? 'Gunakan filter ini untuk membandingkan vendor termurah per product, spread harga, mode pembandingan, dan potensi penghematan pengadaan.'
                    : 'Gunakan filter ini untuk membaca omzet kasir, channel pembayaran, produk terjual, dan audit transaksi POS per periode.'}</p>
        </div>
        <span className="status-pill">
          <FaFilter /> Filter list
        </span>
      </div>

      <div className="report-center-quick-range">
        {[
          { key: 'today', label: 'Hari ini' },
          { key: 'last-7-days', label: '7 hari' },
          { key: 'last-30-days', label: '30 hari' },
          { key: 'this-month', label: 'Bulan ini' },
        ].map(item => (
          <button
            key={item.key}
            type="button"
            className={`report-center-quick-range__chip ${quickRange === item.key ? 'is-active' : ''}`}
            onClick={() => applyQuickRange(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {hasPendingFilterChanges ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Filter di form belum diterapkan.</strong>
            <p>Tabel masih memakai filter sebelumnya. Klik tampilkan report atau refresh untuk memakai filter yang sedang terlihat.</p>
          </div>
          <Button onClick={applyFilters} disabled={loading}>
            <FaSearch /> Terapkan sekarang
          </Button>
        </div>
      ) : null}

      {isCashierSalesSummaryReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat Cashier Sales Summary.</strong>
            <p>Report ini mengagregasi transaksi menjadi performa per kasir. Cocok dipakai owner atau supervisor untuk membandingkan produktivitas kasir tanpa membuka transaksi satu per satu.</p>
          </div>
        </div>
      ) : isProductSalesReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat product sales.</strong>
            <p>Report ini mengagregasi `item transaksi` menjadi performa per produk. Cocok dipakai owner untuk melihat produk paling laku dan admin outlet untuk membaca harga jual rata-rata.</p>
          </div>
        </div>
      ) : isBOMConsumptionReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat BOM consumption.</strong>
            <p>Report ini membaca `transaction inventory consumption` untuk menampilkan bahan baku yang terkonsumsi dari penjualan finished good. Cocok dipakai owner atau kitchen lead untuk audit recipe yang paling berat biaya.</p>
          </div>
        </div>
      ) : isPurchaseReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat purchase.</strong>
            <p>`Cash`, `Transfer`, dan `QRIS` berarti dokumen langsung lunas. `Tempo / Hutang Vendor` berarti stok masuk tetapi sisa bayarnya masih dipantau di menu vendor.</p>
          </div>
        </div>
      ) : isVendorAgingReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat vendor aging.</strong>
            <p>Report ini membaca `bill hutang vendor` yang masih outstanding. Bucket aging dihitung dari `due date`, dan jika due date kosong sistem memakai tanggal bill sebagai referensi umur hutang.</p>
          </div>
        </div>
      ) : isRefundVoidReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat pembatalan transaksi.</strong>
            <p>Report ini membaca `approval request` refund dan void sebagai sumber audit utama. Refund terealisasi akan menampilkan nominal refund aktual dan status sinkronisasi accounting setelah approval selesai.</p>
          </div>
        </div>
      ) : isStockMovementReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat stock movement.</strong>
            <p>Report ini membaca `inventory ledger` sebagai sumber audit tunggal. Semua purchase, sale, refund, adjustment, opname, dan BOM consumption akan muncul sebagai mutasi stok.</p>
          </div>
        </div>
      ) : isStockOpnameVarianceReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat stock opname variance.</strong>
            <p>Report ini membaca `item stock opname` sebagai sumber audit utama. Default filter menampilkan item yang benar-benar memiliki selisih agar user langsung fokus ke variance yang perlu tindakan.</p>
          </div>
        </div>
      ) : isVendorPriceComparisonReport ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Panduan cepat vendor price comparison.</strong>
            <p>Gunakan `comparison mode` untuk memilih dasar ranking harga vendor, lalu kombinasikan dengan kategori, product, vendor, dan minimum purchase count agar insight procurement tidak bias oleh transaksi tunggal.</p>
          </div>
        </div>
      ) : null}

      <Row className="g-3 align-items-end">
        <Col xl={3} md={6}>
          <Form.Group>
            <Form.Label>Outlet</Form.Label>
            <Form.Select
              value={filters.outlet}
              onChange={event => setFilters(prev => ({ ...prev, outlet: event.target.value }))}
            >
              <option value="">Semua outlet</option>
              {outlets.map(outlet => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        {(!isVendorAgingReport && !isStockMovementReport && !isStockOpnameVarianceReport) ? (
          <Col xl={3} md={6}>
            <Form.Group>
              <Form.Label>{isPurchaseReport ? 'Metode bayar' : 'Metode bayar'}</Form.Label>
              <Form.Select
                value={filters.payment_method}
                onChange={event => setFilters(prev => ({ ...prev, payment_method: event.target.value }))}
              >
                <option value="">Semua metode</option>
                {posPaymentMethodOptions.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {activePosReport === 'sales-summary' ? (
          <Col xl={3} md={6}>
            <Form.Group>
              <Form.Label>Produk fokus AI</Form.Label>
              <Form.Select
                value={filters.product_id}
                onChange={event => setFilters(prev => ({ ...prev, product_id: event.target.value }))}
              >
                <option value="">Semua produk</option>
                {posPricingFocusProductOptions.map(item => (
                  <option key={item.value || item.label} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isProductSalesReport || isBOMConsumptionReport ? (
          <Col xl={3} md={6}>
            <Form.Group>
              <Form.Label>{isBOMConsumptionReport ? 'Produk jadi' : 'Produk'}</Form.Label>
              <Form.Select
                value={filters.product_id}
                onChange={event => setFilters(prev => ({ ...prev, product_id: event.target.value }))}
              >
                <option value="">Semua produk</option>
                {(isBOMConsumptionReport ? posBOMSoldProductOptions : posSalesProductOptions).map(item => (
                  <option key={item.value || item.label} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isBOMConsumptionReport ? (
          <Col xl={3} md={6}>
            <Form.Group>
              <Form.Label>Bahan baku</Form.Label>
              <Form.Select
                value={filters.ingredient_product_id}
                onChange={event => setFilters(prev => ({ ...prev, ingredient_product_id: event.target.value }))}
              >
                <option value="">Semua bahan baku</option>
                {posBOMIngredientOptions.map(item => (
                  <option key={item.value || item.label} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isRefundVoidReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Tipe pembatalan</Form.Label>
              <Form.Select
                value={filters.approval_request_type}
                onChange={event => setFilters(prev => ({ ...prev, approval_request_type: event.target.value }))}
              >
                <option value="">Semua tipe</option>
                {POS_APPROVAL_REQUEST_TYPE_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isRefundVoidReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Status approval</Form.Label>
              <Form.Select
                value={filters.approval_status}
                onChange={event => setFilters(prev => ({ ...prev, approval_status: event.target.value }))}
              >
                <option value="">Semua status</option>
                {POS_APPROVAL_STATUS_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isStockMovementReport || isStockOpnameVarianceReport ? (
          <Col xl={3} md={6}>
            <Form.Group>
              <Form.Label>Produk</Form.Label>
              <Form.Select
                value={filters.product_id}
                onChange={event => setFilters(prev => ({ ...prev, product_id: event.target.value }))}
              >
                <option value="">Semua produk</option>
                {(isStockOpnameVarianceReport ? posStockOpnameProductOptions : posStockProductOptions).map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isVendorPriceComparisonReport ? (
          <Col xl={3} md={6}>
            <Form.Group>
              <Form.Label>Kategori</Form.Label>
              <Form.Select
                value={filters.category_id}
                onChange={event => setFilters(prev => ({ ...prev, category_id: event.target.value }))}
              >
                <option value="">Semua kategori</option>
                {posVendorPriceCategoryOptions.map(item => (
                  <option key={item.value || item.label} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {(isPurchaseReport || isVendorAgingReport || isVendorPriceComparisonReport) ? (
          <Col xl={3} md={6}>
            <Form.Group>
              <Form.Label>Vendor</Form.Label>
              <Form.Select
                value={filters.vendor_name}
                onChange={event => setFilters(prev => ({ ...prev, vendor_name: event.target.value }))}
              >
                <option value="">Semua vendor</option>
                {posVendorOptions.map(item => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isVendorPriceComparisonReport ? (
          <Col xl={3} md={6}>
            <Form.Group>
              <Form.Label>Produk</Form.Label>
              <Form.Select
                value={filters.product_id}
                onChange={event => setFilters(prev => ({ ...prev, product_id: event.target.value }))}
              >
                <option value="">Semua produk</option>
                {posVendorPriceProductOptions.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isPurchaseReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Status bayar</Form.Label>
              <Form.Select
                value={filters.purchase_payment_status}
                onChange={event => setFilters(prev => ({ ...prev, purchase_payment_status: event.target.value }))}
              >
                <option value="">Semua status</option>
                {posPurchaseStatusOptions.map(item => (
                  <option key={item} value={item}>
                    {formatPurchasePaymentStatusLabel(item)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isVendorPriceComparisonReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Status bayar</Form.Label>
              <Form.Select
                value={filters.purchase_payment_status}
                onChange={event => setFilters(prev => ({ ...prev, purchase_payment_status: event.target.value }))}
              >
                <option value="">Semua status</option>
                {posPurchaseStatusOptions.map(item => (
                  <option key={item} value={item}>
                    {formatPurchasePaymentStatusLabel(item)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isVendorAgingReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Jenis bill</Form.Label>
              <Form.Select
                value={filters.vendor_bill_type}
                onChange={event => setFilters(prev => ({ ...prev, vendor_bill_type: event.target.value }))}
              >
                <option value="">Semua jenis</option>
                {POS_VENDOR_BILL_TYPE_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isStockMovementReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Movement type</Form.Label>
              <Form.Select
                value={filters.movement_type}
                onChange={event => setFilters(prev => ({ ...prev, movement_type: event.target.value }))}
              >
                <option value="">Semua movement</option>
                {POS_STOCK_MOVEMENT_TYPE_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isStockMovementReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Source</Form.Label>
              <Form.Select
                value={filters.reference_type}
                onChange={event => setFilters(prev => ({ ...prev, reference_type: event.target.value }))}
              >
                <option value="">Semua source</option>
                {POS_STOCK_REFERENCE_TYPE_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isStockOpnameVarianceReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Tipe variance</Form.Label>
              <Form.Select
                value={filters.variance_type}
                onChange={event => setFilters(prev => ({ ...prev, variance_type: event.target.value }))}
              >
                {POS_STOCK_OPNAME_VARIANCE_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isVendorPriceComparisonReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Item type</Form.Label>
              <Form.Select
                value={filters.item_type}
                onChange={event => setFilters(prev => ({ ...prev, item_type: event.target.value }))}
              >
                <option value="">Semua tipe</option>
                {POS_VENDOR_PRICE_ITEM_TYPE_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isVendorPriceComparisonReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Comparison mode</Form.Label>
              <Form.Select
                value={filters.comparison_mode}
                onChange={event => setFilters(prev => ({ ...prev, comparison_mode: event.target.value }))}
              >
                {POS_VENDOR_PRICE_COMPARISON_MODE_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isVendorPriceComparisonReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Min transaksi beli</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="1"
                value={filters.min_purchase_count}
                onChange={event => setFilters(prev => ({ ...prev, min_purchase_count: event.target.value }))}
              />
            </Form.Group>
          </Col>
        ) : null}
        {isVendorAgingReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Status hutang</Form.Label>
              <Form.Select
                value={filters.vendor_bill_status}
                onChange={event => setFilters(prev => ({ ...prev, vendor_bill_status: event.target.value }))}
              >
                <option value="">Outstanding saja</option>
                {POS_VENDOR_BILL_STATUS_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        {isVendorAgingReport ? (
          <Col xl={2} md={6}>
            <Form.Group>
              <Form.Label>Bucket aging</Form.Label>
              <Form.Select
                value={filters.aging_bucket}
                onChange={event => setFilters(prev => ({ ...prev, aging_bucket: event.target.value }))}
              >
                <option value="">Semua bucket</option>
                {POS_VENDOR_AGING_BUCKET_OPTIONS.map(item => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ) : null}
        <Col xl={2} md={6}>
          <Form.Group>
            <Form.Label>Tanggal awal</Form.Label>
            <Form.Control
              type="date"
              value={filters.dateFrom}
              onChange={event => setFilters(prev => ({ ...prev, dateFrom: event.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col xl={2} md={6}>
          <Form.Group>
            <Form.Label>Tanggal akhir</Form.Label>
            <Form.Control
              type="date"
              value={filters.dateTo}
              onChange={event => setFilters(prev => ({ ...prev, dateTo: event.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col xl={isVendorPriceComparisonReport ? 4 : isPurchaseReport || isRefundVoidReport || isVendorAgingReport || isStockMovementReport || isStockOpnameVarianceReport || isProductSalesReport || isBOMConsumptionReport ? 8 : 6} md={12}>
          <Form.Group>
            <Form.Label>Cari cepat</Form.Label>
            <Form.Control
              value={filters.search}
              onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
              placeholder={isCashierSalesSummaryReport
                ? 'Nama kasir, outlet, payment method, ID transaksi, catatan, atau nama produk'
                : isProductSalesReport
                ? 'Nama produk, item type, payment method, kasir, outlet, atau transaksi'
                : isBOMConsumptionReport
                ? 'Produk jadi, bahan baku, transaction id, payment method, kasir, outlet, atau catatan'
                : isPurchaseReport
                ? 'Invoice, vendor, payment method, status bayar, catatan, atau nama produk'
                : isRefundVoidReport
                  ? 'Request id, transaksi, alasan, pengaju, reviewer, payment method, atau nama produk'
                : isVendorAgingReport
                  ? 'Bill no, vendor, jenis bill, status hutang, purchase, atau catatan'
                  : isStockMovementReport
                    ? 'Produk, movement, source, reference id, item type, atau catatan'
                    : isStockOpnameVarianceReport
                      ? 'Produk, opname id, item id, stok sistem, stok aktual, variance, atau catatan'
                    : isVendorPriceComparisonReport
                      ? 'Produk, vendor, kategori, unit, atau risk flag'
                : 'ID transaksi, payment method, cashier, catatan, atau nama produk'}
            />
          </Form.Group>
        </Col>
        <Col xl={4} md={12}>
          <div className="report-center-filter-actions">
            <Button onClick={applyFilters} disabled={loading}>
              <FaSearch /> Tampilkan report
            </Button>
            <Button variant="outline-secondary" onClick={resetFilters} disabled={loading}>
              <FaRedoAlt /> Reset
            </Button>
          </div>
        </Col>
      </Row>
    </section>
  );

  const renderAccountingFilterCard = () => (
    <section className="section-card report-center-filter-card">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">{activeAccountingReportMeta.label}</span>
          <h2>Filter report accounting</h2>
          <p>{isGeneralLedgerReport
            ? 'Gunakan filter ini untuk membaca mutasi per akun berdasarkan outlet, periode, source system, business unit, status posting, dan akun ledger yang relevan.'
            : isTrialBalanceReport
              ? 'Gunakan filter ini untuk memeriksa saldo awal, mutasi, dan saldo akhir semua akun dalam satu trial balance.'
              : isProfitLossReport
                ? 'Gunakan filter ini untuk membaca revenue, expense, dan net profit dari ledger pusat pada periode terpilih.'
                : isBalanceSheetReport
                  ? 'Gunakan filter ini untuk membaca posisi asset, liability, dan equity berdasarkan saldo akhir ledger pusat.'
                  : isCashFlowReport
                    ? 'Gunakan filter ini untuk membaca arus kas operasi, investasi, pendanaan, dan perpindahan antar akun kas/bank dari ledger pusat.'
                    : isAccountsPayableAgingReport
                      ? 'Gunakan filter ini untuk membaca hutang usaha dari dokumen finance pusat berdasarkan scope dokumen, status, umur hutang, dan vendor yang masih outstanding.'
                      : isExpenseBreakdownReport
                        ? 'Gunakan filter ini untuk membaca breakdown biaya dari dokumen finance pusat berdasarkan scope dokumen, status, kategori, vendor, dan periode input dokumen.'
                        : isAssetRegisterReport
                          ? 'Gunakan filter ini untuk membaca daftar aset dari dokumen pembelian aset pusat berdasarkan outlet, periode perolehan, status dokumen, dan kata kunci aset.'
                          : isDepreciationScheduleReport
                            ? 'Gunakan filter ini untuk membaca estimasi penyusutan aset pusat berdasarkan outlet, periode report, status dokumen, dan kata kunci aset yang ingin diaudit.'
                : 'Gunakan filter ini untuk audit jurnal pusat berdasarkan outlet, periode, source system, business unit, status posting, dan account line yang muncul di ledger.'}</p>
        </div>
        <span className="status-pill">
          <FaFilter /> Filter list
        </span>
      </div>

      <div className="report-center-quick-range">
        {[
          { key: 'today', label: 'Hari ini' },
          { key: 'last-7-days', label: '7 hari' },
          { key: 'last-30-days', label: '30 hari' },
          { key: 'this-month', label: 'Bulan ini' },
        ].map(item => (
          <button
            key={item.key}
            type="button"
            className={`report-center-quick-range__chip ${quickRange === item.key ? 'is-active' : ''}`}
            onClick={() => applyQuickRange(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {hasPendingFilterChanges ? (
        <div className="report-center-filter-notice">
          <div>
            <strong>Filter di form belum diterapkan.</strong>
            <p>Tabel masih memakai filter sebelumnya. Klik tampilkan report atau refresh untuk memakai filter yang sedang terlihat.</p>
          </div>
          <Button onClick={applyFilters} disabled={loading}>
            <FaSearch /> Terapkan sekarang
          </Button>
        </div>
      ) : null}

      <div className="report-center-filter-notice">
        <div>
          <strong>{isGeneralLedgerReport
            ? 'Panduan cepat General Ledger.'
            : isTrialBalanceReport
              ? 'Panduan cepat Trial Balance.'
              : isProfitLossReport
                ? 'Panduan cepat Profit & Loss.'
                : isBalanceSheetReport
                  ? 'Panduan cepat Balance Sheet.'
                  : isCashFlowReport
                    ? 'Panduan cepat Cash Flow.'
                    : isAccountsPayableAgingReport
                      ? 'Panduan cepat AP Aging.'
                      : isExpenseBreakdownReport
                        ? 'Panduan cepat Expense Breakdown.'
                        : isAssetRegisterReport
                          ? 'Panduan cepat Asset Register.'
                          : isDepreciationScheduleReport
                            ? 'Panduan cepat Depreciation Schedule.'
                  : 'Panduan cepat Journal Report.'}</strong>
          <p>{isGeneralLedgerReport
            ? 'Report ini membaca `tbjournal` dan `tbjournal_detail` dari ledger pusat lalu menyusunnya per akun. Cocok dipakai untuk audit saldo awal, mutasi debit-credit, dan saldo akhir sebelum trial balance atau financial statement.'
            : isTrialBalanceReport
              ? 'Report ini merangkum saldo awal, mutasi debit-credit, dan saldo akhir seluruh akun. Cocok dipakai untuk cek keseimbangan ledger sebelum baca financial statement.'
              : isProfitLossReport
                ? 'Report ini memfokuskan ledger ke akun revenue dan expense agar owner bisa membaca performa laba rugi per periode tanpa keluar dari Analytics Center.'
                : isBalanceSheetReport
                  ? 'Report ini memfokuskan ledger ke akun asset, liability, dan equity untuk melihat posisi keuangan dan gap keseimbangan di akhir periode.'
                  : isCashFlowReport
                    ? 'Report ini membaca mutasi akun kas/bank lalu mengelompokkan akun lawannya ke operating, investing, financing, dan internal transfer supaya owner bisa membaca arus kas tanpa merakit jurnal manual.'
                    : isAccountsPayableAgingReport
                    ? 'Report ini membaca dokumen payable finance pusat yang masih outstanding lalu mengelompokkannya ke bucket aging berdasarkan due date. Cocok dipakai owner untuk memprioritaskan vendor dan bill yang paling mendesak.'
                      : isExpenseBreakdownReport
                        ? 'Report ini membaca dokumen finance pusat per kategori, vendor, dan scope dokumen untuk memperlihatkan biaya mana yang paling besar serta berapa yang masih outstanding.'
                        : isAssetRegisterReport
                          ? 'Report ini membaca dokumen asset purchase dan asset payable bill dari finance pusat. Cocok dipakai untuk audit aset, vendor, akun aset, outstanding capex, dan estimasi nilai buku tanpa pindah ke workspace input.'
                          : isDepreciationScheduleReport
                            ? 'Report ini membuat jadwal penyusutan straight-line berbasis kategori aset. Cocok dipakai untuk melihat estimasi depresiasi periode, akumulasi penyusutan, dan nilai buku aset sambil menunggu modul fixed asset yang lebih detail.'
                  : 'Report ini membaca `tbjournal` dan `tbjournal_detail` dari ledger pusat. Cocok dipakai untuk audit jurnal harian sebelum lanjut ke general ledger, trial balance, atau financial statement.'}</p>
        </div>
      </div>

      <Row className="g-3 align-items-end">
        <Col xl={3} md={6}>
          <Form.Group>
            <Form.Label>Outlet</Form.Label>
            <Form.Select
              value={filters.outlet}
              onChange={event => setFilters(prev => ({ ...prev, outlet: event.target.value }))}
            >
              <option value="">Semua outlet</option>
              {outlets.map(outlet => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        {isFinanceDocumentAccountingReport ? (
          <>
            {!isFixedAssetAccountingReport ? (
              <Col xl={3} md={6}>
                <Form.Group>
                  <Form.Label>Scope dokumen</Form.Label>
                  <Form.Select
                    value={filters.accounting_document_scope}
                    onChange={event => setFilters(prev => ({ ...prev, accounting_document_scope: event.target.value }))}
                  >
                    {ACCOUNTING_FINANCE_SCOPE_OPTIONS.map(item => (
                      <option key={item.value || 'all'} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            ) : null}
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Status dokumen</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={event => setFilters(prev => ({ ...prev, status: event.target.value }))}
                >
                  {ACCOUNTING_FINANCE_STATUS_OPTIONS.map(item => (
                    <option key={item.value || 'all'} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {isAccountsPayableAgingReport ? (
              <Col xl={2} md={6}>
                <Form.Group>
                  <Form.Label>Bucket aging</Form.Label>
                  <Form.Select
                    value={filters.aging_bucket}
                    onChange={event => setFilters(prev => ({ ...prev, aging_bucket: event.target.value }))}
                  >
                    {ACCOUNTING_FINANCE_AGING_BUCKET_OPTIONS.map(item => (
                      <option key={item.value || 'all'} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            ) : null}
          </>
        ) : (
          <>
            <Col xl={3} md={6}>
              <Form.Group>
                <Form.Label>Source system</Form.Label>
                <Form.Select
                  value={filters.source_system}
                  onChange={event => setFilters(prev => ({ ...prev, source_system: event.target.value }))}
                >
                  <option value="">Semua source</option>
                  <option value="fitness">Fitness</option>
                  <option value="pos">POS</option>
                  <option value="manual">Manual</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Business unit</Form.Label>
                <Form.Select
                  value={filters.business_unit}
                  onChange={event => setFilters(prev => ({ ...prev, business_unit: event.target.value }))}
                >
                  <option value="">Semua unit</option>
                  <option value="fitness">Fitness</option>
                  <option value="pos">POS</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xl={2} md={6}>
              <Form.Group>
                <Form.Label>Status posting</Form.Label>
                <Form.Select
                  value={filters.accounting_posting_status}
                  onChange={event => setFilters(prev => ({ ...prev, accounting_posting_status: event.target.value }))}
                >
                  <option value="">Semua status</option>
                  <option value="posted">Posted</option>
                  <option value="pending">Pending</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </>
        )}
        <Col xl={2} md={6}>
          <Form.Group>
            <Form.Label>Tanggal awal</Form.Label>
            <Form.Control
              type="date"
              value={filters.dateFrom}
              onChange={event => setFilters(prev => ({ ...prev, dateFrom: event.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col xl={2} md={6}>
          <Form.Group>
            <Form.Label>Tanggal akhir</Form.Label>
            <Form.Control
              type="date"
              value={filters.dateTo}
              onChange={event => setFilters(prev => ({ ...prev, dateTo: event.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col xl={isFinanceDocumentAccountingReport ? 7 : 8} md={12}>
          <Form.Group>
            <Form.Label>Cari cepat</Form.Label>
            <Form.Control
              value={filters.search}
              onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
              placeholder={isGeneralLedgerReport
                ? 'Account id, nama akun, atau kode akun yang ingin dibaca mutasinya'
                : isTrialBalanceReport
                  ? 'Account id, nama akun, atau kategori akun yang ingin dicek keseimbangannya'
                  : isProfitLossReport
                    ? 'Akun revenue, akun expense, atau kata kunci akun yang ingin dibaca'
                    : isBalanceSheetReport
                    ? 'Akun asset, liability, equity, atau kata kunci akun yang ingin dibaca'
                      : isCashFlowReport
                        ? 'Journal id, akun kas, akun lawan, section arus kas, outlet, atau source reference'
                        : isAccountsPayableAgingReport
                          ? 'Document no, vendor, kategori, akun hutang, outlet, atau deskripsi bill'
                          : isExpenseBreakdownReport
                            ? 'Document no, vendor, kategori, akun expense, outlet, atau deskripsi biaya'
                            : isAssetRegisterReport
                              ? 'Document no, nama aset, vendor, kategori aset, akun aset, outlet, atau reference capex'
                              : isDepreciationScheduleReport
                                ? 'Document no, nama aset, vendor, kategori aset, akun aset, atau status penyusutan'
                : 'Journal id, deskripsi, source type, source id, external reference, outlet, account id, atau nama akun'}
            />
          </Form.Group>
        </Col>
        <Col xl={4} md={12}>
          <div className="report-center-filter-actions">
            <Button onClick={applyFilters} disabled={loading}>
              <FaSearch /> Tampilkan report
            </Button>
            <Button variant="outline-secondary" onClick={resetFilters} disabled={loading}>
              <FaRedoAlt /> Reset
            </Button>
          </div>
        </Col>
      </Row>
    </section>
  );

  const renderAccountingJournalSectionTabs = () => (
    <section className="section-card report-center-subtab-shell">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Journal workspace</span>
          <h2>Pilih fokus tampilan</h2>
          <p>Susun pembacaan report menjadi tiga langkah: pahami konteks, baca pola, lalu audit jurnal satu per satu.</p>
        </div>
        <span className="status-pill">
          <FaLayerGroup /> 3 tampilan
        </span>
      </div>
      <div className="report-center-subtab-nav" role="tablist" aria-label="Tab journal report">
        {accountingJournalSections.map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={activeAccountingJournalSection === item.key}
              className={`report-center-subtab-button ${activeAccountingJournalSection === item.key ? 'is-active' : ''}`}
              onClick={() => setActiveAccountingJournalSection(item.key)}
            >
              <span className="report-center-subtab-button__icon">
                <Icon />
              </span>
              <span className="report-center-subtab-button__copy">
                <strong>{item.label}</strong>
                <small>{item.copy}</small>
              </span>
            </button>
          );
        })}
      </div>
      <div className="report-center-subtab-active-copy">
        <strong>Tab aktif: {activeAccountingJournalSectionMeta.label}</strong>
        <p>{activeAccountingJournalSectionMeta.copy}</p>
      </div>
    </section>
  );

  const renderAccountingJournalSummarySection = () => {
    const scopeItems = [
      { label: 'Outlet', value: selectedOutletLabel },
      { label: 'Periode', value: selectedAccountingPeriodLabel },
      { label: 'Source system', value: selectedAccountingSourceSystemLabel },
      { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
      { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
      { label: 'Kata kunci', value: selectedAccountingSearchLabel },
    ];
    const guideSteps = [
      {
        label: '1. Mulai dari KPI',
        copy: 'Pastikan total debit dan credit bergerak seimbang, lalu lihat berapa jurnal yang masih pending.',
      },
      {
        label: '2. Cek sebaran',
        copy: 'Baca source system, business unit, dan outlet untuk tahu domain mana yang paling banyak mengisi ledger.',
      },
      {
        label: '3. Lanjut audit detail',
        copy: 'Masuk ke tab Audit Detail saat menemukan pola yang perlu dicek sampai level line jurnal.',
      },
    ];

    return (
      <>
        <div className="report-center-journal-focus-grid">
          <section className="section-card report-center-journal-scope-card">
            <div className="report-center-section-head">
              <div>
                <span className="page-eyebrow">Current scope</span>
                <h2>Ringkasan filter aktif</h2>
                <p>User bisa lihat langsung laporan ini sedang membaca ruang audit yang mana tanpa mengingat semua filter di form.</p>
              </div>
            </div>
            <div className="report-center-journal-scope-grid">
              {scopeItems.map(item => (
                <article key={item.label} className="report-center-journal-scope-item">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </article>
              ))}
            </div>
          </section>

          <section className="section-card report-center-journal-guide-card">
            <div className="report-center-section-head">
              <div>
                <span className="page-eyebrow">Cara baca</span>
                <h2>Alur audit yang disarankan</h2>
                <p>Tampilan ini sengaja diringkas supaya owner atau finance tidak perlu langsung terjun ke tabel panjang.</p>
              </div>
            </div>
            <div className="report-center-journal-guide-list">
              {guideSteps.map(item => (
                <article key={item.label} className="report-center-journal-guide-step">
                  <strong>{item.label}</strong>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
          </section>
        </div>

        <div className="report-center-kpi-grid report-center-kpi-grid--wide">
          {accountingJournalSummaryCards.map(item => {
            const Icon = item.icon;
            return (
              <section key={item.key} className="section-card report-center-kpi-card">
                <span className="report-center-kpi-card__icon">
                  <Icon />
                </span>
                <div className="report-center-kpi-card__copy">
                  <p>{item.label}</p>
                  <strong>{item.value}</strong>
                </div>
              </section>
            );
          })}
        </div>

        <div className="report-center-breakdown-grid">
          {renderBreakdownCard('Source system mix', 'Source', accountingJournalData.source_breakdown, { countLabel: 'jurnal' })}
          {renderBreakdownCard('Business unit mix', 'Business unit', accountingJournalData.business_unit_breakdown, { countLabel: 'jurnal' })}
          {renderBreakdownCard('Posting status', 'Status', accountingJournalData.posting_status_breakdown, { countLabel: 'jurnal' })}
          {renderBreakdownCard('Sebaran outlet', 'Outlet', accountingJournalData.outlet_breakdown, { countLabel: 'jurnal' })}
        </div>
      </>
    );
  };

  const renderAccountingJournalAnalysisSection = () => (
    <>
      <section className="section-card report-center-journal-analysis-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Analysis mode</span>
            <h2>Fokus pada pola ledger</h2>
            <p>Tab ini cocok dipakai setelah ringkasan awal. User bisa membaca ritme posting harian lalu turun ke akun yang paling aktif bergerak.</p>
          </div>
        </div>
        <div className="report-center-journal-scope-grid">
          <article className="report-center-journal-scope-item">
            <span>Jurnal posted</span>
            <strong>{formatCount(accountingJournalData.summary.posted_journals)}</strong>
          </article>
          <article className="report-center-journal-scope-item">
            <span>Jurnal pending</span>
            <strong>{formatCount(accountingJournalData.summary.pending_journals)}</strong>
          </article>
          <article className="report-center-journal-scope-item">
            <span>Akun unik</span>
            <strong>{formatCount(accountingJournalData.summary.unique_accounts)}</strong>
          </article>
          <article className="report-center-journal-scope-item">
            <span>Outlet tercakup</span>
            <strong>{formatCount(accountingJournalData.summary.unique_outlets)}</strong>
          </article>
        </div>
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Daily journal trend</span>
            <h2>Tren jurnal harian</h2>
            <p>Gunakan tren ini untuk membaca kepadatan posting harian dan memastikan debit serta credit bergerak konsisten sepanjang periode audit.</p>
          </div>
        </div>
        <AdminDataTable
          columns={accountingJournalTrendColumns}
          data={accountingJournalData.trends}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada tren jurnal"
          emptyDescription="Ubah filter outlet, source system, business unit, atau periode untuk melihat tren jurnal."
        />
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Account breakdown</span>
            <h2>Breakdown akun ledger</h2>
            <p>Lihat akun yang paling sering bergerak, total debit-credit, dan net movement untuk audit chart of accounts yang paling aktif.</p>
          </div>
          <span className="soft-stat">{formatCount(accountingJournalData.account_breakdown.length)} akun</span>
        </div>
        <AdminDataTable
          columns={accountingJournalAccountColumns}
          data={accountingJournalData.account_breakdown}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada breakdown akun"
          emptyDescription="Belum ada line jurnal yang cocok dengan filter accounting saat ini."
        />
      </section>
    </>
  );

  const renderAccountingJournalDetailSection = () => {
    const selectedRow = selectedAccountingJournalRow;
    const balanceDiff = Math.abs(Number(selectedRow?.total_debit || 0) - Number(selectedRow?.total_credit || 0));
    const isBalanced = isBalancedAmount(selectedRow?.total_debit, selectedRow?.total_credit);
    const journalLines = sortAccountingJournalLinesForDisplay(selectedRow?.lines);
    const pairedJournal = selectedRow
      ? accountingJournalData.results.find(row =>
          Number(row?.id || 0) !== Number(selectedRow.id || 0) &&
          String(row?.source_system || '').trim().toLowerCase() === String(selectedRow.source_system || '').trim().toLowerCase() &&
          String(row?.source_id || '').trim() !== '' &&
          String(row?.source_id || '').trim() === String(selectedRow.source_id || '').trim() &&
          String(row?.external_reference || '').trim() === String(selectedRow.external_reference || '').trim()
        ) || null
      : null;

    return (
      <>
        <section className="section-card table-card">
          <div className="report-center-section-head">
            <div>
              <span className="page-eyebrow">Journal detail</span>
              <h2>Daftar jurnal accounting</h2>
              <p>Klik satu baris untuk membuka inspector jurnal di bawah. User tidak perlu menebak line akun dari preview singkat saja.</p>
            </div>
            <span className="soft-stat">{formatCount(totalRows)} jurnal</span>
          </div>
          <AdminDataTable
            columns={accountingJournalColumns}
            data={accountingJournalPageRows}
            progressPending={loading || bootstrapping}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={rowsPerPage}
            onChangePage={page => setCurrentPage(page)}
            onChangeRowsPerPage={perPage => {
              setRowsPerPage(perPage);
              setCurrentPage(1);
            }}
            onRowClicked={row => setSelectedAccountingJournalId(row.id)}
            conditionalRowStyles={accountingJournalRowStyles}
            highlightOnHover
            emptyTitle="Belum ada data journal report"
            emptyDescription="Ubah outlet, periode, source system, business unit, atau kata kunci pencarian untuk melihat jurnal accounting."
          />
        </section>

        <section className="section-card report-center-journal-detail-shell">
          <div className="report-center-section-head">
            <div>
              <span className="page-eyebrow">Journal inspector</span>
              <h2>{selectedRow ? `Audit ${formatAccountingJournalNumber(selectedRow.id, selectedRow.occurred_at)}` : 'Pilih jurnal untuk melihat detail'}</h2>
              <p>Panel ini menampilkan konteks jurnal dan seluruh line debit-credit dari baris yang sedang dipilih di tabel.</p>
            </div>
            {selectedRow ? (
              <span className={`report-center-status ${isBalanced ? 'is-success' : 'is-failed'}`}>
                {isBalanced ? 'Balanced' : `Selisih ${formatCurrency(balanceDiff)}`}
              </span>
            ) : null}
          </div>

          {selectedRow ? (
            <>
              <section className="section-card report-center-subtab-shell">
                <div className="report-center-section-head">
                  <div>
                    <span className="page-eyebrow">Inspector workspace</span>
                    <h2>Pilih fokus jurnal terpilih</h2>
                    <p>Pecah tampilan per jurnal menjadi beberapa bagian supaya lebih rapi dan tidak menumpuk semua informasi dalam satu layar.</p>
                  </div>
                  <span className="status-pill">
                    <FaLayerGroup /> 3 tampilan
                  </span>
                </div>
                <div className="report-center-subtab-nav" role="tablist" aria-label="Tab detail jurnal">
                  {accountingJournalDetailSections.map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        role="tab"
                        aria-selected={activeAccountingJournalDetailSection === item.key}
                        className={`report-center-subtab-button ${activeAccountingJournalDetailSection === item.key ? 'is-active' : ''}`}
                        onClick={() => setActiveAccountingJournalDetailSection(item.key)}
                      >
                        <span className="report-center-subtab-button__icon">
                          <Icon />
                        </span>
                        <span className="report-center-subtab-button__copy">
                          <strong>{item.label}</strong>
                          <small>{item.copy}</small>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="report-center-subtab-active-copy">
                  <strong>Tab aktif: {activeAccountingJournalDetailSectionMeta.label}</strong>
                  <p>{activeAccountingJournalDetailSectionMeta.copy}</p>
                </div>
              </section>

              {activeAccountingJournalDetailSection === 'overview' ? (
                <section className="section-card report-center-journal-overview-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Ringkasan jurnal</span>
                      <h2>Header dan konteks jurnal</h2>
                      <p>Buka bagian ini saat ingin memahami jurnal terpilih lebih cepat sebelum masuk ke format jurnal umum atau audit teknis.</p>
                    </div>
                  </div>
                  <div className="report-center-journal-meta-grid">
                    <article className="report-center-journal-meta-card report-center-journal-meta-card--wide">
                      <span>Keterangan jurnal</span>
                      <strong>{selectedRow.description || formatAccountingJournalNumber(selectedRow.id, selectedRow.occurred_at)}</strong>
                      <small>Reference / Invoice: {selectedRow.external_reference || '-'} • Source ID: {selectedRow.source_id || '-'}</small>
                    </article>
                    <article className="report-center-journal-meta-card">
                      <span>Nomor jurnal</span>
                      <strong>{formatAccountingJournalNumber(selectedRow.id, selectedRow.occurred_at)}</strong>
                      <small>ID internal #{selectedRow.id || '-'}</small>
                    </article>
                    <article className="report-center-journal-meta-card">
                      <span>Tanggal jurnal</span>
                      <strong>{formatDateTime(selectedRow.occurred_at)}</strong>
                      <small>Dibuat {formatDateTime(selectedRow.created_at)}</small>
                    </article>
                    <article className="report-center-journal-meta-card">
                      <span>Reference / Invoice</span>
                      <strong>{selectedRow.external_reference || '-'}</strong>
                      <small>Source ID {selectedRow.source_id || '-'}</small>
                    </article>
                    <article className="report-center-journal-meta-card">
                      <span>Outlet & jenis jurnal</span>
                      <strong>{selectedRow.outlet_name || '-'}</strong>
                      <small>{formatAccountingSourceSystemLabel(selectedRow.source_system)} • {formatAccountingSourceTypeLabel(selectedRow.source_type)}</small>
                    </article>
                    <article className="report-center-journal-meta-card">
                      <span>Unit & posting</span>
                      <strong>{formatAccountingBusinessUnitLabel(selectedRow.business_unit)}</strong>
                      <small>{formatAccountingPostingStatusLabel(selectedRow.posting_status)} • {formatDateTime(selectedRow.posted_at)}</small>
                    </article>
                    <article className="report-center-journal-meta-card">
                      <span>Total jurnal</span>
                      <strong>{formatCurrency(selectedRow.total_debit)}</strong>
                      <small>Kredit {formatCurrency(selectedRow.total_credit)}</small>
                    </article>
                    <article className="report-center-journal-meta-card">
                      <span>Jumlah baris</span>
                      <strong>{formatCount(selectedRow.line_count)} line</strong>
                      <small>{formatCount(getAccountingSideLines(journalLines, 'debit').length)} debit • {formatCount(getAccountingSideLines(journalLines, 'credit').length)} kredit</small>
                    </article>
                    {pairedJournal ? (
                      <article className="report-center-journal-meta-card report-center-journal-meta-card--wide">
                        <span>Pasangan jurnal transaksi</span>
                        <strong>{formatAccountingSourceTypeLabel(pairedJournal.source_type)}</strong>
                        <small>{formatAccountingJournalNumber(pairedJournal.id, pairedJournal.occurred_at)} • {pairedJournal.description || pairedJournal.external_reference || '-'}</small>
                      </article>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {activeAccountingJournalDetailSection === 'general' ? (
                <section className="section-card report-center-journal-standard-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Jurnal umum</span>
                      <h2>Format jurnal sesuai praktik Indonesia</h2>
                      <p>Baris jurnal disajikan dengan pola yang lazim dipakai di Indonesia: `COA`, `Account and Description`, `Debit`, dan `Kredit`.</p>
                    </div>
                    <span className={`report-center-status ${isBalanced ? 'is-success' : 'is-failed'}`}>
                      {isBalanced ? 'Seimbang' : `Selisih ${formatCurrency(balanceDiff)}`}
                    </span>
                  </div>

                  <div className="report-center-journal-standard-sheet">
                    <div className="report-center-journal-standard-header">
                      <div>
                        <span>Nomor Jurnal</span>
                        <strong>{formatAccountingJournalNumber(selectedRow.id, selectedRow.occurred_at)}</strong>
                      </div>
                      <div>
                        <span>Tanggal</span>
                        <strong>{formatDateTime(selectedRow.occurred_at)}</strong>
                      </div>
                      <div>
                        <span>Reference / Invoice</span>
                        <strong>{selectedRow.external_reference || selectedRow.source_id || '-'}</strong>
                      </div>
                      <div>
                        <span>Jenis Jurnal</span>
                        <strong>{formatAccountingSourceTypeLabel(selectedRow.source_type)}</strong>
                      </div>
                    </div>

                    <div className="report-center-journal-standard-copy">
                      <span>Keterangan</span>
                      <strong>{selectedRow.description || '-'}</strong>
                      <small>
                        Outlet {selectedRow.outlet_name || '-'} • {formatAccountingSourceSystemLabel(selectedRow.source_system)} • {formatAccountingBusinessUnitLabel(selectedRow.business_unit)} • {formatAccountingPostingStatusLabel(selectedRow.posting_status)}
                      </small>
                    </div>

                    <div className="report-center-journal-standard-table-shell">
                      <table className="report-center-journal-standard-table">
                        <thead>
                          <tr>
                            <th>No</th>
                            <th>COA</th>
                            <th>Account and Description</th>
                            <th>Debit</th>
                            <th>Kredit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {journalLines.length ? journalLines.map((line, index) => (
                            <tr key={`${line.account_id || line.account_name || index}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{line.account_id || '-'}</td>
                              <td>
                                <div className={`report-center-journal-standard-account ${getAccountingLineSideKey(line) === 'credit' ? 'is-credit' : ''}`}>
                                  <strong>{line.account_name || line.account_id || '-'}</strong>
                                  <small>COA {line.account_id || '-'} • {formatAccountingLineSideLabel(line)} • {line.account_type || 'Uncategorized'}</small>
                                </div>
                              </td>
                              <td>{Number(line.debit || 0) > 0 ? formatCurrency(line.debit) : '-'}</td>
                              <td>{Number(line.credit || 0) > 0 ? formatCurrency(line.credit) : '-'}</td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="5">Belum ada line jurnal.</td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="3">Total</td>
                            <td>{formatCurrency(selectedRow.total_debit)}</td>
                            <td>{formatCurrency(selectedRow.total_credit)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </section>
              ) : null}

              {activeAccountingJournalDetailSection === 'audit' ? (
                <section className="report-center-journal-lines-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Audit teknis</span>
                      <h2>Detail line jurnal</h2>
                      <p>Tabel ini tetap tersedia untuk kebutuhan audit teknis, sortir cepat, dan pemeriksaan nominal per line.</p>
                    </div>
                    <span className="soft-stat">{formatCount(selectedRow.lines?.length || 0)} line</span>
                  </div>
                  <AdminDataTable
                    columns={accountingJournalLineColumns}
                    data={selectedRow.lines || []}
                    progressPending={loading}
                    pagination={false}
                    highlightOnHover
                    emptyTitle="Belum ada line jurnal"
                    emptyDescription="Jurnal ini belum memiliki line yang bisa ditampilkan."
                  />
                </section>
              ) : null}
            </>
          ) : (
            <div className="report-center-empty-copy">
              Klik salah satu jurnal di tabel untuk membuka inspector detail.
            </div>
          )}
        </section>
      </>
    );
  };

  const renderAccountingGeneralLedgerSectionTabs = () => (
    <section className="section-card report-center-subtab-shell">
      <div className="report-center-section-head">
        <div>
          <span className="page-eyebrow">Ledger workspace</span>
          <h2>Pilih fokus General Ledger</h2>
          <p>Pisahkan ringkasan, analisa, dan detail akun supaya audit mutasi saldo tidak menumpuk di satu tabel.</p>
        </div>
        <span className="status-pill">
          <FaLayerGroup /> 3 tampilan
        </span>
      </div>
      <div className="report-center-subtab-nav" role="tablist" aria-label="Tab general ledger">
        {accountingGeneralLedgerSections.map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={activeAccountingGeneralLedgerSection === item.key}
              className={`report-center-subtab-button ${activeAccountingGeneralLedgerSection === item.key ? 'is-active' : ''}`}
              onClick={() => setActiveAccountingGeneralLedgerSection(item.key)}
            >
              <span className="report-center-subtab-button__icon">
                <Icon />
              </span>
              <span className="report-center-subtab-button__copy">
                <strong>{item.label}</strong>
                <small>{item.copy}</small>
              </span>
            </button>
          );
        })}
      </div>
      <div className="report-center-subtab-active-copy">
        <strong>Tab aktif: {activeAccountingGeneralLedgerSectionMeta.label}</strong>
        <p>{activeAccountingGeneralLedgerSectionMeta.copy}</p>
      </div>
    </section>
  );

  const renderAccountingGeneralLedgerSummarySection = () => {
    const scopeItems = [
      { label: 'Outlet', value: selectedOutletLabel },
      { label: 'Periode', value: selectedAccountingPeriodLabel },
      { label: 'Source system', value: selectedAccountingSourceSystemLabel },
      { label: 'Business unit', value: selectedAccountingBusinessUnitLabel },
      { label: 'Posting status', value: selectedAccountingPostingStatusLabel },
      { label: 'Kata kunci', value: selectedAccountingSearchLabel },
    ];
    const guideSteps = [
      {
        label: '1. Cek scope akun',
        copy: 'Pastikan jumlah akun aktif dan akun yang punya saldo awal sesuai ekspektasi periode audit.',
      },
      {
        label: '2. Baca saldo by kategori',
        copy: 'Gunakan account type breakdown untuk melihat kelompok akun yang paling besar mutasinya.',
      },
      {
        label: '3. Turun ke detail akun',
        copy: 'Buka detail akun untuk memeriksa jurnal pembentuk saldo berjalan per akun.',
      },
    ];

    return (
      <>
        <section className="section-card report-center-journal-analysis-card">
          <div className="report-center-section-head">
            <div>
              <span className="page-eyebrow">Scope aktif</span>
              <h2>Konteks audit General Ledger</h2>
              <p>Ringkasan ini membantu memastikan outlet, periode, dan filter accounting yang dipakai sudah sesuai sebelum membaca saldo akun.</p>
            </div>
          </div>
          <div className="report-center-journal-scope-grid">
            {scopeItems.map(item => (
              <article key={item.label} className="report-center-journal-scope-item">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </article>
            ))}
          </div>
        </section>

        <section className="section-card report-center-journal-guide-card">
          <div className="report-center-section-head">
            <div>
              <span className="page-eyebrow">Cara pakai</span>
              <h2>Urutan baca yang disarankan</h2>
              <p>Mulai dari coverage akun dulu, baru masuk ke tren dan mutasi per akun yang benar-benar bergerak.</p>
            </div>
          </div>
          <div className="report-center-journal-guide-grid">
            {guideSteps.map(item => (
              <article key={item.label} className="report-center-journal-guide-step">
                <strong>{item.label}</strong>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <div className="report-center-kpi-grid report-center-kpi-grid--wide">
          {accountingGeneralLedgerSummaryCards.map(item => {
            const Icon = item.icon;
            return (
              <section key={item.key} className="section-card report-center-kpi-card">
                <span className="report-center-kpi-card__icon">
                  <Icon />
                </span>
                <div className="report-center-kpi-card__copy">
                  <p>{item.label}</p>
                  <strong>{item.value}</strong>
                </div>
              </section>
            );
          })}
        </div>

        <div className="report-center-breakdown-grid">
          {renderBreakdownCard('Kategori akun ledger', 'Account type', accountingGeneralLedgerData.account_type_breakdown, { countLabel: 'akun' })}
          {renderBreakdownCard('Source system mix', 'Source', accountingGeneralLedgerData.source_breakdown, { countLabel: 'line' })}
          {renderBreakdownCard('Sebaran outlet', 'Outlet', accountingGeneralLedgerData.outlet_breakdown, { countLabel: 'line' })}
        </div>
      </>
    );
  };

  const renderAccountingGeneralLedgerAnalysisSection = () => (
    <>
      <section className="section-card report-center-journal-analysis-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Analysis mode</span>
            <h2>Baca ritme mutasi ledger</h2>
            <p>Tab ini dipakai untuk melihat seberapa padat line ledger per hari dan akun mana yang paling besar pergerakannya.</p>
          </div>
        </div>
        <div className="report-center-journal-scope-grid">
          <article className="report-center-journal-scope-item">
            <span>Akun dengan saldo awal</span>
            <strong>{formatCount(accountingGeneralLedgerData.summary.accounts_with_opening)}</strong>
          </article>
          <article className="report-center-journal-scope-item">
            <span>Akun bergerak</span>
            <strong>{formatCount(accountingGeneralLedgerData.summary.accounts_with_movement)}</strong>
          </article>
          <article className="report-center-journal-scope-item">
            <span>Total line</span>
            <strong>{formatCount(accountingGeneralLedgerData.summary.total_entries)}</strong>
          </article>
          <article className="report-center-journal-scope-item">
            <span>Total akun</span>
            <strong>{formatCount(accountingGeneralLedgerData.summary.total_accounts)}</strong>
          </article>
        </div>
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Daily ledger trend</span>
            <h2>Tren line ledger harian</h2>
            <p>Lihat kepadatan line debit-credit per hari untuk membaca ritme posting dan mutasi akun selama periode audit.</p>
          </div>
        </div>
        <AdminDataTable
          columns={accountingGeneralLedgerTrendColumns}
          data={accountingGeneralLedgerData.trends}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada tren general ledger"
          emptyDescription="Ubah filter outlet, periode, atau akun untuk melihat tren mutasi ledger."
        />
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Account ledger</span>
            <h2>Daftar akun dan mutasinya</h2>
            <p>Gunakan tabel ini untuk mengurutkan akun berdasarkan saldo awal, mutasi debit-credit, atau saldo akhir.</p>
          </div>
          <span className="soft-stat">{formatCount(accountingGeneralLedgerData.results.length)} akun</span>
        </div>
        <AdminDataTable
          columns={accountingGeneralLedgerAccountColumns}
          data={accountingGeneralLedgerData.results}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada akun ledger"
          emptyDescription="Belum ada akun yang cocok dengan filter accounting saat ini."
        />
      </section>
    </>
  );

  const renderAccountingGeneralLedgerDetailSection = () => {
    const selectedRow = selectedAccountingGeneralLedgerRow;
    const ledgerEntries = Array.isArray(selectedRow?.entries) ? selectedRow.entries : [];

    return (
      <>
        <section className="section-card table-card">
          <div className="report-center-section-head">
            <div>
              <span className="page-eyebrow">Ledger by account</span>
              <h2>Pilih akun untuk audit detail</h2>
              <p>Klik satu akun untuk membuka saldo awal, mutasi debit-credit, dan ledger line pembentuk saldo akhirnya.</p>
            </div>
            <span className="soft-stat">{formatCount(totalRows)} akun</span>
          </div>
          <AdminDataTable
            columns={accountingGeneralLedgerAccountColumns}
            data={accountingGeneralLedgerPageRows}
            progressPending={loading || bootstrapping}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={rowsPerPage}
            onChangePage={page => setCurrentPage(page)}
            onChangeRowsPerPage={perPage => {
              setRowsPerPage(perPage);
              setCurrentPage(1);
            }}
            onRowClicked={row => setSelectedAccountingGeneralLedgerAccountId(row.account_id)}
            conditionalRowStyles={accountingGeneralLedgerRowStyles}
            highlightOnHover
            emptyTitle="Belum ada data general ledger"
            emptyDescription="Ubah outlet, periode, status posting, atau kata kunci akun untuk melihat mutasi ledger."
          />
        </section>

        {selectedRow ? (
          <>
            <section className="section-card report-center-journal-overview-card">
              <div className="report-center-section-head">
                <div>
                  <span className="page-eyebrow">Ringkasan akun</span>
                  <h2>{selectedRow.account_name || selectedRow.account_id || '-'}</h2>
                  <p>Inspector ini menampilkan saldo awal akun, mutasi debit-credit selama periode, dan saldo akhir yang terbentuk.</p>
                </div>
                <span className="status-pill">
                  <FaReceipt /> {selectedRow.account_id || '-'}
                </span>
              </div>
              <div className="report-center-journal-meta-grid">
                <article className="report-center-journal-meta-card report-center-journal-meta-card--wide">
                  <span>Akun ledger</span>
                  <strong>{selectedRow.account_name || selectedRow.account_id || '-'}</strong>
                  <small>{selectedRow.account_type || 'Uncategorized'} • {selectedRow.account_id || '-'}</small>
                </article>
                <article className="report-center-journal-meta-card">
                  <span>Saldo awal</span>
                  <strong>{formatCurrency(selectedRow.opening_balance)}</strong>
                  <small>Posisi sebelum periode dimulai</small>
                </article>
                <article className="report-center-journal-meta-card">
                  <span>Total debit</span>
                  <strong>{formatCurrency(selectedRow.total_debit)}</strong>
                  <small>Mutasi debit di periode aktif</small>
                </article>
                <article className="report-center-journal-meta-card">
                  <span>Total credit</span>
                  <strong>{formatCurrency(selectedRow.total_credit)}</strong>
                  <small>Mutasi credit di periode aktif</small>
                </article>
                <article className="report-center-journal-meta-card">
                  <span>Saldo akhir</span>
                  <strong>{formatCurrency(selectedRow.closing_balance)}</strong>
                  <small>Saldo setelah seluruh mutasi periode</small>
                </article>
                <article className="report-center-journal-meta-card">
                  <span>Aktivitas</span>
                  <strong>{formatCount(selectedRow.entry_count)} line</strong>
                  <small>Terakhir {formatDateTime(selectedRow.last_occurred_at)}</small>
                </article>
              </div>
            </section>

            <section className="section-card table-card">
              <div className="report-center-section-head">
                <div>
                  <span className="page-eyebrow">Ledger line</span>
                  <h2>Mutasi jurnal pembentuk saldo akun</h2>
                  <p>Gunakan tabel ini untuk membaca running balance akun dari saldo awal sampai saldo akhir yang tampil di atas.</p>
                </div>
                <span className="soft-stat">{formatCount(ledgerEntries.length)} line</span>
              </div>
              <AdminDataTable
                columns={accountingGeneralLedgerEntryColumns}
                data={ledgerEntries}
                progressPending={loading}
                pagination={false}
                highlightOnHover
                emptyTitle="Belum ada mutasi ledger"
                emptyDescription="Akun ini belum memiliki line jurnal pada periode yang sedang dipilih."
              />
            </section>
          </>
        ) : (
          <div className="report-center-empty-copy">
            Klik salah satu akun di tabel untuk membuka detail ledger-nya.
          </div>
        )}
      </>
    );
  };

  const renderAccountingTrialBalanceSection = () => (
    <>
      {renderAccountingScopeCard(
        'Current scope',
        'Ringkasan filter trial balance',
        'Gunakan kartu ini untuk memastikan trial balance yang sedang dibaca memang sesuai outlet, periode, dan scope ledger yang diinginkan.'
      )}

      {renderAccountingKpiGrid(accountingTrialBalanceSummaryCards)}

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Komposisi kategori akun', 'Trial balance', accountingTrialBalanceData.account_type_breakdown, { countLabel: 'akun' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Trial balance detail</span>
            <h2>Daftar saldo akun</h2>
            <p>Baca saldo awal, mutasi debit-credit, dan saldo akhir semua akun untuk cek keseimbangan ledger sebelum financial statement.</p>
          </div>
          <span className="soft-stat">{formatCount(totalRows)} akun</span>
        </div>
        <AdminDataTable
          columns={accountingTrialBalanceColumns}
          data={accountingTrialBalancePageRows}
          progressPending={loading || bootstrapping}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={perPage => {
            setRowsPerPage(perPage);
            setCurrentPage(1);
          }}
          highlightOnHover
          emptyTitle="Belum ada data trial balance"
          emptyDescription="Ubah outlet, periode, atau kata kunci akun untuk melihat trial balance."
        />
      </section>
    </>
  );

  const renderAccountingProfitLossSection = () => (
    <>
      {renderAccountingScopeCard(
        'Current scope',
        'Ringkasan filter profit & loss',
        'Area ini membantu owner dan finance melihat ruang baca laba rugi aktif tanpa kembali ke halaman setup lama.'
      )}

      {renderAccountingKpiGrid(accountingProfitLossSummaryCards)}

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Komposisi akun laba rugi', 'Profit & loss', accountingProfitLossData.account_type_breakdown, { countLabel: 'akun' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">P&L detail</span>
            <h2>Daftar akun revenue dan expense</h2>
            <p>Gunakan tabel ini untuk audit akun pembentuk laba rugi dan melihat nilai bersih per akun pada periode yang dipilih.</p>
          </div>
          <span className="soft-stat">{formatCount(totalRows)} akun</span>
        </div>
        <AdminDataTable
          columns={accountingProfitLossColumns}
          data={accountingProfitLossPageRows}
          progressPending={loading || bootstrapping}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={perPage => {
            setRowsPerPage(perPage);
            setCurrentPage(1);
          }}
          highlightOnHover
          emptyTitle="Belum ada data profit & loss"
          emptyDescription="Ubah outlet, periode, atau kata kunci akun untuk melihat laba rugi."
        />
      </section>
    </>
  );

  const renderAccountingBalanceSheetSection = () => (
    <>
      {renderAccountingScopeCard(
        'Current scope',
        'Ringkasan filter balance sheet',
        'Scope ini menunjukkan posisi asset, liability, dan equity yang sedang dibaca dari saldo akhir ledger pusat.'
      )}

      {renderAccountingKpiGrid(accountingBalanceSheetSummaryCards)}

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Komposisi akun neraca', 'Balance sheet', accountingBalanceSheetData.account_type_breakdown, { countLabel: 'akun' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Balance sheet detail</span>
            <h2>Daftar akun asset, liability, dan equity</h2>
            <p>Lihat saldo awal, mutasi periode, dan saldo akhir tiap akun untuk memverifikasi posisi neraca pada akhir periode.</p>
          </div>
          <span className="soft-stat">{formatCount(totalRows)} akun</span>
        </div>
        <AdminDataTable
          columns={accountingBalanceSheetColumns}
          data={accountingBalanceSheetPageRows}
          progressPending={loading || bootstrapping}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={perPage => {
            setRowsPerPage(perPage);
            setCurrentPage(1);
          }}
          highlightOnHover
          emptyTitle="Belum ada data balance sheet"
          emptyDescription="Ubah outlet, periode, atau kata kunci akun untuk melihat neraca."
        />
      </section>
    </>
  );

  const renderAccountingCashFlowSection = () => (
    <>
      {renderAccountingScopeCard(
        'Current scope',
        'Ringkasan filter cash flow',
        'Scope ini membantu owner membaca arus kas aktif berdasarkan outlet, periode, dan filter ledger yang sedang diterapkan.'
      )}

      {renderAccountingKpiGrid(accountingCashFlowSummaryCards)}

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Arus kas per section', 'Cash flow', accountingCashFlowData.section_breakdown, { countLabel: 'entry' })}
        {renderBreakdownCard('Akun kas paling aktif', 'Cash account', accountingCashFlowData.cash_account_breakdown, { countLabel: 'entry' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Daily trend</span>
            <h2>Tren arus kas harian</h2>
            <p>Lihat inflow, outflow, dan net cash flow per hari untuk membaca kapan kas bergerak paling besar sepanjang periode.</p>
          </div>
        </div>
        <AdminDataTable
          columns={accountingCashFlowTrendColumns}
          data={accountingCashFlowData.trends}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada tren cash flow"
          emptyDescription="Ubah outlet, periode, atau kata kunci untuk melihat tren arus kas."
        />
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Cash flow detail</span>
            <h2>Daftar mutasi kas per jurnal</h2>
            <p>Audit setiap dampak kas berdasarkan akun kas, akun lawan, section arus kas, dan nilai inflow-outflow yang terbaca dari ledger pusat.</p>
          </div>
          <span className="soft-stat">{formatCount(totalRows)} entry</span>
        </div>
        <AdminDataTable
          columns={accountingCashFlowColumns}
          data={accountingCashFlowPageRows}
          progressPending={loading || bootstrapping}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={perPage => {
            setRowsPerPage(perPage);
            setCurrentPage(1);
          }}
          highlightOnHover
          emptyTitle="Belum ada data cash flow"
          emptyDescription="Ubah outlet, periode, atau kata kunci akun untuk melihat arus kas."
        />
      </section>
    </>
  );

  const renderAccountingAccountsPayableAgingSection = () => (
    <>
      {renderAccountingScopeCard(
        'Current scope',
        'Ringkasan filter AP aging',
        'Scope ini membantu owner membaca hutang outstanding berdasarkan outlet, periode dokumen, scope finance, status, dan bucket umur yang sedang diterapkan.'
      )}

      {renderAccountingKpiGrid(accountingAccountsPayableAgingSummaryCards)}

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Sebaran aging bucket', 'Aging bucket', accountingAccountsPayableAgingData.aging_bucket_breakdown, { countLabel: 'bill' })}
        {renderBreakdownCard('Vendor outstanding terbesar', 'Vendor', accountingAccountsPayableAgingData.vendor_breakdown, { countLabel: 'bill' })}
        {renderBreakdownCard('Scope dokumen payable', 'Scope', accountingAccountsPayableAgingData.scope_breakdown, { countLabel: 'bill' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Daily trend</span>
            <h2>Tren hutang outstanding harian</h2>
            <p>Lihat bill outstanding dan nominal overdue per hari dokumen untuk membaca kapan hutang baru mulai menumpuk.</p>
          </div>
        </div>
        <AdminDataTable
          columns={accountingAccountsPayableAgingTrendColumns}
          data={accountingAccountsPayableAgingData.trends}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada tren AP aging"
          emptyDescription="Ubah scope dokumen, status, bucket, atau periode untuk melihat ritme hutang outstanding."
        />
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Payable detail</span>
            <h2>Daftar bill hutang outstanding</h2>
            <p>Audit hutang per dokumen berdasarkan vendor, due date, bucket aging, akun hutang, dan nilai outstanding yang masih tersisa.</p>
          </div>
          <span className="soft-stat">{formatCount(totalRows)} bill</span>
        </div>
        <AdminDataTable
          columns={accountingAccountsPayableAgingColumns}
          data={accountingAccountsPayableAgingPageRows}
          progressPending={loading || bootstrapping}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={perPage => {
            setRowsPerPage(perPage);
            setCurrentPage(1);
          }}
          highlightOnHover
          emptyTitle="Belum ada data AP aging"
          emptyDescription="Ubah scope dokumen, status, bucket aging, atau periode untuk melihat bill hutang yang masih outstanding."
        />
      </section>
    </>
  );

  const renderAccountingExpenseBreakdownSection = () => (
    <>
      {renderAccountingScopeCard(
        'Current scope',
        'Ringkasan filter expense breakdown',
        'Scope ini membantu owner membaca biaya berdasarkan outlet, periode dokumen, scope finance, status dokumen, dan kata kunci yang sedang diterapkan.'
      )}

      {renderAccountingKpiGrid(accountingExpenseBreakdownSummaryCards)}

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Biaya per kategori', 'Category', accountingExpenseBreakdownData.category_breakdown, { countLabel: 'dokumen' })}
        {renderBreakdownCard('Vendor penyumbang biaya', 'Vendor', accountingExpenseBreakdownData.vendor_breakdown, { countLabel: 'dokumen' })}
        {renderBreakdownCard('Scope dokumen biaya', 'Scope', accountingExpenseBreakdownData.scope_breakdown, { countLabel: 'dokumen' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Daily trend</span>
            <h2>Tren biaya harian</h2>
            <p>Lihat jumlah dokumen, total biaya, dan outstanding per hari dokumen untuk membaca ritme biaya operasional atau aset.</p>
          </div>
        </div>
        <AdminDataTable
          columns={accountingExpenseBreakdownTrendColumns}
          data={accountingExpenseBreakdownData.trends}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada tren expense breakdown"
          emptyDescription="Ubah scope dokumen, status, atau periode untuk melihat tren biaya harian."
        />
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Expense detail</span>
            <h2>Daftar dokumen biaya</h2>
            <p>Audit dokumen finance berdasarkan kategori, vendor, akun expense, nilai bayar, nilai outstanding, dan status accounting per dokumen.</p>
          </div>
          <span className="soft-stat">{formatCount(totalRows)} dokumen</span>
        </div>
        <AdminDataTable
          columns={accountingExpenseBreakdownColumns}
          data={accountingExpenseBreakdownPageRows}
          progressPending={loading || bootstrapping}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={perPage => {
            setRowsPerPage(perPage);
            setCurrentPage(1);
          }}
          highlightOnHover
          emptyTitle="Belum ada data expense breakdown"
          emptyDescription="Ubah scope dokumen, status, atau periode untuk melihat dokumen biaya yang ingin diaudit."
        />
      </section>
    </>
  );

  const renderAccountingAssetRegisterSection = () => (
    <>
      {renderAccountingScopeCard(
        'Current scope',
        'Ringkasan filter asset register',
        'Scope ini membantu owner membaca aset yang diperoleh pada periode terpilih, lengkap dengan status dokumen, vendor, akun aset, dan estimasi nilai buku saat ini.'
      )}

      {renderAccountingDepreciationPolicyCard(
        accountingAssetRegisterData.policy,
        'Policy estimasi nilai buku',
        'Estimasi depresiasi dan book value di report ini memakai policy default straight-line berdasarkan kategori aset. Ini belum membaca jurnal penyusutan live per aset.'
      )}

      {renderAccountingKpiGrid(accountingAssetRegisterSummaryCards)}

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Nilai perolehan per kategori', 'Category', accountingAssetRegisterData.category_breakdown, { countLabel: 'aset' })}
        {renderBreakdownCard('Vendor aset terbesar', 'Vendor', accountingAssetRegisterData.vendor_breakdown, { countLabel: 'aset' })}
        {renderBreakdownCard('Akun aset yang dipakai', 'Asset account', accountingAssetRegisterData.account_breakdown, { countLabel: 'aset' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Acquisition trend</span>
            <h2>Tren akuisisi aset bulanan</h2>
            <p>Lihat jumlah aset, nilai perolehan, dan outstanding capex per bulan akuisisi untuk membaca ritme investasi aset.</p>
          </div>
        </div>
        <AdminDataTable
          columns={accountingAssetRegisterTrendColumns}
          data={accountingAssetRegisterData.trends}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada tren asset register"
          emptyDescription="Ubah outlet, periode, status, atau kata kunci untuk melihat ritme akuisisi aset."
        />
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Asset detail</span>
            <h2>Daftar aset per dokumen</h2>
            <p>Audit aset satu per satu berdasarkan vendor, kategori, akun aset, nilai perolehan, outstanding, dan estimasi nilai buku saat ini.</p>
          </div>
          <span className="soft-stat">{formatCount(totalRows)} aset</span>
        </div>
        <AdminDataTable
          columns={accountingAssetRegisterColumns}
          data={accountingAssetRegisterPageRows}
          progressPending={loading || bootstrapping}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={perPage => {
            setRowsPerPage(perPage);
            setCurrentPage(1);
          }}
          highlightOnHover
          emptyTitle="Belum ada data asset register"
          emptyDescription="Ubah outlet, periode, status dokumen, atau kata kunci untuk melihat daftar aset."
        />
      </section>
    </>
  );

  const renderAccountingDepreciationScheduleSection = () => (
    <>
      {renderAccountingScopeCard(
        'Current scope',
        'Ringkasan filter depreciation schedule',
        'Scope ini membantu owner membaca estimasi penyusutan aset sampai periode report yang sedang aktif, tanpa perlu membuka jurnal manual satu per satu.'
      )}

      {renderAccountingDepreciationPolicyCard(
        accountingDepreciationScheduleData.policy,
        'Policy default penyusutan',
        'Schedule ini adalah estimasi straight-line berbasis kategori aset dengan nilai residu default nol dan start rule pada bulan perolehan. Gunakan sebagai working schedule sebelum fixed asset subledger lengkap tersedia.'
      )}

      {renderAccountingKpiGrid(accountingDepreciationScheduleSummaryCards)}

      <div className="report-center-breakdown-grid">
        {renderBreakdownCard('Nilai buku per kategori', 'Category', accountingDepreciationScheduleData.category_breakdown, { countLabel: 'aset' })}
        {renderBreakdownCard('Bucket umur manfaat', 'Useful life', accountingDepreciationScheduleData.useful_life_breakdown, { countLabel: 'aset' })}
      </div>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Monthly trend</span>
            <h2>Tren penyusutan bulanan</h2>
            <p>Lihat beban penyusutan bulanan, akumulasi depresiasi portofolio, dan ending book value sepanjang periode report.</p>
          </div>
        </div>
        <AdminDataTable
          columns={accountingDepreciationScheduleTrendColumns}
          data={accountingDepreciationScheduleData.trends}
          progressPending={loading}
          pagination={false}
          highlightOnHover
          emptyTitle="Belum ada tren penyusutan"
          emptyDescription="Ubah outlet, periode, status, atau kata kunci untuk melihat tren penyusutan aset."
        />
      </section>

      <section className="section-card table-card">
        <div className="report-center-section-head">
          <div>
            <span className="page-eyebrow">Depreciation detail</span>
            <h2>Jadwal penyusutan per aset</h2>
            <p>Audit umur manfaat, bulan penyusutan, beban periode, akumulasi depresiasi, dan nilai buku setiap aset di jalur accounting pusat.</p>
          </div>
          <span className="soft-stat">{formatCount(totalRows)} aset</span>
        </div>
        <AdminDataTable
          columns={accountingDepreciationScheduleColumns}
          data={accountingDepreciationSchedulePageRows}
          progressPending={loading || bootstrapping}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          onChangePage={page => setCurrentPage(page)}
          onChangeRowsPerPage={perPage => {
            setRowsPerPage(perPage);
            setCurrentPage(1);
          }}
          highlightOnHover
          emptyTitle="Belum ada depreciation schedule"
          emptyDescription="Ubah outlet, periode, status dokumen, atau kata kunci untuk melihat schedule penyusutan aset."
        />
      </section>
    </>
  );

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell report-center-page mt-4 px-4">
        <AdminPageHeader
          icon={FaLayerGroup}
          eyebrow={pageEyebrow}
          title={pageTitle}
          description={pageDescription}
          breadcrumbs={pageBreadcrumbs}
          meta={(
            <div className="d-flex flex-wrap gap-2">
              <span className="soft-stat">
                <FaChartLine /> {standaloneMode
                  ? activeBusinessReportMeta.label
                  : availableTabs.find(item => item.key === activeTab)?.label || 'Business'}
              </span>
              <span className="soft-stat">
                <FaCalendarAlt /> {formatCount(totalRows)} rows
              </span>
            </div>
          )}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-secondary"
                onClick={handleRefreshReport}
                disabled={!canUseReportActions || loading || bootstrapping || exporting}
              >
                <FaRedoAlt /> Refresh
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleExportPdf}
                disabled={!canUseReportActions || loading || bootstrapping || exporting}
              >
                <FaFilePdf /> Export PDF
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleExportExcel}
                disabled={!canUseReportActions || loading || bootstrapping || exporting}
              >
                <FaFileExcel /> Export Excel
              </Button>
            </div>
          )}
        />

        {!standaloneMode ? (
          <section className="section-card report-center-shell">
            <div className="report-center-primary-switch" role="tablist" aria-label="Pilih domain report">
              {availableTabs.map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === item.key}
                    className={`report-center-tab-button ${activeTab === item.key ? 'is-active' : ''}`}
                    onClick={() => handleTabChange(item.key)}
                  >
                    <span className="report-center-tab-button__icon">
                      <Icon />
                    </span>
                    <span className="report-center-tab-button__copy">
                      <strong>{item.label}</strong>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="report-center-primary-switch__active-copy">
              <strong>Area aktif: {activeTabMeta.label}</strong>
              <p>{activeTabMeta.copy}</p>
            </div>
          </section>
        ) : null}

        {activeTab === 'business' ? (
          <>
            {!standaloneMode ? (
              <section className="section-card report-center-shell">
                <div className="report-center-section-head">
                  <div>
                    <span className="page-eyebrow">Business reports</span>
                    <h2>Pilih report bisnis</h2>
                    <p>Membership Sales, Member Activity, Check-in, Private Session Sales, Private Session, dan Private Session Staff Performance sudah aktif. Report lain tetap terlihat supaya struktur pusat report tetap rapi sejak awal.</p>
                  </div>
                </div>
                <div className="report-center-secondary-switch" role="tablist" aria-label="Pilih report bisnis">
                  {availableBusinessReports.map(item => (
                    <button
                      key={item.key}
                      type="button"
                      role="tab"
                      aria-selected={activeBusinessReport === item.key}
                      className={`report-center-report-button ${activeBusinessReport === item.key ? 'is-active' : ''} ${item.available ? '' : 'is-disabled'}`.trim()}
                      onClick={() => handleBusinessReportChange(item.key)}
                      disabled={!item.available}
                    >
                      <strong>{item.label}</strong>
                      <span className="report-center-report-button__status">
                        {item.available ? 'Ready' : 'Next'}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="report-center-primary-switch__active-copy">
                  <strong>Report aktif: {activeBusinessReportMeta.label}</strong>
                  <p>{activeBusinessReportMeta.copy}</p>
                </div>
              </section>
            ) : null}

            {renderBusinessFilterCard()}

            {backendError ? (
              <section className="section-card report-center-alert-card">
                <div>
                  <span className="page-eyebrow">Connection issue</span>
                  <h2>{activeBusinessReport === 'staff-transaction-summary'
                    ? 'Staff Transaction Summary belum bisa dimuat'
                    : activeBusinessReport === 'member-activity'
                    ? 'Member Activity Report belum bisa dimuat'
                    : activeBusinessReport === 'check-in'
                      ? 'Check-in Report belum bisa dimuat'
                      : activeBusinessReport === 'pt-sales'
                        ? 'Private Session Sales Report belum bisa dimuat'
                        : activeBusinessReport === 'pt-session'
                          ? 'Private Session Report belum bisa dimuat'
                          : activeBusinessReport === 'pt-trainer'
                            ? 'Private Session Staff Performance Report belum bisa dimuat'
                      : 'Membership Sales Report belum bisa dimuat'}</h2>
                  <p>{backendError}</p>
                </div>
              </section>
            ) : null}

            {activeBusinessReport === 'staff-transaction-summary' ? (
              <>
                <div className="report-center-kpi-grid report-center-kpi-grid--wide">
                  {staffTransactionSummaryCards.map(item => {
                    const Icon = item.icon;
                    return (
                      <section key={item.key} className="section-card report-center-kpi-card">
                        <span className="report-center-kpi-card__icon">
                          <Icon />
                        </span>
                        <div className="report-center-kpi-card__copy">
                          <p>{item.label}</p>
                          <strong>{item.value}</strong>
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="report-center-breakdown-grid">
                  {renderBreakdownCard('Top performa staff', 'Staff', staffTransactionData.top_staff_breakdown, { countLabel: 'trx' })}
                  {renderBreakdownCard('Sebaran outlet', 'Outlet', staffTransactionData.outlet_breakdown, { countLabel: 'staff' })}
                  {renderBreakdownCard('Sebaran jabatan', 'Role', staffTransactionData.position_breakdown, { countLabel: 'staff' })}
                  {renderBreakdownCard('Komposisi channel', 'Channel', staffTransactionData.channel_breakdown, { countLabel: 'trx' })}
                </div>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Daily trend</span>
                      <h2>Tren transaksi staf aktif</h2>
                      <p>Lihat kontribusi harian staff dari penjualan membership, class sale, PT sales, dan POS sales untuk membaca ritme operasional per periode.</p>
                    </div>
                  </div>
                  <AdminDataTable
                    columns={staffTransactionTrendColumns}
                    data={staffTransactionData.trends}
                    progressPending={loading}
                    pagination={false}
                    highlightOnHover
                    emptyTitle="Belum ada tren transaksi staff"
                    emptyDescription="Ubah periode atau outlet untuk melihat tren transaksi staff aktif."
                  />
                </section>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Detail staff</span>
                      <h2>Daftar summary transaksi staff aktif</h2>
                      <p>Audit tiap staff berdasarkan penjualan membership, POS sales, kombinasi channel, average ticket, dan aktivitas terakhir pada periode terpilih.</p>
                    </div>
                    <span className="soft-stat">{formatCount(totalRows)} staff</span>
                  </div>
                  <AdminDataTable
                    columns={staffTransactionColumns}
                    data={staffTransactionData.results}
                    progressPending={loading || bootstrapping}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={perPage => {
                      setRowsPerPage(perPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    emptyTitle="Belum ada data transaksi staff"
                    emptyDescription="Pilih outlet atau periode untuk melihat ringkasan transaksi staff aktif."
                  />
                </section>
              </>
            ) : activeBusinessReport === 'member-activity' ? (
              <>
                <div className="report-center-kpi-grid report-center-kpi-grid--wide">
                  {memberActivitySummaryCards.map(item => {
                    const Icon = item.icon;
                    return (
                      <section key={item.key} className="section-card report-center-kpi-card">
                        <span className="report-center-kpi-card__icon">
                          <Icon />
                        </span>
                        <div className="report-center-kpi-card__copy">
                          <p>{item.label}</p>
                          <strong>{item.value}</strong>
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="report-center-breakdown-grid">
                  {renderBreakdownCard('Lifecycle member', 'Lifecycle', memberActivityData.lifecycle_breakdown, { countLabel: 'member' })}
                  {renderBreakdownCard('Aktivitas kunjungan', 'Activity', memberActivityData.activity_breakdown, { countLabel: 'member' })}
                  {renderBreakdownCard('Paket aktif terakhir', 'Membership mix', memberActivityData.membership_breakdown, { countLabel: 'member' })}
                  {renderBreakdownCard('Sebaran outlet', 'Outlet mix', memberActivityData.outlet_breakdown, { countLabel: 'member' })}
                </div>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Detail member</span>
                      <h2>Daftar aktivitas member</h2>
                      <p>Audit member berdasarkan lifecycle membership, kunjungan terakhir, frekuensi hadir di periode, dan risiko churn.</p>
                    </div>
                    <span className="soft-stat">{formatCount(totalRows)} member</span>
                  </div>
                  <AdminDataTable
                    columns={memberActivityColumns}
                    data={memberActivityData.results}
                    progressPending={loading || bootstrapping}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={perPage => {
                      setRowsPerPage(perPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    emptyTitle="Belum ada data member activity"
                    emptyDescription="Ubah outlet, periode, atau filter lifecycle untuk melihat aktivitas member."
                  />
                </section>
              </>
            ) : activeBusinessReport === 'check-in' ? (
              <>
                <div className="report-center-kpi-grid report-center-kpi-grid--wide">
                  {checkinSummaryCards.map(item => {
                    const Icon = item.icon;
                    return (
                      <section key={item.key} className="section-card report-center-kpi-card">
                        <span className="report-center-kpi-card__icon">
                          <Icon />
                        </span>
                        <div className="report-center-kpi-card__copy">
                          <p>{item.label}</p>
                          <strong>{item.value}</strong>
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="report-center-breakdown-grid">
                  {renderBreakdownCard('Paket saat check-in', 'Membership mix', checkinData.membership_breakdown, { countLabel: 'check-in' })}
                  {renderBreakdownCard('Sebaran outlet', 'Outlet mix', checkinData.outlet_breakdown, { countLabel: 'check-in' })}
                  {renderBreakdownCard('Hari paling ramai', 'Weekday pattern', checkinData.weekday_breakdown, { countLabel: 'check-in' })}
                  {renderBreakdownCard('Jam check-in', 'Hourly pattern', checkinData.hour_breakdown, { countLabel: 'check-in' })}
                  {renderBreakdownCard('Top member check-in', 'Top visitor', checkinData.top_member_breakdown, { countLabel: 'check-in' })}
                </div>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Daily trend</span>
                      <h2>Tren check-in harian</h2>
                      <p>Lihat pola check-in per hari untuk membaca traffic kunjungan dan jumlah member unik yang datang.</p>
                    </div>
                  </div>
                  <AdminDataTable
                    columns={checkinTrendColumns}
                    data={checkinData.trends}
                    progressPending={loading}
                    pagination={false}
                    highlightOnHover
                    emptyTitle="Belum ada tren check-in"
                    emptyDescription="Ubah periode atau filter report untuk melihat pola check-in harian."
                  />
                </section>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Detail check-in</span>
                      <h2>Daftar check-in member</h2>
                      <p>Audit satu per satu kunjungan member berdasarkan outlet, paket saat check-in, frekuensi hadir di periode, dan kedekatan ke masa expired.</p>
                    </div>
                    <span className="soft-stat">{formatCount(totalRows)} check-in</span>
                  </div>
                  <AdminDataTable
                    columns={checkinColumns}
                    data={checkinData.results}
                    progressPending={loading || bootstrapping}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={perPage => {
                      setRowsPerPage(perPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    emptyTitle="Belum ada data check-in"
                    emptyDescription="Pilih outlet, periode, atau paket membership untuk melihat detail check-in."
                  />
                </section>
              </>
            ) : activeBusinessReport === 'pt-sales' ? (
              <>
                <div className="report-center-kpi-grid report-center-kpi-grid--wide">
                  {ptSalesSummaryCards.map(item => {
                    const Icon = item.icon;
                    return (
                      <section key={item.key} className="section-card report-center-kpi-card">
                        <span className="report-center-kpi-card__icon">
                          <Icon />
                        </span>
                        <div className="report-center-kpi-card__copy">
                          <p>{item.label}</p>
                          <strong>{item.value}</strong>
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="report-center-breakdown-grid">
                  {renderBreakdownCard('Penjualan per staff', 'Staff mix', ptSalesData.trainer_breakdown, { countLabel: 'invoice' })}
                  {renderBreakdownCard('Penjualan per layanan', 'Service mix', ptSalesData.product_breakdown, { countLabel: 'invoice' })}
                  {renderBreakdownCard('Tipe session client', 'Client mix', ptSalesData.client_type_breakdown, { countLabel: 'invoice' })}
                  {renderBreakdownCard('Sebaran outlet', 'Outlet mix', ptSalesData.outlet_breakdown, { countLabel: 'invoice' })}
                  {renderBreakdownCard('Status pembayaran', 'Payment status', ptSalesData.payment_status_breakdown, { countLabel: 'invoice' })}
                  {renderBreakdownCard('Status contract', 'Contract status', ptSalesData.contract_breakdown, { countLabel: 'invoice' })}
                </div>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Daily trend</span>
                      <h2>Tren penjualan private session harian</h2>
                      <p>Lihat invoice private session, nilai sale, pembayaran masuk, dan outstanding per hari untuk membaca kualitas penjualan layanan.</p>
                    </div>
                  </div>
                  <AdminDataTable
                    columns={ptSalesTrendColumns}
                    data={ptSalesData.trends}
                    progressPending={loading}
                    pagination={false}
                    highlightOnHover
                    emptyTitle="Belum ada tren penjualan private session"
                    emptyDescription="Ubah periode atau filter report untuk melihat tren harian penjualan private session."
                  />
                </section>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Detail private session sale</span>
                      <h2>Daftar penjualan private session</h2>
                      <p>Audit sale private session berdasarkan invoice, client, staff, layanan, sesi, status pembayaran, dan status contract.</p>
                    </div>
                    <span className="soft-stat">{formatCount(totalRows)} invoice</span>
                  </div>
                  <AdminDataTable
                    columns={ptSalesColumns}
                    data={ptSalesData.results}
                    progressPending={loading || bootstrapping}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={perPage => {
                      setRowsPerPage(perPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    emptyTitle="Belum ada data private session sales"
                    emptyDescription="Pilih staff, layanan, outlet, atau periode untuk melihat penjualan private session."
                  />
                </section>
              </>
            ) : activeBusinessReport === 'pt-session' ? (
              <>
                <div className="report-center-kpi-grid report-center-kpi-grid--wide">
                  {ptSessionSummaryCards.map(item => {
                    const Icon = item.icon;
                    return (
                      <section key={item.key} className="section-card report-center-kpi-card">
                        <span className="report-center-kpi-card__icon">
                          <Icon />
                        </span>
                        <div className="report-center-kpi-card__copy">
                          <p>{item.label}</p>
                          <strong>{item.value}</strong>
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="report-center-breakdown-grid">
                  {renderBreakdownCard('Sesi per staff', 'Staff mix', ptSessionData.trainer_breakdown, { countLabel: 'sesi' })}
                  {renderBreakdownCard('Sesi per client', 'Client mix', ptSessionData.client_breakdown, { countLabel: 'sesi' })}
                  {renderBreakdownCard('Sesi per layanan', 'Service mix', ptSessionData.product_breakdown, { countLabel: 'sesi' })}
                  {renderBreakdownCard('Sebaran outlet', 'Outlet mix', ptSessionData.outlet_breakdown, { countLabel: 'sesi' })}
                  {renderBreakdownCard('Booking status', 'Booking flow', ptSessionData.booking_status_breakdown, { countLabel: 'sesi' })}
                  {renderBreakdownCard('Attendance status', 'Attendance flow', ptSessionData.attendance_status_breakdown, { countLabel: 'sesi' })}
                </div>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Daily trend</span>
                      <h2>Tren private session harian</h2>
                      <p>Lihat jumlah booking, sesi selesai, cancel, reschedule, dan no-show per hari untuk membaca utilisasi private session.</p>
                    </div>
                  </div>
                  <AdminDataTable
                    columns={ptSessionTrendColumns}
                    data={ptSessionData.trends}
                    progressPending={loading}
                    pagination={false}
                    highlightOnHover
                    emptyTitle="Belum ada tren private session"
                    emptyDescription="Ubah periode atau filter report untuk melihat tren harian private session."
                  />
                </section>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Detail private session</span>
                      <h2>Daftar booking dan sesi private session</h2>
                      <p>Audit private session berdasarkan booking code, client, staff, layanan, attendance, contract, dan apakah sesi benar-benar terpakai.</p>
                    </div>
                    <span className="soft-stat">{formatCount(totalRows)} sesi</span>
                  </div>
                  <AdminDataTable
                    columns={ptSessionColumns}
                    data={ptSessionData.results}
                    progressPending={loading || bootstrapping}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={perPage => {
                      setRowsPerPage(perPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    emptyTitle="Belum ada data private session"
                    emptyDescription="Pilih staff, client, layanan, status booking, atau periode untuk melihat private session."
                  />
                </section>
              </>
            ) : activeBusinessReport === 'pt-trainer' ? (
              <>
                <div className="report-center-kpi-grid report-center-kpi-grid--wide">
                  {ptTrainerSummaryCards.map(item => {
                    const Icon = item.icon;
                    return (
                      <section key={item.key} className="section-card report-center-kpi-card">
                        <span className="report-center-kpi-card__icon">
                          <Icon />
                        </span>
                        <div className="report-center-kpi-card__copy">
                          <p>{item.label}</p>
                          <strong>{item.value}</strong>
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="report-center-breakdown-grid">
                  {renderBreakdownCard('Top staff', 'Staff mix', ptTrainerData.trainer_breakdown, { countLabel: 'sesi selesai' })}
                  {renderBreakdownCard('Sebaran outlet', 'Outlet mix', ptTrainerData.outlet_breakdown, { countLabel: 'sesi selesai' })}
                  {renderBreakdownCard('Tipe layanan session', 'Service type', ptTrainerData.product_type_breakdown, { countLabel: 'sesi selesai' })}
                </div>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Monthly trend</span>
                      <h2>Tren performa staff private session</h2>
                      <p>Lihat perubahan staff aktif, sesi selesai, sales attributed, dan settlement eligible per bulan.</p>
                    </div>
                  </div>
                  <AdminDataTable
                    columns={ptTrainerTrendColumns}
                    data={ptTrainerData.trends}
                    progressPending={loading}
                    pagination={false}
                    highlightOnHover
                    emptyTitle="Belum ada tren staff private session"
                    emptyDescription="Ubah periode atau filter report untuk melihat tren bulanan staff private session."
                  />
                </section>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Detail trainer</span>
                      <h2>Daftar performa staff private session</h2>
                      <p>Audit staff berdasarkan client handled, paket terjual, sesi selesai, cancel, no-show, completion rate, dan estimasi settlement.</p>
                    </div>
                    <span className="soft-stat">{formatCount(totalRows)} trainer</span>
                  </div>
                  <AdminDataTable
                    columns={ptTrainerColumns}
                    data={ptTrainerData.results}
                    progressPending={loading || bootstrapping}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={perPage => {
                      setRowsPerPage(perPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    emptyTitle="Belum ada data staff private session"
                    emptyDescription="Pilih staff, outlet, status bayar, status sesi, atau periode untuk melihat performa staff private session."
                  />
                </section>
              </>
            ) : (
              <>
                {renderMembershipGrowthAISection()}

                <div className="report-center-kpi-grid">
                  {summaryCards.map(item => {
                    const Icon = item.icon;
                    return (
                      <section key={item.key} className="section-card report-center-kpi-card">
                        <span className="report-center-kpi-card__icon">
                          <Icon />
                        </span>
                        <div className="report-center-kpi-card__copy">
                          <p>{item.label}</p>
                          <strong>{item.value}</strong>
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="report-center-breakdown-grid">
                  {renderBreakdownCard('Breakdown status', 'Distribution', reportData.status_breakdown)}
                  {renderBreakdownCard('Breakdown payment method', 'Distribution', reportData.payment_method_breakdown)}
                  {renderBreakdownCard('Breakdown jenis transaksi', 'Sales source', reportData.source_breakdown)}
                  {renderBreakdownCard('Top membership package', 'Package mix', reportData.membership_breakdown)}
                  {renderBreakdownCard('Top processor', 'Execution', reportData.processor_breakdown)}
                </div>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Daily trend</span>
                      <h2>Tren penjualan harian</h2>
                      <p>Lihat pola transaksi dan nominal harian untuk membaca momentum penjualan membership.</p>
                    </div>
                  </div>
                  <AdminDataTable
                    columns={trendColumns}
                    data={reportData.trends}
                    progressPending={loading}
                    pagination={false}
                    highlightOnHover
                    emptyTitle="Belum ada tren penjualan"
                    emptyDescription="Ubah periode atau filter report untuk melihat tren harian."
                  />
                </section>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Detail transaksi</span>
                      <h2>Daftar penjualan membership</h2>
                      <p>Gunakan tabel ini untuk audit transaksi satu per satu berdasarkan member, paket, metode bayar, status, dan petugas.</p>
                    </div>
                    <span className="soft-stat">{formatCount(totalRows)} transaksi</span>
                  </div>
                  <AdminDataTable
                    columns={detailColumns}
                    data={reportData.results}
                    progressPending={loading || bootstrapping}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={perPage => {
                      setRowsPerPage(perPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    emptyTitle="Belum ada data membership sales"
                    emptyDescription="Pilih outlet atau periode yang ingin dianalisis untuk melihat transaksi membership."
                  />
                </section>
              </>
            )}
          </>
        ) : null}

        {activeTab === 'pos' ? (
          <>
            <section className="section-card report-center-shell">
              <div className="report-center-section-head">
                <div>
                  <span className="page-eyebrow">POS reports</span>
                  <h2>Pilih report POS</h2>
                  <p>Sales Summary, Cashier Sales Summary, Product Sales Report, Pembatalan Transaksi, Purchase Report, Vendor Aging, Stock Movement, Stock Opname Variance, BOM Consumption Report, dan Vendor Price Comparison sudah aktif dengan pola tampilan yang sama supaya audit POS, stok, vendor, dan kebocoran transaksi tetap konsisten di satu tempat.</p>
                </div>
              </div>
              <div className="report-center-secondary-switch" role="tablist" aria-label="Pilih report POS">
                {availablePosReports.map(item => (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={activePosReport === item.key}
                    className={`report-center-report-button ${activePosReport === item.key ? 'is-active' : ''} ${item.available ? '' : 'is-disabled'}`.trim()}
                    onClick={() => handlePosReportChange(item.key)}
                    disabled={!item.available}
                  >
                    <strong>{item.label}</strong>
                    <span className="report-center-report-button__status">
                      {item.available ? 'Ready' : 'Next'}
                    </span>
                  </button>
                ))}
              </div>
              <div className="report-center-primary-switch__active-copy">
                <strong>Report aktif: {activePosReportMeta.label}</strong>
                <p>{activePosReportMeta.copy}</p>
              </div>
            </section>

            {renderPosFilterCard()}

            {backendError ? (
              <section className="section-card report-center-alert-card">
                <div>
                  <span className="page-eyebrow">Connection issue</span>
                  <h2>{activePosReport === 'purchase-report'
                    ? 'Purchase Report belum bisa dimuat'
                    : activePosReport === 'cashier-sales-summary'
                      ? 'Cashier Sales Summary belum bisa dimuat'
                    : activePosReport === 'product-sales-report'
                      ? 'Product Sales Report belum bisa dimuat'
                    : activePosReport === 'bom-consumption-report'
                      ? 'BOM Consumption Report belum bisa dimuat'
                    : activePosReport === 'refund-void-report'
                      ? 'Refund / Void Report belum bisa dimuat'
                    : activePosReport === 'vendor-aging'
                      ? 'Vendor Aging belum bisa dimuat'
                      : activePosReport === 'stock-movement'
                        ? 'Stock Movement belum bisa dimuat'
                        : activePosReport === 'stock-opname-variance'
                          ? 'Stock Opname Variance belum bisa dimuat'
                        : activePosReport === 'vendor-price-comparison'
                          ? 'Vendor Price Comparison belum bisa dimuat'
                      : 'POS Sales Summary belum bisa dimuat'}</h2>
                  <p>{backendError}</p>
                </div>
              </section>
            ) : null}

            {activePosReport === 'purchase-report' ? (
              <>
                {renderPurchaseSectionTabs()}

                {activePurchaseSection === 'summary' ? renderPurchaseSummarySection() : null}
                {activePurchaseSection === 'analysis' ? renderPurchaseAnalysisSection() : null}
                {activePurchaseSection === 'detail' ? renderPurchaseDetailSection() : null}
              </>
            ) : activePosReport === 'refund-void-report' ? (
              <>
                {renderRefundVoidSummarySection()}
                {renderRefundVoidAnalysisSection()}
                {renderRefundVoidDetailSection()}
              </>
            ) : activePosReport === 'cashier-sales-summary' ? (
              <>
                {renderCashierSalesSummarySection()}
                {renderCashierSalesAnalysisSection()}
                {renderCashierSalesDetailSection()}
              </>
            ) : activePosReport === 'product-sales-report' ? (
              <>
                {renderProductSalesSummarySection()}
                {renderProductSalesAnalysisSection()}
                {renderProductSalesDetailSection()}
              </>
            ) : activePosReport === 'bom-consumption-report' ? (
              <>
                {renderBOMConsumptionSummarySection()}
                {renderBOMConsumptionAnalysisSection()}
                {renderBOMConsumptionDetailSection()}
              </>
            ) : activePosReport === 'vendor-aging' ? (
              <>
                {renderVendorAgingSummarySection()}
                {renderVendorAgingAnalysisSection()}
                {renderVendorAgingDetailSection()}
              </>
            ) : activePosReport === 'stock-movement' ? (
              <>
                {renderStockMovementSummarySection()}
                {renderStockMovementAnalysisSection()}
                {renderStockMovementDetailSection()}
              </>
            ) : activePosReport === 'stock-opname-variance' ? (
              <>
                {renderStockOpnameVarianceSummarySection()}
                {renderStockOpnameVarianceAnalysisSection()}
                {renderStockOpnameVarianceDetailSection()}
              </>
            ) : activePosReport === 'vendor-price-comparison' ? (
              <>
                {renderVendorPriceSectionTabs()}

                {activeVendorPriceSection === 'summary' ? renderVendorPriceSummarySection() : null}
                {activeVendorPriceSection === 'analysis' ? renderVendorPriceAnalysisSection() : null}
                {activeVendorPriceSection === 'detail' ? renderVendorPriceDetailSection() : null}
              </>
            ) : (
              <>
                {renderPOSPricingAISection()}

                <div className="report-center-kpi-grid report-center-kpi-grid--wide">
                  {posSalesSummaryCards.map(item => {
                    const Icon = item.icon;
                    return (
                      <section key={item.key} className="section-card report-center-kpi-card">
                        <span className="report-center-kpi-card__icon">
                          <Icon />
                        </span>
                        <div className="report-center-kpi-card__copy">
                          <p>{item.label}</p>
                          <strong>{item.value}</strong>
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="report-center-breakdown-grid">
                  {renderBreakdownCard('Payment method mix', 'Payment', posSalesData.payment_method_breakdown)}
                  {renderBreakdownCard('Sebaran outlet', 'Outlet', posSalesData.outlet_breakdown)}
                  {renderBreakdownCard('Transaksi per kasir', 'Cashier', posSalesData.cashier_breakdown)}
                  {renderBreakdownCard('Top produk terjual', 'Product', posSalesData.product_breakdown, { countLabel: 'qty' })}
                </div>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Daily trend</span>
                      <h2>Tren penjualan POS harian</h2>
                      <p>Lihat jumlah transaksi, item keluar, dan omzet per hari untuk membaca ritme penjualan kasir.</p>
                    </div>
                  </div>
                  <AdminDataTable
                    columns={posSalesTrendColumns}
                    data={posSalesData.trends}
                    progressPending={loading}
                    pagination={false}
                    highlightOnHover
                    emptyTitle="Belum ada tren penjualan POS"
                    emptyDescription="Pilih outlet atau periode untuk melihat tren penjualan POS."
                  />
                </section>

                <section className="section-card table-card">
                  <div className="report-center-section-head">
                    <div>
                      <span className="page-eyebrow">Detail transaksi</span>
                      <h2>Daftar penjualan POS</h2>
                      <p>Audit transaksi kasir berdasarkan outlet, payment method, item terjual, tax, discount, total, dan status sync accounting.</p>
                    </div>
                    <span className="soft-stat">{formatCount(totalRows)} transaksi</span>
                  </div>
                  <AdminDataTable
                    columns={posSalesColumns}
                    data={posSalesPageRows}
                    progressPending={loading || bootstrapping}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={rowsPerPage}
                    onChangePage={page => setCurrentPage(page)}
                    onChangeRowsPerPage={perPage => {
                      setRowsPerPage(perPage);
                      setCurrentPage(1);
                    }}
                    highlightOnHover
                    emptyTitle="Belum ada data POS sales"
                    emptyDescription="Ubah outlet, payment method, atau periode untuk melihat transaksi POS."
                  />
                </section>
              </>
            )}
          </>
        ) : null}

        {activeTab === 'accounting' ? (
          <>
            <section className="section-card report-center-shell">
              <div className="report-center-section-head">
                <div>
                  <span className="page-eyebrow">Accounting reports</span>
                  <h2>Pilih report accounting</h2>
                  <p>Accounting sekarang mencakup Journal Report, General Ledger, Trial Balance, Profit & Loss, Balance Sheet, Cash Flow, AP Aging, dan Expense Breakdown supaya audit ledger, hutang, sampai financial statement bisa dibaca di satu workspace.</p>
                </div>
              </div>
              <div className="report-center-secondary-switch" role="tablist" aria-label="Pilih report accounting">
                {availableAccountingReports.map(item => (
                  <button
                    key={item.key}
                    type="button"
                    role="tab"
                    aria-selected={activeAccountingReport === item.key}
                    className={`report-center-report-button ${activeAccountingReport === item.key ? 'is-active' : ''} ${item.available ? '' : 'is-disabled'}`.trim()}
                    onClick={() => handleAccountingReportChange(item.key)}
                    disabled={!item.available}
                  >
                    <strong>{item.label}</strong>
                    <span className="report-center-report-button__status">
                      {item.available ? 'Ready' : 'Next'}
                    </span>
                  </button>
                ))}
              </div>
              <div className="report-center-primary-switch__active-copy">
                <strong>Report aktif: {activeAccountingReportMeta.label}</strong>
                <p>{activeAccountingReportMeta.copy}</p>
              </div>
            </section>

            <section className="section-card report-center-alert-card">
              <div>
                <span className="page-eyebrow">Accounting hub</span>
                <h2>Baca report di sini, tindak lanjuti dari workspace terkait</h2>
                <p>
                  `Analytics Center` adalah jalur utama untuk baca report accounting. Kalau angka perlu dibenahi,
                  lompat langsung ke `Accounting Control`, `Expenses & Payables`, atau `Assets & Investments`
                  tanpa kehilangan scope outlet yang sedang dipakai.
                </p>
              </div>
              <div className="report-center-filter-actions">
                <Button
                  variant="primary"
                  onClick={() => navigate(buildScopedManagementPath('/accounting-setup'))}
                  disabled={!canOpenAccountingControl}
                >
                  <FaCog /> Accounting Control
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate(buildScopedManagementPath('/operasional-finance'))}
                  disabled={!canOpenExpensesPayables}
                >
                  <FaReceipt /> Expenses & Payables
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate(buildScopedManagementPath('/asset-management'))}
                  disabled={!canOpenAssetsInvestments}
                >
                  <FaBuilding /> Assets & Investments
                </Button>
              </div>
            </section>

            {renderAccountingFilterCard()}

            {backendError ? (
              <section className="section-card report-center-alert-card">
                <div>
                  <span className="page-eyebrow">Connection issue</span>
                  <h2>{activeAccountingReport === 'general-ledger'
                    ? 'General Ledger belum bisa dimuat'
                    : activeAccountingReport === 'trial-balance'
                      ? 'Trial Balance belum bisa dimuat'
                    : activeAccountingReport === 'profit-loss'
                      ? 'Profit & Loss belum bisa dimuat'
                    : activeAccountingReport === 'balance-sheet'
                      ? 'Balance Sheet belum bisa dimuat'
                    : activeAccountingReport === 'cash-flow'
                      ? 'Cash Flow belum bisa dimuat'
                    : activeAccountingReport === 'accounts-payable-aging'
                      ? 'AP Aging belum bisa dimuat'
                    : activeAccountingReport === 'expense-breakdown'
                      ? 'Expense Breakdown belum bisa dimuat'
                      : activeAccountingReport === 'asset-register'
                        ? 'Asset Register belum bisa dimuat'
                        : activeAccountingReport === 'depreciation-schedule'
                          ? 'Depreciation Schedule belum bisa dimuat'
                      : 'Journal Report belum bisa dimuat'}</h2>
                  <p>{backendError}</p>
                </div>
              </section>
            ) : null}

            {isJournalReport ? (
              <>
                {renderAccountingJournalSectionTabs()}

                {!backendError ? (
                  <>
                    {activeAccountingJournalSection === 'summary' ? renderAccountingJournalSummarySection() : null}
                    {activeAccountingJournalSection === 'analysis' ? renderAccountingJournalAnalysisSection() : null}
                    {activeAccountingJournalSection === 'detail' ? renderAccountingJournalDetailSection() : null}
                  </>
                ) : null}
              </>
            ) : null}

            {isGeneralLedgerReport ? (
              <>
                {renderAccountingGeneralLedgerSectionTabs()}

                {!backendError ? (
                  <>
                    {activeAccountingGeneralLedgerSection === 'summary' ? renderAccountingGeneralLedgerSummarySection() : null}
                    {activeAccountingGeneralLedgerSection === 'analysis' ? renderAccountingGeneralLedgerAnalysisSection() : null}
                    {activeAccountingGeneralLedgerSection === 'detail' ? renderAccountingGeneralLedgerDetailSection() : null}
                  </>
                ) : null}
              </>
            ) : null}

            {isTrialBalanceReport && !backendError ? renderAccountingTrialBalanceSection() : null}
            {isProfitLossReport && !backendError ? renderAccountingProfitLossSection() : null}
            {isBalanceSheetReport && !backendError ? renderAccountingBalanceSheetSection() : null}
            {isCashFlowReport && !backendError ? renderAccountingCashFlowSection() : null}
            {isAccountsPayableAgingReport && !backendError ? renderAccountingAccountsPayableAgingSection() : null}
            {isExpenseBreakdownReport && !backendError ? renderAccountingExpenseBreakdownSection() : null}
            {isAssetRegisterReport && !backendError ? renderAccountingAssetRegisterSection() : null}
            {isDepreciationScheduleReport && !backendError ? renderAccountingDepreciationScheduleSection() : null}
          </>
        ) : null}
      </Container>

      <div className="report-center-floating-actions" role="group" aria-label="Report quick actions">
        <Button
          variant="primary"
          onClick={handleRefreshReport}
          disabled={!canUseReportActions || loading || bootstrapping || exporting}
          title="Refresh report"
        >
          <FaRedoAlt />
          <span>Refresh</span>
        </Button>
        <Button
          variant="outline-light"
          onClick={handleExportPdf}
          disabled={!canUseReportActions || loading || bootstrapping || exporting}
          title="Export PDF"
        >
          <FaFilePdf />
          <span>PDF</span>
        </Button>
        <Button
          variant="outline-light"
          onClick={handleExportExcel}
          disabled={!canUseReportActions || loading || bootstrapping || exporting}
          title="Export Excel"
        >
          <FaFileExcel />
          <span>Excel</span>
        </Button>
      </div>
    </>
  );
}

export default ReportCenter;
